(function () {
  'use strict';
  const WORKER_URL = 'https://upload.oatandink.com';

  /* ── Canvas / upload constants ── */
  const JPEG_Q = 0.90, PRINT_Q = 1.0;
  /* Increment whenever print geometry, font fitting, crop math, or canvas output
     changes. A cached asset is reusable only when this version and every input
     below match exactly. */
  const PRINT_RENDER_VERSION = 'oi-print-v4';

  function _canonicalJson(value) {
    return JSON.stringify(value, function (_key, item) {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        return Object.fromEntries(Object.keys(item).sort().map(k => [k, item[k]]));
      }
      return item;
    });
  }

  async function _sha256(value) {
    const bytes = new TextEncoder().encode(typeof value === 'string' ? value : _canonicalJson(value));
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('');
  }

  function _primaryFontFamily(family) {
    return String(family || 'Georgia').split(',')[0].trim().replace(/^['"]|['"]$/g, '');
  }

  async function _requireCanvasFonts(families) {
    if (typeof document === 'undefined' || !document.fonts) return;
    await document.fonts.ready;
    const requiredWebFonts = new Set(['Cormorant Garamond', 'Playfair Display', 'Libre Baskerville', 'Work Sans', 'Caveat', 'Great Vibes', 'Special Elite', 'Lora', 'Montserrat']);
    for (const family of new Set(families || [])) {
      const primary = _primaryFontFamily(family);
      const spec = `400 16px "${primary.replace(/"/g, '\\"')}"`;
      const loaded = await document.fonts.load(spec);
      if (!document.fonts.check(spec) || (requiredWebFonts.has(primary) && loaded.length === 0)) {
        throw new Error(`The selected font "${primary}" is not ready. Please check your connection and try again.`);
      }
    }
  }

  function _renderEnvironment() {
    return {
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      platform: typeof navigator !== 'undefined' ? (navigator.userAgentData?.platform || navigator.platform || '') : '',
    };
  }

  /* ── IndexedDB photo store — v2 with per-project scope field ── */
  class PhotoStore {
    async _open() {
      if (this._db) return this._db;
      return new Promise((res, rej) => {
        const req = indexedDB.open('oiPhotos', 2);
        req.onupgradeneeded = e => {
          const db = e.target.result;
          let store;
          if (e.oldVersion < 1) {
            store = db.createObjectStore('photos', { keyPath: 'id', autoIncrement: true });
          } else {
            store = e.target.transaction.objectStore('photos');
          }
          if (!store.indexNames.contains('by_scope')) {
            store.createIndex('by_scope', 'scope', { unique: false });
          }
          /* Quarantine pre-scope records so they never bleed into a new project */
          if (e.oldVersion === 1) {
            store.openCursor().onsuccess = function (ev) {
              const cursor = ev.target.result;
              if (!cursor) return;
              if (!cursor.value.scope) cursor.update({ ...cursor.value, scope: 'legacy' });
              cursor.continue();
            };
          }
        };
        req.onsuccess = e => { this._db = e.target.result; res(this._db); };
        req.onerror   = () => rej(req.error);
      });
    }
    async getAll(scope) {
      const db = await this._open();
      return new Promise((res, rej) => {
        const req = db.transaction('photos', 'readonly')
          .objectStore('photos').index('by_scope').getAll(IDBKeyRange.only(scope));
        req.onsuccess = () => res((req.result || []).filter(r => !r.isCover));
        req.onerror   = () => rej(req.error);
      });
    }
    async getCover(scope) {
      const db = await this._open();
      return new Promise((res, rej) => {
        const req = db.transaction('photos', 'readonly')
          .objectStore('photos').index('by_scope').getAll(IDBKeyRange.only(scope));
        req.onsuccess = () => res((req.result || []).find(r => r.isCover) || null);
        req.onerror   = () => rej(req.error);
      });
    }
    async add(blob, filename, assetKey = null, sourceWidth = null, sourceHeight = null, scope = 'draft:unknown', contentFingerprint = null, originalFileSize = null) {
      const db = await this._open();
      return new Promise((res, rej) => {
        const req = db.transaction('photos', 'readwrite').objectStore('photos')
          .add({ blob, filename, assetKey, sourceWidth, sourceHeight, addedAt: Date.now(), scope, contentFingerprint, originalFileSize });
        req.onsuccess = () => res(req.result);
        req.onerror   = () => rej(req.error);
      });
    }
    async delete(id) {
      const db = await this._open();
      return new Promise((res, rej) => {
        const req = db.transaction('photos', 'readwrite').objectStore('photos').delete(id);
        req.onsuccess = () => res();
        req.onerror   = () => rej(req.error);
      });
    }
    async getById(id) {
      const db = await this._open();
      return new Promise((res, rej) => {
        const req = db.transaction('photos', 'readonly').objectStore('photos').get(id);
        req.onsuccess = () => res(req.result || null);
        req.onerror   = () => rej(req.error);
      });
    }
    async clearByScope(scope) {
      const db = await this._open();
      return new Promise((res, rej) => {
        const tx  = db.transaction('photos', 'readwrite');
        const req = tx.objectStore('photos').index('by_scope').openCursor(IDBKeyRange.only(scope));
        req.onsuccess = ev => { const c = ev.target.result; if (c) { c.delete(); c.continue(); } };
        tx.oncomplete = () => res();
        tx.onerror    = () => rej(tx.error);
      });
    }
    async adoptScope(oldScope, newScope) {
      const db = await this._open();
      return new Promise((res, rej) => {
        const tx  = db.transaction('photos', 'readwrite');
        const req = tx.objectStore('photos').index('by_scope').openCursor(IDBKeyRange.only(oldScope));
        req.onsuccess = ev => { const c = ev.target.result; if (c) { c.update({ ...c.value, scope: newScope }); c.continue(); } };
        tx.oncomplete = () => res();
        tx.onerror    = () => rej(tx.error);
      });
    }
  }

  /* ── Canvas helpers ── */
  function _loadImg(url, timeoutMs = 30_000) {
    return new Promise((res, rej) => {
      const img = new Image();
      const t = setTimeout(() => rej(new Error('image decode timed out')), timeoutMs);
      img.onload  = () => { clearTimeout(t); res(img); };
      img.onerror = () => { clearTimeout(t); rej(new Error('load failed')); };
      img.src = url;
    });
  }

  /* ── Decode geometry control ───────────────────────────────────────────────
     Order 1026 printed a 123 MP panorama stretched 13% horizontally. The cause
     was not the fit maths: _drawFit always samples a source rect whose aspect
     equals the destination's. It was the DECODE. Handed an image that large, a
     browser may decode it at a reduced size while naturalWidth/naturalHeight
     still describe the file — so the source rectangle is expressed in one
     coordinate space and sampled from a raster of another shape, which scales
     the axes unequally. It only bites photos big enough to trip the limit,
     which is why a handful of spreads were affected and the rest were fine.

     The fix is to decide the raster size ourselves instead of leaving it to the
     browser. Nothing here modifies, resizes or re-encodes a stored file: the
     original bytes in IndexedDB and Drive are the archive and are never
     rewritten. This governs only the in-memory raster used for one draw. */

  /* Dimensions an image declares in its own header, so we can check what the
     decoder handed back against what the file actually claims.

     Covers PNG and WebP as well as JPEG. Customers hand us all three — a
     storybook cover arrived as a 1024x1536 PNG named .jpg, and because only
     JPEG was understood, that file got neither a controlled decode nor the
     geometry check, silently falling back to whatever the browser chose.
     Extensions and MIME types lie; the magic bytes do not. */
  async function _declaredImageSize(blob) {
    try {
      const head = new DataView(await blob.slice(0, Math.min(blob.size, 256 * 1024)).arrayBuffer());
      if (head.byteLength < 32) return null;

      /* PNG: 8-byte signature, then IHDR carrying width and height. */
      if (head.getUint32(0, false) === 0x89504E47 && head.getUint32(4, false) === 0x0D0A1A0A) {
        if (head.getUint32(12, false) !== 0x49484452) return null;   /* 'IHDR' */
        const w = head.getUint32(16, false), h = head.getUint32(20, false);
        return (w && h) ? { w, h } : null;
      }

      /* WebP: RIFF container; VP8 / VP8L / VP8X each store size differently. */
      if (head.getUint32(0, false) === 0x52494646 && head.getUint32(8, false) === 0x57454250) {
        const fourcc = head.getUint32(12, false);
        if (fourcc === 0x56503858) {                                  /* VP8X */
          const w = 1 + (head.getUint8(24) | (head.getUint8(25) << 8) | (head.getUint8(26) << 16));
          const h = 1 + (head.getUint8(27) | (head.getUint8(28) << 8) | (head.getUint8(29) << 16));
          return { w, h };
        }
        if (fourcc === 0x56503820) {                                  /* VP8  (lossy) */
          return { w: head.getUint16(26, true) & 0x3FFF, h: head.getUint16(28, true) & 0x3FFF };
        }
        if (fourcc === 0x5650384C) {                                  /* VP8L (lossless) */
          const b0 = head.getUint8(21), b1 = head.getUint8(22), b2 = head.getUint8(23), b3 = head.getUint8(24);
          return { w: 1 + (((b1 & 0x3F) << 8) | b0),
                   h: 1 + (((b3 & 0x0F) << 10) | (b2 << 2) | ((b1 & 0xC0) >> 6)) };
        }
        return null;
      }

      if (head.getUint16(0, false) !== 0xFFD8) return null;           /* not a JPEG either */
      let w = 0, h = 0, orientation = 1, off = 2;
      while (off + 4 <= head.byteLength) {
        if (head.getUint8(off) !== 0xFF) { off++; continue; }
        const marker = head.getUint8(off + 1);
        off += 2;
        if (marker === 0xD9 || marker === 0xDA) break;
        if (marker === 0x01 || (marker >= 0xD0 && marker <= 0xD8)) continue;
        if (off + 2 > head.byteLength) break;
        const len = head.getUint16(off, false);
        if (len < 2 || off + len > head.byteLength) break;
        /* SOF0..SOF15 carry the frame size; DHT/JPG/DAC are not frame headers. */
        if (marker >= 0xC0 && marker <= 0xCF && marker !== 0xC4 && marker !== 0xC8 && marker !== 0xCC) {
          h = head.getUint16(off + 3, false); w = head.getUint16(off + 5, false);
        } else if (marker === 0xE1 && len >= 10 && head.getUint32(off + 2, false) === 0x45786966) {
          const tiff = off + 8;
          const little = head.getUint16(tiff, false) === 0x4949;
          if (tiff + 8 <= head.byteLength && head.getUint16(tiff + 2, little) === 42) {
            const ifd = tiff + head.getUint32(tiff + 4, little);
            if (ifd + 2 <= head.byteLength) {
              const n = head.getUint16(ifd, little);
              for (let i = 0; i < n; i++) {
                const e = ifd + 2 + i * 12;
                if (e + 12 > head.byteLength) break;
                if (head.getUint16(e, little) === 0x0112) { orientation = head.getUint16(e + 8, little); break; }
              }
            }
          }
        }
        off += len;
      }
      if (!w || !h) return null;
      /* Orientations 5–8 rotate by 90°, so the displayed frame is transposed. */
      return (orientation >= 5 && orientation <= 8) ? { w: h, h: w } : { w, h };
    } catch (_) { return null; }
  }

  /* Raster size to decode at for a given destination. Never upscales, never
     drops below the destination's own resolution, and keeps 2x linear headroom
     so the downsample into the slot is supersampled rather than point-sampled —
     print output is therefore identical to a full-size decode, because a
     2160x2700 slot cannot carry more detail than this provides. */
  const DECODE_SUPERSAMPLE = 2;
  const MAX_DECODE_PX      = 80e6;
  function _decodeDimsFor(nw, nh, destW, destH, zoom = 1) {
    const ca = destW / destH, ia = nw / nh;
    const cropW = ia > ca ? nh * ca : nw;            /* cover-fit crop at native res */
    /* Zoom shrinks the sampled sub-rect: _drawFit takes cropW/s and stretches it
       across the whole destination, so a 3x zoom needs 3x the source pixels to
       hold the same output resolution. Sizing without it silently softened every
       zoomed photo. Zoom-out (s < 1) samples beyond the crop and needs no extra. */
    const z = Math.max(1, zoom || 1);
    let k = Math.min(1, (destW * DECODE_SUPERSAMPLE * z) / cropW);
    k = Math.max(k, Math.min(1, (destW * z) / cropW)); /* never below 1:1 after zoom */
    const px = nw * k * nh * k;
    if (px > MAX_DECODE_PX) k *= Math.sqrt(MAX_DECODE_PX / px);
    return { w: Math.max(1, Math.round(nw * k)), h: Math.max(1, Math.round(nh * k)) };
  }

  /* Decode `blob` to a raster of known geometry for drawing into destW x destH.
     Returns { src, release }. Throws rather than returning a mis-shaped raster:
     a blocked checkout the customer can retry beats a stretched printed book. */
  async function _decodeForDraw(blob, destW, destH, zoom = 1) {
    const declared = await _declaredImageSize(blob);
    if (declared && typeof createImageBitmap === 'function') {
      const d = _decodeDimsFor(declared.w, declared.h, destW, destH, zoom);
      try {
        const bm = await createImageBitmap(blob, {
          resizeWidth: d.w, resizeHeight: d.h, resizeQuality: 'high', imageOrientation: 'from-image',
        });
        if (bm.width === d.w && bm.height === d.h) return { src: bm, release: () => { try { bm.close(); } catch (_) {} } };
        try { bm.close(); } catch (_) {}
      } catch (_) { /* option unsupported — fall through to the checked <img> path */ }
    }
    const url = URL.createObjectURL(blob);
    try {
      const img = await _loadImg(url);
      if (declared) {
        const got = img.naturalWidth / img.naturalHeight, want = declared.w / declared.h;
        if (!(got > 0) || Math.abs(got / want - 1) > 0.01) {
          throw new Error('This photo could not be decoded at its true proportions on this device — '
            + 'it would print distorted. Please try a different browser, or re-save the photo and add it again.');
        }
      }
      return { src: img, release: () => URL.revokeObjectURL(url) };
    } catch (e) { URL.revokeObjectURL(url); throw e; }
  }
  /* ── Layout geometry helper ──
     Converts grid cell specs [row-start, col-start, row-end, col-end] (1-based, exclusive)
     into normalized {x,y,w,h} rectangles (0–1 of canvas). G = inter-slot gap fraction.
     Edge slots bleed to the canvas boundary (no gap at outer edges). */
  function _L(rows, cols, cells, G) {
    const g = G || 0;
    return cells.map(([rs, cs, re, ce]) => {
      const x0 = (cs - 1) / cols, x1 = (ce - 1) / cols;
      const y0 = (rs - 1) / rows, y1 = (re - 1) / rows;
      return {
        x: x0 + (x0 > 0 ? g / 2 : 0),
        y: y0 + (y0 > 0 ? g / 2 : 0),
        w: (x1 - x0) - (x0 > 0 ? g / 2 : 0) - (x1 < 1 ? g / 2 : 0),
        h: (y1 - y0) - (y0 > 0 ? g / 2 : 0) - (y1 < 1 ? g / 2 : 0),
      };
    });
  }
  const _PAGE_G = 0.06, _SPRD_G = 0.03;

  /* Pan/zoom: normalized nx/ny ∈ [-1, 1] where ±1 = maximum pan before edge exposure.
     Canvas: pan in source-image space = nx * (coverFitW - zoomedW) / 2
     CSS:    pan in CSS pixels         = nx * slotW * (scale - 1) / 2            */
  /* The cover-fit window and the zoomed sampling window, in source pixels.
     _drawFit draws with these; the drag handlers size their pixel sensitivity
     from them. Sharing one definition keeps dragging and the sliders in step —
     they drifted apart when the pan range changed. */
  function _fitWindow(iw, ih, w, h, s) {
    const ia = iw / ih, ca = w / h;
    const bw = ia > ca ? ih * ca : iw;
    const bh = ia > ca ? ih : iw / ca;
    return { bw, bh, sw: bw / s, sh: bh / s };
  }

  /* Slot pixels travelled per unit of nx/ny, matching _drawFit's pan range of
     the full source. nx/ny sweep [-1,1], so one unit is half the total travel;
     source pixels convert to slot pixels by the window's own magnification. */
  function _panTravelPx(iw, ih, w, h, s) {
    const { sw, sh } = _fitWindow(iw, ih, w, h, Math.max(0.5, s || 1));
    const travelX = (Math.abs(iw - sw) / 2) * (w / sw);
    const travelY = (Math.abs(ih - sh) / 2) * (h / sh);
    /* An axis the cover-crop fills exactly has nothing to pan within, and the
       renderer moves it 0px. Infinity makes drag delta / travel == 0 there, so
       that axis is inert rather than hyper-sensitive from a small divisor. */
    return {
      maxX: travelX > 0.5 ? travelX : Infinity,
      maxY: travelY > 0.5 ? travelY : Infinity,
    };
  }

  /* Which per-spread entries a local backup should contribute back on restore.

     _autoSaveDraft has always written layouts and spreadBg into oiTextBackup
     beside the text, but the merge only ever read the text back, so a layout
     change that never reached the server sat in localStorage while
     _loadFromServer overwrote state.layouts with the server's older copy. The
     customer's work was present on the device and discarded on open.

     Resurrecting a stale layout over good server work would be worse than the
     bug being fixed, so recovery is tiered on evidence of freshness:

       override - both sides carry a savedAt and the local one is strictly
                  newer, i.e. this device holds edits the server never got.
       fill     - no two timestamps to compare (a draft last written before
                  savedAt shipped). Only keys the server has no value for are
                  contributed, which cannot overwrite server state at any age.

     Pure and side-effect free, so the unit tests can drive it with no DOM, no
     server and no localStorage. */
  function _backupLayoutPlan(serverState, backup) {
    const plan = { layouts: {}, spreadBg: {}, mode: 'none', keys: [] };
    if (!serverState || typeof serverState !== 'object') return plan;
    if (!backup      || typeof backup      !== 'object') return plan;
    const localAt  = Date.parse(backup.savedAt      || '');
    const serverAt = Date.parse(serverState.savedAt || '');
    const comparable = Number.isFinite(localAt) && Number.isFinite(serverAt);
    /* The server already holds this device's work, or newer work from another. */
    if (comparable && localAt <= serverAt) return plan;
    plan.mode = comparable ? 'override' : 'fill';
    for (const map of ['layouts', 'spreadBg']) {
      const src = backup[map];
      if (!src || typeof src !== 'object') continue;
      const srv = (serverState[map] && typeof serverState[map] === 'object') ? serverState[map] : {};
      for (const [k, v] of Object.entries(src)) {
        if (v === undefined || v === null) continue;
        const have = srv[k];
        const missing = have === undefined || have === null;
        /* In fill mode a value the server already holds always wins. In override
           mode it loses only to a genuinely different local value. */
        if (!missing && (plan.mode !== 'override' || JSON.stringify(have) === JSON.stringify(v))) continue;
        plan[map][k] = v;
        plan.keys.push(map + '.' + k);
      }
    }
    return plan;
  }

  /* Cover text character limits, set from every order to Oct 2026: longest title
     27, subtitle 44, spine 24. The boxes enforce them; a book saved with longer
     text keeps it and is simply set smaller by coverTextScale. */
  const COVER_TEXT_MAX = { title: 28, subtitle: 48, spineText: 30 };

  /* Cover text stays on one line by getting smaller, never by squeezing the
     letters (canvas maxWidth) or wrapping. Preview and print both call this
     with the same proportions, so they shrink by the same ratio.
     size is the normal font size, spacingRatio the letter-spacing as a fraction
     of it, maxWidth the room in the same units. Returns a scale ≤ 1. */
  let _fitCtx = null;
  /* Cormorant Garamond's numbers are old-style (a "1" is x-height), so in a
     capitals title "1ST" the 1 sits low. Cover text takes its digits from
     Cormorant Infant, the same design with full-height numbers: the theme loads
     Infant with only 0-9 in it, so every letter still falls back to Garamond. */
  const COVER_DIGITS_FAMILY = "'Cormorant Infant'";
  function coverDrawFamily(family) {
    return _primaryFontFamily(family) === 'Cormorant Garamond' ? `${COVER_DIGITS_FAMILY},${family}` : family;
  }
  function coverTextScale(text, { weight = 400, family, size, spacingRatio = 0, maxWidth }) {
    if (!text || !(maxWidth > 0) || !(size > 0) || typeof document === 'undefined') return 1;
    if (!_fitCtx) _fitCtx = document.createElement('canvas').getContext('2d');
    const ref = 100, spacing = ref * spacingRatio;
    _fitCtx.font = `${weight} ${ref}px ${family}`;
    let w;
    if ('letterSpacing' in _fitCtx) { _fitCtx.letterSpacing = spacing + 'px'; w = _fitCtx.measureText(text).width; }
    else w = _fitCtx.measureText(text).width + spacing * text.length;
    return Math.min(1, maxWidth / (w / ref * size));
  }

  function _drawFit(ctx, img, x, y, w, h, tf) {
    /* An ImageBitmap exposes width/height; an HTMLImageElement naturalWidth/Height. */
    const iw = img.naturalWidth  || img.width;
    const ih = img.naturalHeight || img.height;
    const ia = iw / ih, ca = w / h;
    let bx, by, bw, bh;
    if (ia > ca) { bh = ih; bw = bh * ca; bx = (iw - bw) / 2; by = 0; }
    else         { bw = iw; bh = bw / ca; bx = 0; by = (ih - bh) / 2; }
    /* scale < 1 (zoom out): sw/sh below grow larger than the cover-fit rect, sampling
       more of the source image — correctly centered by the same algebra used for
       scale > 1. If that larger rect extends past the image bounds, ctx.drawImage's
       native partial-source behavior leaves the rest of (x,y,w,h) untouched, which is
       exactly the spread's already-painted background (see applySpreadBackground call
       at the top of renderSpreadFromSlots) — no separate letterboxing logic needed. */
    const s  = Math.max(0.5, tf?.scale || 1);
    const nx = tf?.nx || 0;
    const ny = tf?.ny || 0;
    const fp = tf?.fp;
    let sw = bw / s, sh = bh / s;
    let sx, sy;
    if (fp && s === 1 && nx === 0 && ny === 0) {
      sx = Math.max(0, Math.min(iw - sw, fp.x * iw - sw / 2));
      sy = Math.max(0, Math.min(ih - sh, fp.y * ih - sh / 2));
    } else {
      /* Pan across the WHOLE source, not just the cover-fit window.
         The old form travelled within [by, by+bh] — the band the initial
         cover-crop happened to select — so the slivers it discarded were
         unreachable at any zoom. On a photo whose aspect is close to the
         slot's that is a few per cent; on a 0.46 panorama in a 0.8 slot it
         strands 42% of the image, which is why panning "stopped moving"
         before reaching the edges.

         Math.abs keeps the direction consistent when zoomed out, where the
         window is larger than the image and (iw - sw) goes negative.

         At s = 1 this is algebraically identical to the previous
         bx*(1-nx) / by*(1-ny) form, so unzoomed framing is unchanged. */
      sx = (iw - sw) / 2 - nx * Math.abs(iw - sw) / 2;
      sy = (ih - sh) / 2 - ny * Math.abs(ih - sh) / 2;
    }
    /* Zooming out below scale 1 grows the sampled rectangle past the edges of
       the photo. The old code relied on drawImage clipping that overhang and
       leaving the rest of the destination as already-painted background:

         "ctx.drawImage's native partial-source behavior leaves the rest of
          (x,y,w,h) untouched ... no separate letterboxing logic needed"

       That is true in Chromium. It is false in WebKit when the source is an
       ImageBitmap, which is exactly what the print path passes: WebKit paints
       NOTHING AT ALL, while the on-screen preview — which passes an
       HTMLImageElement — keeps drawing the photo perfectly. So the customer saw
       their picture on screen and the print file came out blank, with no error
       anywhere. Measured, same geometry, both engines:

         zoom-out 0.926   webkit/ImageBitmap   0%     <- the fault
                          webkit/HTMLImage    93%     <- what they saw
                          chromium  both      93%

       Order 1029 lost nine photographs this way and order 1032 lost its first
       page at scale 0.5. Do the letterboxing here instead of trusting the
       engine: clip the source rect into the image and shrink the destination by
       the same proportion, so the rectangle handed to drawImage is always
       inside the bitmap and every engine agrees. A rect already inside the
       image is untouched, so nothing at scale >= 1 changes by even a pixel. */
    const clipL = Math.max(0, -sx), clipT = Math.max(0, -sy);
    const clipR = Math.max(0, sx + sw - iw), clipB = Math.max(0, sy + sh - ih);
    if (clipL || clipT || clipR || clipB) {
      const pxPerSrcX = w / sw, pxPerSrcY = h / sh;
      sx += clipL; sy += clipT;
      sw -= clipL + clipR; sh -= clipT + clipB;
      x  += clipL * pxPerSrcX; y += clipT * pxPerSrcY;
      w  -= (clipL + clipR) * pxPerSrcX;
      h  -= (clipT + clipB) * pxPerSrcY;
      /* Nothing of the photo falls inside the slot — leave the background. */
      if (!(sw > 0 && sh > 0 && w > 0 && h > 0)) return;
    }
    ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
  }

  /* Front panel of a flat cover wrap (back | spine | front, turn-in all round), as a
     small JPEG object URL for list thumbnails. Geometry is what renderCoverWrapBlob
     prints with at 300 DPI; an unexpected size falls back to the right half, and
     anything that is not a wide wrap is returned as it is. Same cut as My Books. */
  function _frontCoverThumbUrl(blob, maxEdge) {
    return new Promise(resolve => {
      const src = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        const W = img.naturalWidth, H = img.naturalHeight;
        const WRAP = Math.round(COVER_WRAP_IN * 300), SPINE = Math.round(SPINE_WIDTH_IN * 300);
        let pw = (W - 2 * WRAP - SPINE) / 2, ph = H - 2 * WRAP, sx = W - WRAP - pw, sy = WRAP;
        if (!(W / H > 1.3 && pw > 0 && ph > 0 && pw / ph > 0.5 && pw / ph < 1.8)) {
          if (W / H > 1.3) { pw = W / 2; ph = H; sx = W / 2; sy = 0; }
          else { resolve(src); return; }
        }
        const f = Math.min(1, maxEdge / Math.max(pw, ph));
        const c = document.createElement('canvas');
        c.width = Math.round(pw * f); c.height = Math.round(ph * f);
        const ctx = c.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, sx, sy, pw, ph, 0, 0, c.width, c.height);
        c.toBlob(b => {
          if (!b) { resolve(src); return; }
          URL.revokeObjectURL(src);
          resolve(URL.createObjectURL(b));
        }, 'image/jpeg', 0.88);
      };
      img.onerror = () => resolve(src);
      img.src = src;
    });
  }

  /* ── Decoded-photo cache for the on-screen slots ──
     Every page change rebuilds the spread with fresh <img> elements, and each
     one had to be decoded again before its slot canvas could draw — profiled
     at ~150 ms per page change on desktop, every time, even for pages already
     seen. A slot now draws from a decoded copy kept here, so returning to a
     page (or arriving at a neighbour, warmed in advance) draws immediately.

     The copy is a plain canvas drawn from the decoded <img>, not an
     ImageBitmap, so EXIF orientation is applied exactly as the old
     drawImage(img) did. It is capped at 2048px on its long side (in-session
     uploads are already 1600px previews, so those are copied 1:1); when a
     slot is zoomed past what the copy holds, _oiDraw draws the original
     instead, so close crops stay as sharp as before. Display only — the print
     path never reads this. Least-recently-used copies are dropped past a pixel
     budget that is smaller on phones. */
  const _SLOT_CACHE_EDGE   = 2048;
  const _SLOT_CACHE_MAX_PX = (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) ? 48e6 : 160e6;
  const _slotCache = new Map();   /* absolute src -> { canvas, px } once decoded, { pending } before */
  let _slotCachePx = 0;
  function _slotCacheKey(src) { try { return new URL(src, location.href).href; } catch (_) { return String(src); } }
  function _slotCacheGet(src) {
    const k = _slotCacheKey(src), e = _slotCache.get(k);
    if (!e || !e.canvas) return null;
    _slotCache.delete(k); _slotCache.set(k, e);   /* Map order = recency */
    return e.canvas;
  }
  function _slotCacheTrim() {
    for (const [k, e] of _slotCache) {
      if (_slotCachePx <= _SLOT_CACHE_MAX_PX) break;
      if (!e.canvas) continue;
      _slotCache.delete(k); _slotCachePx -= e.px;
      e.canvas.width = e.canvas.height = 0;
    }
  }
  /* Decode src off the main thread (img.decode) and keep a copy. Pass the slot's
     own <img> to reuse its fetch; otherwise a detached Image is used, which is
     how neighbouring pages are warmed before they are opened. Resolves to the
     copy, or null if the image cannot be decoded. */
  function _slotCacheWarm(src, imgEl) {
    if (!src) return Promise.resolve(null);
    const k = _slotCacheKey(src);
    const have = _slotCache.get(k);
    if (have) return have.canvas ? Promise.resolve(have.canvas) : have.pending;
    let img = imgEl;
    if (!img || typeof img.decode !== 'function' || _slotCacheKey(img.src) !== k) {
      img = new Image();
      img.decoding = 'async';
      img.src = src;
    }
    const pending = img.decode().then(() => {
      const iw = img.naturalWidth, ih = img.naturalHeight;
      if (!iw || !ih) return null;
      const f = Math.min(1, _SLOT_CACHE_EDGE / Math.max(iw, ih));
      const c = document.createElement('canvas');
      c.width  = Math.max(1, Math.round(iw * f));
      c.height = Math.max(1, Math.round(ih * f));
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      if (_slotCache.get(k)?.pending !== pending) return c;
      _slotCache.set(k, { canvas: c, px: c.width * c.height });
      _slotCachePx += c.width * c.height;
      _slotCacheTrim();
      return c;
    }).catch(() => null).then(c => {
      if (!c && _slotCache.get(k)?.pending === pending) _slotCache.delete(k);
      return c;
    });
    _slotCache.set(k, { pending });
    return pending;
  }

  /* Serialize a contentEditable text-zone box to plain text, converting DOM line-break
     structure (<br>, block-element boundaries from Enter) back into literal '\n'.
     .textContent alone concatenates every text node with no separator and silently
     drops line breaks; this walks the tree and reinserts them. */
  function _tzEditorText(root) {
    /* Serialize logical lines, not CSS paragraph spacing. A final BR is the
       browser's empty-line caret placeholder; a blank DIV still contributes its
       block boundary. Handle boundaries after blocks as well as before them. */
    const read = parent => {
      let text = '', previousBlock = false, seen = false;
      for (const node of parent.childNodes) {
        if (node.nodeType !== Node.TEXT_NODE && node.nodeType !== Node.ELEMENT_NODE) continue;
        const block = /^(DIV|P|LI|H[1-6]|BLOCKQUOTE|PRE)$/.test(node.nodeName);
        let value;
        if (node.nodeType === Node.TEXT_NODE) value = node.nodeValue;
        else if (node.nodeName === 'BR') value = node === parent.lastChild ? '' : '\n';
        else value = read(node);
        if (!value && !block) continue;
        if (seen && (block || previousBlock) && (previousBlock || !text.endsWith('\n'))) text += '\n';
        text += value;
        previousBlock = block;
        seen = true;
      }
      return text;
    };
    return read(root).replace(/\r\n?/g, '\n');
  }

  /* Resolves a text zone's position and box, honouring a customer-dragged posX/posY but
     clamping it into the band the layout allocated. Applied at the point of use by BOTH
     the editor and the print renderer, so they agree — and so a position saved before
     the drag clamp existed can't leave text sitting outside its region (or on a photo).

     x has no slack: the box is always the zone's full width, so it pins to zone.x.
     y may slide within the band, and the usable height is whatever remains below it.
     Returns fractions of the canvas, not pixels. */
  function _zoneBox(zone) {
    const zoneH = zone.h != null ? zone.h : 0.18;
    return (fmt) => {
      const rawY = (fmt && fmt.posY != null) ? fmt.posY : zone.y;
      const y = Math.min(Math.max(rawY, zone.y), zone.y + zoneH);
      return {
        x: zone.x,
        y,
        w: zone.w,
        /* Remaining band below the text start, and never past the page edge. */
        h: Math.max(0, Math.min(zone.y + zoneH - y, 1 - y)),
      };
    };
  }

  /* Largest font size at or below `requestedPx` whose wrapped text fits boxW × boxH.
     Shrinks only — a customer's chosen size is a ceiling, never raised.

     Shared by the print renderer and the on-screen editor so the two cannot disagree
     about what fits: both take the size this returns. Multiplicative steps because a
     heading can start at 200px+ and decrementing by 1 would loop for ages.

     Returns { fs, lines, lineH, fits }. fits === false means even `minPx` overflows —
     the caller decides whether to clip or refuse. */
  function _fitFontSize(ctx, text, boxW, boxH, fontPrefix, fontFam, requestedPx, minPx) {
    let fs = Math.max(1, Math.round(requestedPx));
    const floor = Math.max(1, Math.round(minPx));
    for (;;) {
      ctx.font = `${fontPrefix} ${fs}px ${fontFam}`;
      const lineH = fs * 1.5;
      const lines = _wrapText(ctx, text, boxW);
      if (lines.length * lineH <= boxH + 1) return { fs, lines, lineH, fits: true };
      if (fs <= floor) return { fs, lines, lineH, fits: false };
      fs = Math.max(floor, Math.floor(fs * 0.94));
    }
  }

  /* Smallest size auto-fit may shrink to: half the requested size (and ~1% of page
     height), so long text isn't silently printed unreadably small. But never above the
     size one line needs to fit the box height, else a too-large size choice on a short
     caption strip could never fit and checkout would refuse short text. */
  function _fitFloorPx(requestedPx, boxH, pageH) {
    const oneLinePx = Math.floor(boxH / 1.5);
    return Math.max(Math.min(Math.round(requestedPx * 0.5), oneLinePx), Math.round(pageH * 0.010));
  }

  /* Word-wrap helper for canvas text rendering. Returns an array of line strings. */
  function _wrapText(ctx, text, maxWidth) {
    const paragraphs = String(text).split('\n');
    const lines = [];
    for (const para of paragraphs) {
      const words = para.match(/\s+|\S+/gu) || [];
      let line = '';
      for (const word of words) {
        if (line && ctx.measureText(line + word).width > maxWidth) { lines.push(line); line = ''; }
        /* Preserve spaces and split overlong words just as the editor's
           word-break:break-word does, without splitting emoji/combining glyphs. */
        const parts = typeof Intl.Segmenter === 'function'
          ? [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(word)].map(p => p.segment)
          : Array.from(word);
        for (const part of parts) {
          if (line && ctx.measureText(line + part).width > maxWidth) { lines.push(line); line = ''; }
          line += part;
        }
      }
      lines.push(line);
    }
    return lines;
  }

  /* Render a spread as JPEG blob from slot images (keyed by spread index + slot index).
     slots = [{x,y,w,h}] normalized 0–1 of canvas. canvasW/canvasH are output pixel dims.
     textZones (optional) = [{key,x,y,w,h,fontSize,align,style}] for text overlays. */
  async function renderSpreadFromSlots(spreadIdx, slots, slotImages, isWide, canvasW, canvasH, quality, slotBlobs, slotTransforms, textZones, textContent, textFormat, bgColor, warnOut, measureOnly = false, defaultFamily = null) {
    const canvas = document.createElement('canvas');
    canvas.width  = measureOnly ? 1 : canvasW;
    canvas.height = measureOnly ? 1 : canvasH;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    if (!measureOnly) applySpreadBackground(ctx, bgColor, canvasW, canvasH);

    /* Kick off caption font loads now so they overlap with the photo fetch/draw loop
       below; awaited just before text is drawn. Unlike DOM text, canvas text doesn't
       wait for @font-face downloads — ctx.font would silently use the fallback font
       if drawn before the real one finishes loading. */
    let _fontsReady = null;
    if (textZones && textZones.length && typeof document !== 'undefined' && document.fonts) {
      const families = new Set();
      for (const zone of textZones) {
        const text = (textContent && textContent[`${spreadIdx}:${zone.key}`]) || '';
        if (!text.trim()) continue;
        const fmt = (textFormat && textFormat[`${spreadIdx}:${zone.key}`]) || {};
        families.add(fmt.family || 'Georgia, serif');
      }
      if (families.size) {
        _fontsReady = _requireCanvasFonts([...families]);
      }
    }

    for (let i = 0; !measureOnly && i < slots.length; i++) {
      const slotKey = `s${spreadIdx}:${i}`;
      const sl  = slots[i];
      const x   = sl.x * canvasW;
      const y   = sl.y * canvasH;
      const wPx = sl.w * canvasW;
      const hPx = sl.h * canvasH;

      const url  = slotImages[slotKey];
      const blob = slotBlobs && slotBlobs[slotKey];
      if (!url && !blob) {
        /* Empty slot: dashed outline so layout structure is visible in the Drive proof */
        ctx.strokeStyle = 'rgba(28,24,20,0.20)';
        ctx.lineWidth = Math.max(2, Math.round(canvasW * 0.0015));
        ctx.setLineDash([Math.round(canvasW * 0.012), Math.round(canvasW * 0.012)]);
        ctx.strokeRect(x + 2, y + 2, wPx - 4, hPx - 4);
        ctx.setLineDash([]);
        continue;
      }
      const tf = slotTransforms && slotTransforms[slotKey];
      let drawn = false;
      /* Prefer the original Blob for full print fidelity; fall back to the preview URL
         only when no Blob is available (e.g. server-restored state without original file). */
      if (blob) {
        /* Decode at a size we control — see _decodeForDraw. A geometry failure
           must not fall through to the URL path below, which would decode the
           same bytes the same way and print the same distortion; rethrow so the
           spread fails loudly and checkout stops. */
        let release = null;
        try {
          const decoded = await _decodeForDraw(blob, wPx, hPx, tf?.scale);
          release = decoded.release;
          _drawFit(ctx, decoded.src, x, y, wPx, hPx, tf);
          drawn = true;
        } catch(e) {
          if (/true proportions/.test(e?.message || '')) throw e;
          console.warn('[OI] canvas: slot', slotKey, 'failed to load from blob, trying URL');
        } finally { if (release) release(); }
      }
      if (!drawn && url) {
        let tempBlobUrl = null, release = null;
        try {
          let loadUrl = url, fetched = null;
          /* For server URLs, fetch as blob to avoid cross-origin canvas taint.
             A tainted canvas causes toBlob() to throw SecurityError — the root cause
             of "Pages could not be saved" on server-restored books. */
          if (!url.startsWith('blob:') && !url.startsWith('data:')) {
            try {
              const resp = await fetch(url, { credentials: 'include' });
              if (resp.ok) {
                fetched = await resp.blob();
                tempBlobUrl = URL.createObjectURL(fetched);
                loadUrl = tempBlobUrl;
              }
            } catch (_fe) {
              console.warn('[OI] canvas: fetch failed for slot', slotKey, '— cross-origin taint risk', _fe?.message);
            }
          }
          /* Same controlled decode as the blob path whenever we hold real bytes. */
          if (fetched) {
            const decoded = await _decodeForDraw(fetched, wPx, hPx, tf?.scale);
            release = decoded.release;
            _drawFit(ctx, decoded.src, x, y, wPx, hPx, tf);
          } else {
            _drawFit(ctx, await _loadImg(loadUrl), x, y, wPx, hPx, tf);
          }
          drawn = true;
        } catch(e) {
          if (/true proportions/.test(e?.message || '')) throw e;
          console.warn('[OI] canvas: slot', slotKey, 'failed to load from URL', e?.message);
        } finally {
          if (release) release();
          if (tempBlobUrl) URL.revokeObjectURL(tempBlobUrl);
        }
      }
      if (!drawn) throw new Error(`Photo for slot ${slotKey} could not be loaded — please try again.`);
    }

    /* Draw text zones on top of photo slots */
    if (_fontsReady) await _fontsReady;
    if (textZones && textZones.length) {
      for (const zone of textZones) {
        const text = (textContent && textContent[`${spreadIdx}:${zone.key}`]) || '';
        if (!text.trim()) continue;
        const fmt = (textFormat && textFormat[`${spreadIdx}:${zone.key}`]) || {};
        /* Position and box come from _zoneBox, which clamps a dragged position into the
           zone's allocated band. The box height is the band's own height (previously this
           was canvasH - zy, i.e. everything down to the page bottom, which is why text
           could run clean off the page). Editor and print share this resolution. */
        const box = _zoneBox(zone)(fmt);
        const zx = box.x * canvasW;
        const zy = box.y * canvasH;
        const zw = box.w * canvasW;
        const zh = Math.max(1, box.h * canvasH);
        const isHeading = zone.style === 'heading';
        ctx.save();
        /* A text box with no font of its own uses the book's font, as the editor shows
           it; printing Georgia here made the print differ from what the customer saw. */
        const fontFam = fmt.family || defaultFamily || 'Georgia, serif';
        const align = fmt.align || zone.align || 'center';
        const requestedFs = fmt.fontSizeFrac != null
          ? Math.round(fmt.fontSizeFrac * canvasH)
          : Math.round(zone.fontSize * canvasH);
        const fontWeight = fmt.bold ? '700' : (isHeading ? '300' : '400');
        const fontStyle  = fmt.italic ? 'italic' : 'normal';
        /* Shrink to fit rather than spill (floor: _fitFloorPx) — if it still doesn't
           fit there, the guard below refuses instead. */
        const fitted = _fitFontSize(
          ctx, text, zw, zh, `${fontStyle} ${fontWeight}`, fontFam, requestedFs,
          _fitFloorPx(requestedFs, zh, canvasH)
        );
        const fs = fitted.fs;
        ctx.font = `${fontStyle} ${fontWeight} ${fs}px ${fontFam}`;
        ctx.fillStyle = fmt.color || '#1C1814';
        ctx.textBaseline = 'top';
        ctx.textAlign = align;
        /* Sanity check (checkout-time only, via warnOut): if the requested font never
           actually resolved, ctx.font silently drew with a fallback — same failure mode
           as the missing-font-load bug, kept here as a backstop against a future regression. */
        if (warnOut && typeof document !== 'undefined' && document.fonts && !document.fonts.check(ctx.font)) {
          warnOut.push(`Spread ${spreadIdx + 1}, "${zone.key}": font "${fontFam}" wasn't loaded — printed with a fallback font.`);
        }
        const anchorX = align === 'center' ? zx + zw / 2 : align === 'right' ? zx + zw : zx;
        const lineH = fitted.lineH;
        const lines = fitted.lines;
        /* Only refuse when auto-fit has already shrunk to its floor and the text still
           overflows, or the zone itself sits off-page. Ordinary "too big" no longer
           reaches here — it gets scaled down instead of blocking checkout. */
        if (zy < 0 || zx < 0 || zx + zw > canvasW + 1 || !fitted.fits) {
          ctx.restore();
          const err = new Error('There is too much text here to fit the space, even at the smallest size. Please shorten it or move it to its own page.');
          err.textViewIdx = spreadIdx;
          err.textZoneKey = zone.key;
          throw err;
        }
        let ty = zy;
        let drawnLines = 0;
        for (const line of lines) {
          if (ty + fs > zy + zh) break;
          if (!measureOnly) ctx.fillText(line, anchorX, ty);
          ty += lineH;
          drawnLines++;
        }
        /* Sanity check: text taller than its box gets silently clipped — same as the
           on-screen overflow:hidden behaviour, but invisible in the exported print file. */
        if (warnOut && drawnLines < lines.length) {
          warnOut.push(`Spread ${spreadIdx + 1}, "${zone.key}": text overflowed its box — ${lines.length - drawnLines} line(s) cut off.`);
        }
        ctx.restore();
      }
    }

    if (measureOnly) return;
    return new Promise((res, rej) => {
      canvas.toBlob(b => b ? res(b) : rej(new Error('blob failed')), 'image/jpeg', quality || JPEG_Q);
    });
  }

  /* Lazy-load face-api.js + TinyFaceDetector model once per page; subsequent
     calls return the same promise so the network hit happens only once. */
  let _faceApiReady = null;
  function _loadFaceApi() {
    if (_faceApiReady) return _faceApiReady;
    _faceApiReady = (async () => {
      if (!window.faceapi) {
        await new Promise((res, rej) => {
          const s = document.createElement('script');
          s.src = 'https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js';
          s.onload = res; s.onerror = rej;
          document.head.appendChild(s);
        });
      }
      await faceapi.nets.tinyFaceDetector.loadFromUri(
        'https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/weights'
      );
    })();
    return _faceApiReady;
  }

  /* Smart-crop: shift object-position to the largest detected face center.
     Uses face-api.js TinyFaceDetector (works in all browsers, ~2 MB model,
     loaded lazily on first photo placement). Falls back silently to 50% 35%. */
  async function _autoAlign(img) {
    try {
      await _loadFaceApi();
      if (!img.complete || !img.naturalWidth) {
        await new Promise((res, rej) => {
          img.addEventListener('load', res, { once: true });
          img.addEventListener('error', rej, { once: true });
        });
      }
      const opts = new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.3 });
      const detections = await faceapi.detectAllFaces(img, opts);
      if (!detections.length) return null;
      const biggest = detections.reduce((a, b) =>
        b.box.width * b.box.height > a.box.width * a.box.height ? b : a
      );
      const { x, y, width, height } = biggest.box;
      const cx = Math.round((x + width  / 2) / img.naturalWidth  * 100);
      const cy = Math.round((y + height / 2) / img.naturalHeight * 100);
      return { x: (x + width / 2) / img.naturalWidth, y: (y + height / 2) / img.naturalHeight };
    } catch(e) {
      return null;
    }
  }

  /* Resize a photo (blob URL or data URL) to print dimensions — no text, no borders. */
  async function resizePhotoBlob(src, w, h, quality) {
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = src; });
    const scale = Math.max(w / img.naturalWidth, h / img.naturalHeight);
    const dw = img.naturalWidth * scale, dh = img.naturalHeight * scale;
    ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
    return new Promise(r => canvas.toBlob(r, 'image/jpeg', quality));
  }

  /* Production print specification — override when printer spec is available. */
  /* Hardcover construction supplied by the printer: the cover artwork has a
     0.5-inch turn-in on every outside edge and a centred 1-inch spine. The
     turn-in is the complete production allowance, so no extra bleed is added. */
  const COVER_WRAP_IN       = 0.5;
  const SPINE_WIDTH_IN      = 1;
  /* Back-cover OAT & INK wordmark weight (Cormorant Garamond). SemiBold: lighter
     weights print faintly in foil. The page loads this face, and the cover render
     requires it, so print and screen always use the same weight. */
  const LOGO_WEIGHT         = 600;
  /* Changes to how cover text and the logo are drawn, for the cover fingerprint:
     600 logo without dots, full-strength "&" and subtitle. */
  const COVER_STYLE         = 'logo600-solid-nodots';
  const INTERIOR_PAPER_COLOR = '#ffffff'; /* silk-matte white paper — used for spreads, thumbnails and print canvas */

  /* ── Spread themes ── */
  const SPREAD_THEMES = [
    {
      id: 'jungle', label: 'Jungle', color: '#ffffff', tileSize: 160, bgAssetKey: 'jungleBg',
      drawTile(ctx, s) {
        ctx.fillStyle = 'rgba(120,200,120,0.20)';
        [[0.20,0.15,-0.4,0.8],[0.72,0.30,0.9,0.55],[0.10,0.72,0.2,0.65],
         [0.60,0.78,-0.8,0.75],[0.45,0.47,1.5,0.45]].forEach(([px,py,rot,sc]) => {
          ctx.save(); ctx.translate(px*s, py*s); ctx.rotate(rot); ctx.scale(sc, sc);
          const h=s*0.18, w=s*0.06;
          ctx.beginPath(); ctx.moveTo(0,-h);
          ctx.bezierCurveTo(w,-h*0.3,w,h*0.3,0,h);
          ctx.bezierCurveTo(-w,h*0.3,-w,-h*0.3,0,-h); ctx.fill();
          ctx.strokeStyle='rgba(100,180,100,0.10)'; ctx.lineWidth=s*0.008;
          ctx.beginPath(); ctx.moveTo(0,-h); ctx.lineTo(0,h); ctx.stroke();
          ctx.restore();
        });
      }
    },
    {
      id: 'ocean', label: 'Ocean', color: '#122840', tileSize: 120,
      drawTile(ctx, s) {
        ctx.strokeStyle = 'rgba(80,160,220,0.18)'; ctx.lineWidth = s*0.013; ctx.lineCap = 'round';
        [0.22, 0.52, 0.82].forEach(yf => {
          const y = yf*s; ctx.beginPath();
          for(let x=0; x<=s; x++){
            const wy = y + Math.sin((x/s)*Math.PI*2.5)*s*0.045;
            x===0 ? ctx.moveTo(x,wy) : ctx.lineTo(x,wy);
          }
          ctx.stroke();
        });
      }
    },
    {
      id: 'blush', label: 'Blush', color: '#B87870', tileSize: 110,
      drawTile(ctx, s) {
        [[0.25,0.20],[0.72,0.15],[0.50,0.55],[0.15,0.70],[0.80,0.72],[0.40,0.88]].forEach(([px,py]) => {
          const cx=px*s, cy=py*s, r=s*0.055;
          ctx.fillStyle = 'rgba(240,200,195,0.30)';
          for(let p=0;p<5;p++){
            const a=(p/5)*Math.PI*2-Math.PI/2;
            ctx.beginPath(); ctx.arc(cx+Math.cos(a)*r*0.7, cy+Math.sin(a)*r*0.7, r*0.5, 0, Math.PI*2); ctx.fill();
          }
          ctx.beginPath(); ctx.arc(cx,cy,r*0.25,0,Math.PI*2); ctx.fillStyle='rgba(255,225,220,0.35)'; ctx.fill();
        });
      }
    },
    {
      id: 'noir', label: 'Noir', color: '#0E0C0A', tileSize: 90,
      drawTile(ctx, s) {
        ctx.strokeStyle = 'rgba(255,255,255,0.06)'; ctx.lineWidth = s*0.012;
        const step = s*0.18;
        for(let i=-s; i<s*2; i+=step){ ctx.beginPath(); ctx.moveTo(i,0); ctx.lineTo(i+s,s); ctx.stroke(); }
      }
    },
    {
      id: 'classic', label: 'Classic', color: '#EEE8DC', tileSize: 100,
      drawTile(ctx, s) {
        ctx.fillStyle = 'rgba(120,100,70,0.14)';
        const n=5;
        for(let r=0;r<n;r++) for(let c=0;c<n;c++){
          ctx.beginPath(); ctx.arc((c+0.5)*s/n,(r+0.5)*s/n,s*0.012,0,Math.PI*2); ctx.fill();
        }
      }
    },
  ];

  /* Returns the solid base hex for a bgValue (handles both '#hex' and 'theme:id') */
  function spreadBgColor(bgValue) {
    if (!bgValue) return INTERIOR_PAPER_COLOR;
    if (bgValue.startsWith('theme:')) return SPREAD_THEMES.find(t=>t.id===bgValue.slice(6))?.color || INTERIOR_PAPER_COLOR;
    return bgValue;
  }

  /* Preloaded images for image-based themes (keyed by theme id) */
  const _themeImgCache = {};

  (function _preloadThemeImages() {
    if (typeof window === 'undefined' || !window._oiAssets) return;
    SPREAD_THEMES.forEach(t => {
      if (!t.bgAssetKey) return;
      const url = window._oiAssets[t.bgAssetKey];
      if (!url) return;
      const entry = { img: null, loaded: false, url };
      _themeImgCache[t.id] = entry;
      const img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = () => {
        entry.img = img; entry.loaded = true;
        document.dispatchEvent(new CustomEvent('oi:theme-image-loaded', { detail: { themeId: t.id } }));
      };
      img.src = url;
    });
  })();

  /* Draws solid colour, tile pattern, or image-based theme onto an existing canvas context */
  function applySpreadBackground(ctx, bgValue, W, H) {
    if (bgValue && bgValue.startsWith('theme:')) {
      const theme = SPREAD_THEMES.find(t=>t.id===bgValue.slice(6));
      if (theme) {
        ctx.fillStyle = theme.color; ctx.fillRect(0, 0, W, H);
        const imgEntry = _themeImgCache[theme.id];
        if (imgEntry && imgEntry.loaded && imgEntry.img) {
          const img = imgEntry.img;
          // Scale to full spread width, anchor to top edge
          const scale = W / img.naturalWidth;
          const dw = W, dh = img.naturalHeight * scale;
          ctx.drawImage(img, 0, 0, dw, dh);
        } else {
          const tileS = Math.max(40, Math.min(480, Math.round(theme.tileSize * Math.max(W,H) / 600)));
          const tc = document.createElement('canvas'); tc.width = tc.height = tileS;
          theme.drawTile(tc.getContext('2d'), tileS);
          const pat = ctx.createPattern(tc, 'repeat');
          if (pat) { ctx.fillStyle = pat; ctx.fillRect(0, 0, W, H); }
        }
        return;
      }
    }
    ctx.fillStyle = bgValue || INTERIOR_PAPER_COLOR; ctx.fillRect(0, 0, W, H);
  }

  /* Pure dimension helper — usable without a DOM or upload. */
  function coverWrapDimensions({ trimWIn, trimHIn, wrapIn = COVER_WRAP_IN, spineWidthIn = SPINE_WIDTH_IN, dpi = 300 }) {
    const wrapPx  = Math.round(wrapIn * dpi);
    const spinePx = Math.round(spineWidthIn * dpi);
    const pageWpx = Math.round(trimWIn * dpi);
    const pageHpx = Math.round(trimHIn * dpi);
    return {
      wrapPx, spinePx, pageWpx, pageHpx,
      totalW: wrapPx * 2 + pageWpx * 2 + spinePx,
      totalH: wrapPx * 2 + pageHpx,
      backX:  wrapPx,
      spineX: wrapPx + pageWpx,
      frontX: wrapPx + pageWpx + spinePx,
    };
  }

  /* Back-cover OAT & INK wordmark, centred, in the stamp colour. The brand's
     five-dot ink mark is left off the cover: at back-cover size its dots printed
     as specks, and in brand gold they clashed with silver, white and blind stamps. */
  function drawBackLogo(ctx, cx, cy, panelW, pageH, stamp) {
    const size   = Math.round(pageH * 0.044);
    ctx.save();
    ctx.textBaseline = 'middle';
    ctx.font = `${LOGO_WEIGHT} ${size}px 'Cormorant Garamond',serif`;
    const gap    = size * 0.48;
    const parts  = ['O A T', '&', 'I N K'];
    const widths = parts.map(p => ctx.measureText(p).width);
    const total  = widths.reduce((a, b) => a + b, 0) + gap * 2;
    let x = cx - total / 2;
    ctx.textAlign = 'left';
    ctx.fillStyle = stamp;
    parts.forEach((p, i) => { ctx.fillText(p, x, cy); x += widths[i] + gap; });
    ctx.restore();
  }


  /* Render a flat cover-wrap JPEG (back + spine + front) at print resolution.
     Cloth fills the entire canvas including turn-in. All content is positioned
     relative to the trimmed panel origins; no guides appear in the output. */
  /* Spine text direction: 'ttb' (top to bottom, the default: English/Indian
     convention, BS 6738) or 'btt' (bottom to top, continental Europe). */
  function _spineDir(v) { return v === 'btt' ? 'btt' : 'ttb'; }

  async function renderCoverWrapBlob(src, opts = {}) {
    const {
      clothHex      = '#E8E0D0',
      title         = '', subtitle = '', spineText = '', spineDir = 'ttb',
      stampColor    = '#C9A55F', fontFamily = "'Cormorant Garamond',serif",
      fontWeight    = 400,
      pageWpx, pageHpx,
      wrapIn        = COVER_WRAP_IN,
      spineWidthIn  = SPINE_WIDTH_IN,
      dpi           = 300,
      quality       = PRINT_Q,
      coverLayout   = 'coverPhotoTitle',
      showCoverText   = true,
      showBackLogo    = true,
      coverTransform  = { scale: 1, nx: 0, ny: 0 },
    } = opts;

    const wrapPx  = Math.round(wrapIn * dpi);
    const spinePx = Math.round(spineWidthIn * dpi);
    const cW = pageWpx || Math.round(8 * dpi);
    const cH = pageHpx || Math.round(8 * dpi);

    /* Canvas includes the hardcover turn-in on all four outside edges. */
    const totalW = wrapPx * 2 + cW * 2 + spinePx;
    const totalH = wrapPx * 2 + cH;

    /* Panel origins at the finished board edge, inside the turn-in. */
    const backX  = wrapPx;
    const spineX = wrapPx + cW;
    const frontX = wrapPx + cW + spinePx;

    const canvas = document.createElement('canvas');
    canvas.width  = totalW;
    canvas.height = totalH;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    const ls  = (px) => { try { ctx.letterSpacing = px + 'px'; } catch(e) {} };

    /* Ensure the selected webfont is loaded before any canvas text measurement.
       Without this, canvas silently falls back to a system font during export
       while the Google Font is still downloading. */
    if (typeof document !== 'undefined' && document.fonts) {
      try {
        await document.fonts.ready;
        await document.fonts.load(`400 16px ${fontFamily}`);
        await document.fonts.load(`500 16px ${fontFamily}`);
      } catch(e) { /* best effort — proceed with whatever is cached */ }
    }

    /* The best-effort preload above is retained for old browser behaviour, but
       export itself is strict: never fingerprint or sell fallback-font pixels. */
    await _requireCanvasFonts([fontFamily, "'Cormorant Garamond',serif"]);
    /* A non-regular cover weight is its own font file: without it the canvas would
       quietly substitute the nearest weight, so require it like the font itself. */
    if (fontWeight !== 400 && typeof document !== 'undefined' && document.fonts) {
      const primary = _primaryFontFamily(fontFamily);
      const loaded = await document.fonts.load(`${fontWeight} 16px "${primary.replace(/"/g, '\\"')}"`);
      if (!loaded.length) throw new Error(`The selected font "${primary}" is not ready. Please check your connection and try again.`);
    }
    const textFamily = coverDrawFamily(fontFamily);
    if (textFamily !== fontFamily && /\d/.test(`${title}${subtitle}${spineText}`) && typeof document !== 'undefined' && document.fonts) {
      const loaded = await document.fonts.load(`${fontWeight} 16px ${COVER_DIGITS_FAMILY}`, '0123456789');
      if (!loaded.length) throw new Error('The cover font is not ready. Please check your connection and try again.');
    }
    if (showBackLogo && typeof document !== 'undefined' && document.fonts) {
      const loaded = await document.fonts.load(`${LOGO_WEIGHT} 16px "Cormorant Garamond"`);
      if (!loaded.length) throw new Error('The logo font is not ready. Please check your connection and try again.');
    }

    /* Cloth fills the finished boards, spine and outer turn-in. */
    ctx.fillStyle = clothHex;
    ctx.fillRect(0, 0, totalW, totalH);

    /* Load front cover photo once, used by both layout branches.
       Decoded through _decodeForDraw so an oversized source cannot be silently
       subsampled into a differently-shaped raster — the cover is the most
       visible page in the book. Sized against the front board (cW x cH), an
       upper bound on either branch's photo rect. A geometry failure propagates:
       _preflight must not ship a distorted cover. */
    let coverImg = null, coverRelease = null;
    if (src) {
      try {
        const bytes = await (await fetch(src)).blob();
        const decoded = await _decodeForDraw(bytes, Math.round(cW), Math.round(cH), coverTransform?.scale);
        coverImg = decoded.src; coverRelease = decoded.release;
      } catch(e) {
        if (/true proportions/.test(e?.message || '')) throw e;
      }
    }

    /* Shared text metrics */
    const tPad    = Math.round(cH * 18 / 392);
    const titleSz = Math.round(cH * 25 / 392);
    const subtSz  = Math.round(cH * 8.5 / 392);
    const tGap    = Math.round(cH * 7 / 392);
    const fCx     = frontX + cW / 2;

    if (coverLayout === 'coverFull') {
      /* Keep the photograph centred inside the finished front board. The
         spine and turn-in remain independent production regions. */
      if (coverImg) {
        const phW = cW;
        const phH = cH;
        ctx.save();
        ctx.beginPath(); ctx.rect(frontX, wrapPx, phW, phH); ctx.clip();
        _drawFit(ctx, coverImg, frontX, wrapPx, phW, phH, coverTransform);
        ctx.restore();
      }
      /* No title/subtitle in full-front layout */

    } else {
      /* coverPhotoTitle — inset photo with reserved text area */
      const iS  = Math.round(cW * 24 / 340);
      const iT  = Math.round(cH * 24 / 392);
      /* No text: bottom inset mirrors the top so the photo is centered, matching
         the live front-cover's padding-bottom toggle. "Add Text" left on with
         nothing typed counts as no text — an empty band printed blank (#1041). */
      const textOn = showCoverText && !!(String(title || '').trim() || String(subtitle || '').trim());
      const tbH = textOn ? Math.round(cH * 96 / 392) : iT;
      const phX = frontX + iS, phY = wrapPx + iT;
      const phW = cW - iS * 2,  phH = cH - iT - tbH;

      if (coverImg) {
        ctx.save();
        ctx.beginPath(); ctx.rect(phX, phY, phW, phH); ctx.clip();
        _drawFit(ctx, coverImg, phX, phY, phW, phH, coverTransform);
        ctx.restore();
      }

      /* Front cover text — only when there is text to stamp */
      if (textOn) {
        const tbY     = wrapPx + cH - tbH;
        const titleY  = tbY + tPad;
        const subtY   = titleY + Math.round(titleSz * 1.2) + tGap;
        ctx.fillStyle = stampColor;
        ctx.textAlign = 'center';
        /* Long text is set smaller (same ratio as the preview) and stays centred
           in its line; maxWidth remains only as a guard against rounding. */
        if (title) {
          const T  = title.toUpperCase();
          const sz = Math.floor(titleSz * coverTextScale(T, { weight: fontWeight, family: textFamily, size: titleSz, spacingRatio: 0.16, maxWidth: cW * 0.82 }));
          ctx.font = `${fontWeight} ${sz}px ${textFamily}`; ls(Math.round(sz * 0.16));
          ctx.textBaseline = 'top';
          ctx.fillText(T, fCx, titleY + (titleSz - sz) / 2, cW * 0.82);
        }
        if (subtitle) {
          const S  = subtitle.toUpperCase();
          const sz = Math.floor(subtSz * coverTextScale(S, { weight: fontWeight, family: textFamily, size: subtSz, spacingRatio: 0.4, maxWidth: cW * 0.82 }));
          ctx.font = `${fontWeight} ${sz}px ${textFamily}`; ls(Math.round(sz * 0.4));
          ctx.textBaseline = 'top';
          ctx.fillText(S, fCx, subtY + (subtSz - sz) / 2, cW * 0.82);
        }
      }
    }

    /* Spine text — centred on spine panel, vertically centred on trim height.
       Top to bottom (default) reads the right way up when the book lies front
       cover up; bottom to top is the customer's opt-in (see _spineDir). */
    if (spineText && spinePx > 0) {
      const spineBase = Math.min(Math.round(spinePx * 0.55), Math.round(cH * 11.5 / 392));
      const SP = spineText.toUpperCase();
      const spineSz = Math.floor(spineBase * coverTextScale(SP, { weight: fontWeight, family: textFamily, size: spineBase, spacingRatio: 0.21, maxWidth: cH * 0.8 }));
      ctx.save();
      ctx.translate(spineX + spinePx / 2, wrapPx + cH / 2);
      ctx.rotate(_spineDir(spineDir) === 'btt' ? -Math.PI / 2 : Math.PI / 2);
      ctx.font = `${fontWeight} ${spineSz}px ${textFamily}`; ls(Math.round(spineSz * 0.21));
      ctx.fillStyle = stampColor; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(SP, 0, 0, cH * 0.8);
      ctx.restore();
    }

    /* Back cover wordmark — five-dot ink mark, centred on back panel trim */
    if (showBackLogo) {
      const bCx  = backX + cW / 2;
      const markY = wrapPx + cH - Math.round(cH * 32 / 392);
      drawBackLogo(ctx, bCx, markY, cW, cH, stampColor);
    }

    if (coverRelease) coverRelease();
    return new Promise((res, rej) => canvas.toBlob(b => b ? res(b) : rej(new Error('cover blob')), 'image/jpeg', quality));
  }

  /* ── Print-resolution calculation ── */
  function calculateEffectiveDpi({ sourceWidth, sourceHeight, targetWidthIn, targetHeightIn, scale }) {
    if (!sourceWidth || !sourceHeight || !targetWidthIn || !targetHeightIn) return null;
    /* scale < 1 (zoom out) samples MORE of the source image into the same print area —
       genuinely higher resolution, not lower. Dividing usableW/usableH by a fraction below
       grows them accordingly, so DPI correctly increases when a customer zooms out instead
       of being understated by a floor that assumed scale could never go below 1. */
    const s = Math.max(0.5, scale || 1);
    const sourceAspect = sourceWidth  / sourceHeight;
    const targetAspect = targetWidthIn / targetHeightIn;
    let usableW, usableH;
    if (sourceAspect > targetAspect) {
      usableH = sourceHeight;
      usableW = sourceHeight * targetAspect;
    } else {
      usableW = sourceWidth;
      usableH = sourceWidth / targetAspect;
    }
    usableW /= s;
    usableH /= s;
    return Math.floor(Math.min(usableW / targetWidthIn, usableH / targetHeightIn));
  }

  function dpiLabel(dpi) {
    if (dpi === null || dpi === undefined) return null;
    if (dpi >= 300) return { text: `Good — ${dpi} DPI`,           color: '#7EB87E' };
    if (dpi >= 200) return { text: `Acceptable — ${dpi} DPI`,     color: '#C9A55F' };
    if (dpi >= 150) return { text: `Low resolution — ${dpi} DPI`, color: '#D4844A' };
    return               { text: `Very low — ${dpi} DPI`,         color: '#C05050' };
  }

  /* Crop the exact finished front board from a cover wrap. Never infer the
     panel from image height: landscape and portrait albums are not square. */
  function coverFrontThumbBlob(wrapBlob, size, pageWpx, pageHpx, wrapPx, spinePx) {
    return new Promise(function(resolve, reject) {
      var img = new Image();
      var url = URL.createObjectURL(wrapBlob);
      img.onload = function() {
        URL.revokeObjectURL(url);
        var sx = wrapPx + pageWpx + spinePx;
        var sy = wrapPx;
        /* `size` is the long edge, not a square. The front panel carries the
           book's real aspect — 2400x3600 for an 8x12 — and forcing it into a
           square canvas stretched every non-square book's cover preview: 1.5x
           wider on 8x12, 1.25x on 8x10, squashed on 12x8 landscape. The print
           file was always correct; this thumbnail is what the customer sees in
           the cart, in My Books and as _Preview_URL on the order, so the book
           looked wrong everywhere except where it mattered.
           Consumers scale it with object-fit:cover, so a non-square thumb is
           fine — it was only ever square by accident of this line. */
        var scale = size / Math.max(pageWpx, pageHpx);
        var tw = Math.max(1, Math.round(pageWpx * scale));
        var th = Math.max(1, Math.round(pageHpx * scale));
        var c = document.createElement('canvas');
        c.width = tw; c.height = th;
        var tctx = c.getContext('2d');
        tctx.imageSmoothingEnabled = true;
        tctx.imageSmoothingQuality = 'high';
        tctx.drawImage(img, sx, sy, pageWpx, pageHpx, 0, 0, tw, th);
        c.toBlob(function(b) { b ? resolve(b) : reject(new Error('thumb-blob')); }, 'image/jpeg', 0.82);
      };
      img.onerror = function() { URL.revokeObjectURL(url); reject(new Error('thumb-load')); };
      img.src = url;
    });
  }

  function blobToThumbDataUrl(blob, size) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      var url = URL.createObjectURL(blob);
      img.onload = function () {
        URL.revokeObjectURL(url);
        var c = document.createElement('canvas');
        c.width = c.height = size;
        c.getContext('2d').drawImage(img, 0, 0, size, size);
        resolve(c.toDataURL('image/jpeg', 0.72));
      };
      img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('thumb')); };
      img.src = url;
    });
  }

  /* canvas.toBlob('image/jpeg') never writes a resolution into the file — the JFIF density
     field it emits is either absent or "no units" (1:1 aspect ratio only). Software that
     reads that field (Explorer's file properties, Photoshop's Image Size dialog, some print
     prepress tools) then falls back to showing 96 DPI, regardless of how many actual pixels
     are in the image. The rendered pixels ARE full print resolution (pageWpx/pageHpx above
     are already computed from trim size × real DPI) — only the metadata lies. This patches
     the JFIF APP0 segment in place (or inserts one if the encoder omitted it) so the file's
     declared resolution matches what was actually rendered. */
  function _patchJfifDensity(bytes, dpi) {
    if (bytes.length < 4 || bytes[0] !== 0xFF || bytes[1] !== 0xD8) return bytes; /* not a JPEG */
    const hasJfif = bytes[2] === 0xFF && bytes[3] === 0xE0
      && bytes[6] === 0x4A && bytes[7] === 0x46 && bytes[8] === 0x49 && bytes[9] === 0x46 && bytes[10] === 0x00;
    const hi = (dpi >> 8) & 0xFF, lo = dpi & 0xFF;
    if (hasJfif) {
      const segLen = (bytes[4] << 8) | bytes[5];
      if (segLen < 16) return bytes; /* malformed/truncated APP0 — leave untouched */
      const out = bytes.slice();
      out[13] = 1;              /* density units: 1 = pixels per inch */
      out[14] = hi; out[15] = lo; /* Xdensity */
      out[16] = hi; out[17] = lo; /* Ydensity */
      return out;
    }
    /* No APP0/JFIF segment at all — insert a minimal one (16 bytes) right after SOI */
    const jfif = new Uint8Array([
      0xFF, 0xE0, 0x00, 0x10,                   /* APP0 marker, length 16 */
      0x4A, 0x46, 0x49, 0x46, 0x00,             /* "JFIF\0" */
      0x01, 0x02,                               /* version 1.02 */
      0x01,                                     /* units: pixels per inch */
      hi, lo, hi, lo,                           /* Xdensity, Ydensity */
      0x00, 0x00,                               /* no thumbnail */
    ]);
    const out = new Uint8Array(bytes.length + jfif.length);
    out.set(bytes.subarray(0, 2), 0);
    out.set(jfif, 2);
    out.set(bytes.subarray(2), 2 + jfif.length);
    return out;
  }

  async function _setJpegDpi(blob, dpi) {
    try {
      const bytes   = new Uint8Array(await blob.arrayBuffer());
      const patched = _patchJfifDensity(bytes, dpi);
      return patched === bytes ? blob : new Blob([patched], { type: blob.type || 'image/jpeg' });
    } catch(_e) {
      return blob; /* metadata is cosmetic — never let a patch failure block the upload */
    }
  }

  /* Retry a render-and-upload before telling the customer anything went wrong.

     Order 1027 is why this exists: a real customer's checkout failed twice and
     succeeded on the third identical attempt, with nothing changed in between.
     Each failure cost them a full re-render and re-upload of a 16-spread book,
     and nothing but persistence got the order placed. These failures are mostly
     transient — a decode under memory pressure, a connection blip mid-upload —
     and a retry inside the flow fixes them without the customer ever knowing.

     Deliberately narrow about what it will NOT retry: an abort (the customer
     cancelled or the flow moved on) and an expired session, neither of which a
     second attempt can help. Everything else gets another go, including
     deterministic faults — those simply fail again a second or two later and
     are reported as they would have been. */
  async function _retryRender(fn, { attempts = 3, baseDelayMs = 700, signal, label = '' } = {}) {
    let lastErr;
    for (let attempt = 1; attempt <= attempts; attempt++) {
      if (signal?.aborted) throw new Error('aborted');
      try {
        return await fn(attempt);
      } catch (e) {
        lastErr = e;
        const msg = String(e?.message || e);
        if (signal?.aborted || e?.name === 'AbortError') throw e;
        if (/sign in|session|Authentication required|\(401\)/i.test(msg)) throw e;
        if (attempt === attempts) break;
        console.warn(`[OI retry] ${label} attempt ${attempt}/${attempts} failed: ${msg}`);
        await new Promise(r => setTimeout(r, baseDelayMs * attempt));
      }
    }
    throw lastErr;
  }

  async function uploadBlob(projectId, blob, index, key, dpi = null, renderFingerprint = null) {
    if (dpi && blob?.type === 'image/jpeg') blob = await _setJpegDpi(blob, dpi);
    const fd = new FormData();
    fd.append('projectId', projectId); fd.append('assetKey', key);
    fd.append('spreadIndex', String(index));
    if (renderFingerprint) fd.append('renderFingerprint', renderFingerprint);
    if (dpi) fd.append('renderDpi', String(dpi));
    if (renderFingerprint) fd.append('renderVersion', PRINT_RENDER_VERSION);
    const pad = String(index+1).padStart(2,'0');
    const EXT_MAP = { 'image/jpeg':'jpg','image/png':'png','image/webp':'webp','image/gif':'gif','image/avif':'avif','image/bmp':'bmp' };
    const ext   = EXT_MAP[blob.type] || 'jpg';
    const fname = key === 'cover'         ? `cover.${ext}`
                : key === 'cover:thumb'   ? `cover-thumb.${ext}`
                : key.startsWith('photo-') ? `photo-${pad}.${ext}`
                :                            `spread-${pad}.${ext}`;
    fd.append('file', blob, fname);
    const ac = new AbortController();
    const t  = setTimeout(() => ac.abort(), 60_000); // 60 s per upload
    try {
      const res = await fetch(`${WORKER_URL}/api/photos/upload-spread`, { method:'POST', credentials:'include', body:fd, signal: ac.signal });
      if (!res.ok) {
        if (res.status === 401) document.dispatchEvent(new CustomEvent('oi:session-expired'));
        const errBody = await res.json().catch(() => ({}));
        const errMsg = errBody.error || `Upload ${key} failed (${res.status})`;
        console.error('[OI upload]', key, res.status, errMsg);
        throw new Error(errMsg);
      }
      return res.json();
    } catch(e) {
      if (e.name === 'AbortError') throw new Error(`Upload ${key} timed out — please try again.`);
      throw e;
    } finally { clearTimeout(t); }
  }

  /* ── Upload progress overlay ── */
  const _oiUploadOverlay = (function () {
    const DOT_POS = [[30,77],[50,62],[64,76],[42,89]];
    const C = 326.7;
    let _dots, _brushTip, _logoRow, _logoStage, _ringWrap, _ringFill, _ringPct, _ringLbl, _label;
    function _el(id) { return document.getElementById(id); }
    function _sleep(ms) { return new Promise(r => setTimeout(r, ms)); }
    function _easeInOut(t) { return t < .5 ? 2*t*t : -1+(4-2*t)*t; }
    function _easeOut(t) { return 1-(1-t)*(1-t); }
    function _tween(dur, cb) {
      return new Promise(resolve => {
        const t0 = performance.now();
        function frame(now) { const p = Math.min((now-t0)/dur,1); cb(p); if(p<1) requestAnimationFrame(frame); else resolve(); }
        requestAnimationFrame(frame);
      });
    }
    function _spawnRipple(x, y, sz, color) {
      const r = document.createElement('div');
      r.className = 'oi2-ripple';
      r.style.cssText = `left:${x}px;top:${y}px;width:${sz*2.2}px;height:${sz*2.2}px;color:${color}`;
      _logoStage.appendChild(r);
      r.addEventListener('animationend', () => r.remove(), {once:true});
    }
    async function _runLogoAnim() {
      const stageW = _logoStage.offsetWidth, stageH = _logoStage.offsetHeight;
      const rowRect = _logoRow.getBoundingClientRect(), stageRect = _logoStage.getBoundingClientRect();
      const rowLeft = rowRect.left - stageRect.left, rowRight = rowLeft + rowRect.width;
      const rowMid  = rowRect.top  - stageRect.top  + rowRect.height / 2;
      _brushTip.style.top = rowMid+'px'; _brushTip.style.left=(rowLeft-55)+'px';
      _brushTip.style.opacity='1'; _brushTip.style.transition='none';
      await _tween(1600, p => {
        const ease=_easeInOut(p), x=rowLeft-55+(rowRight-rowLeft+110)*ease;
        _brushTip.style.left=x+'px';
        const rev=Math.max(0,Math.min(100,((x-rowLeft+50)/rowRect.width)*100));
        _logoRow.style.clipPath=`inset(0 ${100-rev}% 0 0)`;
      });
      _logoRow.classList.add('oi2-wet'); await _sleep(120);
      const dotSizes=[11,8,7,6], dotColors=['#C9A468','#A8823F','#C9A468','#7E9A6E'];
      for(let i=0;i<4;i++){
        const [lp,tp]=DOT_POS[i], tx=(lp/100)*stageW, ty=(tp/100)*stageH;
        const fromX=parseFloat(_brushTip.style.left), fromY=parseFloat(_brushTip.style.top);
        await _tween(280, p=>{ const e=_easeOut(p); _brushTip.style.left=fromX+(tx-fromX)*e+'px'; _brushTip.style.top=fromY+(ty-fromY)*e+'px'; });
        _brushTip.style.transition='transform .08s ease';
        _brushTip.style.transform='translate(-50%,-50%) rotate(-8deg) scale(.6,1.4)';
        await _sleep(80);
        _brushTip.style.transform='translate(-50%,-50%) rotate(-8deg) scale(1,1)';
        _spawnRipple(tx,ty,dotSizes[i]*2.5,dotColors[i]);
        _dots[i].style.left=tx+'px'; _dots[i].style.top=ty+'px';
        await _sleep(40); _dots[i].classList.add('oi2-placed'); await _sleep(220);
      }
      const bx0=parseFloat(_brushTip.style.left), by0=parseFloat(_brushTip.style.top);
      await _tween(420, p=>{ const e=_easeOut(p); _brushTip.style.left=bx0+50*e+'px'; _brushTip.style.top=by0-30*e+'px'; _brushTip.style.opacity=(1-e).toFixed(3); });
      _brushTip.style.opacity='0';
      await _sleep(200); _logoRow.classList.remove('oi2-wet'); _logoRow.classList.add('oi2-dry');
      await _sleep(700); _logoRow.classList.add('oi2-gone'); await _sleep(700);
      _dots.forEach((d,i)=>{ d.classList.add('oi2-tw'+(i+1)); });
      _dots.forEach(d=>{ d.style.transition='none'; }); void _dots[0].offsetWidth;
      _dots.forEach(d=>{ d.classList.remove('oi2-placed'); }); void _dots[0].offsetWidth;
      _dots.forEach(d=>{ d.style.transition=''; });
    }
    return {
      _animPromise: null,
      show() {
        const overlay=_el('oi2-upload-overlay'); if(!overlay) return;
        _dots=[0,1,2,3].map(i=>_el('oi2-d'+i)); _brushTip=_el('oi2-brush-tip');
        _logoRow=_el('oi2-logo-row'); _logoStage=_el('oi2-logo-stage');
        _ringWrap=_el('oi2-ring-wrap'); _ringFill=_el('oi2-ring-fill');
        _ringPct=_el('oi2-ring-pct'); _ringLbl=_el('oi2-ring-lbl'); _label=_el('oi2-upload-label');
        _ringFill.style.strokeDashoffset=C+''; _ringFill.style.stroke='#C9A55F';
        _ringPct.textContent='0%'; _ringPct.style.color=''; _ringLbl.textContent='saved'; _ringLbl.style.color='#C9A55F';
        _ringWrap.classList.remove('oi2-ring-vis');
        _logoRow.style.transition='none'; void _logoRow.offsetWidth;
        _logoRow.classList.remove('oi2-wet','oi2-dry','oi2-gone');
        _logoRow.style.clipPath='inset(0 100% 0 0)'; void _logoRow.offsetWidth; _logoRow.style.transition='';
        _dots.forEach(d=>{ d.style.transition='none'; d.classList.remove('oi2-placed','oi2-tw1','oi2-tw2','oi2-tw3','oi2-tw4'); });
        void _dots[0].offsetWidth;
        _dots.forEach(d=>{ d.style.left=''; d.style.top=''; });
        requestAnimationFrame(()=>_dots.forEach(d=>{ d.style.transition=''; }));
        _brushTip.style.opacity='0'; _brushTip.style.left='-200px'; _brushTip.style.top='0px';
        _brushTip.style.transform='translate(-50%,-50%) rotate(-8deg)'; _brushTip.style.transition='none';
        overlay.style.display='flex'; overlay.style.opacity='';
        setTimeout(()=>{ if(_ringWrap) _ringWrap.classList.add('oi2-ring-vis'); }, 2400);
        this._animPromise=_runLogoAnim();
      },
      update(pct, lbl) {
        if(!_ringFill) return;
        _ringFill.style.strokeDashoffset=C*(1-pct/100);
        if(_ringPct) _ringPct.textContent=Math.round(pct)+'%';
        if(_label && lbl) _label.textContent=lbl;
      },
      async finish() {
        if(_ringFill) _ringFill.style.stroke='#5A7A4A';
        if(_ringPct) _ringPct.style.color='#7E9A6E';
        if(_ringLbl) { _ringLbl.textContent='complete'; _ringLbl.style.color='#7E9A6E'; }
        this.update(100,'Added to bag');
        if(this._animPromise) await this._animPromise;
        await _sleep(1200);
      },
      hide() {
        const overlay=_el('oi2-upload-overlay'); if(!overlay) return;
        overlay.style.transition='opacity .6s ease'; overlay.style.opacity='0';
        setTimeout(()=>{ overlay.style.display='none'; overlay.style.opacity=''; overlay.style.transition='';
          _logoStage && _logoStage.querySelectorAll('.oi2-ripple').forEach(r=>r.remove()); }, 600);
      }
    };
  })();

  /* ── Pricing normalization ──
     Single source of truth for all format and paper identifier variants.
     Returns { canonFormat, base, adder } — use these when reconstructing
     the scoped pricing state so stale values never produce an incorrect price.
     Unknown identifiers: base = 0, adder = 0 (safe / no implicit charge). */
  function _normalizePricingFields(rawFormat, rawMaterial) {
    const fmt = String(rawFormat  || '').toLowerCase().trim();
    const mat = String(rawMaterial || '').toLowerCase().trim();

    /* Format aliases → canonical ID + base price (₹) */
    let canonFormat = rawFormat || '';
    let base = 0;
    if      (fmt === 'square'    || fmt === '8x8'  || fmt.startsWith('8x8 ('))  { canonFormat = 'square';      base = 1899; }
    else if (fmt === 'landscape' || fmt === '12x8' || fmt.startsWith('12x8 (')) { canonFormat = 'landscape';   base = 2099; }
    else if (fmt === 'portrait810'|| fmt === '8x10' || fmt.startsWith('8x10 (')) { canonFormat = 'portrait810'; base = 2099; }
    else if (fmt === 'portrait812'|| fmt === 'portrait' || fmt === '8x12' || fmt.startsWith('8x12 (')) { canonFormat = 'portrait812'; base = 2099; }
    /* Unknown format: base = 0 so price shows '—' rather than a stale/wrong amount */

    /* Material / paper aliases → adder (₹) */
    let adder = 0;
    if (mat === 'non tearable' || mat === 'premium ntr' || mat === 'premium non tearable') {
      adder = 300;
    }
    /* Standard-paper aliases ('premium paper', 'standard archival') and unknowns: adder = 0 */

    return { canonFormat, base, adder };
  }

  /* ── Main interactive preview class ── */
  class OiBookPreview {

    CLOTHS = [
      { id:'linen',    label:'Linen Oatmeal', hex:'#E8E0D0', on:'dark'  },
      { id:'olive',    label:'Velvet Olive',  hex:'#4A5C3A', on:'light' },
      { id:'charcoal', label:'Soft Charcoal', hex:'#2E3038', on:'light' },
      { id:'sand',     label:'Leather Sand',  hex:'#C4A882', on:'dark'  },
      { id:'oxblood',  label:'Oxblood',       hex:'#5E2A2A', on:'light' },
      { id:'white',    label:'Pearl White',   hex:'#F8F6F2', on:'dark'  },
      { id:'yellow',   label:'Canary Yellow', hex:'#FFF917', on:'dark'  },
    ];
    FOILS = [
      { id:'gold',   label:'Gold',         color:'#C9A55F' },
      { id:'silver', label:'Matte Silver', color:'#ADADAD' },
      { id:'white',  label:'White ink',    color:'#F3EEE4' },
      { id:'charcoal', label:'Charcoal ink', color:'#333333' },
    ];
    /* Retired stamps -> the stamp that now replaces them. Blind deboss was removed:
       on screen it looked like matte silver, so books that had it keep that look. */
    LEGACY_FOILS = { blind: 'silver' };
    FONTS = [
      /* coverWeight: weight of the cover title/subtitle/spine stamping. Cormorant at
         400 is hairline-thin and prints faintly, so its cover text uses SemiBold. */
      { id:'cormorant',   label:'Cormorant',   family:"'Cormorant Garamond',serif", coverWeight:600 },
      { id:'playfair',    label:'Playfair',     family:"'Playfair Display',serif"     },
      { id:'baskerville', label:'Baskerville',  family:"'Libre Baskerville',serif"    },
      { id:'worksans',    label:'Work Sans',    family:"'Work Sans',sans-serif"       },
      { id:'caveat',      label:'Caveat',       family:"'Caveat',cursive"             },
      /* Added Oct 2026. pagesOnly: a script for page titles and names; it is not
         offered as the book/cover font because covers are set in capitals, which a
         script cannot do legibly (and its thin strokes foil poorly). */
      { id:'greatvibes',  label:'Great Vibes',  family:"'Great Vibes',cursive", pagesOnly:true },
      { id:'specialelite',label:'Special Elite',family:"'Special Elite',cursive"       },
      { id:'lora',        label:'Lora',         family:"'Lora',serif"                 },
      { id:'montserrat',  label:'Montserrat',   family:"'Montserrat',sans-serif"      },
    ];
    VIEWS = [
      { kind:'page',   pages:[1]      },
      { kind:'spread', pages:[2,3]    },
      { kind:'spread', pages:[4,5]    },
      { kind:'spread', pages:[6,7]    },
      { kind:'spread', pages:[8,9]    },
      { kind:'spread', pages:[10,11]  },
      { kind:'spread', pages:[12,13]  },
      { kind:'spread', pages:[14,15]  },
      { kind:'spread', pages:[16,17]  },
      { kind:'spread', pages:[18,19]  },
      { kind:'page',   pages:[20]     },
    ];
    LAYOUTS_PAGE = [
      { id:'gallery',         label:'Gallery page',   slots: [{x:.05,  y:.05,  w:.90,   h:.90  }] },
      { id:'fullBleed',       label:'Full bleed',     slots: [{x:0,    y:0,    w:1,     h:1    }] },
      { id:'portrait',        label:'Portrait focus', slots: [{x:.17,  y:.05,  w:.66,   h:.90  }] },
      { id:'twoVertical',     label:'Two vertical',   slots: [{x:.05,  y:.05,  w:.425,  h:.90  },
                                                              {x:.525, y:.05,  w:.425,  h:.90  }] },
      { id:'twoHorizontal',   label:'Two horizontal', slots: [{x:.05,  y:.05,  w:.90,   h:.425 },
                                                              {x:.05,  y:.525, w:.90,   h:.425 }] },
      /* Square layouts: slots are computed for the book's real page shape by
         _fitSquareLayouts, so the photos stay square on every format. */
      { id:'twoSquares',      label:'Two squares',    squares: { cols: 2, rows: 1 }, slots: [] },
      { id:'fourSquares',     label:'Four squares',   squares: { cols: 2, rows: 2 }, slots: [] },
      { id:'heroDetails',     label:'Hero + details', slots: [{x:.05,  y:.05,  w:.575,  h:.90  },
                                                              {x:.675, y:.05,  w:.275,  h:.425 },
                                                              {x:.675, y:.525, w:.275,  h:.425 }] },
      { id:'photoCaption',    label:'Photo + caption', slots: [{x:.05, y:.04, w:.90, h:.70}],
        textZones: [{ key:'caption', x:.05, y:.77, w:.90, h:.18, fontSize:.038, align:'center', style:'caption', placeholder:'Add a caption…' }] },
      { id:'textFull',        label:'Text page', slots: [],
        textZones: [
          { key:'title', x:.08, y:.20, w:.84, h:.16, fontSize:.058, align:'center', style:'heading', placeholder:'Title' },
          { key:'body',  x:.08, y:.40, w:.84, h:.44, fontSize:.030, align:'center', style:'body',    placeholder:'Write your story here…' },
        ] },
    ];
    LAYOUTS_SPREAD = [
      { id:'balanced',        label:'Balanced pair',    slots: [{x:.025,  y:.05,  w:.45,    h:.90   },
                                                                {x:.525,  y:.05,  w:.45,    h:.90   }] },
      { id:'galleryPair',     label:'Gallery pair',     slots: [{x:.075,  y:.10,  w:.35,    h:.80   },
                                                                {x:.575,  y:.10,  w:.35,    h:.80   }] },
      { id:'heroTwo',         label:'Hero + two',       slots: [{x:.025,  y:.05,  w:.45,    h:.90   },
                                                                {x:.525,  y:.05,  w:.45,    h:.425  },
                                                                {x:.525,  y:.525, w:.45,    h:.425  }] },
      /* Hero + two with a caption under each photo. Same slot order as heroTwo
         (hero, top right, bottom right) so switching keeps photos in place; its
         own caption keys so text from other caption layouts does not move in. */
      { id:'heroTwoCaptions', label:'Hero + two · captions', slots: [{x:.025, y:.04, w:.45, h:.70 },
                                                                    {x:.525, y:.04, w:.45, h:.32 },
                                                                    {x:.525, y:.52, w:.45, h:.32 }],
        textZones: [
          { key:'captionHero',   x:.025, y:.77, w:.45, h:.17, fontSize:.034, align:'center', style:'caption', placeholder:'Caption…' },
          { key:'captionTop',    x:.525, y:.37, w:.45, h:.10, fontSize:.030, align:'center', style:'caption', placeholder:'Caption…' },
          { key:'captionBottom', x:.525, y:.85, w:.45, h:.10, fontSize:.030, align:'center', style:'caption', placeholder:'Caption…' },
        ] },
      /* Hero + two with one caption, under the hero only. Right photos stay full
         size as in heroTwo; shares captionHero so the hero caption survives a
         switch to or from heroTwoCaptions. */
      { id:'heroTwoOneCaption', label:'Hero + two · one caption', slots: [{x:.025, y:.05,  w:.45, h:.78  },
                                                                        {x:.525, y:.05,  w:.45, h:.425 },
                                                                        {x:.525, y:.525, w:.45, h:.425 }],
        textZones: [
          { key:'captionHero', x:.025, y:.85, w:.45, h:.10, fontSize:.034, align:'center', style:'caption', placeholder:'Caption…' },
        ] },
      { id:'twoHero',         label:'Two + hero',       slots: [{x:.025,  y:.05,  w:.45,    h:.425  },
                                                                {x:.025,  y:.525, w:.45,    h:.425  },
                                                                {x:.525,  y:.05,  w:.45,    h:.90   }] },
      { id:'editorial',       label:'Editorial story',  slots: [{x:.025,  y:.05,  w:.45,    h:.54   },
                                                                {x:.025,  y:.64,  w:.2125,  h:.31   },
                                                                {x:.2625, y:.64,  w:.2125,  h:.31   },
                                                                {x:.525,  y:.05,  w:.2125,  h:.40   },
                                                                {x:.7625, y:.05,  w:.2125,  h:.40   },
                                                                {x:.525,  y:.50,  w:.45,    h:.45   }] },
      { id:'twoSquaresEach',  label:'Two squares each page',  squares: { cols: 2, rows: 1 }, slots: [] },
      { id:'fourSquaresEach', label:'Four squares each page', squares: { cols: 2, rows: 2 }, slots: [] },
      { id:'captionPair',     label:'Photos + captions', slots: [{x:.025, y:.04, w:.45, h:.70}, {x:.525, y:.04, w:.45, h:.70}],
        textZones: [
          { key:'captionL', x:.025, y:.77, w:.45,  h:.18, fontSize:.036, align:'center', style:'caption', placeholder:'Left caption…'  },
          { key:'captionR', x:.525, y:.77, w:.45,  h:.18, fontSize:.036, align:'center', style:'caption', placeholder:'Right caption…' },
        ] },
      { id:'captionPairTop',  label:'Captions + photos', slots: [{x:.025, y:.23, w:.45, h:.72}, {x:.525, y:.23, w:.45, h:.72}],
        textZones: [
          { key:'captionL', x:.025, y:.04, w:.45,  h:.16, fontSize:.036, align:'center', style:'caption', placeholder:'Left caption…'  },
          { key:'captionR', x:.525, y:.04, w:.45,  h:.16, fontSize:.036, align:'center', style:'caption', placeholder:'Right caption…' },
        ] },
      { id:'captionBotL',    label:'Caption below · left',  slots: [{x:.025, y:.04, w:.45, h:.70}, {x:.525, y:.05, w:.45, h:.90}],
        textZones: [
          { key:'caption', x:.025, y:.77, w:.45, h:.18, fontSize:.036, align:'center', style:'caption', placeholder:'Add caption…' },
        ] },
      { id:'captionBotR',    label:'Caption below · right', slots: [{x:.025, y:.05, w:.45, h:.90}, {x:.525, y:.04, w:.45, h:.70}],
        textZones: [
          { key:'caption', x:.525, y:.77, w:.45, h:.18, fontSize:.036, align:'center', style:'caption', placeholder:'Add caption…' },
        ] },
      { id:'captionTopL',    label:'Caption above · left',  slots: [{x:.025, y:.23, w:.45, h:.72}, {x:.525, y:.05, w:.45, h:.90}],
        textZones: [
          { key:'caption', x:.025, y:.04, w:.45, h:.16, fontSize:.036, align:'center', style:'caption', placeholder:'Add caption…' },
        ] },
      { id:'captionTopR',    label:'Caption above · right', slots: [{x:.025, y:.05, w:.45, h:.90}, {x:.525, y:.23, w:.45, h:.72}],
        textZones: [
          { key:'caption', x:.525, y:.04, w:.45, h:.16, fontSize:.036, align:'center', style:'caption', placeholder:'Add caption…' },
        ] },
      { id:'captionLeft',     label:'Caption left',  slots: [{x:.525, y:.05, w:.45, h:.90}],
        textZones: [
          { key:'caption', x:.025, y:.05, w:.45, h:.90, fontSize:.042, align:'center', style:'caption', placeholder:'Add caption…' },
        ] },
      { id:'captionRight',    label:'Caption right', slots: [{x:.025, y:.05, w:.45, h:.90}],
        textZones: [
          { key:'caption', x:.525, y:.05, w:.45, h:.90, fontSize:.042, align:'center', style:'caption', placeholder:'Add caption…' },
        ] },
      { id:'storyLeft',       label:'Story left', slots: [{x:.525, y:.05, w:.45, h:.90}],
        textZones: [
          { key:'title', x:.05, y:.16, w:.43, h:.14, fontSize:.062, align:'left', style:'heading', placeholder:'Title' },
          { key:'body',  x:.05, y:.34, w:.43, h:.52, fontSize:.028, align:'left', style:'body',    placeholder:'Write your story…' },
        ] },
      { id:'storyRight',      label:'Story right', slots: [{x:.025, y:.05, w:.45, h:.90}],
        textZones: [
          { key:'title', x:.535, y:.16, w:.43, h:.14, fontSize:.062, align:'left', style:'heading', placeholder:'Title' },
          { key:'body',  x:.535, y:.34, w:.43, h:.52, fontSize:.028, align:'left', style:'body',    placeholder:'Write your story…' },
        ] },
      { id:'dualBleed',       label:'Dual bleed',  slots: [{x:0,  y:0, w:.5, h:1}, {x:.5, y:0, w:.5, h:1}] },
    ];

    constructor(root) {
      this.root  = root;
      this.store      = new PhotoStore();
      this._t         = null;
      this._savedCount = 0;
      this._kickTotal  = 0;
      this._pendingPhotoUploads = [];
      this._autoSaveTimer = null;
      this._loadingFromServer = false;
      this._restoring = false;
      this._uploadStuck = false;   /* true after 5 consecutive upload failures — shows error + Retry */
      this._photosConfirmedOnServer = 0;
      this._initInProgress = false;
      this._inFlightUploads = new Map(); /* fingerprint → Promise<entry> for concurrency coalescing */

      /* ── Individual source-photo upload queue (bounded concurrency + retry) ──
         Replaces firing every new photo's upload at once with no retry. Each photo
         gets its own backoff; a stuck photo doesn't block others from uploading. */
      this._photoUploadQueue        = [];        /* items waiting for a free concurrency slot */
      this._photoUploadActive       = 0;
      this._photoUploadConcurrency  = 2;          /* matches _readFiles' decode concurrency */
      this._photoUploadKeysPending  = new Set();  /* assetKeys currently queued/uploading/backing off — dedup guard */
      this._photoUploadStuck        = false;      /* true once any photo hits 5 consecutive failures */
      this._stuckPhotoUploads       = [];         /* items parked after 5 failures, awaiting manual Retry */

      /* ── Issue #26: per-project scope ──
         Scope = 'project:<id>' once a projectId is known, else 'draft:<uuid>'.
         oiActiveBook is the global pointer; all storage keys are suffixed with it. */
      {
        let _ab = null;
        try { _ab = localStorage.getItem('oiActiveBook'); } catch(e) {}
        if (!_ab) {
          /* Migration: bootstrap from legacy unscoped oiCraft if a projectId exists */
          let _leg = {};
          try { _leg = JSON.parse(localStorage.getItem('oiCraft') || '{}'); } catch(e) {}
          if (_leg.projectId) {
            _ab = 'project:' + _leg.projectId;
            try { localStorage.setItem('oiCraft:' + _ab, JSON.stringify(_leg)); } catch(e) {}
          } else {
            _ab = 'draft:' + crypto.randomUUID();
          }
          try { localStorage.setItem('oiActiveBook', _ab); } catch(e) {}
        }
        this._scope = _ab;
        /* If My Books linked here with ?resume=<projectId>, switch to that project's scope.
           Without this the URL param is silently ignored and the wrong project could load. */
        try {
          const _resumeId = new URLSearchParams(location.search).get('resume');
          if (_resumeId) {
            const _resumeScope = 'project:' + _resumeId;
            if (!localStorage.getItem('oiCraft:' + _resumeScope)) {
              localStorage.setItem('oiCraft:' + _resumeScope, JSON.stringify({ projectId: _resumeId }));
            }
            localStorage.setItem('oiActiveBook', _resumeScope);
            this._scope = _resumeScope;
          }
        } catch(_e) {}
        this._epoch = 0;
      }

      /* Sync format/material from URL params (booksize, paper, finish, material) into oiCraftPricing
         so _formatDims() gets the right dimensions even on a direct/shared URL load */
      (() => {
        var p = new URLSearchParams(location.search);
        var bs = p.get('booksize');
        if (!bs) return;
        var pricing = {};
        try { pricing = JSON.parse(localStorage.getItem('oiCraftPricing:' + this._scope) || '{}'); } catch(e) {}
        var paper    = p.get('paper');
        var finish   = p.get('finish');
        var material = p.get('material');
        var _urlNorm = _normalizePricingFields(bs, paper || pricing.material);
        pricing.format = _urlNorm.canonFormat;
        pricing.base   = _urlNorm.base;
        if (paper) { pricing.material = paper; pricing.adder = _urlNorm.adder; }
        if (finish)   pricing.finish   = finish;
        /* ?material is the cover cloth — stored in oiCraft, not in pricing */
        localStorage.setItem('oiCraftPricing:' + this._scope, JSON.stringify(pricing));
      })();

      /* Load saved state */
      let saved = {};
      try { saved = JSON.parse(localStorage.getItem('oiCraft:' + this._scope) || '{}'); } catch(e) {}

      /* Foil: URL param wins so shared/bookmarked links carry the right foil. */
      {
        const _urlFoil = new URLSearchParams(location.search).get('foil');
        if (_urlFoil) saved.foil = _urlFoil;
      }

      /* Destination/category nav: always apply URL params so picking a new destination from the
         flyout updates the cover immediately, even if a previous destination was already saved. */
      {
        const _urlDest       = new URLSearchParams(location.search).get('destination');
        const _urlCoverPhoto = new URLSearchParams(location.search).get('coverPhoto');
        if (_urlDest) {
          const _prevDest = (saved.destination || '').toLowerCase();
          const _destChanged = _urlDest.toLowerCase() !== _prevDest;
          /* If the cover title was auto-derived from the old destination, clear it so the new
             destination name shows on the cover instead. */
          if (_destChanged && saved.title && saved.title.toLowerCase() === _prevDest) {
            saved.title = '';
          }
          saved.destination = _urlDest;
          /* Cover photo: use the URL-carried image (all categories), else look up in travel list. */
          if (_urlCoverPhoto) {
            saved.coverPhoto = _urlCoverPhoto;
          } else {
            const _dests = window.OI_DESTINATIONS || [];
            const _m = _dests.find(function(d) { return d.city && d.city.toLowerCase() === _urlDest.toLowerCase(); });
            saved.coverPhoto = (_m && _m.image) ? _m.image : null;
          }
        }
      }

      /* Restore extra spreads from previous session — VIEWS resets on each page load but
         pricing.sleeves persists, so without this the spread count and cart items are wrong. */
      let _pricingInit = {};
      try { _pricingInit = JSON.parse(localStorage.getItem('oiCraftPricing:' + this._scope) || '{}'); } catch(e) {}
      const _prevSleeves = Math.max(0, parseInt(_pricingInit.sleeves || 0, 10));
      for (let _s = 0; _s < _prevSleeves; _s++) {
        this.VIEWS.splice(this.VIEWS.length - 1, 0, { kind: 'spread', pages: [] });
      }
      if (_prevSleeves > 0) this._renumberViews();

      /* Detect destination change — if user picked a new destination since the last
         project was created, discard the stale projectId and clear IDB photos so the
         preview starts clean. projectDestination is written each time we get a new projectId. */
      if (saved.projectId && saved.destination && saved.projectDestination) {
        const curSlug  = saved.destination.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        const projSlug = saved.projectDestination.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        if (curSlug !== projSlug) {
          /* Destination changed — abandon the current scope and start a fresh draft */
          const freshScope = 'draft:' + crypto.randomUUID();
          this._scope = freshScope;
          try { localStorage.setItem('oiActiveBook', freshScope); } catch(e) {}
          saved = {};
        }
      }

      let _savedTransforms = {};
      try { _savedTransforms = JSON.parse(localStorage.getItem('oiSlotTransforms:' + this._scope) || '{}'); } catch(e) {}

      this.state = {
        view:          'cover',
        spreadIdx:     0,
        clothId:       (this.CLOTHS.find(c => c.id === saved.clothId) ? saved.clothId : null) || this._matToCloth(saved.material),
        foilId:        this._knownFoil(saved.foilId) || this._foilToId(saved.foil),
        fontId:        (this.FONTS.find(f => f.id === saved.fontId)   ? saved.fontId  : null) || 'cormorant',
        title:         (saved.title        || '').toUpperCase(),
        subtitle:      (saved.subtitle      || '').toUpperCase(),
        spineText:     (saved.destination  || saved.title || '').toUpperCase(),
        coverImage:    null,   /* blob URL */
        coverPhoto:    saved.coverPhoto || null, /* destination image from book-selector */
        uploads:       [],     /* [{url, name}] from IDB */
        selectedUpload: null,
        slotImages:    {},     /* { 'sI:N': blobUrl } */
        slotBlobs:     {},     /* { 'sI:N': File|Blob } — blob source for canvas render fallback */
        slotTransforms: _savedTransforms, /* { 'sI:N': { scale, x, y } } — pan/zoom per slot */
        activeSlot:    null,   /* mobile slot-pick mode: key of slot awaiting a photo */
        captions:      {},
        textContent:   {},   /* { 'spreadIdx:zoneKey': string } — text zone content */
        textFormat:    {},   /* { 'spreadIdx:zoneKey': { fontId, family, align } } — per-zone font+align */
        layouts:       saved.layouts || {},
        spreadModes:   saved.spreadModes || {},
        spreadBg:      saved.spreadBg || {},
        coverLayout:   saved.coverLayout || null,  /* null = auto-default */
        showBackLogo:  saved.showBackLogo !== false,
        showCoverText: saved.showCoverText !== false,
        showSpineText: saved.showSpineText !== false,
        spineDir:      _spineDir(saved.spineDir),
        coverTransform: saved.coverTransform
          ? { scale: Math.max(0.5, Math.min(10, saved.coverTransform.scale || 1)),
              nx:    Math.max(-1, Math.min(1, saved.coverTransform.nx   || 0)),
              ny:    Math.max(-1, Math.min(1, saved.coverTransform.ny   || 0)) }
          : { scale: 1, nx: 0, ny: 0 },
        /* Issue 11: track original photo identity separately from the generated cover wrap */
        coverSourceAssetKey: 'coverSourceAssetKey' in saved ? saved.coverSourceAssetKey : null,
        coverRemoved: !!saved.coverRemoved,
        projectId:     saved.projectId || null,
        added:         false,
        dirtyViews:    new Set(),   /* view indices needing re-upload at cart time */
      };
      this._filmThumbCache = new Map(); /* spreadIdx → blob URL of rendered thumbnail */

      /* DOM refs */
      const q = sel => root.querySelector(sel);
      this.el = {
        backCover:    q('[data-back-cover]'),
        backMark:     q('[data-back-mark]'),
        backFine:     q('[data-back-fine]'),
        spine:        q('[data-spine]'),
        spineText:    q('[data-spine-text]'),
        frontCover:   q('[data-front-cover]'),
        coverSlot:    q('[data-cover-slot]'),
        coverPhoto:   q('[data-cover-photo]'),
        coverPh:      q('[data-cover-ph]'),
        foldBack:            q('[data-fold-back]'),
        foldFront:           q('[data-fold-front]'),
        coverWrapGuide:      q('[data-cover-wrap-guide]'),
        coverLayoutCards:    q('[data-cover-layout-cards]'),
        mobCoverLayoutCards: q('[data-mob-cover-layout-cards]'),
        backLogoWrap:        q('[data-back-logo-wrap]'),
        backLogoChk:         q('[data-back-logo-chk]'),
        mobBackLogoChk:      q('[data-mob-back-logo-chk]'),
        coverTitle:   q('[data-cover-title]'),
        coverSubtitle:q('[data-cover-subtitle]'),
        viewCover:    q('[data-view-cover]'),
        viewSpread:   q('[data-view-spread]'),
        viewAnim:     q('[data-view-anim]'),
        coverOuter:   q('[data-cover-outer]'),
        spreadOuter:  q('[data-spread-outer]'),
        spreadGrid:   q('[data-spread-grid]'),
        gutter:       q('[data-gutter]'),
        spreadLabel:  q('[data-spread-label]'),
        caption:      q('[data-caption]'),
        book:         q('[data-book]'),
        stage:        q('[data-stage]'),
        navPrev:      q('[data-nav-prev]'),
        navNext:      q('[data-nav-next]'),
        panelTitle:   q('[data-panel-title]'),
        panelHint:    q('[data-panel-hint]'),
        panelCover:   q('[data-panel-cover]'),
        panelSpread:  q('[data-panel-spread]'),
        swatches:     q('[data-swatches]'),
        swatchLabel:  q('[data-swatch-label]'),
        foils:        q('[data-foils]'),
        fonts:        q('[data-fonts]'),
        inputTitle:   q('[data-input-title]'),
        inputSubtitle:q('[data-input-subtitle]'),
        inputSpine:   q('[data-input-spine]'),
        layoutHeading:q('[data-layout-heading]'),
        layoutCards:  q('[data-layout-cards]'),
        textStyleSection:      q('[data-text-style-section]'),
        textStyleBody:         q('[data-text-style-body]'),
        specs:        q('[data-specs]'),
        cartErr:      q('[data-cart-err]'),
        filmCover:    q('[data-filmstrip-cover]'),
        filmSpreads:  q('[data-filmstrip-spreads]'),
        thumbBack:    q('[data-thumb-back]'),
        thumbSpine:   q('[data-thumb-spine]'),
        thumbFront:   q('[data-thumb-front]'),
        thumbPhoto:   q('[data-thumb-photo]'),
        price:        q('[data-price]'),
        cartBtn:      q('[data-cart-btn]'),
        uploadCount:  q('[data-upload-count]'),
        uploadGrid:   q('[data-upload-grid]'),
        uploadEmpty:  q('[data-upload-empty]'),
        saveStatus:   q('[data-save-status]'),
        alert:        q('[data-oi-alert]'),
        dropZone:     q('[data-drop-zone]'),
        fileInput:    q('[data-file-input]'),
        /* Mobile refs */
        mobNavPrev:   q('[data-mob-nav-prev]'),
        mobNavNext:   q('[data-mob-nav-next]'),
        mpbPrev:      q('[data-mpb-prev]'),
        mpbNext:      q('[data-mpb-next]'),
        mpbLabel:     q('[data-mpb-label]'),
        mpbWhere:     q('[data-mpb-where]'),
        mpbOf:        q('[data-mpb-of]'),
        mobPagebar:   q('[data-mob-pagebar]'),
        mobUploadCount: q('[data-mob-upload-count]'),
        mobUploadGrid:  q('[data-mob-upload-grid]'),
        mobUploadEmpty: q('[data-mob-upload-empty]'),
        mobDropZone:    q('[data-mob-drop-zone]'),
        mobFileInput:   q('[data-mob-file-input]'),
        mobSwatches:    q('[data-mob-swatches]'),
        mobSwatchLabel: q('[data-mob-swatch-label]'),
        mobFoils:       q('[data-mob-foils]'),
        mobFonts:       q('[data-mob-fonts]'),
        mobInputTitle:  q('[data-mob-input-title]'),
        mobInputSpine:  q('[data-mob-input-spine]'),
        mobInputSubtitle: q('[data-mob-input-subtitle]'),
        mobPanelCover:  q('[data-mob-panel-cover]'),
        mobPanelSpread: q('[data-mob-panel-spread]'),
        mobLayoutHeading: q('[data-mob-layout-heading]'),
        mobLayoutCards: q('[data-mob-layout-cards]'),
        mobTextStyleSection:   q('[data-mob-text-style-section]'),
        mobTextStyleBody:      q('[data-mob-text-style-body]'),
        mobTextEmpty:          q('[data-mob-text-empty]'),
        mobFilmstrip:   q('[data-mob-filmstrip]'),
        mobRailPhotos:  q('[data-mob-rail-photos]'),
      };

      /* Prefill text inputs */
      this._syncTextInputs();

      /* Wire everything */
      this._initParallax();
      this._initNav();
      this._initInputs();
      this._initPhotoWell();
      this._initCart();
      this._initBookPreview();
      this._initMobileTabs();
      this._initMobileScale();
      this._initTouch();
      this._initCoverCrop();
      this._initCropControls();
      this._applyFormat();
      /* Follow the book's own setting. This used to be a hard-coded false, from when
         Add Text started unchecked; once the switch began reflecting showCoverText
         (on unless the book turned it off) that left the switch on and the title on
         the cover, but the Title / Subtitle / Stamping / Typeface fields hidden — on
         desktop and phone — so a customer could not find where to edit the title. */
      this._applyEngraveToggle(this.state.showCoverText);
      this._renderAll();
      /* Cover text is sized by measuring the typeface; measure again once a web
         font finishes loading, or long titles are sized from the fallback font. */
      try { document.fonts.addEventListener('loadingdone', () => this._renderCover()); } catch (e) {}
      this._renderMobPageBar();
      this._renderFilmstrip();
      this._renderPrice();
      this._renderSpecs();
      /* Show destination cover photo saved by book-selector before photos load */
      if (this.state.coverPhoto) this._placeCover(this.state.coverPhoto);
      /* Kept so ?preview=1 (My Books → Preview) can wait for the book to finish loading. */
      this._photosLoaded = this._loadPhotos();
      /* Always try to init the project on load so My Books is populated as soon as
         the user is signed in, even before they upload any photos. _initProject guards
         against double-init and unauthenticated calls internally. */
      this._initProject();

      /* Hide Shopify chrome */
      document.querySelectorAll('header[role="banner"], .shopify-section-group-header-group, footer[role="contentinfo"], .shopify-section-group-footer-group').forEach(el => {
        el.style.display = 'none';
      });
    }

    /* ── Mobile: dock tabs + bottom sheet ── */
    _initMobileTabs() {
      /* Render filmstrip when Pages section is opened */
      const pagesSection = document.getElementById('oi2-sec-pages');
      if (pagesSection) {
        pagesSection.addEventListener('toggle', () => {
          if (pagesSection.open) this._renderMobileFilmstrip();
        });
      }

      /* Mobile nav buttons mirror main buttons */
      if (this.el.mobNavPrev) this.el.mobNavPrev.addEventListener('click', () => this.el.navPrev?.click());
      if (this.el.mobNavNext) this.el.mobNavNext.addEventListener('click', () => this.el.navNext?.click());

      /* Page bar: arrows step through the book, the label opens every page. */
      if (this.el.mpbPrev) this.el.mpbPrev.addEventListener('click', () => this.el.navPrev?.click());
      if (this.el.mpbNext) this.el.mpbNext.addEventListener('click', () => this.el.navNext?.click());
      if (this.el.mpbLabel) this.el.mpbLabel.addEventListener('click', () => {
        const open = this.root.querySelector('[data-dock-tab="pages"]')?.getAttribute('aria-selected') === 'true';
        if (open) this._closeMobSheet(); else this._openMobSheet('pages');
      });

      /* Dock tab buttons: toggle sheet open/closed */
      this.root.querySelectorAll('[data-dock-tab]').forEach(btn => {
        btn.addEventListener('click', () => {
          const tab = btn.dataset.dockTab;
          const isActive = btn.getAttribute('aria-selected') === 'true';
          if (isActive) {
            this._closeMobSheet();
          } else {
            this._openMobSheet(tab);
          }
        });
      });

      /* Tap on stage closes sheet */
      this.el.stage?.addEventListener('touchstart', () => this._closeMobSheet(), { passive: true });
    }

    _openMobSheet(tab) {
      const bar = this.root.querySelector('.oi2-mob-bar');
      if (!bar) return;

      /* 1. Guard: validate Edit context before any DOM mutation */
      if (tab === 'edit') {
        if (this.state.view === 'spread') {
          const key = this.state.activeSlot;
          if (!key || !this.state.slotImages[key]) { this._closeMobSheet(); return; }
        } else {
          if (!this.state.coverImage) { this._closeMobSheet(); return; }
        }
      }

      /* 1b. Typing in a text box on the page keeps the phone keyboard up: tapping a
         photo or a tab does not take the cursor out of it (iOS especially), so the
         keyboard would cover the sheet that opens. Leave the text box first (its
         text is already saved on every keystroke). Fields inside the sheet are left. */
      const typing = document.activeElement;
      if (typing && typing !== document.body && this.el.stage?.contains(typing) &&
          (typing.isContentEditable || /^(INPUT|TEXTAREA)$/.test(typing.tagName))) typing.blur();

      /* 2. Navigate to cover view when Cover tab is tapped */
      if (tab === 'cover' && this.state.view !== 'cover') this._goCover();
      /* The cover has no page background: Background from the cover goes to page 1. */
      if ((tab === 'background' || tab === 'text') && this.state.view === 'cover') this._goSpread(0);

      /* 3. Update dock tab indicators */
      this.root.querySelectorAll('[data-dock-tab]').forEach(b => {
        b.setAttribute('aria-selected', b.dataset.dockTab === tab ? 'true' : 'false');
      });

      /* 4. Show the correct view panel; hide the other */
      const isCover = this.state.view === 'cover';
      if (this.el.mobPanelCover)  this.el.mobPanelCover.style.display  = isCover  ? 'flex' : 'none';
      if (this.el.mobPanelSpread) this.el.mobPanelSpread.style.display = !isCover ? 'flex' : 'none';

      /* 5. Tool isolation: hide every labelled section, then reveal only the active tool */
      bar.querySelectorAll('[data-tool-section]').forEach(el => el.classList.add('oi2-mob-tool-hidden'));
      bar.querySelectorAll(`[data-tool-section="${tab}"]`).forEach(el => el.classList.remove('oi2-mob-tool-hidden'));

      /* 6. Open tray and reset scroll (data-sheet lets CSS size a sheet per tool) */
      bar.dataset.sheet = tab;
      /* Phone held sideways: the sheet is a right-hand column that starts under
         the header (CSS); oi2-sheet-open makes the stage give it room. */
      const hdr = this.root.querySelector('.oi2-hdr');
      this.root.style.setProperty('--oi-sheet-top', Math.round(hdr ? hdr.getBoundingClientRect().bottom : 56) + 'px');
      this.root.classList.add('oi2-sheet-open');
      bar.classList.add('oi2-mob-bar--open');
      const scroll = bar.querySelector('.oi2-mob-panel-scroll');
      if (scroll) scroll.scrollTop = 0;

      /* 7. Open the matching details element and populate controls */
      const panel = isCover ? this.el.mobPanelCover : this.el.mobPanelSpread;
      if (tab === 'layout') {
        const d = panel?.querySelector('[data-tool-section="layout"]');
        if (d) d.open = true;
      } else if (tab === 'background') {
        const d = panel?.querySelector('[data-tool-section="background"]');
        if (d) d.open = true;
      } else if (tab === 'text') {
        const d = panel?.querySelector('[data-tool-section="text"]');
        if (d) d.open = true;
        this._renderTextStyleSection();
      } else if (tab === 'edit') {
        if (isCover) {
          const d = this.el.mobPanelCover?.querySelector('[data-tool-section="edit"]');
          if (d) d.open = true;
        } else {
          const key = this.state.activeSlot;
          this._renderSlotCropControls(key);
          const d = this.el.mobPanelSpread?.querySelector('[data-tool-section="edit"]');
          if (d) d.open = true;
        }
      } else if (tab === 'cover') {
        const d = this.el.mobPanelCover?.querySelector('[data-tool-section="cover"]');
        if (d) d.open = true;
      } else if (tab === 'pages') {
        const sec = document.getElementById('oi2-sec-pages');
        if (sec) { sec.open = true; this._renderMobileFilmstrip(); }
      } else if (tab === 'photos') {
        const sec = document.getElementById('oi2-sec-photos');
        if (sec) sec.open = true;
      }
    }

    _closeMobSheet() {
      const bar = this.root.querySelector('.oi2-mob-bar');
      if (!bar) return;
      bar.classList.remove('oi2-mob-bar--open');
      this.root.classList.remove('oi2-sheet-open');
      this.root.querySelectorAll('[data-dock-tab]').forEach(b => b.setAttribute('aria-selected', 'false'));
      /* Clear tool isolation so the next open starts from a clean slate — but only
         once the sheet has finished sliding shut. Clearing it straight away showed
         every section (Layout, Cover settings, Pages) inside the closing sheet, a
         flash of the wrong tools on every close. _openMobSheet re-isolates anyway. */
      clearTimeout(this._mobSheetCleanTimer);
      this._mobSheetCleanTimer = setTimeout(() => {
        if (bar.classList.contains('oi2-mob-bar--open')) return;
        bar.querySelectorAll('.oi2-mob-tool-hidden').forEach(el => el.classList.remove('oi2-mob-tool-hidden'));
      }, 600);
    }

    /* Open a specific section by name — used when tapping an empty slot. */
    _openMobDrawer(id) {
      this._openMobSheet(id);
    }

    /* ── Mobile: scale book to fit stage ── */
    _initMobileScale() {
      if (!this.el.stage) return;
      const ro = new ResizeObserver(() => this._applyMobileScale());
      ro.observe(this.el.stage);
      this._applyMobileScale();
    }

    _applyMobileScale() {
      const { book, stage } = this.el;
      if (book) { book.style.position = ''; book.style.left = ''; book.style.top = ''; book.style.transform = ''; book.style.transformOrigin = ''; }
      if (!stage || !book) return;
      const stageW = stage.clientWidth;
      const stageH = stage.clientHeight;
      const v = this.VIEWS[this.state.spreadIdx] || this.VIEWS[0];
      const isWide = this.state.view === 'spread' && v.kind === 'spread';
      const dims = this._formatDims();
      const bookW = this.state.view === 'cover' ? (dims.pageW * 2 + 28) : (isWide ? dims.spreadW : dims.pageW);
      const bookH = dims.pageH + 38;
      /* Scale DOWN when stage is too narrow; never scale UP (cap at 1.0).
         This handles intermediate viewports (900–1300px) where the spread
         would otherwise overflow the stage and be clipped by the right panel. */
      /* The phone/tablet page bar sits at the bottom of the stage; keep the book clear of it. */
      const pb = this.el.mobPagebar;
      const pbH = pb && pb.offsetParent ? pb.offsetHeight + 16 : 0;
      const scale = Math.min((stageW - 24) / bookW, (stageH - 40 - pbH) / bookH, 1.0);
      if (scale >= 1.0) {
        if (book.parentElement) book.parentElement.style.zoom = '';
      } else {
        if (book.parentElement) book.parentElement.style.zoom = scale.toString();
      }
    }

    /* ── Mobile: touch swipe on stage ── */
    _initTouch() {
      const stage = this.el.stage;
      if (!stage) return;
      let x0 = null;
      stage.addEventListener('touchstart', e => {
        /* Don't initiate swipe when the user is panning/pinching the cover photo */
        if (this.state.view === 'cover' && this.state.coverImage && this.el.coverSlot?.contains(e.target)) { x0 = null; return; }
        x0 = e.touches[0].clientX;
      }, { passive: true });
      stage.addEventListener('touchend', e => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        x0 = null;
        if (Math.abs(dx) < 40) return;
        if (dx < 0) this.el.navNext && this.el.navNext.click();
        else         this.el.navPrev && this.el.navPrev.click();
      }, { passive: true });
    }

    _initCoverCrop() {
      const coverSlot = this.el.coverSlot;
      if (!coverSlot) return;
      const getPhoto = () => this.el.coverPhoto;

      const applyTF = tf => {
        this.state.coverTransform = tf;
        const img = getPhoto();
        if (img) this._applySlotTransformCSS(img, tf);
        this._renderCropControls();
      };
      const saveTF = () => {
        this._saveLocalStyle({ coverTransform: this.state.coverTransform });
        this._autoSaveDraft();
      };

      coverSlot.addEventListener('wheel', e => {
        if (!this.state.coverImage) return;
        e.preventDefault(); e.stopPropagation();
        const tf  = this.state.coverTransform;
        const fac = e.deltaY < 0 ? 1.1 : 0.91;
        applyTF({ ...tf, scale: Math.max(0.5, Math.min(10, tf.scale * fac)) });
        saveTF();
      }, { passive: false });

      coverSlot.addEventListener('mousedown', e => {
        if (!this.state.coverImage || e.button !== 0) return;
        e.preventDefault();
        const tf0 = { ...this.state.coverTransform };
        const ox = e.clientX, oy = e.clientY;
        let moved = false;
        const s  = tf0.scale;
        coverSlot.style.cursor = 'grabbing';
        const mv = e2 => {
          if (Math.hypot(e2.clientX - ox, e2.clientY - oy) >= 3) moved = true;
          const sW = coverSlot.offsetWidth, sH = coverSlot.offsetHeight;
          /* Same shared travel maths as the slot drag, so the cover photo
             moves as far as the drag asks and can reach its own edges. */
          const cim = this.el.coverPhoto;
          const { maxX, maxY } = (cim && cim.naturalWidth && cim.naturalHeight)
            ? _panTravelPx(cim.naturalWidth, cim.naturalHeight, sW, sH, s)
            : { maxX: sW * 0.5, maxY: sH * 0.5 };
          applyTF({
            scale: s,
            nx: Math.max(-1, Math.min(1, tf0.nx + (e2.clientX - ox) / maxX)),
            ny: Math.max(-1, Math.min(1, tf0.ny + (e2.clientY - oy) / maxY)),
          });
        };
        const up = () => {
          /* Browsers emit a click after mouse drag. Do not let that click run
             the gallery-to-cover placement handler and reset this crop. */
          if (moved) this._suppressCoverPlacementClickUntil = Date.now() + 500;
          saveTF();
          coverSlot.style.cursor = 'grab';
          document.removeEventListener('mousemove', mv);
          document.removeEventListener('mouseup', up);
        };
        document.addEventListener('mousemove', mv);
        document.addEventListener('mouseup', up);
      });

      let touch0 = null;
      coverSlot.addEventListener('touchstart', e => {
        if (!this.state.coverImage) return;
        /* Like page photos: keep this touch from the stage, whose touchstart closes
           the tool sheet (so a second tap could never toggle the crop tools shut)
           and whose swipe would turn the page during a crop drag. */
        e.stopPropagation();
        touch0 = { tf: { ...this.state.coverTransform }, pts: Array.from(e.touches).map(t => ({ x: t.clientX, y: t.clientY })), moved: false };
      }, { passive: true });
      coverSlot.addEventListener('touchmove', e => {
        if (!touch0 || !this.state.coverImage) return;
        e.preventDefault();
        const pts = Array.from(e.touches).map(t => ({ x: t.clientX, y: t.clientY }));
        touch0.moved = true;
        const sW  = coverSlot.offsetWidth, sH = coverSlot.offsetHeight;
        if (pts.length === 2 && touch0.pts.length >= 2) {
          const d0 = Math.hypot(touch0.pts[1].x - touch0.pts[0].x, touch0.pts[1].y - touch0.pts[0].y);
          const d1 = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
          const s  = Math.max(0.5, Math.min(10, touch0.tf.scale * (d1 / d0)));
          const mx0 = (touch0.pts[0].x + touch0.pts[1].x) / 2;
          const my0 = (touch0.pts[0].y + touch0.pts[1].y) / 2;
          const mx1 = (pts[0].x + pts[1].x) / 2;
          const my1 = (pts[0].y + pts[1].y) / 2;
          const maxX = s > 1 ? sW * (s - 1) / 2 : sW * 0.5;
          const maxY = s > 1 ? sH * (s - 1) / 2 : sH * 0.5;
          applyTF({
            scale: s,
            nx: Math.max(-1, Math.min(1, touch0.tf.nx + (mx1 - mx0) / maxX)),
            ny: Math.max(-1, Math.min(1, touch0.tf.ny + (my1 - my0) / maxY)),
          });
        } else if (pts.length === 1 && touch0.pts.length === 1) {
          const s = touch0.tf.scale;
          const maxX = s > 1 ? sW * (s - 1) / 2 : sW * 0.5;
          const maxY = s > 1 ? sH * (s - 1) / 2 : sH * 0.5;
          applyTF({
            scale: s,
            nx: Math.max(-1, Math.min(1, touch0.tf.nx + (pts[0].x - touch0.pts[0].x) / maxX)),
            ny: Math.max(-1, Math.min(1, touch0.tf.ny + (pts[0].y - touch0.pts[0].y) / maxY)),
          });
        }
      }, { passive: false });
      coverSlot.addEventListener('touchend', () => {
        if (!touch0) return;
        if (touch0.moved) this._suppressCoverPlacementClickUntil = Date.now() + 500;
        saveTF(); touch0 = null;
      }, { passive: true });
    }

    _initCropControls() {
      /* Re-apply theme background CSS when a theme image finishes loading.
         We only patch background styles — never rebuild the DOM — so any
         focused text zone keeps its cursor and keyboard stays open. */
      document.addEventListener('oi:theme-image-loaded', ({ detail: { themeId } }) => {
        if (this._themeCssPats) delete this._themeCssPats[themeId];
        const tVal = `theme:${themeId}`;
        Object.entries(this.state.spreadBg).forEach(([idx, bg]) => {
          if (bg === tVal) this._refreshFilmThumb(parseInt(idx, 10));
        });
        if (this.state.spreadBg[this.state.spreadIdx] === tVal) {
          /* Patch the live spreadGrid background without a full DOM rebuild */
          const sg = this.el.spreadGrid;
          if (sg) {
            const _tp = this._getThemeCssPattern(themeId);
            sg.style.background = _tp ? _tp.color : INTERIOR_PAPER_COLOR;
            if (_tp && _tp.isImage) {
              sg.style.backgroundImage = `url('${_tp.dataUrl}')`;
              sg.style.backgroundSize = '100% auto';
              sg.style.backgroundRepeat = 'no-repeat';
              sg.style.backgroundPosition = 'top center';
            } else if (_tp) {
              sg.style.backgroundImage = `url('${_tp.dataUrl}')`;
              sg.style.backgroundSize = `${_tp.tileSize}px ${_tp.tileSize}px`;
              sg.style.backgroundRepeat = 'repeat';
            } else {
              sg.style.backgroundImage = '';
            }
          }
        }
      });

      /* Desktop right panel tab switching */
      this.root.querySelectorAll('[data-panel-spread] [data-tab]').forEach(tab => {
        tab.addEventListener('click', () => {
          const pane = tab.dataset.tab;
          const panel = tab.closest('[data-panel-spread]');
          if (!panel) return;
          panel.querySelectorAll('[data-tab]').forEach(t => t.classList.toggle('active', t === tab));
          panel.querySelectorAll('[data-tab-pane]').forEach(p => p.classList.toggle('active', p.dataset.tabPane === pane));
        });
      });

      const applyCV = tf => {
        this.state.coverTransform = tf;
        const img = this.el.coverPhoto;
        if (img) this._applySlotTransformCSS(img, tf);
        this._saveLocalStyle({ coverTransform: tf });
        this._autoSaveDraft();
        this._renderCropControls();
      };

      /* Cover crop inputs — querySelectorAll finds both desktop and mobile */
      this.root.querySelectorAll('[data-crop-zoom-range]').forEach(el => {
        el.addEventListener('input', () => {
          applyCV({ ...this.state.coverTransform, scale: Math.max(0.5, Math.min(10, (parseFloat(el.value) || 100) / 100)) });
        });
      });
      this.root.querySelectorAll('[data-crop-zoom-num]').forEach(el => {
        el.addEventListener('change', () => {
          applyCV({ ...this.state.coverTransform, scale: Math.max(0.5, Math.min(10, (parseFloat(el.value) || 100) / 100)) });
        });
      });
      /* x/y range inputs use -100…100 scale; state uses -1…1 */
      this.root.querySelectorAll('[data-crop-x-range]').forEach(el => {
        el.addEventListener('input', () => {
          applyCV({ ...this.state.coverTransform, nx: Math.max(-1, Math.min(1, (parseFloat(el.value) || 0) / 100)) });
        });
      });
      this.root.querySelectorAll('[data-crop-y-range]').forEach(el => {
        el.addEventListener('input', () => {
          applyCV({ ...this.state.coverTransform, ny: Math.max(-1, Math.min(1, (parseFloat(el.value) || 0) / 100)) });
        });
      });
      this.root.querySelectorAll('[data-crop-reset]').forEach(el => {
        el.addEventListener('click', () => applyCV({ scale: 1, nx: 0, ny: 0 }));
      });
      this.root.querySelectorAll('[data-crop-remove]').forEach(el => {
        el.addEventListener('click', () => this._removeCoverPhoto());
      });

      /* Background colour swatches + picker.
         "Apply to all pages" is a one-time bulk write at click time, not a
         sticky mode — it never re-fires on its own, so a customer who applies
         one design to every page and then changes a single page afterward
         (checkbox off, or just clicking again) gets that one page saved
         normally, same as before this existed.
         newSpreadValue (null = reset to plain paper) is remembered on
         this._lastAppliedAllBg so _addSpread() can carry the same look onto
         a page added later in the same session, instead of it defaulting
         back to plain paper while the rest of the book matches. */
      const applyBg = (setter, newSpreadValue) => {
        if (this._bgApplyAll) {
          for (let i = 0; i < this.VIEWS.length; i++) setter(i);
          this._bgApplyAllUsed = true;
          this._lastAppliedAllBg = newSpreadValue || null;
          this._updateBgSwatches();
          this._saveLocalStyle({ spreadBg: this.state.spreadBg });
          this._autoSaveDraft();
          this._renderSpread();
          for (let i = 0; i < this.VIEWS.length; i++) this._refreshFilmThumb(i);
          return;
        }
        const idx = this.state.spreadIdx;
        if (idx == null) return;
        setter(idx);
        this._updateBgSwatches();
        this._saveLocalStyle({ spreadBg: this.state.spreadBg });
        this._autoSaveDraft();
        this._renderSpread();
        this._refreshFilmThumb(idx);
      };

      this.root.querySelectorAll('[data-bg-apply-all]').forEach(cb => {
        cb.addEventListener('change', () => {
          this._bgApplyAll = cb.checked;
          /* Keep the desktop and mobile checkboxes mirrored */
          this.root.querySelectorAll('[data-bg-apply-all]').forEach(other => { other.checked = cb.checked; });
        });
      });

      this.root.querySelectorAll('.oi2-bg-swatch').forEach(sw => {
        sw.addEventListener('click', () => {
          const color = sw.dataset.color;
          const isReset = color === INTERIOR_PAPER_COLOR;
          applyBg(i => {
            if (isReset) delete this.state.spreadBg[i];
            else this.state.spreadBg[i] = color;
          }, isReset ? null : color);
        });
      });
      this.root.querySelectorAll('#oi2-bg-picker').forEach(picker => {
        picker.addEventListener('input', () => {
          applyBg(i => { this.state.spreadBg[i] = picker.value; }, picker.value);
        });
      });

      /* Theme swatches — render live canvas preview into each button, then wire click */
      this.root.querySelectorAll('.oi2-theme-swatch').forEach(sw => {
        const theme = SPREAD_THEMES.find(t => t.id === sw.dataset.theme);
        if (theme) {
          sw.style.background = theme.color;
          const imgEntry = _themeImgCache[theme.id];
          if (imgEntry && imgEntry.url) {
            sw.style.backgroundImage = `url('${imgEntry.url}')`;
            sw.style.backgroundSize = 'cover';
            sw.style.backgroundPosition = 'center';
          } else {
            const s = 40;
            const tc = document.createElement('canvas'); tc.width = tc.height = s;
            const tc2 = tc.getContext('2d');
            tc2.fillStyle = theme.color; tc2.fillRect(0, 0, s, s);
            theme.drawTile(tc2, s);
            sw.style.backgroundImage = `url('${tc.toDataURL()}')`;
            sw.style.backgroundSize = 'cover';
          }
        }
        sw.addEventListener('click', () => {
          const val = `theme:${sw.dataset.theme}`;
          applyBg(i => { this.state.spreadBg[i] = val; }, val);
        });
      });

      this.root.querySelectorAll('[data-crop-restore]').forEach(el => {
        el.addEventListener('click', () => {
          if (this.state.coverPhoto) {
            this._placeCover(this.state.coverPhoto, { resetCrop: true, sourceAssetKey: 'coverPhoto' });
          }
        });
      });

      /* Slot crop inputs */
      const applySlot = tf => {
        const key = this._activeCropKey;
        if (!key) return;
        this.state.slotTransforms[key] = tf;
        const slot = this.el.spreadGrid?.querySelector(`[data-slot="${key}"]`);
        const img  = slot?.querySelector('img');
        if (img) this._applySlotTransformCSS(img, tf);
        this._saveTransforms();
        const vi = parseInt(key.split(':')[0].slice(1), 10);
        if (!isNaN(vi)) this.state.dirtyViews.add(vi);
        this._autoSaveDraft();
        clearTimeout(this._cropKickTimer);
        this._cropKickTimer = setTimeout(() => this._kickBackgroundUploads(), 1500);
        this._renderSlotCropControls(key);
      };

      this.root.querySelectorAll('[data-crop-zoom-range-slot]').forEach(el => {
        el.addEventListener('input', () => {
          const key = this._activeCropKey; if (!key) return;
          applySlot({ ...this._getSlotTransform(key), scale: Math.max(0.5, Math.min(10, (parseFloat(el.value) || 100) / 100)) });
        });
      });
      this.root.querySelectorAll('[data-crop-zoom-num-slot]').forEach(el => {
        el.addEventListener('change', () => {
          const key = this._activeCropKey; if (!key) return;
          applySlot({ ...this._getSlotTransform(key), scale: Math.max(0.5, Math.min(10, (parseFloat(el.value) || 100) / 100)) });
        });
      });
      this.root.querySelectorAll('[data-crop-x-range-slot]').forEach(el => {
        el.addEventListener('input', () => {
          const key = this._activeCropKey; if (!key) return;
          applySlot({ ...this._getSlotTransform(key), nx: Math.max(-1, Math.min(1, (parseFloat(el.value) || 0) / 100)) });
        });
      });
      this.root.querySelectorAll('[data-crop-y-range-slot]').forEach(el => {
        el.addEventListener('input', () => {
          const key = this._activeCropKey; if (!key) return;
          applySlot({ ...this._getSlotTransform(key), ny: Math.max(-1, Math.min(1, (parseFloat(el.value) || 0) / 100)) });
        });
      });
      this.root.querySelectorAll('[data-crop-reset-slot]').forEach(el => {
        el.addEventListener('click', () => applySlot({ scale: 1, nx: 0, ny: 0 }));
      });
      this.root.querySelectorAll('[data-crop-remove-slot]').forEach(el => {
        el.addEventListener('click', () => {
          const key = this._activeCropKey; if (!key) return;
          delete this.state.slotImages[key];
          delete this.state.slotTransforms[key];
          delete this.state.slotBlobs[key];
          const vi = parseInt(key.split(':')[0].slice(1), 10);
          if (!isNaN(vi)) this.state.dirtyViews.add(vi);
          this._saveTransforms();
          this._autoSaveDraft();
          this._activeCropKey = null;
          if (this.state.view === 'spread') this._renderSpread();
          this._renderSlotCropControls(null);
        });
      });
    }

    /* ── DPI helpers ── */
    _slotPhysicalDims(viewIdx, slotIdx) {
      const { trimW, trimH } = this._formatDims();
      const v   = this.VIEWS[viewIdx];
      const def = this.layoutDef(viewIdx);
      const sr  = def?.slots[slotIdx];
      if (!sr) return null;
      const isWide = v?.kind === 'spread';
      const pageWIn = isWide ? trimW * 2 : trimW;
      return { targetWidthIn: sr.w * pageWIn, targetHeightIn: sr.h * trimH };
    }

    _effectiveDpiForCover() {
      const url = this.state.coverImage;
      if (!url) return null;
      const up = this.state.uploads.find(u => u.url === url);
      if (!up?.sourceWidth || !up?.sourceHeight) return null;
      const { trimW, trimH } = this._formatDims();
      const layout = this._coverLayout();
      let targetWidthIn, targetHeightIn;
      if (layout === 'coverFull') {
        targetWidthIn  = trimW;
        targetHeightIn = trimH;
      } else {
        /* coverPhotoTitle rectangle: trimW * 292/340 wide, trimH * 272/392 tall */
        targetWidthIn  = trimW * (292 / 340);
        targetHeightIn = trimH * (272 / 392);
      }
      return calculateEffectiveDpi({
        sourceWidth: up.sourceWidth, sourceHeight: up.sourceHeight,
        targetWidthIn, targetHeightIn,
        scale: this.state.coverTransform?.scale || 1,
      });
    }

    _effectiveDpiForSlot(key) {
      if (!key || !this.state.slotImages[key]) return null;
      const url = this.state.slotImages[key];
      const up  = this.state.uploads.find(u => u.url === url);
      if (!up?.sourceWidth || !up?.sourceHeight) return null;
      const [viStr, siStr] = key.split(':');
      const viewIdx = parseInt(viStr.slice(1), 10);
      const slotIdx = parseInt(siStr, 10);
      const dims = this._slotPhysicalDims(viewIdx, slotIdx);
      if (!dims) return null;
      const tf = this._getSlotTransform(key);
      return calculateEffectiveDpi({
        sourceWidth: up.sourceWidth, sourceHeight: up.sourceHeight,
        ...dims,
        scale: tf?.scale || 1,
      });
    }

    _renderCropControls() {
      const hasPhoto    = !!this.state.coverImage;
      const hasOriginal = !!this.state.coverPhoto && this.state.coverSourceAssetKey !== 'coverPhoto';
      const tf  = this.state.coverTransform;
      const pct = Math.round((tf?.scale || 1) * 100);

      this.root.querySelectorAll('[data-crop-panel]').forEach(p => {
        p.style.display = hasPhoto ? (p.tagName === 'DETAILS' ? '' : 'flex') : 'none';
      });
      /* Show restore button only when user has swapped away from the destination photo */
      this.root.querySelectorAll('[data-crop-restore]').forEach(el => {
        el.style.display = (hasPhoto && hasOriginal) ? '' : 'none';
      });
      if (!hasPhoto) return;

      this.root.querySelectorAll('[data-crop-zoom-range]').forEach(el => {
        if (document.activeElement !== el) el.value = pct;
      });
      this.root.querySelectorAll('[data-crop-zoom-num]').forEach(el => {
        if (document.activeElement !== el) el.value = pct;
      });
      /* x/y range uses -100…100 scale */
      this.root.querySelectorAll('[data-crop-x-range]').forEach(el => {
        if (document.activeElement !== el) el.value = Math.round((tf?.nx || 0) * 100);
      });
      this.root.querySelectorAll('[data-crop-y-range]').forEach(el => {
        if (document.activeElement !== el) el.value = Math.round((tf?.ny || 0) * 100);
      });
      const dpi = this._effectiveDpiForCover();
      const lbl = dpiLabel(dpi);
      this.root.querySelectorAll('[data-dpi-status]').forEach(el => {
        el.textContent = lbl ? lbl.text : '';
        el.style.color = lbl ? lbl.color : '';
      });
    }

    _renderSlotCropControls(key) {
      this._activeCropKey = key || null;
      const hasPhoto = key ? !!this.state.slotImages[key] : false;
      const tf  = hasPhoto ? this._getSlotTransform(key) : null;
      const pct = Math.round((tf?.scale || 1) * 100);

      this.root.querySelectorAll('[data-crop-panel-slot]').forEach(p => {
        if (hasPhoto) {
          p.style.display = p.tagName === 'DETAILS' ? '' : 'flex';
        } else {
          p.style.display = 'none';
        }
      });
      if (hasPhoto) {
        const editTab = this.root.querySelector('[data-panel-spread] [data-tab="edit"]');
        if (editTab && !editTab.classList.contains('active')) editTab.click();
      }
      if (!hasPhoto) return;

      this.root.querySelectorAll('[data-crop-zoom-range-slot]').forEach(el => {
        if (document.activeElement !== el) el.value = pct;
      });
      this.root.querySelectorAll('[data-crop-zoom-num-slot]').forEach(el => {
        if (document.activeElement !== el) el.value = pct;
      });
      this.root.querySelectorAll('[data-crop-x-range-slot]').forEach(el => {
        if (document.activeElement !== el) el.value = Math.round((tf?.nx || 0) * 100);
      });
      this.root.querySelectorAll('[data-crop-y-range-slot]').forEach(el => {
        if (document.activeElement !== el) el.value = Math.round((tf?.ny || 0) * 100);
      });
      const dpi = this._effectiveDpiForSlot(key);
      const lbl = dpiLabel(dpi);
      this.root.querySelectorAll('[data-dpi-status-slot]').forEach(el => {
        el.textContent = lbl ? lbl.text : '';
        el.style.color = lbl ? lbl.color : '';
      });
    }

    _removeCoverPhoto() {
      this.state.coverImage          = null;
      this.state.coverSourceAssetKey = null;
      this.state.coverRemoved        = true;
      this.state.coverTransform      = { scale: 1, nx: 0, ny: 0 };
      this._saveLocalStyle({
        coverTransform:      this.state.coverTransform,
        coverSourceAssetKey: null,
        coverRemoved:        true,
      });
      const { coverPhoto, coverPh, thumbPhoto } = this.el;
      if (coverPhoto) {
        const prevHq = coverPhoto.dataset.oiHqSrc;
        if (prevHq) { try { URL.revokeObjectURL(prevHq); } catch(_e) {} delete coverPhoto.dataset.oiHqSrc; }
        coverPhoto.src = ''; coverPhoto.style.display = 'none';
      }
      if (coverPh) coverPh.style.display = '';
      if (thumbPhoto) { thumbPhoto.src = ''; thumbPhoto.style.display = 'none'; }
      this._renderCropControls();
      this._renderFilmstripCover();
      this._autoSaveDraft();
      /* Un-badge the photo: removing the cover can leave it placed nowhere, and
         this path doesn't re-render the well on its own. */
      this._refreshWellUsage();
    }

    /* ── Mobile: render filmstrip in pages drawer ── */
    _renderMobileFilmstrip() {
      const { mobFilmstrip } = this.el;
      if (!mobFilmstrip) return;
      mobFilmstrip.innerHTML = '';

      /* Cover thumbnail */
      const isCoverCurrent = this.state.view === 'cover';
      const coverBtn = document.createElement('button');
      coverBtn.style.cssText = `background:none;border:none;padding:4px 2px 0;cursor:pointer;display:flex;flex-direction:column;align-items:center;gap:5px;flex:none;opacity:${isCoverCurrent?'1':'0.55'};transition:opacity .3s ease`;
      const coverPrev = document.createElement('div');
      coverPrev.style.cssText = `position:relative;width:60px;height:40px;background:${this.cloth().hex};border-radius:1px;box-shadow:${isCoverCurrent?'0 0 0 1px rgba(201,165,95,0.75),0 4px 10px rgba(0,0,0,0.35)':'0 2px 5px rgba(0,0,0,0.3)'};box-sizing:border-box;overflow:hidden`;
      const spineEl = document.createElement('div');
      spineEl.style.cssText = `position:absolute;left:50%;top:0;bottom:0;width:3px;background:${this.cloth().on === 'light' ? 'rgba(255,255,255,0.14)' : 'rgba(28,24,20,0.1)'};transform:translateX(-50%)`;
      coverPrev.appendChild(spineEl);
      /* The front half shows the cover photo, so the cover is recognisable. */
      if (this.state.coverImage) {
        const cimg = document.createElement('img');
        cimg.src = this.state.coverImage; cimg.alt = ''; cimg.draggable = false;
        cimg.style.cssText = 'position:absolute;left:58%;top:16%;width:34%;height:56%;object-fit:cover;border-radius:1px';
        coverPrev.appendChild(cimg);
      }
      const coverLbl = document.createElement('div');
      coverLbl.style.cssText = `font-size:8px;letter-spacing:1.4px;color:${isCoverCurrent?'#C9A55F':'rgba(243,238,228,0.4)'}`;
      coverLbl.textContent = 'Cover';
      coverBtn.appendChild(coverPrev); coverBtn.appendChild(coverLbl);
      coverBtn.addEventListener('click', () => { this._goCover(); this._closeMobSheet(); });
      mobFilmstrip.appendChild(coverBtn);

      /* Divider */
      const divEl = document.createElement('div');
      divEl.style.cssText = 'width:1px;height:28px;background:rgba(243,238,228,0.1);flex:none;align-self:center';
      mobFilmstrip.appendChild(divEl);

      /* Spread thumbnails */
      this.VIEWS.forEach((v, i) => {
        const isWide = v.kind === 'spread';
        const def    = this.layoutDef(i);
        const isCurrent = this.state.view !== 'cover' && i === this.state.spreadIdx;
        const btn = document.createElement('button');
        btn.style.cssText = `background:none;border:none;padding:4px 2px 0;cursor:pointer;display:flex;flex-direction:column;align-items:center;gap:5px;flex:none;opacity:${isCurrent?'1':'0.55'};transition:opacity .3s ease`;
        btn.dataset.mobFilmSpread = String(i);
        const preview = document.createElement('div');
        preview.style.cssText = `position:relative;width:${isWide?'88px':'44px'};height:44px;background:${spreadBgColor(this.state.spreadBg[i])};padding:2px;border-radius:1px;box-shadow:${isCurrent?'0 0 0 1px rgba(201,165,95,0.75),0 4px 10px rgba(0,0,0,0.35)':'0 2px 5px rgba(0,0,0,0.3)'};box-sizing:border-box`;
        def.slots.forEach(sr => {
          const cell = document.createElement('div');
          cell.style.cssText = [
            'position:absolute',
            `left:${(sr.x*100).toFixed(2)}%`,
            `top:${(sr.y*100).toFixed(2)}%`,
            `width:${(sr.w*100).toFixed(2)}%`,
            `height:${(sr.h*100).toFixed(2)}%`,
            'background:rgba(28,24,20,0.16)',
            'border-radius:1px',
            'box-sizing:border-box',
          ].join(';');
          preview.appendChild(cell);
        });
        /* Real thumbnail of the page (the same one the desktop filmstrip renders),
           so pages are recognisable rather than grey boxes. */
        const thumbUrl = this._filmThumbCache && this._filmThumbCache.get(i);
        if (thumbUrl) this._setMobFilmThumb(preview, thumbUrl);
        const lbl = document.createElement('div');
        lbl.style.cssText = `font-size:8px;letter-spacing:1.4px;color:${isCurrent?'#C9A55F':'rgba(243,238,228,0.4)'}`;
        lbl.textContent = isWide ? `${v.pages[0]}–${v.pages[1]}` : String(v.pages[0]);
        btn.appendChild(preview); btn.appendChild(lbl);
        btn.addEventListener('click', () => { this._goSpread(i); this._closeMobSheet(); });
        mobFilmstrip.appendChild(btn);
      });

      /* "+" add spread button (mobile) */
      const mobSpreadCount = this.VIEWS.filter(v => v.kind === 'spread').length;
      if (mobSpreadCount < 37) {
        const addBtn = document.createElement('button');
        addBtn.title = 'Add spread';
        addBtn.style.cssText = 'background:none;border:1px dashed rgba(201,165,95,0.35);border-radius:3px;padding:0;width:46px;height:46px;cursor:pointer;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:5px;flex:none;margin-top:6px;opacity:0.6;transition:opacity .25s ease,border-color .25s ease;align-self:flex-start';
        addBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><line x1="7" y1="2" x2="7" y2="12" stroke="#C9A55F" stroke-width="1.5" stroke-linecap="round"/><line x1="2" y1="7" x2="12" y2="7" stroke="#C9A55F" stroke-width="1.5" stroke-linecap="round"/></svg><span style="font-size:8px;letter-spacing:1.4px;color:rgba(201,165,95,0.75);text-transform:uppercase;line-height:1">Add</span>';
        addBtn.addEventListener('click', () => { this._addSpread(); });
        mobFilmstrip.appendChild(addBtn);
      }

      /* "−" remove spread button (mobile) */
      if (mobSpreadCount > 9) {
        const rmBtn = document.createElement('button');
        rmBtn.title = 'Remove last spread';
        rmBtn.style.cssText = 'background:none;border:1px dashed rgba(243,238,228,0.2);border-radius:3px;padding:0;width:46px;height:46px;cursor:pointer;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:5px;flex:none;margin-top:6px;opacity:0.55;transition:opacity .25s ease,border-color .25s ease;align-self:flex-start';
        rmBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><line x1="2" y1="7" x2="12" y2="7" stroke="rgba(243,238,228,0.75)" stroke-width="1.5" stroke-linecap="round"/></svg><span style="font-size:8px;letter-spacing:1.4px;color:rgba(243,238,228,0.5);text-transform:uppercase;line-height:1">Remove</span>';
        rmBtn.addEventListener('click', () => { this._removeSpread(); });
        mobFilmstrip.appendChild(rmBtn);
      }
      this._renderMobPageBar();   /* page count may have changed */
      this._fillMobFilmThumbs();
    }

    /* Thumbnails are otherwise only made after a page is edited, so a book that was
       just opened has none. Make the missing ones while the Pages strip is open —
       one page at a time, so a phone never decodes every photo at once — and stop
       as soon as the strip closes. _renderFilmThumb skips empty pages. */
    async _fillMobFilmThumbs() {
      if (this._mobThumbFilling) return;
      this._mobThumbFilling = true;
      const stripOpen = () => this.root.querySelector('[data-dock-tab="pages"]')?.getAttribute('aria-selected') === 'true';
      try {
        await new Promise(r => setTimeout(r, 350));   /* let the sheet finish opening */
        for (let i = 0; i < this.VIEWS.length; i++) {
          if (!stripOpen()) break;
          if (this._filmThumbCache.has(i)) continue;
          await this._renderFilmThumb(i).catch(() => {});
        }
      } finally { this._mobThumbFilling = false; }
    }

    /* Put a rendered page thumbnail into a Pages-strip preview box. */
    _setMobFilmThumb(preview, url) {
      preview.querySelectorAll('div').forEach(c => { c.style.display = 'none'; });
      let img = preview.querySelector('[data-mob-film-thumb]');
      if (!img) {
        img = document.createElement('img');
        img.dataset.mobFilmThumb = '1'; img.alt = ''; img.draggable = false;
        img.style.cssText = 'position:absolute;inset:2px;width:calc(100% - 4px);height:calc(100% - 4px);object-fit:cover;border-radius:1px';
        preview.appendChild(img);
      }
      img.src = url;
    }

    /* ── State helpers ── */
    cloth() { return this.CLOTHS.find(c => c.id === this.state.clothId) || this.CLOTHS[0]; }
    foil()  { return this.FOILS.find(f => f.id === this._knownFoil(this.state.foilId)) || this.FOILS[0]; }
    _knownFoil(id) { const fid = this.LEGACY_FOILS[id] || id; return this.FOILS.some(f => f.id === fid) ? fid : null; }
    font()  { return this.FONTS.find(f => f.id === this.state.fontId)   || this.FONTS[0]; }
    _coverFontWeight() { return this.font().coverWeight || 400; }

    _stampColor() { return this.foil().color; }
    /* Front-cover text actually printed: "Add Text" on AND something typed.
       With nothing typed the photo takes the text band's space, on screen and in print. */
    _hasCoverText() {
      return !!this.state.showCoverText && !!((this.state.title || '').trim() || (this.state.subtitle || '').trim());
    }
    /* Fingerprint marker for books whose cover changed when empty text stopped
       reserving a band — only they re-render; every other cover keeps its key. */
    _emptyTextMark() { return this.state.showCoverText && !this._hasCoverText() ? 'photo-fills' : null; }
    /* Fingerprint marker for covers whose text is too long for one line: those
       used to print squeezed and are now set smaller, so only they re-render.
       Proportions follow the print (front 392 units tall, trimW/trimH wide). */
    /* Fingerprint marker for Cormorant covers with numbers in their text: the
       numbers now print full height, so only those covers re-render. */
    _digitsMark() {
      if (coverDrawFamily(this.font().family) === this.font().family) return null;
      const text = (this._hasCoverText() ? (this.state.title || '') + (this.state.subtitle || '') : '')
        + (this.state.showSpineText ? (this.state.spineText || '') : '');
      return /\d/.test(text) ? 'lining-digits' : null;
    }
    /* Fingerprint marker for covers with top-to-bottom spine text. Bottom to top
       is how every cover printed before, so it keeps the old fingerprint and
       only covers whose spine actually turned re-render. */
    _spineMark() {
      return this.state.showSpineText && String(this.state.spineText || '').trim()
        && this.state.spineDir !== 'btt' ? 'spine-ttb' : null;
    }
    _textFitMark() {
      const fn = this.font(), weight = fn.coverWeight || 400, family = coverDrawFamily(fn.family);
      const { trimW, trimH } = this._formatDims();
      const frontW = 392 * (trimW / trimH);
      const long = (text, size, spacingRatio, maxWidth) => !!text && coverTextScale(text.toUpperCase(), { weight, family, size, spacingRatio, maxWidth }) < 1;
      const fits = (this._hasCoverText() && (long(this.state.title, 25, 0.16, frontW * 0.82) || long(this.state.subtitle, 8.5, 0.4, frontW * 0.82)))
        || (this.state.showSpineText && long(this.state.spineText, 11.5, 0.21, 392 * 0.8));
      return fits ? 'text-fit' : null;
    }
    _inkOnCloth() {
      return this.cloth().on === 'light' ? 'rgba(243,238,228,0.92)' : 'rgba(28,24,20,0.86)';
    }
    _matToCloth(m) {
      return { 'Linen Oatmeal':'linen','Velvet Olive':'olive','Soft Charcoal':'charcoal','Leather Sand':'sand' }[m] || 'linen';
    }
    _foilToId(f) {
      if (!f) return 'gold';
      const s = f.toLowerCase();
      if (s.includes('silver') || s.includes('matte silver') || s.includes('platinum')) return 'silver';
      if (s.includes('white') || s.includes('pearl')) return 'white';
      if (s.includes('charcoal')) return 'charcoal';
      if (s.includes('blind') || s.includes('deboss')) return 'silver'; /* retired stamp */
      return 'gold';
    }
    _saveLocalStyle(patch) {
      try {
        const s = JSON.parse(localStorage.getItem('oiCraft:' + this._scope) || '{}');
        Object.assign(s, patch);
        localStorage.setItem('oiCraft:' + this._scope, JSON.stringify(s));
      } catch(e) {}
    }

    layoutSet(i) { this._fitSquareLayouts(); return (this.VIEWS[i] || this.VIEWS[0]).kind === 'spread' ? this.LAYOUTS_SPREAD : this.LAYOUTS_PAGE; }

    /* Fill in the slots of the square layouts for the current page shape. Slots are
       fractions of the page (or of the spread), so a fixed fraction is only square on
       a square page; these are worked out in inches from the trim size instead, then
       converted. The layout objects are updated in place (lists keep their identity)
       and only when the format changes. Spreads repeat the page grid on both pages. */
    _fitSquareLayouts() {
      const d = this._formatDims();
      const key = d.trimW + 'x' + d.trimH;
      if (this._squareFitKey === key) return;
      this._squareFitKey = key;
      const W = d.trimW, H = d.trimH, M = 0.4, G = 0.25;   /* inches: outer margin, gap */
      const grid = (cols, rows) => {
        const side = Math.min((W - 2 * M - (cols - 1) * G) / cols, (H - 2 * M - (rows - 1) * G) / rows);
        const x0 = (W - (cols * side + (cols - 1) * G)) / 2, y0 = (H - (rows * side + (rows - 1) * G)) / 2;
        const cells = [];
        for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) cells.push({ x: x0 + c * (side + G), y: y0 + r * (side + G), s: side });
        return cells;
      };
      const round = v => Math.round(v * 10000) / 10000;
      for (const l of this.LAYOUTS_PAGE) if (l.squares) {
        l.slots = grid(l.squares.cols, l.squares.rows).map(c => ({ x: round(c.x / W), y: round(c.y / H), w: round(c.s / W), h: round(c.s / H) }));
      }
      for (const l of this.LAYOUTS_SPREAD) if (l.squares) {
        const cells = grid(l.squares.cols, l.squares.rows);
        l.slots = [0, 1].flatMap(page => cells.map(c => ({ x: round((page * W + c.x) / (2 * W)), y: round(c.y / H), w: round(c.s / (2 * W)), h: round(c.s / H) })));
      }
    }
    layoutId(i, layouts = this.state.layouts)  {
      const set = this.layoutSet(i), chosen = layouts?.[i];
      if (chosen && set.some(l => l.id === chosen)) return chosen;
      return set === this.LAYOUTS_SPREAD ? 'balanced' : 'gallery';
    }
    layoutDef(i, layouts = this.state.layouts) { return this.layoutSet(i).find(l => l.id === this.layoutId(i, layouts)) || this.layoutSet(i)[0]; }
    viewLabel(i) {
      const v = this.VIEWS[i] || this.VIEWS[0];
      return v.kind === 'spread' ? `Pages ${v.pages[0]}–${v.pages[1]}` : `Page ${v.pages[0]}`;
    }

    /* ── Format / size helpers ── */
    _formatDims() {
      let pricing = {};
      try { pricing = JSON.parse(localStorage.getItem('oiCraftPricing:' + this._scope) || '{}'); } catch(e) {}
      const fmt = (pricing.format || '').toLowerCase();
      const PAGE_W = 340;
      /* Canonical IDs first, then substring aliases for backward compat */
      if (fmt === 'portrait810' || fmt.includes('8x10')) {
        return { pageW: PAGE_W, pageH: 425, spreadW: PAGE_W * 2, trimW: 8, trimH: 10 };
      }
      if (fmt === 'landscape' || fmt.includes('12x8')) {
        return { pageW: PAGE_W, pageH: 227, spreadW: PAGE_W * 2, trimW: 12, trimH: 8 };
      }
      if (fmt === 'portrait' || fmt === 'portrait812' || fmt.includes('8x12')) {
        return { pageW: PAGE_W, pageH: 510, spreadW: PAGE_W * 2, trimW: 8, trimH: 12 };
      }
      return { pageW: PAGE_W, pageH: 392, spreadW: 784, trimW: 8, trimH: 8 };
    }

    _applyFormat() {
      const { coverOuter, spreadOuter } = this.el;
      const d = this._formatDims();
      if (coverOuter) coverOuter.style.height = d.pageH + 'px';
      if (spreadOuter) {
        spreadOuter.style.height = d.pageH + 'px';
        spreadOuter.style.width  = d.spreadW + 'px';
      }
      const lbl = this.root.querySelector('[data-format-label]');
      if (lbl) lbl.innerHTML = `${d.trimW}&times;${d.trimH} in`;
      this._applyMobileScale();
    }

    /* ── Parallax ── */
    _initParallax() {}

    /* ── Navigation ── */
    _initNav() {
      const { navPrev, navNext, filmCover } = this.el;
      if (navPrev) navPrev.addEventListener('click', () => {
        const s = this.state;
        if (s.view === 'cover') this._goSpread(this.VIEWS.length - 1);
        else if (s.spreadIdx === 0) this._goCover();
        else this._goSpread(s.spreadIdx - 1);
      });
      if (navNext) navNext.addEventListener('click', () => {
        const s = this.state;
        if (s.view === 'cover') this._goSpread(0);
        else if (s.spreadIdx === this.VIEWS.length - 1) this._goCover();
        else this._goSpread(s.spreadIdx + 1);
      });
      if (filmCover) filmCover.addEventListener('click', () => this._goCover());
      document.addEventListener('keydown', e => {
        const el = document.activeElement;
        if (!el) return;
        const tag = el.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || el.contentEditable === 'true') return;
        if (e.key === 'ArrowLeft')  navPrev && navPrev.click();
        if (e.key === 'ArrowRight') navNext && navNext.click();
      });
    }

    _goCover() {
      this._saveDirtySpread();
      this.state.view = 'cover';
      this.state.activeSlot = null;
      if (this.el.book) this.el.book.style.transform = '';
      this._renderView();
      this._renderRight();
      this._updateFilmstripActive();
      this._applyMobileScale();
      const pSec = document.getElementById('oi2-sec-pages');
      if (pSec && pSec.open) this._renderMobileFilmstrip();
      this._warmNeighbourPhotos();
    }
    /* Decode the photos of the pages either side ahead of time, once the browser
       is idle, so the next page change draws from _slotCache straight away. */
    _warmNeighbourPhotos() {
      const run = () => {
        const i = this.state.view === 'spread' ? this.state.spreadIdx : -1;
        const pages = [i + 1, i - 1, i + 2].filter(n => n >= 0 && n < this.VIEWS.length);
        for (const n of pages) {
          for (const [key, url] of Object.entries(this.state.slotImages || {})) {
            if (url && key.startsWith('s' + n + ':')) _slotCacheWarm(url);
          }
        }
      };
      if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 1500 });
      else setTimeout(run, 200);
    }
    _goSpread(i) {
      this._saveDirtySpread();
      this.state.view = 'spread';
      this.state.activeSlot = null;
      this._tzPanelZoneIdx = 0;
      this.state.spreadIdx = Math.max(0, Math.min(this.VIEWS.length - 1, i));
      if (this.el.caption) this.el.caption.value = this.state.captions[this.state.spreadIdx] || '';
      this._renderView();
      this._renderSpread();
      this._renderRight();
      this._updateFilmstripActive();
      this._applyMobileScale();
      const pSec = document.getElementById('oi2-sec-pages');
      if (pSec && pSec.open) this._renderMobileFilmstrip();
      this._warmNeighbourPhotos();
    }

    /* Put the current title / subtitle / spine text into every box that edits them.
       Called at start-up and again once a book has been restored from the server:
       the restore set state and the cover, but never the boxes, so a reopened book
       showed "UDAIPUR" on its cover above an empty Title box, and typing a single
       letter replaced the whole title. A box being typed in is left alone. */
    _syncTextInputs() {
      const set = (el, v) => { if (el && el !== document.activeElement) el.value = v || ''; };
      set(this.el.inputTitle,       this.state.title);
      set(this.el.inputSubtitle,    this.state.subtitle);
      set(this.el.inputSpine,       this.state.spineText);
      set(this.el.mobInputTitle,    this.state.title);
      set(this.el.mobInputSubtitle, this.state.subtitle);
      set(this.el.mobInputSpine,    this.state.spineText);
      this._syncTextCounters();
    }

    /* Character limit + counter on every cover text box. The counter appears near
       the limit (and stays when a book saved before the limit is already over it,
       where the box only lets them shorten the text). */
    _initTextLimits() {
      const boxes = [['title', 'inputTitle'], ['subtitle', 'inputSubtitle'], ['spineText', 'inputSpine'],
                     ['title', 'mobInputTitle'], ['subtitle', 'mobInputSubtitle'], ['spineText', 'mobInputSpine']];
      this._textCounters = [];
      boxes.forEach(([field, ref]) => {
        const el = this.el[ref];
        if (!el || el.dataset.oiLimit) return;
        el.dataset.oiLimit = '1';
        el.maxLength = COVER_TEXT_MAX[field];
        const wrap = document.createElement('span');
        wrap.style.cssText = 'position:relative;display:block;min-width:0;' + (el.style.flex ? `flex:${el.style.flex}` : 'width:100%');
        el.parentNode.insertBefore(wrap, el);
        wrap.appendChild(el);
        el.style.width = '100%'; el.style.boxSizing = 'border-box'; el.style.paddingRight = '46px';
        const c = document.createElement('span');
        c.setAttribute('aria-live', 'polite');
        c.style.cssText = 'position:absolute;right:10px;top:50%;transform:translateY(-50%);font-size:10px;letter-spacing:.5px;color:rgba(243,238,228,0.4);pointer-events:none;font-variant-numeric:tabular-nums';
        wrap.appendChild(c);
        this._textCounters.push({ field, el, c });
      });
      this._syncTextCounters();
    }

    _syncTextCounters() {
      (this._textCounters || []).forEach(({ field, el, c }) => {
        const max = COVER_TEXT_MAX[field], n = (el.value || '').length;
        c.textContent = n >= max - 5 ? `${n}/${max}` : '';
        c.style.color = n >= max ? 'rgba(201,165,95,0.9)' : 'rgba(243,238,228,0.4)';
      });
    }

    /* Reflect showSpineText in both spine switches and show/hide both text boxes. */
    _syncSpineToggles() {
      const on = !!this.state.showSpineText;
      this.root.querySelectorAll('[data-spine-toggle], [data-mob-spine-toggle]').forEach(t => { t.checked = on; });
      this.root.querySelectorAll('[data-spine-section], [data-mob-spine-section]').forEach(s => { s.style.display = on ? '' : 'none'; });
      this.root.querySelectorAll('[data-spine-dir]').forEach(b => {
        b.setAttribute('aria-checked', String(b.dataset.spineDir === this.state.spineDir));
      });
    }

    /* ── Inputs ── */
    _initInputs() {
      const { inputTitle, inputSubtitle, inputSpine, caption,
              mobInputTitle, mobInputSubtitle, mobInputSpine } = this.el;
      this._initTextLimits();
      const upd = (field, ...els) => els.forEach(el => {
        if (!el) return;
        el.addEventListener('input', () => {
          this.state[field] = el.value.toUpperCase();
          /* Keep paired input in sync */
          const pairs = { title: [inputTitle, mobInputTitle], subtitle: [inputSubtitle, mobInputSubtitle], spineText: [inputSpine, mobInputSpine] };
          (pairs[field] || []).forEach(p => { if (p && p !== el) p.value = el.value; });
          this._syncTextCounters();
          this._renderCover();
          this._renderFilmstripCover();
          this._autoSaveDraft();
        });
      });
      upd('title',     inputTitle,    mobInputTitle);
      upd('subtitle',  inputSubtitle, mobInputSubtitle);
      upd('spineText', inputSpine, mobInputSpine);
      if (caption) caption.addEventListener('input', () => {
        this.state.captions[this.state.spreadIdx] = caption.value;
        this._autoSaveDraft();
      });

      /* Engraving toggle */
      const engraveToggle    = this.root.querySelector('[data-engrave-toggle]');
      const mobEngraveToggle = this.root.querySelector('[data-mob-engrave-toggle]');
      const onEngrave = (checked) => {
        if (engraveToggle)    engraveToggle.checked    = checked;
        if (mobEngraveToggle) mobEngraveToggle.checked = checked;
        this.state.showCoverText = checked;
        this._saveLocalStyle({ showCoverText: checked });
        this._applyEngraveToggle(checked);
        this._autoSaveDraft();
      };
      if (engraveToggle)    engraveToggle.checked    = this.state.showCoverText;
      if (mobEngraveToggle) mobEngraveToggle.checked = this.state.showCoverText;
      this._applyEngraveToggle(this.state.showCoverText);
      if (engraveToggle)    engraveToggle.addEventListener('change',    e => onEngrave(e.target.checked));
      if (mobEngraveToggle) mobEngraveToggle.addEventListener('change', e => onEngrave(e.target.checked));

      /* Spine toggle — desktop panel and the phone's Text & Spine section */
      const onSpine = (checked) => {
        this.state.showSpineText = checked;
        this._saveLocalStyle({ showSpineText: checked });
        this._syncSpineToggles();
        this._renderCover();
        this._autoSaveDraft();
      };
      this._syncSpineToggles();
      this.root.querySelectorAll('[data-spine-toggle], [data-mob-spine-toggle]').forEach(t => {
        t.addEventListener('change', e => onSpine(e.target.checked));
      });
      this.root.querySelectorAll('[data-spine-dir]').forEach(b => {
        b.addEventListener('click', () => {
          this.state.spineDir = _spineDir(b.dataset.spineDir);
          this._saveLocalStyle({ spineDir: this.state.spineDir });
          this._syncSpineToggles();
          this._renderCover();
          this._autoSaveDraft();
        });
      });

      /* Back logo toggle */
      const { backLogoChk, mobBackLogoChk } = this.el;
      const onBackLogo = (checked) => {
        this.state.showBackLogo = checked;
        this._syncBackLogoUi();
        this._saveLocalStyle({ showBackLogo: checked });
        this._renderCover();
        this._renderPrice();
        this._autoSaveDraft();
      };
      /* Removing the logo is a paid option, so it needs the customer's agreement;
         putting it back is free and needs no prompt. */
      const onBackLogoChange = (checked) => {
        if (checked) { onBackLogo(true); return; }
        this._syncBackLogoUi(); /* stay ticked until they agree */
        this._confirmUnbranded(() => onBackLogo(false));
      };
      this._syncBackLogoUi();
      if (backLogoChk)    backLogoChk.addEventListener('change',    e => onBackLogoChange(e.target.checked));
      if (mobBackLogoChk) mobBackLogoChk.addEventListener('change', e => onBackLogoChange(e.target.checked));
    }

    _unbrandedFee() { return this.state.showBackLogo === false ? (Number(window.OI_UNBRANDED_PRICE) || 300) : 0; }

    _syncBackLogoUi() {
      const { backLogoChk, mobBackLogoChk } = this.el;
      const on = this.state.showBackLogo !== false;
      if (backLogoChk)    backLogoChk.checked    = on;
      if (mobBackLogoChk) mobBackLogoChk.checked = on;
      const fee = Number(window.OI_UNBRANDED_PRICE) || 300;
      this.root.querySelectorAll('[data-back-logo-note]').forEach(n => {
        n.textContent = `Unbranded cover · +₹${fee.toLocaleString('en-IN')}`;
        n.style.display = on ? 'none' : '';
      });
    }

    _confirmUnbranded(onConfirm) {
      document.getElementById('oi-unbranded-confirm')?.remove();
      const fee = (Number(window.OI_UNBRANDED_PRICE) || 300).toLocaleString('en-IN');
      const wrap = document.createElement('div');
      wrap.id = 'oi-unbranded-confirm';
      wrap.setAttribute('role', 'dialog');
      wrap.setAttribute('aria-modal', 'true');
      wrap.setAttribute('aria-labelledby', 'oi-unbranded-title');
      wrap.style.cssText = 'position:fixed;inset:0;z-index:100000;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(10,9,8,0.72)';
      wrap.innerHTML = `
        <div style="max-width:380px;width:100%;background:#1C1915;border:1px solid rgba(201,165,95,0.35);border-radius:6px;padding:26px 24px 22px;color:#F3EEE4;font-family:inherit;box-shadow:0 20px 60px rgba(0,0,0,0.5)">
          <div id="oi-unbranded-title" style="font-size:11px;letter-spacing:2.4px;text-transform:uppercase;color:#C9A55F;margin-bottom:14px">Unbranded cover — ₹${fee}</div>
          <p style="font-size:13.5px;line-height:1.6;color:rgba(243,238,228,0.82);margin:0 0 22px">By removing the Oat &amp; Ink mark, you agree to an additional ₹${fee} per book. This applies to the printed back cover only. You can add the logo back anytime before ordering and the charge will be removed.</p>
          <div style="display:flex;gap:10px;justify-content:flex-end">
            <button type="button" data-unbranded-cancel style="min-height:40px;padding:0 18px;background:transparent;border:1px solid rgba(243,238,228,0.25);border-radius:3px;color:rgba(243,238,228,0.8);font-size:10.5px;letter-spacing:1.8px;text-transform:uppercase;cursor:pointer">Cancel</button>
            <button type="button" data-unbranded-ok style="min-height:40px;padding:0 18px;background:#C9A55F;border:1px solid #C9A55F;border-radius:3px;color:#14120F;font-size:10.5px;letter-spacing:1.8px;text-transform:uppercase;cursor:pointer">Remove logo</button>
          </div>
        </div>`;
      const close = () => { wrap.remove(); document.removeEventListener('keydown', onKey); };
      const onKey = e => { if (e.key === 'Escape') close(); };
      wrap.addEventListener('click', e => { if (e.target === wrap) close(); });
      wrap.querySelector('[data-unbranded-cancel]').addEventListener('click', close);
      wrap.querySelector('[data-unbranded-ok]').addEventListener('click', () => { close(); onConfirm(); });
      document.addEventListener('keydown', onKey);
      document.body.appendChild(wrap);
      wrap.querySelector('[data-unbranded-cancel]').focus();
    }

    _applyEngraveToggle(enabled) {
      const engraveSection    = this.root.querySelector('[data-engrave-section]');
      const mobEngraveSection = this.root.querySelector('[data-mob-engrave-section]');
      const { frontCover }    = this.el;

      if (engraveSection)    { engraveSection.style.display    = enabled ? '' : 'none'; }
      if (mobEngraveSection) { mobEngraveSection.style.display = enabled ? '' : 'none'; }
      if (frontCover)        { frontCover.style.paddingBottom  = enabled ? '0' : '24px'; }

      this._renderCover();
    }

    /* ── Photo well ── */
    _initPhotoWell() {
      const { dropZone, fileInput, coverSlot, mobDropZone, mobFileInput } = this.el;
      if (fileInput) fileInput.addEventListener('change', e => {
        this._readFiles(Array.from(e.target.files));
        e.target.value = '';
      });
      if (dropZone) {
        dropZone.addEventListener('dragover', e => { e.preventDefault(); dropZone.style.borderColor = 'rgba(201,165,95,0.7)'; dropZone.style.background = 'rgba(201,165,95,0.07)'; });
        dropZone.addEventListener('dragleave', () => { dropZone.style.borderColor = ''; dropZone.style.background = ''; });
        dropZone.addEventListener('drop', e => { e.preventDefault(); dropZone.style.borderColor = ''; dropZone.style.background = ''; this._readFiles(Array.from(e.dataTransfer.files)); });
      }
      /* Mobile file inputs — wire all (rail + mob-bar) */
      this.root.querySelectorAll('[data-mob-file-input]').forEach(fi => {
        fi.addEventListener('change', e => { this._readFiles(Array.from(e.target.files)); e.target.value = ''; });
      });
      /* Paste images anywhere on the page (Ctrl+V / Cmd+V) */
      document.addEventListener('paste', e => {
        const items = Array.from((e.clipboardData || {}).items || []);
        const files = items
          .filter(it => it.kind === 'file' && it.type.startsWith('image/'))
          .map(it => it.getAsFile())
          .filter(Boolean);
        if (files.length) this._readFiles(files);
      });

      if (coverSlot) {
        coverSlot.addEventListener('click', () => {
          if (Date.now() < (this._suppressCoverPlacementClickUntil || 0)) return;
          const { selectedUpload, uploads } = this.state;
          if (selectedUpload !== null && uploads[selectedUpload]) {
            const up = uploads[selectedUpload];
            const sameCover = (up.assetKey && up.assetKey === this.state.coverSourceAssetKey) ||
              [up.url, up.originalUrl, up.previewUrl].filter(Boolean).includes(this.state.coverImage);
            /* Clicking/pressing the image already used on the cover is a crop
               interaction, not a request to place it again and reset zoom. */
            if (!sameCover) {
              this._placeCover(up.url, { sourceAssetKey: up.assetKey });
              this.state.selectedUpload = null;
              this._renderWell();
              return;
            }
          }
          /* Phones/tablets: tapping the cover photo opens its crop tools (there is
             no Edit tab). A crop drag that ended here was suppressed above. */
          if (window.innerWidth <= 900 && this.state.coverImage) {
            /* Tap again to close, as with page photos. */
            const editOpen = this.root.querySelector('[data-dock-tab="edit"]')?.getAttribute('aria-selected') === 'true';
            if (editOpen) this._closeMobSheet(); else this._openMobSheet('edit');
          }
        });
        coverSlot.addEventListener('dragover', e => e.preventDefault());
        coverSlot.addEventListener('drop', async e => {
          e.preventDefault(); e.stopPropagation();
          const url = await this._resolveDropUpload(e);
          if (url) this._placeCover(url);
        });
      }
    }

    /* Shared upload-entry factory — validates, creates URL, awaits decode, returns entry with dims.
       Returns null on type mismatch or decode failure so callers can skip broken files. */
    /* Scale an ImageBitmap to a canvas (maxEdge px on longest side) and return a Blob object URL. */
    _scaleBitmapToUrl(bm, maxEdge) {
      const scale = Math.min(1, maxEdge / Math.max(bm.width, bm.height, 1));
      const w = Math.max(1, Math.round(bm.width  * scale));
      const h = Math.max(1, Math.round(bm.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const bmpCtx = canvas.getContext('2d');
      bmpCtx.imageSmoothingEnabled = true;
      bmpCtx.imageSmoothingQuality = 'high';
      bmpCtx.drawImage(bm, 0, 0, w, h);
      return new Promise((resolve, reject) => {
        canvas.toBlob(b => b ? resolve(URL.createObjectURL(b)) : reject(new Error('toBlob failed')), 'image/jpeg', 0.82);
      });
    }

    /* Lazily load heic2any from CDN (cached after first load). */
    _loadHeic2any() {
      if (this._heic2anyPromise) return this._heic2anyPromise;
      this._heic2anyPromise = new Promise((res, rej) => {
        if (window.heic2any) { res(window.heic2any); return; }
        const s = document.createElement('script');
        s.src = 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js';
        s.onload  = () => res(window.heic2any);
        s.onerror = () => rej(new Error('Could not load HEIC converter'));
        document.head.appendChild(s);
      });
      return this._heic2anyPromise;
    }

    /* Shared upload-entry factory.
       On Safari: createImageBitmap decodes HEIC natively → re-encode to full-resolution JPEG at
       quality 0.96 for server upload (single encode, no CDN dependency, no pixel loss).
       On Chrome/Firefox: createImageBitmap fails for HEIC → fall back to heic2any (CDN) at 0.96
       then retry decode for preview generation.
       Non-HEIC images (JPEG, PNG, WebP, …) go straight through createImageBitmap unchanged. */
    async _createUploadFromFile(file) {
      if (!file) return null;
      const isHeic = /\.(heic|heif)$/i.test(file.name) ||
                     file.type === 'image/heic' || file.type === 'image/heif';
      if (!file.type.startsWith('image/') && !isHeic) return null;

      const assetKey = `photo-${crypto.randomUUID()}`;
      let uploadFile = file;
      let bm = null;

      try {
        if (typeof createImageBitmap !== 'undefined') {
          /* Path 1 — native decode (always works for JPEG/PNG; works for HEIC on Safari) */
          try {
            bm = await createImageBitmap(file);
            if (isHeic) {
              /* Safari natively decoded the HEIC — re-encode to full-resolution JPEG for upload */
              const el = this.el.saveStatus;
              if (el) el.innerHTML = '<span>Processing photo…</span>';
              const c = document.createElement('canvas');
              c.width = bm.width; c.height = bm.height;
              c.getContext('2d').drawImage(bm, 0, 0);
              const jpegBlob = await new Promise((res, rej) =>
                c.toBlob(b => b ? res(b) : rej(new Error('toBlob failed')), 'image/jpeg', 1.0)
              );
              uploadFile = new File([jpegBlob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg', lastModified: file.lastModified });
            }
          } catch (decodeErr) {
            if (!isHeic) throw decodeErr;
            /* Path 2 — HEIC, native decode failed (Chrome/Firefox) → heic2any CDN fallback */
            let heicConverted = false;
            try {
              const buf   = await file.slice(4, 12).arrayBuffer();
              const bytes = new Uint8Array(buf);
              if (bytes[0]===0x66 && bytes[1]===0x74 && bytes[2]===0x79 && bytes[3]===0x70) {
                const brand = String.fromCharCode(bytes[4], bytes[5], bytes[6], bytes[7]);
                if (/^(heic|heis|hevx|heix|mif1|msf1|hevc|hevs|avci|avcs)/i.test(brand)) {
                  const el = this.el.saveStatus;
                  if (el) el.innerHTML = '<span>Converting HEIC…</span>';
                  const heic2any  = await this._loadHeic2any();
                  const result    = await heic2any({ blob: file, toType: 'image/jpeg', quality: 1.0 });
                  const jpegBlob  = Array.isArray(result) ? result[0] : result;
                  uploadFile = new File([jpegBlob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg', lastModified: file.lastModified });
                  bm = await createImageBitmap(uploadFile);
                  heicConverted = true;
                }
              }
            } catch (_) {}
            if (!heicConverted) {
              this._showUploadError('HEIC photo could not be converted — try exporting as JPEG first.');
              return null;
            }
          }
          const sourceWidth  = bm.width;
          const sourceHeight = bm.height;
          const [previewUrl, thumbUrl] = await Promise.all([
            this._scaleBitmapToUrl(bm, 1600),
            this._scaleBitmapToUrl(bm, 240),
          ]);
          bm.close();
          const originalUrl = URL.createObjectURL(uploadFile);
          return { file: uploadFile, url: previewUrl, originalUrl, previewUrl, thumbUrl, name: uploadFile.name, assetKey, sourceWidth, sourceHeight, originalFileSize: file.size };
        } else {
          /* Path 3 — browsers without createImageBitmap (very old); HEIC still needs conversion */
          if (isHeic) {
            try {
              const buf   = await file.slice(4, 12).arrayBuffer();
              const bytes = new Uint8Array(buf);
              if (bytes[0]===0x66 && bytes[1]===0x74 && bytes[2]===0x79 && bytes[3]===0x70) {
                const brand = String.fromCharCode(bytes[4], bytes[5], bytes[6], bytes[7]);
                if (/^(heic|heis|hevx|heix|mif1|msf1|hevc|hevs|avci|avcs)/i.test(brand)) {
                  const el = this.el.saveStatus;
                  if (el) el.innerHTML = '<span>Converting HEIC…</span>';
                  const heic2any = await this._loadHeic2any();
                  const result   = await heic2any({ blob: file, toType: 'image/jpeg', quality: 1.0 });
                  const jpegBlob = Array.isArray(result) ? result[0] : result;
                  uploadFile = new File([jpegBlob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg', lastModified: file.lastModified });
                }
              }
            } catch (_) {
              this._showUploadError('HEIC photo could not be converted — try exporting as JPEG first.');
              return null;
            }
          }
          if (!uploadFile.type.startsWith('image/')) return null;
          const originalUrl = URL.createObjectURL(uploadFile);
          try {
            const dims = await new Promise((res, rej) => {
              const img = new Image();
              img.onload  = () => res({ w: img.naturalWidth, h: img.naturalHeight });
              img.onerror = () => rej(new Error('decode failed'));
              img.src = originalUrl;
            });
            return { file: uploadFile, url: originalUrl, originalUrl, previewUrl: originalUrl, thumbUrl: originalUrl, name: uploadFile.name, assetKey, sourceWidth: dims.w, sourceHeight: dims.h, originalFileSize: file.size };
          } catch (e) {
            URL.revokeObjectURL(originalUrl);
            return null;
          }
        }
      } catch (e) {
        if (bm) try { bm.close(); } catch (_) {}
        return null;
      }
    }

    /* Briefly display an upload error in the save-status bar, then restore normal status. */
    _showUploadError(msg) {
      const el = this.el.saveStatus;
      if (!el) { console.warn('[OI] upload error:', msg); return; }
      el.innerHTML = `<span style="color:#D4844A">${msg}</span>`;
      clearTimeout(this._uploadErrTimer);
      this._uploadErrTimer = setTimeout(() => this._renderSaveStatus(), 4000);
    }

    /* Return existing upload entry for a file (matched by name+size or lastModified), or null.
       HEIC files are stored as converted JPEGs so we also check the normalised name. Used as
       a fallback inside _acquireUploadEntry for IDB-restored entries that have no fingerprint. */
    _findExistingUpload(file) {
      const normName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
      return this.state.uploads.filter(u => {
        const storedName = u.file?.name ?? u.name;
        const nameMatch = storedName === file.name || storedName === normName;
        if (!nameMatch) return false;
        if (u.originalFileSize != null) return u.originalFileSize === file.size;
        const storedLastMod = u.file?.lastModified;
        if (storedLastMod !== undefined) return storedLastMod === file.lastModified;
        return true; /* IDB-restored Blobs with no size/lastModified: name match alone */
      });
    }

    /* SHA-256 of the complete file content — exact identity, no collisions.
       Returns the hex digest string, or null if the hash cannot be computed.
       Callers must treat null as "identity unknown" and never make destructive
       decisions (dedup, IDB delete) based on a null fingerprint. */
    async _fingerprintFile(file) {
      try {
        const buf    = await file.arrayBuffer();
        const digest = await crypto.subtle.digest('SHA-256', buf);
        const bytes  = new Uint8Array(digest);
        let s = '';
        for (let i = 0; i < bytes.length; i++) s += bytes[i].toString(16).padStart(2, '0');
        return s;
      } catch {
        return null;
      }
    }

    /* Revoke all blob: URLs on an entry that was rejected from state.uploads (i.e. a duplicate
       candidate created before the coalescing check could find the winner). */
    _revokeUploadUrls(entry) {
      if (!entry) return;
      const seen = new Set();
      for (const key of ['url', 'originalUrl', 'previewUrl', 'thumbUrl']) {
        const u = entry[key];
        if (u && u.startsWith('blob:') && !seen.has(u)) {
          seen.add(u);
          try { URL.revokeObjectURL(u); } catch {}
        }
      }
    }

    /* Unified upload-entry factory shared by _readFiles and _resolveDropUpload.
       Guarantees exactly one gallery entry, one IDB record, and one Drive upload per
       unique photo, regardless of how many concurrent callers supply the same file.

       Returns { entry, isNew } where isNew = true only for the one caller that actually
       pushed the entry into state.uploads. All other callers receive the same entry object
       with isNew = false. Returns { entry: null } on decode failure. */
    async _acquireUploadEntry(file) {
      /* SHA-256 of the original file bytes — stable identity across multiple drags of
         the same photo, distinct for photos with the same filename but different content.
         fp === null means the hash could not be computed; we fall through to a safer
         but less robust name+size path that skips coalescing. */
      const fp = await this._fingerprintFile(file);

      let existing = null;
      if (fp !== null) {
        /* Fast path: look for an entry already carrying this exact fingerprint */
        existing = this.state.uploads.find(u => u.fingerprint === fp);
      }
      if (!existing) {
        /* Fallback: IDB-restored or legacy entries may have no fingerprint yet.
           Examine ALL compatible name+size candidates, hashing each until one matches.
           Stopping at the first candidate risks missing the actual match if names collide. */
        const candidates = this._findExistingUpload(file);
        for (const candidate of candidates) {
          if (candidate.fingerprint) {
            /* Already fingerprinted — accept only if fingerprints agree; try next on mismatch */
            if (fp !== null && candidate.fingerprint === fp) { existing = candidate; break; }
          } else if (candidate.file) {
            /* Legacy/IDB entry: hash its stored bytes to confirm identity */
            const candidateFp = await this._fingerprintFile(candidate.file);
            if (candidateFp !== null && fp !== null && candidateFp === fp) {
              candidate.fingerprint = candidateFp; /* verified — backfill */
              existing = candidate;
              break;
            }
            /* Hash failed or content differs — try the next candidate */
          }
        }
      }
      if (existing) return { entry: existing, isNew: false };

      /* fp null path: identity unknown, skip deduplication entirely. Substituting an existing
         photo based only on name+size risks merging distinct images. Accept as new unconditionally. */
      if (fp === null) {
        const entry = await this._createUploadFromFile(file);
        if (!entry) return { entry: null, isNew: false };
        this.state.uploads.push(entry);
        return { entry, isNew: true };
      }

      /* Coalescing: if another concurrent caller is already decoding this exact content,
         await their result instead of starting a duplicate decode. */
      if (this._inFlightUploads.has(fp)) {
        const entry = await this._inFlightUploads.get(fp);
        return { entry, isNew: false };
      }

      /* We are the decode owner — register in the in-flight map before awaiting. */
      let resolveInflight;
      const inflightPromise = new Promise(r => { resolveInflight = r; });
      this._inFlightUploads.set(fp, inflightPromise);

      try {
        const entry = await this._createUploadFromFile(file);
        if (!entry) { resolveInflight(null); return { entry: null, isNew: false }; }

        /* Fingerprint of the ORIGINAL file (before any HEIC conversion) — this is what
           gets stored as contentFingerprint in IDB so future sessions can match re-drags. */
        entry.fingerprint = fp;

        /* Final dedup: a concurrent caller may have pushed an entry while we decoded. */
        const raceWinner = this.state.uploads.find(u => u.fingerprint === fp);
        if (raceWinner) {
          this._revokeUploadUrls(entry);
          resolveInflight(raceWinner);
          return { entry: raceWinner, isNew: false };
        }

        this.state.uploads.push(entry);
        resolveInflight(entry);
        return { entry, isNew: true };
      } catch {
        resolveInflight(null);
        return { entry: null, isNew: false };
      } finally {
        this._inFlightUploads.delete(fp);
      }
    }

    /* Process up to 2 files at a time, rendering progressive gallery thumbnails per batch.
       Uses _acquireUploadEntry so concurrent OS drops and file-picker events share one
       decode per photo — duplicates are coalesced, not doubled. Only entries that were
       genuinely inserted into state.uploads (isNew=true) are auto-filled and persisted. */
    async _readFiles(files) {
      const all       = Array.from(files);
      const VIDEO_EXT = /\.(mov|mp4|m4v|avi|mkv|webm|3gp)$/i;
      const RAW_EXT   = /\.(cr2|cr3|nef|arw|raf|orf|rw2|rwl|pef|dng|tif|tiff|raw|sr2|x3f|3fr|mrw)$/i;

      const videos = all.filter(f => f.type.startsWith('video/') || VIDEO_EXT.test(f.name));
      const raws   = all.filter(f => !videos.includes(f) && (
        RAW_EXT.test(f.name) ||
        f.type === 'image/tiff' || f.type === 'image/x-adobe-dng' || f.type === 'image/x-raw'
      ));
      const imgs   = all.filter(f =>
        !videos.includes(f) && !raws.includes(f) &&
        (f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name))
      );

      const errParts = [];
      if (videos.length) errParts.push("Videos can't be uploaded — please select a photo.");
      if (raws.length)   errParts.push("RAW camera files aren't supported — export as JPEG from Lightroom, Photos, or your camera app.");
      if (errParts.length) this._showUploadError(errParts.join(' '));

      if (!imgs.length) return;
      const CONCURRENCY = 2;
      const acceptedNewUploads = []; /* only entries that were pushed into state.uploads this call */
      let failCount = 0;

      for (let i = 0; i < imgs.length; i += CONCURRENCY) {
        const batch = await Promise.all(imgs.slice(i, i + CONCURRENCY).map(f => this._acquireUploadEntry(f)));
        for (const { entry, isNew } of batch) {
          if (!entry) { failCount++; continue; }
          if (isNew) acceptedNewUploads.push(entry);
          /* isNew=false means the entry already existed or another concurrent caller pushed it;
             either way no duplicate is added and no stale URL reaches _placeSlot. */
        }
        /* New uploads are not auto-selected either: a selected photo is placed by the
           next tap on the cover or an empty slot (see the restore paths). */
        this._renderWell();
      }

      if (failCount) this._showUploadError(`${failCount} image${failCount > 1 ? 's' : ''} could not be read — skipped`);
      if (!acceptedNewUploads.length) return;

      /* Persist and upload only the entries that were genuinely new this call. */
      acceptedNewUploads.forEach(up => this._savePhotoBackground(up.file, up.name, up.assetKey));
      this._initProject();
      this._kickBackgroundUploads();
    }

    /* Resolve a drop event to a URL, creating a new gallery entry when an OS file is dropped.
       For gallery-to-slot drags the existing URL is returned directly without duplication.
       Returns a Promise so that source dimensions are captured before placement. */
    /* Resolve a drop event to an originalUrl for slot/cover placement.
       OS file drops go through _acquireUploadEntry (dedup + coalescing).
       Gallery-to-slot drags carry the stable assetKey as their drag payload. */
    async _resolveDropUpload(e) {
      const files = e.dataTransfer && e.dataTransfer.files;
      if (files && files.length) {
        const all       = Array.from(files);
        const VIDEO_EXT = /\.(mov|mp4|m4v|avi|mkv|webm|3gp)$/i;
        const RAW_EXT   = /\.(cr2|cr3|nef|arw|raf|orf|rw2|rwl|pef|dng|tif|tiff|raw|sr2|x3f|3fr|mrw)$/i;
        if (all.some(f => f.type.startsWith('video/') || VIDEO_EXT.test(f.name))) {
          this._showUploadError("Videos can't be uploaded — please select a photo.");
          return null;
        }
        if (all.some(f => RAW_EXT.test(f.name) || f.type === 'image/tiff' || f.type === 'image/x-adobe-dng' || f.type === 'image/x-raw')) {
          this._showUploadError("RAW camera files aren't supported — export as JPEG from Lightroom, Photos, or your camera app.");
          return null;
        }
        const f = all.find(x => x.type.startsWith('image/') || /\.(heic|heif)$/i.test(x.name));
        if (!f) return null;
        const { entry, isNew } = await this._acquireUploadEntry(f);
        if (!entry) { this._showUploadError('Could not read the dropped image'); return null; }
        if (isNew) {
          this.state.selectedUpload = this.state.uploads.length - 1;
          this._renderWell();
          this._savePhotoBackground(entry.file, entry.name, entry.assetKey);
          this._initProject();
        } else {
          const idx = this.state.uploads.indexOf(entry);
          if (idx !== -1) this.state.selectedUpload = idx;
        }
        return entry.originalUrl;
      }
      /* Gallery-to-slot drag: payload is the stable assetKey set by _renderWell's dragstart */
      const assetKey = (e.dataTransfer && e.dataTransfer.getData('text/plain')) || null;
      if (!assetKey) return null;
      const up = this.state.uploads.find(u => u.assetKey === assetKey);
      if (!up) return null;
      const idx = this.state.uploads.indexOf(up);
      if (idx !== -1) this.state.selectedUpload = idx;
      return up.originalUrl || up.url;
    }

    _selectUpload(i) {
      this.state.selectedUpload = this.state.selectedUpload === i ? null : i;
      this._renderWell();
    }

    _placeCover(url, { resetCrop = true, sourceAssetKey = undefined } = {}) {
      if (resetCrop) {
        this.state.coverTransform = { scale: 1, nx: 0, ny: 0 };
        this._saveLocalStyle({ coverTransform: this.state.coverTransform });
      }
      this.state.coverImage  = url;
      this.state.coverRemoved = false;

      /* Determine source identity so cross-device restore knows which original file to use,
         and never accidentally restores the generated cover wrap. */
      let coverUp = null;
      if (sourceAssetKey !== undefined) {
        this.state.coverSourceAssetKey = sourceAssetKey;
        /* Search by assetKey first, then all URL variants, so callers with a stable assetKey
           find the entry even when the passed url is a transient blob URL. */
        coverUp = this.state.uploads.find(u =>
          u.assetKey === sourceAssetKey ||
          u.url === url || u.originalUrl === url || u.previewUrl === url
        );
      } else {
        /* Auto-detect: resolve via all URL variants; fall back to coverPhoto sentinel. */
        coverUp = this.state.uploads.find(u =>
          u.url === url || u.originalUrl === url || u.previewUrl === url
        );
        if (!coverUp && url === this.state.coverPhoto) {
          this.state.coverSourceAssetKey = 'coverPhoto';
        }
        /* else: unknown external URL (rare drag-drop) — leave coverSourceAssetKey unchanged */
      }
      /* Always use the canonical assetKey from the found entry, regardless of which branch found it. */
      if (coverUp) this.state.coverSourceAssetKey = coverUp.assetKey;
      this._saveLocalStyle({ coverSourceAssetKey: this.state.coverSourceAssetKey, coverRemoved: false });

      const { coverPhoto, coverPh, thumbPhoto } = this.el;
      if (coverPhoto) {
        /* Prefer original file blob for full-quality display; fall back to the passed URL. */
        const prevHq = coverPhoto.dataset.oiHqSrc;
        if (prevHq) { try { URL.revokeObjectURL(prevHq); } catch(_e) {} }
        const hqSrc = coverUp?.file ? URL.createObjectURL(coverUp.file) : url;
        if (coverUp?.file) { coverPhoto.dataset.oiHqSrc = hqSrc; } else { delete coverPhoto.dataset.oiHqSrc; }
        coverPhoto.src = hqSrc;
        coverPhoto.style.display = 'block';
        coverPhoto.style.animation = 'none'; void coverPhoto.offsetWidth;
        coverPhoto.style.animation = 'oi2-photoIn .8s cubic-bezier(.16,1,.3,1) backwards';
        this._applySlotTransformCSS(coverPhoto, this.state.coverTransform);
      }
      if (coverPh)    coverPh.style.display = 'none';
      if (thumbPhoto) { thumbPhoto.src = url; thumbPhoto.style.display = 'block'; }
      this._renderCropControls();
      /* A cover photo counts as placed. Done here rather than at the call sites
         because _placeCover is reached from the click handler, a drop, and restore. */
      this._refreshWellUsage();
    }

    _readSlotMap()       { try { return JSON.parse(localStorage.getItem('oiSlotMap:' + this._scope) || '{}'); } catch { return {}; } }
    _writeSlotMap(map)   { try { localStorage.setItem('oiSlotMap:' + this._scope, JSON.stringify(map)); } catch {} }
    _clearSlotMap()      { try { localStorage.removeItem('oiSlotMap:' + this._scope); } catch {}; try { localStorage.removeItem('oiSlotTransforms:' + this._scope); } catch {}; this._photosConfirmedOnServer = 0; }

    _getSlotTransform(key) {
      const tf = this.state.slotTransforms[key];
      if (!tf) return { scale: 1, nx: 0, ny: 0 };
      /* Migrate old CSS-pixel format {x,y} → discard pan, keep scale */
      if ('x' in tf && !('nx' in tf)) return { scale: Math.max(0.5, tf.scale || 1), nx: 0, ny: 0 };
      return tf;
    }

    /* Renders through a <canvas> sibling using the exact same _drawFit function the print
       pipeline uses, instead of styling the <img> itself with object-fit:cover. Kept the old
       name (call sites still say "TransformCSS") to avoid touching the ~18 callers.

       Why not style the <img> directly: object-fit:cover always crops to the SAME fraction
       of the source image for any box of a given aspect ratio, regardless of the box's
       absolute size. Shrinking the img's box below 100% (for zoom-out) therefore just
       re-displays the identical crop smaller with padding — it can never reveal more of the
       photo the way _drawFit's print path does (which grows its source-sampling rectangle
       below scale=1). That mismatch would make the on-screen preview lie about what actually
       prints. Routing both through _drawFit guarantees they can't diverge. */
    _applySlotTransformCSS(img, tf) {
      const canvas = this._ensurePreviewCanvas(img);
      if (!canvas) return;   /* img not in the DOM yet — see _ensurePreviewCanvas */
      canvas._oiTf = tf;
      canvas._oiDraw();
    }

    /* Creates (once per <img>) an absolutely-positioned <canvas> sibling filling the same
       overflow:hidden parent, and hides the source <img> — kept in the DOM purely as pixel
       data, since drawImage works fine on an opacity:0 image.

       The draw closure is created ONCE here and reused by the resize observer, the image
       load handler and _applySlotTransformCSS. It reads canvas._oiTf on each call, so a new
       transform is picked up without rebuilding anything — and because the load listener is
       attached once per canvas, repeated draws cannot accumulate listeners. */
    _ensurePreviewCanvas(img) {
      let canvas = img._oiCanvas;
      if (canvas) return canvas;
      /* No parent yet means a caller styled the img before appending it. Bail instead of
         throwing: ro.observe(null) raises, and inside the spread-render loop that exception
         blanked the entire spread. Callers should append first; this is the backstop. */
      const host = img.parentElement || img.parentNode;
      if (!host) return null;

      canvas = document.createElement("canvas");
      canvas.style.cssText = "position:absolute;inset:0;width:100%;height:100%;display:block;pointer-events:none";
      img.style.position      = "absolute";
      img.style.inset         = "0";
      img.style.width         = "100%";
      img.style.height        = "100%";
      img.style.opacity       = "0";
      img.style.pointerEvents = "none";
      host.insertBefore(canvas, img.nextSibling);
      img._oiCanvas = canvas;

      canvas._oiDraw = () => {
        /* Give up only once the canvas has no parent at all. An isConnected check here
           also tripped mid-build and tore down the observer before it ever delivered. */
        const parent = canvas.parentElement;
        if (!parent) { canvas._oiRo && canvas._oiRo.disconnect(); return; }
        const rect = parent.getBoundingClientRect();
        /* Zero-sized means layout has not resolved: slots are percentage-sized against a
           grid whose width is set in the same tick, and a cached image draws synchronously
           before that settles. Retry after layout rather than burning the draw on a 1px
           canvas — that is what left slots blank until a click forced a redraw. */
        if (rect.width < 2 || rect.height < 2) {
          if (!canvas._oiRetry) {
            canvas._oiRetry = true;
            requestAnimationFrame(() => { canvas._oiRetry = false; canvas._oiDraw(); });
          }
          return;
        }
        const w = Math.round(rect.width), h = Math.round(rect.height);
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const rw = Math.round(w * dpr), rh = Math.round(h * dpr);

        /* Draw from the decoded copy (see _slotCacheWarm). With none yet, decode
           asynchronously and draw when it lands, instead of handing drawImage an
           undecoded <img> and stalling the page change on a synchronous decode. */
        let source = _slotCacheGet(img.src);
        if (source) {
          const need = Math.max(rw, rh) * Math.max(1, canvas._oiTf?.scale || 1);
          if (need > Math.max(source.width, source.height) * 1.05 &&
              img.complete && img.naturalWidth > source.width) source = img;
        } else if (canvas._oiNoCacheSrc !== img.src && typeof img.decode === 'function') {
          const want = img.src;
          _slotCacheWarm(want, img).then(c => {
            if (img.src !== want) return;
            if (!c) canvas._oiNoCacheSrc = want;   /* undecodable here: fall back to drawing the img */
            canvas._oiDraw();
          });
          return;
        } else {
          /* Still decoding. _drawFit divides by naturalHeight, so NaN geometry would paint
             nothing at all; the load handler re-drives this once the bitmap exists. */
          if (!img.naturalWidth) return;
          source = img;
        }
        if (canvas.width !== rw || canvas.height !== rh) { canvas.width = rw; canvas.height = rh; }
        const ctx = canvas.getContext("2d");
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);
        _drawFit(ctx, source, 0, 0, w, h, canvas._oiTf);
      };

      /* Not { once: true }: a slot img is given a new src when the photo is swapped or
         recovered, and each of those needs a redraw. */
      img.addEventListener("load", canvas._oiDraw);
      const ro = new ResizeObserver(() => canvas._oiDraw());
      ro.observe(host);
      canvas._oiRo = ro;
      canvas._oiDraw();
      return canvas;
    }

    /* nx/ny ∈ [-1,1] always keeps the image covering the slot (proven: edge exposed only at |n|>1) */
    _clampSlotTransform(key, slotEl, img) {
      const tf = this._getSlotTransform(key);
      const clamped = {
        ...tf,
        scale: Math.max(0.5, Math.min(10, tf.scale)),
        nx: Math.max(-1, Math.min(1, tf.nx || 0)),
        ny: Math.max(-1, Math.min(1, tf.ny || 0)),
      };
      this.state.slotTransforms[key] = clamped;
      this._applySlotTransformCSS(img, clamped);
    }

    _saveTransforms() { try { localStorage.setItem('oiSlotTransforms:' + this._scope, JSON.stringify(this.state.slotTransforms)); } catch(e) {} }
    _saveDirtyViews()  { try { localStorage.setItem('oiDirtyViews:'    + this._scope, JSON.stringify([...this.state.dirtyViews])); }   catch(_e) {} }

    _placeSlot(key, url, { markDirty = true } = {}) {
      if (markDirty) this.state.slotTransforms[key] = { scale: 1, nx: 0, ny: 0 };
      /* Match by assetKey or any URL variant. assetKey is the canonical stable identifier;
         URL variants (url/originalUrl/previewUrl) are blob: strings that work for in-session
         IDB-restored entries. Always store and display originalUrl for full print quality. */
      const up = this.state.uploads.find(u => u.assetKey === url || u.url === url || u.originalUrl === url || u.previewUrl === url);
      const displayUrl = up?.originalUrl || url;
      this.state.slotImages[key] = displayUrl;
      if (up && up.file) this.state.slotBlobs[key] = up.file;
      if (markDirty) {
        const viewIdx = parseInt(key.slice(1), 10);
        if (!isNaN(viewIdx)) this.state.dirtyViews.add(viewIdx);
      }
      const slot = this.el.spreadGrid && this.el.spreadGrid.querySelector(`[data-slot="${key}"]`);
      if (slot) {
        const wasEmpty = !slot.querySelector('img');
        this._fillSlot(slot, displayUrl);
        /* Attach pan/zoom listeners when slot was empty (no DOM rebuild needed). */
        if (wasEmpty && this.state.view === 'spread') {
          const vIdx = parseInt(key.slice(1), 10);
          if (!isNaN(vIdx) && vIdx === this.state.spreadIdx) {
            this._attachSlotPanZoom(key, slot);
            this._scheduleFilmThumb(vIdx);
            this._renderSlotCropControls(this._activeCropKey || null);
          }
        }
      }
      /* Persist slot→idbId so arrangement survives page reload.
         Also evict any stale entry where this photo sat previously. */
      if (up?.idbId != null) {
        const map = this._readSlotMap();
        Object.keys(map).forEach(k => { if (k !== key && map[k] === up.idbId) delete map[k]; });
        map[key] = up.idbId;
        this._writeSlotMap(map);
      }
      /* Schedule filmstrip thumbnail refresh for this spread */
      const _ftvi = parseInt(key.slice(1), 10);
      if (!isNaN(_ftvi)) this._scheduleFilmThumb(_ftvi);
      /* Badge the photo as placed straight away. This path deliberately does
         targeted DOM updates instead of a re-render, so the well won't repaint
         on its own. */
      this._refreshWellUsage();
    }

    /* Recover a slot img whose blob URL was revoked by the browser (common on mobile under
       memory pressure). Tries the in-memory File first; falls back to IDB. */
    async _recoverSlotImg(img, key) {
      img.onerror = null; // prevent retry loop
      const badUrl = this.state.slotImages[key];
      if (!badUrl) return;
      const up = this.state.uploads.find(u =>
        u.originalUrl === badUrl || u.url === badUrl || u.previewUrl === badUrl
      );
      let fresh = null;
      /* Try IDB first — always produces a fresh blob even after iOS memory eviction.
         In-memory up.file may be an evicted blob that creates a URL but fails to decode. */
      if (up?.idbId != null) {
        try {
          const rec = await this.store.getById(up.idbId);
          if (rec?.blob) {
            fresh = URL.createObjectURL(rec.blob);
            up.file = rec.blob;
            if (up.originalUrl === badUrl) up.originalUrl = fresh;
            if (up.url        === badUrl) up.url          = fresh;
            if (up.previewUrl === badUrl) up.previewUrl   = fresh;
          }
        } catch(_) {}
      }
      if (!fresh && up?.file) {
        try {
          fresh = URL.createObjectURL(up.file);
          if (up.originalUrl === badUrl) up.originalUrl = fresh;
          if (up.url        === badUrl) up.url          = fresh;
          if (up.previewUrl === badUrl) up.previewUrl   = fresh;
        } catch(_) {}
      }
      if (fresh) {
        this.state.slotImages[key] = fresh;
        img.onerror = null; // already cleared, but be explicit
        img.src = fresh;
      }
    }

    _fillSlot(slotEl, url) {
      const key = slotEl.dataset.slot;
      let img = slotEl.querySelector('img');
      if (!img) {
        img = document.createElement('img');
        img.style.cssText = 'width:100%;height:100%;object-fit:cover;object-position:50% 35%;display:block;position:absolute;inset:0;transform-origin:center;will-change:transform;animation:oi2-photoIn .8s cubic-bezier(.16,1,.3,1) backwards';
        slotEl.appendChild(img);
      }
      img.draggable = false; /* set on both new and reused img — prevents native image drag */
      img.onerror = () => this._recoverSlotImg(img, key);
      img.src = url;
      img.style.objectPosition = '50% 35%';
      slotEl.style.background = 'transparent';
      slotEl.style.border     = '';
      slotEl.style.cursor     = 'grab';
      const ph = slotEl.querySelector('[data-slot-ph]');
      if (ph) ph.style.display = 'none';
      const tf = this._getSlotTransform(key);
      this._applySlotTransformCSS(img, tf);
      if (tf.scale === 1 && !(tf.nx || 0) && !(tf.ny || 0)) {
        _autoAlign(img).then(fp => {
          if (!fp) return;
          const cur = this.state.slotTransforms[key];
          /* Guard: only apply if customer hasn't manually repositioned yet */
          if (!cur || (cur.scale === 1 && !(cur.nx || 0) && !(cur.ny || 0))) {
            /* Convert face centre (0–1 fraction of image) to nx/ny ∈ [-1,1].
               Formula inverse of objectPosition: nx = 1 - 2*fp.x, ny = 1 - 2*fp.y */
            const nx = Math.max(-1, Math.min(1, 1 - 2 * fp.x));
            const ny = Math.max(-1, Math.min(1, 1 - 2 * fp.y));
            const aligned = { scale: 1, nx, ny };
            this.state.slotTransforms[key] = aligned;
            if (img.isConnected) this._applySlotTransformCSS(img, aligned);
          }
        }).catch(() => {});
      }
    }

    /* ── Render: cover ── */
    _renderCover() {
      const cl = this.cloth(), stamp = this._stampColor(), ink = this._inkOnCloth();
      const fn = this.font();
      const { backCover, backMark, backFine, backLogoWrap, spine, spineText, frontCover,
              coverTitle, coverSubtitle, coverPh, coverPhoto, foldBack, foldFront, coverWrapGuide } = this.el;

      /* Scale physical cover construction relative to the displayed board;
         browser CSS pixels are not physical 96-DPI print pixels. */
      const PAGE_W_CSS     = 340;
      const d              = this._formatDims();
      const previewSpinePx = Math.round(PAGE_W_CSS * SPINE_WIDTH_IN / d.trimW);
      const previewWrapPx  = PAGE_W_CSS * COVER_WRAP_IN / d.trimW;

      if (backCover)    { backCover.style.background = cl.hex; }
      if (backLogoWrap) { backLogoWrap.style.display = this.state.showBackLogo ? '' : 'none'; }
      if (backMark && this.state.showBackLogo) {
        const sp = `color:${stamp}`;
        backMark.innerHTML = `<span style="display:inline-flex;align-items:center;gap:7px;font-weight:${LOGO_WEIGHT}">`
          + `<span style="letter-spacing:3px;${sp}">O A T</span>`
          + `<span style="letter-spacing:3px;${sp}">&amp;</span>`
          + `<span style="letter-spacing:3px;${sp}">I N K</span>`
          + '</span>';
      }
      if (backFine)     { backFine.style.color = ink; }
      if (spine)        { spine.style.background = cl.hex; spine.style.width = previewSpinePx + 'px'; }
      if (foldBack)  { foldBack.style.left  = PAGE_W_CSS + 'px'; }
      if (foldFront) { foldFront.style.left = (PAGE_W_CSS + previewSpinePx) + 'px'; }
      if (coverWrapGuide) coverWrapGuide.style.setProperty('--oi-cover-wrap', previewWrapPx.toFixed(2) + 'px');
      if (spineText) {
        const spineOn = this.state.showSpineText;
        spineText.style.fontFamily = coverDrawFamily(fn.family); spineText.style.fontWeight = String(fn.coverWeight || 400);
        spineText.style.color = stamp;
        spineText.textContent = spineOn ? (this.state.spineText || 'YOUR BOOK') : '';
        /* vertical-rl reads top to bottom; turned half way round it reads bottom to top. */
        spineText.style.transform = this.state.spineDir === 'btt' ? 'rotate(180deg)' : '';
        const h = frontCover ? frontCover.offsetHeight : 0;
        const k = coverTextScale(spineText.textContent.toUpperCase(), { weight: fn.coverWeight || 400, family: coverDrawFamily(fn.family), size: 11.5, spacingRatio: 0.21, maxWidth: h * 0.8 });
        spineText.style.fontSize = (11.5 * k).toFixed(2) + 'px';
        spineText.style.letterSpacing = (11.5 * k * 0.21).toFixed(2) + 'px';
      }
      if (frontCover)   { frontCover.style.background = cl.hex; }

      /* Cover layout — switch slot geometry between inset and full-front */
      const coverLayout = this._coverLayout();
      const coverSlot   = this.el.coverSlot;
      const coverTextBlock = this.root.querySelector('[data-cover-text-block]');
      if (coverSlot) {
        if (coverLayout === 'coverFull') {
          coverSlot.style.cssText = 'position:absolute;inset:0;overflow:hidden;cursor:pointer;display:flex;align-items:center;justify-content:center;z-index:0';
          if (coverTextBlock) coverTextBlock.style.display = 'none';
        } else {
          coverSlot.style.cssText = 'flex:1;position:relative;overflow:hidden;cursor:pointer;display:flex;align-items:center;justify-content:center';
          /* Text block shows only when there is text to stamp: "Add Text" on with
             nothing typed gives the photo the band, exactly as the print does. */
          const engraveOn = this._hasCoverText();
          /* 'flex', not '': clearing the inline display dropped the block to plain
             block layout, which stopped it centring its lines as a column. */
          if (coverTextBlock) coverTextBlock.style.display = engraveOn ? 'flex' : 'none';
          /* No text block: mirror the top padding on the bottom so the photo is centered
             instead of flush against the bottom edge (front-cover's base padding-bottom is 0,
             sized for the text block sitting below it). */
          if (frontCover) frontCover.style.paddingBottom = engraveOn ? '' : '24px';
        }
      }

      if (coverPh) {
        const phColor = (cl.on === 'dark' ? 'rgba(28,24,20,' : 'rgba(243,238,228,') + '0.40)';
        const icon = coverPh.querySelector('[data-cover-ph-icon]');
        const text = coverPh.querySelector('[data-cover-ph-text]');
        if (icon) icon.style.borderColor = phColor;
        if (text) text.style.color = phColor;
      }
      if (coverTitle) {
        coverTitle.style.fontFamily = coverDrawFamily(fn.family); coverTitle.style.fontWeight = String(fn.coverWeight || 400);
        coverTitle.style.color = stamp;
        /* Only what will print: the spine text / "TITLE" fallbacks never print. */
        coverTitle.textContent = this.state.title || '';
        /* One line, set smaller when long — same rule and ratio as the print. The
           line keeps its height so the photo above does not move. */
        const w = frontCover ? frontCover.offsetWidth : 0;
        const k = coverTextScale((this.state.title || '').toUpperCase(), { weight: fn.coverWeight || 400, family: coverDrawFamily(fn.family), size: 25, spacingRatio: 0.16, maxWidth: w * 0.82 });
        coverTitle.style.fontSize = (25 * k).toFixed(2) + 'px';
        coverTitle.style.letterSpacing = (25 * k * 0.16).toFixed(2) + 'px';
      }
      if (coverSubtitle){
        coverSubtitle.style.fontFamily = coverDrawFamily(fn.family); coverSubtitle.style.fontWeight = String(fn.coverWeight || 400);
        coverSubtitle.style.color = stamp;
        /* As wide as its text and centred by the flex column, so a line wider than
           the block's padding still sits centred (the print centres it on the cover). */
        coverSubtitle.style.width = 'max-content';
        coverSubtitle.style.boxSizing = 'border-box';
        coverSubtitle.style.textAlign = 'center';
        /* CSS letter-spacing adds space after the final glyph. Matching
           text-indent keeps the visible letters optically centered. */
        coverSubtitle.textContent = this.state.subtitle || '';
        const w = frontCover ? frontCover.offsetWidth : 0;
        const k = coverTextScale((this.state.subtitle || '').toUpperCase(), { weight: fn.coverWeight || 400, family: coverDrawFamily(fn.family), size: 8.5, spacingRatio: 0.4, maxWidth: w * 0.82 });
        coverSubtitle.style.fontSize = (8.5 * k).toFixed(2) + 'px';
        coverSubtitle.style.letterSpacing = (8.5 * k * 0.4).toFixed(2) + 'px';
        coverSubtitle.style.textIndent = (8.5 * k * 0.4).toFixed(2) + 'px';
      }

      /* Re-apply cover crop transform after layout geometry may have changed */
      if (coverPhoto && this.state.coverImage) {
        this._applySlotTransformCSS(coverPhoto, this.state.coverTransform);
      }
      if (coverSlot) coverSlot.style.cursor = this.state.coverImage ? 'grab' : 'pointer';
      this._renderCropControls();

      this._renderCoverLayoutCards();
    }

    _renderFilmstripCover() {
      const cl = this.cloth();
      const { thumbBack, thumbSpine, thumbFront } = this.el;
      if (thumbBack)  thumbBack.style.backgroundColor  = cl.hex;
      if (thumbSpine) thumbSpine.style.backgroundColor = cl.hex;
      if (thumbFront) thumbFront.style.backgroundColor = cl.hex;
    }

    /* ── Render: view transition ── */
    _renderView() {
      const { viewCover, viewSpread, viewAnim, panelTitle, panelHint,
              panelCover, panelSpread, mobPanelCover, mobPanelSpread } = this.el;
      const isCover = this.state.view === 'cover';
      const isWide  = !isCover && (this.VIEWS[this.state.spreadIdx] || {}).kind === 'spread';

      if (viewAnim) {
        viewAnim.style.animation = 'none'; void viewAnim.offsetWidth;
        viewAnim.style.animation = (isCover ? 'oi2-toCover' : 'oi2-toSpread') + ' .62s cubic-bezier(.16,1,.3,1) both';
      }
      if (viewCover)  viewCover.style.display  = isCover ? '' : 'none';
      if (viewSpread) viewSpread.style.display = isCover ? 'none' : '';

      const label = isCover ? 'Cover Atelier' : this.viewLabel(this.state.spreadIdx);
      if (panelTitle) panelTitle.textContent = label;
      const { trimW, trimH } = this._formatDims();
      const spreadLabel = isWide ? `${trimW * 2}×${trimH} in · full spread` : `${trimW}×${trimH} in · single page`;
      if (panelHint)  panelHint.textContent  = isCover ? 'front · spine · back' : spreadLabel;

      if (panelCover)     panelCover.style.display     = isCover ? 'flex' : 'none';
      if (panelSpread)    panelSpread.style.display    = isCover ? 'none' : 'flex';
      if (mobPanelCover)  mobPanelCover.style.display  = isCover ? 'flex' : 'none';
      if (mobPanelSpread) mobPanelSpread.style.display = isCover ? 'none' : 'flex';
      this._renderMobPageBar();
    }

    /* Phone/tablet page bar: where you are, out of how many pages. */
    _renderMobPageBar() {
      const { mpbWhere, mpbOf } = this.el;
      if (!mpbWhere) return;
      const isCover = this.state.view === 'cover';
      const last = this.VIEWS[this.VIEWS.length - 1];
      const total = last && last.pages ? last.pages[last.pages.length - 1] : 0;
      mpbWhere.textContent = isCover ? 'Cover' : this.viewLabel(this.state.spreadIdx);
      if (mpbOf) mpbOf.textContent = isCover ? (total ? total + ' pages' : '') : (total ? 'of ' + total : '');
    }

    /* ── Render: spread ── */
    _renderSpread() {
      const { spreadOuter, spreadGrid, spreadLabel, gutter } = this.el;
      if (!spreadGrid) return;
      const i = this.state.spreadIdx;
      const v = this.VIEWS[i] || this.VIEWS[0];
      const isWide = v.kind === 'spread';
      const def    = this.layoutDef(i);

      const d = this._formatDims();
      if (spreadOuter) spreadOuter.style.width = (isWide ? d.spreadW : d.pageW) + 'px';
      if (gutter)      gutter.style.display    = isWide ? '' : 'none';
      if (spreadLabel) spreadLabel.textContent  = this.viewLabel(i);

      spreadGrid.innerHTML = '';
      const _bgVal = this.state.spreadBg[i];
      if (_bgVal && _bgVal.startsWith('theme:')) {
        const _tp = this._getThemeCssPattern(_bgVal.slice(6));
        spreadGrid.style.background = _tp ? _tp.color : INTERIOR_PAPER_COLOR;
        if (_tp && _tp.isImage) {
          spreadGrid.style.backgroundImage = `url('${_tp.dataUrl}')`;
          spreadGrid.style.backgroundSize = '100% auto';
          spreadGrid.style.backgroundRepeat = 'no-repeat';
          spreadGrid.style.backgroundPosition = 'top center';
        } else if (_tp) {
          spreadGrid.style.backgroundImage = `url('${_tp.dataUrl}')`;
          spreadGrid.style.backgroundSize = `${_tp.tileSize}px ${_tp.tileSize}px`;
          spreadGrid.style.backgroundRepeat = 'repeat';
        } else {
          spreadGrid.style.backgroundImage = '';
        }
      } else {
        spreadGrid.style.background = _bgVal || INTERIOR_PAPER_COLOR;
        spreadGrid.style.backgroundImage = '';
      }
      spreadGrid.addEventListener('click', e => {
        if (e.target === spreadGrid && this._activeCropKey) this._renderSlotCropControls(null);
      }, { once: true });

      def.slots.forEach((slotRect, si) => {
        const key  = `s${i}:${si}`;
        const slot = document.createElement('div');
        slot.dataset.slot = key;
        /* Slots use absolute positioning within a position:relative container */
        slot.style.cssText = [
          'position:absolute',
          `left:${(slotRect.x * 100).toFixed(3)}%`,
          `top:${(slotRect.y * 100).toFixed(3)}%`,
          `width:${(slotRect.w * 100).toFixed(3)}%`,
          `height:${(slotRect.h * 100).toFixed(3)}%`,
          'overflow:hidden',
          'display:flex',
          'align-items:center',
          'justify-content:center',
          'cursor:pointer',
          'transition:background .3s ease',
        ].join(';');

        const existingUrl = this.state.slotImages[key];
        if (existingUrl) {
          slot.style.background = 'transparent';
          const img = document.createElement('img');
          img.draggable = false; /* prevent native image drag triggering OS-file branch in drop handler */
          img.onerror = () => this._recoverSlotImg(img, key);
          img.src = existingUrl;
          img.style.cssText = 'width:100%;height:100%;object-fit:cover;object-position:50% 35%;display:block;position:absolute;inset:0;transform-origin:center;will-change:transform';
          const tf0 = this._getSlotTransform(key);
          slot.style.cursor = 'grab';
          slot.appendChild(img);
          /* Must come AFTER appendChild: the preview canvas attaches itself next to
             this img, so the img needs a parent to attach into. */
          this._applySlotTransformCSS(img, tf0);
        } else {
          slot.style.background = INTERIOR_PAPER_COLOR;
          slot.style.border = '1px dashed rgba(28,24,20,0.18)';
          const ph = document.createElement('div');
          ph.dataset.slotPh = '';
          ph.style.cssText = 'width:20px;height:20px;border:1px solid rgba(28,24,20,0.28);border-radius:2px;pointer-events:none';
          slot.appendChild(ph);
        }
        if (this.state.activeSlot === key) {
          slot.style.boxShadow = '0 0 0 2px #C9A55F, 0 0 14px rgba(201,165,95,0.45) inset';
          slot.style.zIndex = '2';
        }

        slot.addEventListener('click', () => {
          if (window.innerWidth <= 900) {
            const wasActive = this.state.activeSlot === key;
            this.state.activeSlot = wasActive ? null : key;
            /* Update slot highlights without a full spread rebuild */
            const grid = slot.parentElement;
            if (grid) {
              grid.querySelectorAll('[data-slot]').forEach(s => {
                const active = s.dataset.slot === this.state.activeSlot;
                s.style.boxShadow = active ? '0 0 0 2px #C9A55F, 0 0 14px rgba(201,165,95,0.45) inset' : '';
                s.style.zIndex    = active ? '2' : '';
              });
            }
            if (this.state.activeSlot) {
              this._renderWell();
              if (this.state.slotImages[this.state.activeSlot]) {
                /* A photo: open its crop tools straight away (there is no Edit tab
                   to go looking for). Not after a crop drag that ended here. */
                this._renderSlotCropControls(this.state.activeSlot);
                if (Date.now() >= (this._suppressSlotTapUntil || 0)) this._openMobSheet('edit');
              } else if (window.innerWidth > 600) {
                /* Empty slot: on phone (≤600px) the rail is always visible; above
                   that, open Photos to pick one for it. */
                this._openMobDrawer('photos');
              }
            } else if (this.root.querySelector('[data-dock-tab="edit"]')?.getAttribute('aria-selected') === 'true') {
              /* Second tap closes the crop tools. Keep them in the sheet while it
                 slides shut, and clear them once it is closed. */
              this._closeMobSheet();
              setTimeout(() => { if (!this.state.activeSlot) this._renderSlotCropControls(null); }, 600);
            } else {
              this._renderSlotCropControls(null);
            }
          } else if (this.state.slotImages[key]) {
            /* Desktop, filled slot: click opens crop controls */
            this._activeCropKey = key;
            this._renderSlotCropControls(key);
          } else {
            /* Desktop, empty slot: place the selected well photo, if any — mirrors
               the cover-slot click handler and the "click a photo, then click a
               slot" instruction shown in the Photo Well. Otherwise drag-and-drop
               remains the only way to fill it. */
            const { selectedUpload, uploads } = this.state;
            if (selectedUpload !== null && uploads[selectedUpload]) {
              this._placeSlot(key, uploads[selectedUpload].url);
              this.state.selectedUpload = null;
              this._renderWell();
              this._kickBackgroundUploads();
            }
          }
        });
        slot.addEventListener('dragover', e => e.preventDefault());
        slot.addEventListener('drop', async e => {
          e.preventDefault(); e.stopPropagation();
          const url = await this._resolveDropUpload(e);
          if (url) {
            this._placeSlot(key, url);
            this._kickBackgroundUploads();
          }
        });

        /* ── Pan & zoom on filled slots ── */
        if (existingUrl) this._attachSlotPanZoom(key, slot);

        spreadGrid.appendChild(slot);
      });

      /* Text zones — editable divs layered on top of the spread */
      if (def.textZones && def.textZones.length) {
        if (!document.getElementById('oi-tz-styles')) {
          const s = document.createElement('style');
          s.id = 'oi-tz-styles';
          s.textContent = '.oi-tz-wrap{position:absolute;overflow:hidden;box-sizing:border-box;z-index:3}.oi-tz-editor{position:relative;width:100%;outline:none;cursor:text;word-break:break-word;white-space:pre-wrap;box-sizing:border-box;line-height:1.5}.oi-tz-editor:focus{box-shadow:0 0 0 1px rgba(201,165,95,0.55)}.oi-tz-editor[data-empty="1"]::before{content:attr(data-ph);position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:rgba(28,24,20,0.28);font-style:italic;pointer-events:none}.oi-tzb{all:unset;box-sizing:border-box;cursor:pointer;padding:4px 9px;border-radius:5px;font-size:12px;line-height:1;color:rgba(243,238,228,0.65);transition:background 0.12s,color 0.12s;display:inline-flex;align-items:center}.oi-tzb:hover{background:rgba(201,165,95,0.12);color:rgba(243,238,228,0.9)}.oi-tzb.oi-tzb-on{background:rgba(201,165,95,0.18);color:#C9A55F}';
          document.head.appendChild(s);
        }
        const spreadH = this.el.spreadOuter ? this.el.spreadOuter.offsetHeight || 392 : 392;
        const fontFam = this.FONTS.find(f => f.id === this.state.fontId)?.family || "'Cormorant Garamond',serif";
        def.textZones.forEach((zone, zIdx) => {
          const zKey = `${i}:${zone.key}`;
          const _isTouchOnly = ('ontouchstart' in window || navigator.maxTouchPoints > 0) && !window.matchMedia('(pointer:fine)').matches;

          /* Wrapper — owns position, dimensions, dragging; never contentEditable */
          const wrap = document.createElement('div');
          wrap.className = 'oi-tz-wrap';
          wrap.dataset.textZone = zKey;
          const baseFs = Math.round(zone.fontSize * spreadH);
          const zoneFmt = this.state.textFormat[zKey] || {};
          const effectiveFs = zoneFmt.fontSizeFrac != null
            ? Math.round(zoneFmt.fontSizeFrac * spreadH)
            : baseFs;
          wrap.dataset.baseFs = baseFs;
          wrap.dataset.spreadH = spreadH;
          /* Same resolution the print renderer uses, so the box on screen is the box that
             prints — including clamping any previously-dragged position into the band. */
          const zBox = _zoneBox(zone)(zoneFmt);
          wrap.style.cssText = [
            `left:${(zBox.x * 100).toFixed(2)}%`,
            `top:${(zBox.y * 100).toFixed(2)}%`,
            `width:${(zBox.w * 100).toFixed(2)}%`,
            /* Never taller than the zone: min-height beats max-height in CSS, so an
               oversized font would otherwise push the box off the page. */
            `min-height:${Math.min(Math.round(effectiveFs * 1.6), Math.floor(zBox.h * spreadH))}px`,
            `max-height:${(zBox.h * 100).toFixed(2)}%`,
            `overflow:hidden`,
            `height:auto`,
          ].join(';');

          /* Drag handle — sibling of editor, never inside the contentEditable */
          const tzHandle = document.createElement('div');
          tzHandle.style.cssText = `position:absolute;top:0;left:50%;transform:translateX(-50%);padding:4px 12px 6px;cursor:grab;opacity:${_isTouchOnly ? '0.55' : '0'};transition:opacity .18s;z-index:5;user-select:none`;
          tzHandle.innerHTML = '<svg width="18" height="6" viewBox="0 0 18 6" fill="rgba(28,24,20,0.45)"><rect width="18" height="1.5" rx="0.75"/><rect y="4.5" width="18" height="1.5" rx="0.75"/></svg>';
          wrap.appendChild(tzHandle);

          /* Editor — the sole contentEditable; contains only user text nodes */
          const editor = document.createElement('div');
          editor.className = 'oi-tz-editor';
          editor.contentEditable = 'true';
          editor.setAttribute('role', 'textbox');
          editor.setAttribute('aria-label', zone.placeholder || 'Tap to add text…');
          editor.setAttribute('aria-multiline', 'true');
          editor.setAttribute('inputmode', 'text');
          editor.setAttribute('spellcheck', 'true');
          const saved = this.state.textContent[zKey] || '';
          editor.dataset.ph = zone.placeholder || 'Tap to add text…';
          editor.dataset.empty = saved ? '0' : '1';
          editor.appendChild(document.createTextNode(saved));
          editor.style.cssText = [
            `font-size:${effectiveFs}px`,
            `text-align:${zoneFmt.align || zone.align || 'center'}`,
            `font-family:${zoneFmt.family || fontFam}`,
            `font-weight:${zoneFmt.bold ? '700' : (zone.style === 'heading' ? '300' : '400')}`,
            `font-style:${zoneFmt.italic ? 'italic' : 'normal'}`,
            `letter-spacing:${zone.style === 'heading' ? '0.06em' : 'normal'}`,
            `color:${zoneFmt.color || '#1C1814'}`,
          ].join(';');
          wrap.appendChild(editor);

          if (!_isTouchOnly) {
            wrap.addEventListener('mouseenter',  () => { if (document.activeElement !== editor) tzHandle.style.opacity = '1'; });
            wrap.addEventListener('mouseleave',  () => { tzHandle.style.opacity = '0'; });
            editor.addEventListener('focus',     () => { tzHandle.style.opacity = '0'; });
          }

          /* Whichever zone the user is actually typing in drives the style panel —
             no separate tab click should be needed to see/change its font, size,
             colour or alignment. */
          editor.addEventListener('focus', () => {
            if (this._tzPanelZoneIdx !== zIdx) {
              this._tzPanelZoneIdx = zIdx;
              this._renderTextStyleSection();
            }
          });

          const _startTzDrag = (startMX, startMY) => {
            const grid = wrap.parentElement;
            const startLeft = wrap.offsetLeft, startTop = wrap.offsetTop;
            const wrapW = wrap.offsetWidth,    wrapH = wrap.offsetHeight;
            const onMove = (mx, my) => {
              const gW = grid.offsetWidth, gH = grid.offsetHeight;
              /* Keep the box inside the region the layout allocated for this zone, the
                 way a photo can only pan inside its slot. Previously this clamped to the
                 page edges, so a caption could be dragged on top of a photo — text was
                 constrained in size but free in position, which is the asymmetry between
                 slots and zones. Layout bands don't overlap slots, so bounding to the
                 band keeps text off photos without hard-coding anything about them. */
              const bandL = zone.x * gW;
              const bandT = zone.y * gH;
              const bandW = zone.w * gW;
              const bandH = (zone.h != null ? zone.h : 0.18) * gH;
              /* A box as wide/tall as its band has no slack — clamping to the band start
                 pins it rather than letting max() flip the bounds inside out. */
              const maxL = Math.max(bandL, bandL + bandW - wrapW);
              const maxT = Math.max(bandT, bandT + bandH - wrapH);
              const newL = Math.min(maxL, Math.max(bandL, startLeft + mx - startMX));
              const newT = Math.min(maxT, Math.max(bandT, startTop  + my - startMY));
              wrap.style.left = (newL / gW * 100).toFixed(2) + '%';
              wrap.style.top  = (newT / gH * 100).toFixed(2) + '%';
            };
            const onEnd = () => {
              const gW = grid.offsetWidth, gH = grid.offsetHeight;
              const posX = wrap.offsetLeft / gW;
              const posY = wrap.offsetTop  / gH;
              this.state.textFormat[zKey] = { ...(this.state.textFormat[zKey] || {}), posX, posY };
              this._markTzDirty(zKey);
            };
            return { onMove, onEnd };
          };

          const _focusTz = () => {
            editor.focus();
            requestAnimationFrame(() => {
              try {
                const sel = window.getSelection();
                if (!sel) return;
                const range = document.createRange();
                let textNode = null;
                for (const c of editor.childNodes) { if (c.nodeType === Node.TEXT_NODE) { textNode = c; break; } }
                if (textNode) { range.setStart(textNode, textNode.length); range.setEnd(textNode, textNode.length); }
                else          { range.setStart(editor, 0); range.setEnd(editor, 0); }
                sel.removeAllRanges();
                sel.addRange(range);
              } catch (_) {}
            });
          };

          editor.addEventListener('input', () => {
            const val = _tzEditorText(editor);
            this.state.textContent[zKey] = val;
            editor.dataset.empty = val.trim() ? '0' : '1';
            this.state.dirtyViews.add(i);
            /* Re-fit as they type: the size that fits depends on how much text there is,
               so the box shrinks the text to stay inside itself rather than overflowing. */
            const fittedPx = this._fitTzFontPx(zKey, wrap);
            if (fittedPx != null) editor.style.fontSize = fittedPx + 'px';
            this._autoSaveDraft();
            clearTimeout(this._cropKickTimer);
            this._cropKickTimer = setTimeout(() => this._kickBackgroundUploads(), 2000);
          });

          wrap.addEventListener('click', (e) => {
            if (tzHandle.contains(e.target)) return;
            if (editor.contains(e.target)) return; /* native click already placed the caret correctly */
            _focusTz();
          });

          tzHandle.addEventListener('mousedown', (e) => {
            e.preventDefault();
            let moved = false;
            tzHandle.style.cursor = 'grabbing';
            const { onMove, onEnd } = _startTzDrag(e.clientX, e.clientY);
            const mv = (e) => { moved = true; onMove(e.clientX, e.clientY); };
            const up = () => {
              tzHandle.style.cursor = 'grab';
              document.removeEventListener('mousemove', mv);
              document.removeEventListener('mouseup', up);
              if (moved) onEnd(); else _focusTz();
            };
            document.addEventListener('mousemove', mv);
            document.addEventListener('mouseup', up);
          });

          tzHandle.addEventListener('touchstart', (e) => {
            const t0 = e.touches[0];
            let moved = false;
            const { onMove, onEnd } = _startTzDrag(t0.clientX, t0.clientY);
            const mv = (e) => { e.preventDefault(); moved = true; const t = e.touches[0]; onMove(t.clientX, t.clientY); };
            const up = () => {
              tzHandle.removeEventListener('touchmove', mv);
              tzHandle.removeEventListener('touchend', up);
              if (moved) onEnd(); else _focusTz();
            };
            tzHandle.addEventListener('touchmove', mv, { passive: false });
            tzHandle.addEventListener('touchend', up);
          }, { passive: true });

          spreadGrid.appendChild(wrap);
          /* Fit once it has a real width, so a saved oversized size opens at the size
             that prints instead of overflowing until the customer types. */
          const fittedPx = this._fitTzFontPx(zKey, wrap);
          if (fittedPx != null) {
            editor.style.fontSize = fittedPx + 'px';
            wrap.style.minHeight = Math.min(Math.round(fittedPx * 1.6), Math.floor(zBox.h * spreadH)) + 'px';
          }
        });
      }

      /* Sync slot crop controls to the most recently active slot */
      this._renderSlotCropControls(this._activeCropKey || null);
      /* Refresh filmstrip thumbnail for the current spread after any render */
      this._scheduleFilmThumb(i);

    }

    /* Screen-side twin of the print auto-fit (_fitFontSize). The editor must show the
       size that will actually print, otherwise text grows past its box on screen and is
       silently shrunk at render — the preview/print divergence this project keeps paying
       for. Measured on a scratch canvas: the fit is scale-invariant, so measuring in
       screen pixels yields the same size fraction the print renderer arrives at. */
    _fitTzFontPx(zKey, wrap) {
      const parts   = zKey.split(':');
      const vi      = parseInt(parts[0], 10);
      const zoneKey = parts.slice(1).join(':');
      const def  = this.layoutDef(vi);
      const zone = def && def.textZones && def.textZones.find(z => z.key === zoneKey);
      if (!zone) return null;

      const fmt  = this.state.textFormat[zKey] || {};
      const sh   = parseFloat(wrap.dataset.spreadH) || 392;
      const requested = fmt.fontSizeFrac != null
        ? Math.round(fmt.fontSizeFrac * sh)
        : Math.round(zone.fontSize * sh);

      const text = this.state.textContent[zKey] || '';
      if (!text.trim()) return requested;

      /* Same box the print renderer will use, so the size fitted here is the size printed. */
      const zBox = _zoneBox(zone)(fmt);
      const boxH = Math.max(1, zBox.h * sh);
      const boxW = wrap.offsetWidth || Math.round(zBox.w * sh * 2);

      this._tzFitCtx = this._tzFitCtx || document.createElement('canvas').getContext('2d');
      const fam    = fmt.family || (this.FONTS.find(f => f.id === this.state.fontId) || {}).family || 'Georgia, serif';
      const weight = fmt.bold ? '700' : (zone.style === 'heading' ? '300' : '400');
      const style  = fmt.italic ? 'italic' : 'normal';
      return _fitFontSize(
        this._tzFitCtx, text, boxW, boxH, `${style} ${weight}`, fam, requested,
        _fitFloorPx(requested, boxH, sh)
      ).fs;
    }

    _applyTzFormat(zKey) {
      const wrap = this.el.spreadGrid?.querySelector(`[data-text-zone="${zKey}"]`);
      if (!wrap) return;
      const editor = wrap.querySelector('.oi-tz-editor');
      const fmt = this.state.textFormat[zKey] || {};
      if (editor) {
        if (fmt.family)          editor.style.fontFamily  = fmt.family;
        if (fmt.align)           editor.style.textAlign   = fmt.align;
        if (fmt.color)           editor.style.color       = fmt.color;
        if (fmt.bold != null)    editor.style.fontWeight  = fmt.bold   ? '700' : '400';
        if (fmt.italic != null)  editor.style.fontStyle   = fmt.italic ? 'italic' : 'normal';
        /* Always set the size, not only when the customer has overridden it: the
           auto-fit result depends on how much text there is, so it changes as they type. */
        const fittedPx = this._fitTzFontPx(zKey, wrap);
        if (fittedPx != null) editor.style.fontSize = fittedPx + 'px';
      }
      /* Reapply through the same clamp as creation and print, so this path can't put a
         zone back outside its band (it reads the raw saved fmt, which for books dragged
         before the clamp existed may still hold an out-of-band position). */
      const parts = zKey.split(':');
      const pDef  = this.layoutDef(parseInt(parts[0], 10));
      const pZone = pDef && pDef.textZones && pDef.textZones.find(z => z.key === parts.slice(1).join(':'));
      if (pZone) {
        const pBox = _zoneBox(pZone)(fmt);
        wrap.style.left      = (pBox.x * 100).toFixed(2) + '%';
        wrap.style.top       = (pBox.y * 100).toFixed(2) + '%';
        wrap.style.maxHeight = (pBox.h * 100).toFixed(2) + '%';
        const sh = parseFloat(wrap.dataset.spreadH) || 392;
        const fs = parseFloat(editor && editor.style.fontSize) || 0;
        wrap.style.minHeight = Math.min(Math.round(fs * 1.6), Math.floor(pBox.h * sh)) + 'px';
      }
    }

    _markTzDirty(zKey) {
      const vi = parseInt(zKey.split(':')[0], 10);
      if (!isNaN(vi)) this.state.dirtyViews.add(vi);
      this._autoSaveDraft();
      clearTimeout(this._cropKickTimer);
      this._cropKickTimer = setTimeout(() => this._kickBackgroundUploads(), 1500);
    }

    /* Attach pan/zoom/touch listeners to a filled slot without rebuilding spread DOM. */
    _attachSlotPanZoom(key, slot) {
      const getImg  = () => slot.querySelector('img');
      const markCropDirty = () => {
        const vi = parseInt(key.split(':')[0].slice(1), 10);
        if (!isNaN(vi)) this.state.dirtyViews.add(vi);
        this._autoSaveDraft();
        // Re-upload spread after pan/zoom so Drive always reflects the latest crop position
        clearTimeout(this._cropKickTimer);
        this._cropKickTimer = setTimeout(() => this._kickBackgroundUploads(), 1500);
      };
      const applyTF = tf => {
        this.state.slotTransforms[key] = tf;
        const im = getImg(); if (im) this._applySlotTransformCSS(im, tf);
      };

      /* Give JS full control of touch gestures on this slot — prevents the browser from
         starting a page scroll during a pan/pinch before JS can call preventDefault. */
      slot.style.touchAction = 'none';

      slot.addEventListener('wheel', e => {
        e.preventDefault(); e.stopPropagation();
        const tf  = this._getSlotTransform(key);
        const fac = e.deltaY < 0 ? 1.1 : 0.91;
        applyTF({ ...tf, scale: Math.max(0.5, Math.min(10, tf.scale * fac)) });
        this._saveTransforms(); markCropDirty();
        this._renderSlotCropControls(key);
      }, { passive: false });

      slot.addEventListener('mousedown', e => {
        if (e.button !== 0) return;
        e.preventDefault();
        const tf0 = { ...this._getSlotTransform(key) };
        const ox = e.clientX, oy = e.clientY;
        const s = tf0.scale;
        slot.style.cursor = 'grabbing';
        /* Compute pan range once — slot/image dims don't change mid-drag.
           Derived from the same window maths _drawFit uses, so a drag of N
           pixels moves the photo exactly as far as the slider position it
           produces. The previous form assumed the pan travelled only within
           the cover-fit band, which under-reported the range once panning was
           allowed across the whole source. */
        const sW = slot.offsetWidth, sH = slot.offsetHeight;
        const im0 = getImg();
        const { maxX, maxY } = (im0 && im0.naturalWidth && im0.naturalHeight)
          ? _panTravelPx(im0.naturalWidth, im0.naturalHeight, sW, sH, s)
          : { maxX: sW * 0.5, maxY: sH * 0.5 };
        const mv = e2 => {
          const dpx = e2.clientX - ox, dpy = e2.clientY - oy;
          applyTF({
            ...tf0,
            scale: s,
            nx: Math.max(-1, Math.min(1, tf0.nx + dpx / maxX)),
            ny: Math.max(-1, Math.min(1, tf0.ny + dpy / maxY)),
          });
        };
        const up = () => {
          const im = getImg(); if (im) this._clampSlotTransform(key, slot, im);
          this._saveTransforms(); markCropDirty();
          this._renderSlotCropControls(key);
          slot.style.cursor = 'grab';
          document.removeEventListener('mousemove', mv);
          document.removeEventListener('mouseup', up);
        };
        document.addEventListener('mousemove', mv);
        document.addEventListener('mouseup', up);
      });

      let touch0 = null;
      slot.addEventListener('touchstart', e => {
        /* Stop the stage-level swipe-to-navigate handler from capturing this touch —
           without this, a horizontal pan on a slot can trigger spread navigation. */
        e.stopPropagation();
        touch0 = { tf: { ...this._getSlotTransform(key) }, pts: Array.from(e.touches).map(t => ({ x: t.clientX, y: t.clientY })) };
      }, { passive: true });
      slot.addEventListener('touchmove', e => {
        if (!touch0) return;
        e.preventDefault();
        touch0.moved = true;
        const pts = Array.from(e.touches).map(t => ({ x: t.clientX, y: t.clientY }));
        const sW  = slot.offsetWidth, sH = slot.offsetHeight;
        if (pts.length === 2 && touch0.pts.length >= 2) {
          const d0 = Math.hypot(touch0.pts[1].x - touch0.pts[0].x, touch0.pts[1].y - touch0.pts[0].y);
          const d1 = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
          const s  = Math.max(0.5, Math.min(10, touch0.tf.scale * (d1 / d0)));
          const mx0 = (touch0.pts[0].x + touch0.pts[1].x) / 2, my0 = (touch0.pts[0].y + touch0.pts[1].y) / 2;
          const mx1 = (pts[0].x + pts[1].x) / 2, my1 = (pts[0].y + pts[1].y) / 2;
          const dx = mx1 - mx0, dy = my1 - my0;
          const maxX = s > 1 ? sW * (s - 1) / 2 : sW * 0.5;
          const maxY = s > 1 ? sH * (s - 1) / 2 : sH * 0.5;
          applyTF({
            ...touch0.tf,
            scale: s,
            nx: Math.max(-1, Math.min(1, touch0.tf.nx + dx / maxX)),
            ny: Math.max(-1, Math.min(1, touch0.tf.ny + dy / maxY)),
          });
        } else if (pts.length === 1 && touch0.pts.length === 1) {
          const s = touch0.tf.scale;
          const dx = pts[0].x - touch0.pts[0].x, dy = pts[0].y - touch0.pts[0].y;
          const maxX = s > 1 ? sW * (s - 1) / 2 : sW * 0.5;
          const maxY = s > 1 ? sH * (s - 1) / 2 : sH * 0.5;
          applyTF({
            ...touch0.tf,
            scale: s,
            nx: Math.max(-1, Math.min(1, touch0.tf.nx + dx / maxX)),
            ny: Math.max(-1, Math.min(1, touch0.tf.ny + dy / maxY)),
          });
        }
      }, { passive: false });
      slot.addEventListener('touchend', () => {
        const im = getImg(); if (im) this._clampSlotTransform(key, slot, im);
        this._saveTransforms(); markCropDirty();
        this._renderSlotCropControls(key);
        /* A crop drag is not a tap: don't let its trailing click open the crop sheet. */
        if (touch0?.moved) this._suppressSlotTapUntil = Date.now() + 500;
        touch0 = null;
      }, { passive: true });
      slot.addEventListener('touchcancel', () => {
        /* iOS cancels the touch sequence on notifications / system gestures.
           The transform was already updated in state during touchmove — save and kick so it isn't lost. */
        const im = getImg(); if (im) this._clampSlotTransform(key, slot, im);
        this._saveTransforms(); markCropDirty();
        touch0 = null;
      }, { passive: true });
    }

    /* ── Render: right panel ── */
    _renderRight() {
      this._renderSwatches();
      this._renderFoils();
      this._renderFonts();
      this._renderLayoutCards();
      this._renderCoverLayoutCards();
    }

    /* Returns the resolved cover layout ID, falling back to a safe default. */
    _coverLayout() {
      if (this.state.coverLayout === 'coverFull') return 'coverPhotoTitle'; /* coverFull removed */
      if (this.state.coverLayout) return this.state.coverLayout;
      return 'coverPhotoTitle';
    }

    _renderCoverLayoutCards() {
      const { coverLayoutCards, mobCoverLayoutCards } = this.el;
      if (!coverLayoutCards && !mobCoverLayoutCards) return;
      const activeId = this._coverLayout();
      const layouts  = [
        { id: 'coverPhotoTitle', label: 'Full' },
      ];

      const buildCard = (l, compact) => {
        const active = l.id === activeId;
        const card   = document.createElement('button');
        card.setAttribute('aria-selected', String(active));
        card.setAttribute('aria-label', l.label);
        card.style.cssText = `background:${active?'rgba(201,165,95,0.1)':'rgba(255,255,255,0.03)'};border:1px solid ${active?'rgba(201,165,95,0.55)':'rgba(243,238,228,0.1)'};border-radius:4px;padding:${compact?'6px':'9px'};cursor:pointer;display:flex;flex-direction:column;gap:${compact?'5px':'8px'};transition:all .3s ease;font-family:inherit`;
        const thumbH = compact ? '40px' : '58px';
        const preview = document.createElement('div');
        preview.style.cssText = `position:relative;height:${thumbH};background:rgba(243,238,228,0.06);box-sizing:border-box`;
        const slotColor = active ? 'rgba(201,165,95,0.55)' : 'rgba(243,238,228,0.28)';
        if (l.id === 'coverFull') {
          const cell = document.createElement('div');
          cell.style.cssText = `position:absolute;inset:0;background:${slotColor};border-radius:1px`;
          preview.appendChild(cell);
        } else {
          const photo = document.createElement('div');
          photo.style.cssText = `position:absolute;left:0;top:0;right:0;bottom:25%;background:${slotColor};border-radius:1px 1px 0 0`;
          const text = document.createElement('div');
          text.style.cssText = `position:absolute;left:0;right:0;bottom:0;height:23%;background:${active?'rgba(201,165,95,0.2)':'rgba(243,238,228,0.1)'};border-radius:0 0 1px 1px`;
          preview.appendChild(photo); preview.appendChild(text);
        }
        const lbl = document.createElement('div');
        lbl.style.cssText = `font-size:${compact?'8':'9'}px;letter-spacing:1.4px;text-transform:uppercase;color:${active?'#C9A55F':'rgba(243,238,228,0.42)'};text-align:left`;
        lbl.textContent = l.label;
        card.appendChild(preview); card.appendChild(lbl);
        card.addEventListener('click', () => {
          this.state.coverLayout = l.id;
          this._saveLocalStyle({ coverLayout: l.id });
          this._renderCover();
          this._renderCoverLayoutCards();
          this._autoSaveDraft();
          if (compact) this._closeMobSheet();
        });
        return card;
      };

      if (coverLayoutCards)    { coverLayoutCards.innerHTML    = ''; layouts.forEach(l => coverLayoutCards.appendChild(buildCard(l, false))); }
      if (mobCoverLayoutCards) { mobCoverLayoutCards.innerHTML = ''; layouts.forEach(l => mobCoverLayoutCards.appendChild(buildCard(l, true))); }
    }

    _renderSwatches() {
      const { swatches, swatchLabel, mobSwatches, mobSwatchLabel } = this.el;
      const render = (container, sz) => {
        if (!container) return;
        container.innerHTML = '';
        this.CLOTHS.forEach(c => {
          const btn = document.createElement('button');
          const active = c.id === this.state.clothId;
          btn.title = c.label;
          btn.style.cssText = `width:${sz}px;height:${sz}px;border-radius:50%;background-color:${c.hex};border:none;padding:0;cursor:pointer;transition:box-shadow .3s ease,transform .3s cubic-bezier(.16,1,.3,1);` +
            (active ? 'box-shadow:0 0 0 1px #14120F,0 0 0 2px #C9A55F,0 4px 12px rgba(0,0,0,0.4);transform:scale(1.06)' : 'box-shadow:0 0 0 1px rgba(243,238,228,0.16),0 2px 6px rgba(0,0,0,0.3)');
          btn.addEventListener('click', () => { this.state.clothId = c.id; this._saveLocalStyle({ clothId: c.id }); this._renderAll(); this._autoSaveDraft(); });
          container.appendChild(btn);
        });
      };
      render(swatches, 34); render(mobSwatches, 30);
      const lbl = this.cloth().label;
      if (swatchLabel) swatchLabel.textContent = lbl;
      if (mobSwatchLabel) mobSwatchLabel.textContent = lbl;
    }

    _renderFoils() {
      const { foils, mobFoils } = this.el;
      const render = (container, compact) => {
        if (!container) return;
        container.innerHTML = '';
        const base = `background:rgba(255,255,255,0.03);border:1px solid rgba(243,238,228,0.12);color:rgba(243,238,228,0.7);border-radius:3px;padding:${compact?'6px 8px':'8px 11px'};font-size:${compact?'9':'10'}px;letter-spacing:1.4px;text-transform:uppercase;cursor:pointer;font-family:inherit;transition:all .3s ease;flex:1`;
        const activeStyle = 'background:rgba(201,165,95,0.14);border-color:rgba(201,165,95,0.6);color:#E0C287;';
        this.FOILS.forEach(f => {
          const btn = document.createElement('button');
          const active = f.id === this.state.foilId;
          btn.textContent = f.label;
          btn.style.cssText = base + (active ? ';' + activeStyle : '');
          btn.addEventListener('click', () => { this.state.foilId = f.id; this._saveLocalStyle({ foilId: f.id }); this._renderAll(); this._autoSaveDraft(); });
          container.appendChild(btn);
        });
      };
      render(foils, false); render(mobFoils, true);
    }

    _renderFonts() {
      const { fonts, mobFonts } = this.el;
      const render = (container, compact) => {
        if (!container) return;
        container.innerHTML = '';
        this.FONTS.forEach((f, idx) => {
          if (f.pagesOnly) return;   /* book/cover font: no scripts (see FONTS) */
          const active = f.id === this.state.fontId;
          const btn = document.createElement('button');
          const sz = compact ? 13 : 14;
          const pad = compact ? '5px 11px' : '6px 14px';
          btn.style.cssText = `background:${active?'rgba(201,165,95,0.14)':'rgba(255,255,255,0.04)'};border:1px solid ${active?'rgba(201,165,95,0.55)':'rgba(243,238,228,0.1)'};border-radius:20px;padding:${pad};cursor:pointer;transition:background .25s ease,border-color .25s ease,color .25s ease,transform .2s cubic-bezier(.16,1,.3,1),box-shadow .25s ease;font-family:${f.family};font-size:${sz}px;color:${active?'#F3EEE4':'rgba(243,238,228,0.62)'};animation:oi2-thumbIn .35s cubic-bezier(.16,1,.3,1) both;animation-delay:${idx*45}ms;${active?'box-shadow:0 0 0 1px rgba(201,165,95,0.3),0 2px 8px rgba(201,165,95,0.15)':''}`;
          btn.textContent = f.label;
          btn.addEventListener('mouseenter', () => { if (!btn.matches(':active')) btn.style.transform = 'scale(1.05)'; });
          btn.addEventListener('mouseleave', () => { btn.style.transform = ''; });
          btn.addEventListener('click', () => { this.state.fontId = f.id; this._saveLocalStyle({ fontId: f.id }); this._renderAll(); this._autoSaveDraft(); });
          container.appendChild(btn);
        });
      };
      render(fonts, false); render(mobFonts, true);
    }

    _renderLayoutCards() {
      const { layoutCards, layoutHeading, mobLayoutCards, mobLayoutHeading } = this.el;
      if (!layoutCards && !mobLayoutCards) return;
      const i = this.state.spreadIdx;
      const v = this.VIEWS[i] || this.VIEWS[0];
      const isWide = v.kind === 'spread';
      const set = this.layoutSet(i);
      const activeId = this.layoutId(i);
      const heading = isWide ? 'Spread Layout' : 'Page Layout';
      if (layoutHeading) layoutHeading.textContent = heading;
      if (mobLayoutHeading) mobLayoutHeading.textContent = heading;

      const buildCard = (l, compact) => {
        const active = l.id === activeId;
        const card = document.createElement('button');
        card.setAttribute('aria-selected', String(active));
        card.setAttribute('aria-label', l.label);
        card.style.cssText = `background:${active?'rgba(201,165,95,0.1)':'rgba(255,255,255,0.03)'};border:1px solid ${active?'rgba(201,165,95,0.55)':'rgba(243,238,228,0.1)'};border-radius:4px;padding:${compact?'6px':'9px'};cursor:pointer;display:flex;flex-direction:column;gap:${compact?'5px':'8px'};transition:all .3s ease;font-family:inherit`;
        const thumbH = compact ? (isWide ? '28px' : '40px') : (isWide ? '40px' : '58px');
        const preview = document.createElement('div');
        preview.style.cssText = `position:relative;width:100%;height:${thumbH};background:rgba(243,238,228,0.06);padding:2px;box-sizing:border-box;overflow:hidden`;
        const slotColor = active ? 'rgba(201,165,95,0.55)' : 'rgba(243,238,228,0.28)';
        l.slots.forEach(sr => {
          const cell = document.createElement('div');
          cell.style.cssText = [
            'position:absolute',
            `left:${(sr.x*100).toFixed(2)}%`,
            `top:${(sr.y*100).toFixed(2)}%`,
            `width:${(sr.w*100).toFixed(2)}%`,
            `height:${(sr.h*100).toFixed(2)}%`,
            `background:${slotColor}`,
            'border-radius:1px',
            'box-sizing:border-box',
          ].join(';');
          preview.appendChild(cell);
        });
        const lbl = document.createElement('div');
        lbl.style.cssText = `font-size:${compact?'8':'9'}px;letter-spacing:1.4px;text-transform:uppercase;color:${active?'#C9A55F':'rgba(243,238,228,0.42)'};text-align:left`;
        lbl.textContent = l.label;
        card.appendChild(preview); card.appendChild(lbl);
        card.addEventListener('click', () => {
          const oldDef = this.layoutDef(this.state.spreadIdx);
          this.state.layouts[this.state.spreadIdx] = l.id;
          this.state.dirtyViews.add(this.state.spreadIdx);
          const newDef = this.layoutDef(this.state.spreadIdx);
          /* Clear ALL transforms for this spread — slot shapes change so old crop positions are wrong */
          const spreadPrefix = `s${this.state.spreadIdx}:`;
          Object.keys(this.state.slotTransforms).forEach(k => {
            if (k.startsWith(spreadPrefix)) delete this.state.slotTransforms[k];
          });
          /* Re-map photos by geometric position, not array index. Different layouts can put a
             different side at the same slot index — e.g. "Caption above · left" has slot 0 on
             the LEFT, but "Story left"'s only slot (also index 0) is on the RIGHT. Keeping
             images by raw index silently moved a photo to the wrong side, or dropped the one
             that should have carried over. Match each new slot to whichever old slot's center
             is closest, and only carry the photo across if that match is actually close. */
          const oldImages = {}, oldBlobs = {};
          oldDef.slots.forEach((sr, si) => {
            const key = `${spreadPrefix}${si}`;
            if (this.state.slotImages[key]) oldImages[si] = this.state.slotImages[key];
            if (this.state.slotBlobs[key])  oldBlobs[si]  = this.state.slotBlobs[key];
            delete this.state.slotImages[key];
            delete this.state.slotBlobs[key];
          });
          const usedOld = new Set();
          newDef.slots.forEach((nr, ni) => {
            const nCenterX = nr.x + nr.w / 2, nCenterY = nr.y + nr.h / 2;
            let bestSi = -1, bestDist = Infinity;
            oldDef.slots.forEach((sr, si) => {
              if (usedOld.has(si) || (!oldImages[si] && !oldBlobs[si])) return;
              const dist = Math.hypot(nCenterX - (sr.x + sr.w / 2), nCenterY - (sr.y + sr.h / 2));
              if (dist < bestDist) { bestDist = dist; bestSi = si; }
            });
            if (bestSi !== -1 && bestDist < 0.15) {
              usedOld.add(bestSi);
              const newKey = `${spreadPrefix}${ni}`;
              if (oldImages[bestSi]) this.state.slotImages[newKey] = oldImages[bestSi];
              if (oldBlobs[bestSi])  this.state.slotBlobs[newKey]  = oldBlobs[bestSi];
            }
          });
          /* Captions/titles/body text are stored by zone key (e.g. "title", "body",
             "caption"), so text already carries over correctly whenever the new layout
             has a zone with the same key — no remapping needed there. But nothing
             previously cleared text for a key that no longer exists in the new layout,
             so switching away and later back to a layout that reuses that same key made
             old, unrelated text silently reappear. Drop text for zone keys the new
             layout doesn't have. */
          const newTextKeys = new Set((newDef.textZones || []).map(tz => tz.key));
          (oldDef.textZones || []).forEach(tz => {
            if (!newTextKeys.has(tz.key)) delete this.state.textContent[`${this.state.spreadIdx}:${tz.key}`];
          });
          /* A dragged position is relative to the specific box it was dragged within, not
             a general preference — e.g. "Story left"/"Story right" and "Caption left"/
             "Caption right" deliberately reuse the same zone key ("title"/"body", "caption")
             for their mirrored, opposite-side boxes. Left uncleared, a drag position saved
             under one of those layouts silently carried into the other on switch, putting
             text back where it was last dragged instead of the new layout's own default side.
             Unconditional on any layout change — not just when the immediately-previous
             layout happened to share the key — because a stale posX can have been set
             several switches ago under a layout that isn't oldDef, and there's no per-drag
             record of which layout it belongs to. A position is only ever safe to trust
             within the same layout it was dragged in. */
          (newDef.textZones || []).forEach(tz => {
            const fKey = `${this.state.spreadIdx}:${tz.key}`;
            const f = this.state.textFormat[fKey];
            if (f && (f.posX != null || f.posY != null)) {
              const { posX, posY, ...rest } = f;
              this.state.textFormat[fKey] = rest;
            }
          });
          this._saveLocalStyle({ layouts: this.state.layouts });
          this._renderSpread();
          this._renderLayoutCards();
          this._autoSaveDraft();
          if (compact) this._closeMobSheet();
        });
        return card;
      };

      if (layoutCards) { layoutCards.innerHTML = ''; set.forEach(l => layoutCards.appendChild(buildCard(l, false))); }
      if (mobLayoutCards) { mobLayoutCards.innerHTML = ''; set.forEach(l => mobLayoutCards.appendChild(buildCard(l, true))); }
      this._renderTextStyleSection();
      this._updateBgSwatches();
    }

    _updateBgSwatches() {
      const idx = this.state.spreadIdx;
      const cur = (idx != null && this.state.spreadBg[idx]) || INTERIOR_PAPER_COLOR;
      const isTheme = cur.startsWith('theme:');
      const themeId = isTheme ? cur.slice(6) : null;
      this.root.querySelectorAll('.oi2-bg-swatch').forEach(sw => {
        sw.classList.toggle('active', !isTheme && sw.dataset.color.toLowerCase() === cur.toLowerCase());
      });
      this.root.querySelectorAll('.oi2-theme-swatch').forEach(sw => {
        sw.classList.toggle('active', isTheme && sw.dataset.theme === themeId);
      });
      this.root.querySelectorAll('#oi2-bg-picker').forEach(p => { p.value = isTheme ? '#ffffff' : cur; });
    }

    /* Returns a cached CSS pattern object for a theme */
    _getThemeCssPattern(themeId) {
      if (!this._themeCssPats) this._themeCssPats = {};
      if (!this._themeCssPats[themeId]) {
        const theme = SPREAD_THEMES.find(t => t.id === themeId);
        if (!theme) return null;
        const imgEntry = _themeImgCache[themeId];
        if (imgEntry && imgEntry.url) {
          this._themeCssPats[themeId] = { color: theme.color, dataUrl: imgEntry.url, isImage: true };
        } else {
          const s = 100;
          const tc = document.createElement('canvas'); tc.width = tc.height = s;
          theme.drawTile(tc.getContext('2d'), s);
          this._themeCssPats[themeId] = { color: theme.color, dataUrl: tc.toDataURL(), tileSize: s };
        }
      }
      return this._themeCssPats[themeId];
    }

    _renderTextStyleSection() {
      const { textStyleSection, textStyleBody, mobTextStyleSection, mobTextStyleBody } = this.el;
      const i = this.state.spreadIdx;
      const def = this.layoutDef(i);
      const hasText = def.textZones && def.textZones.length > 0;

      if (textStyleSection) {
        textStyleSection.style.display = hasText ? 'flex' : 'none';
      }
      if (mobTextStyleSection) mobTextStyleSection.style.display = hasText ? 'flex' : 'none';
      /* Phone Text tab on a page without text boxes: say why, instead of an empty panel. */
      if (this.el.mobTextEmpty) this.el.mobTextEmpty.style.display = hasText ? 'none' : 'block';
      if (hasText) {
        const editTab = this.root.querySelector('[data-panel-spread] [data-tab="edit"]');
        if (editTab && !editTab.classList.contains('active')) editTab.click();
      }
      if (!hasText) return;

      /* Reset selected zone index if out of range or spread changed */
      if ((this._tzPanelZoneIdx || 0) >= def.textZones.length) this._tzPanelZoneIdx = 0;
      const selIdx = this._tzPanelZoneIdx || 0;

      const ZONE_LABELS = { title:'Title', body:'Subtext', caption:'Caption', captionL:'Left', captionR:'Right',
                            captionHero:'Large photo', captionTop:'Top right', captionBottom:'Bottom right' };
      const ALIGN_SVGS = [
        { a:'left',   svg:'<svg width="14" height="11" viewBox="0 0 14 11" fill="none"><rect width="14" height="2" rx="1" fill="currentColor"/><rect y="4.5" width="9" height="2" rx="1" fill="currentColor"/><rect y="9" width="11" height="2" rx="1" fill="currentColor"/></svg>' },
        { a:'center', svg:'<svg width="14" height="11" viewBox="0 0 14 11" fill="none"><rect width="14" height="2" rx="1" fill="currentColor"/><rect x="2.5" y="4.5" width="9" height="2" rx="1" fill="currentColor"/><rect x="1.5" y="9" width="11" height="2" rx="1" fill="currentColor"/></svg>' },
        { a:'right',  svg:'<svg width="14" height="11" viewBox="0 0 14 11" fill="none"><rect width="14" height="2" rx="1" fill="currentColor"/><rect x="5" y="4.5" width="9" height="2" rx="1" fill="currentColor"/><rect x="3" y="9" width="11" height="2" rx="1" fill="currentColor"/></svg>' },
      ];

      const fill = (container, compact) => {
        if (!container) return;
        container.innerHTML = '';

        /* Zone selector tabs — only show if more than one zone */
        if (def.textZones.length > 1) {
          const tabs = document.createElement('div');
          tabs.style.cssText = compact ? 'display:flex;gap:8px;flex-wrap:nowrap;overflow-x:auto;scrollbar-width:none' : 'display:flex;gap:6px';
          def.textZones.forEach((zone, idx) => {
            const on = idx === selIdx;
            const tab = document.createElement('button');
            tab.style.cssText = `background:${on?'rgba(201,165,95,0.14)':'rgba(255,255,255,0.04)'};border:1px solid ${on?'rgba(201,165,95,0.55)':'rgba(243,238,228,0.1)'};border-radius:20px;padding:${compact?'10px 16px':'4px 13px'};cursor:pointer;flex:none;white-space:nowrap;font-size:${compact?11:10.5}px;letter-spacing:1.2px;text-transform:uppercase;color:${on?'#C9A55F':'rgba(243,238,228,0.5)'};transition:all .2s ease`;
            tab.textContent = ZONE_LABELS[zone.key] || zone.key;
            tab.addEventListener('click', () => { this._tzPanelZoneIdx = idx; this._renderTextStyleSection(); });
            tabs.appendChild(tab);
          });
          container.appendChild(tabs);
        }

        /* Controls for selected zone */
        const zone = def.textZones[selIdx];
        const zKey = `${i}:${zone.key}`;
        const fmt = this.state.textFormat[zKey] || {};
        const activeFontId  = fmt.fontId || this.state.fontId;
        const activeAlign   = fmt.align  || zone.align || 'center';
        const activeColor   = fmt.color  || '#1C1814';
        const REF = 400;
        const fontSizeFrac  = fmt.fontSizeFrac != null ? fmt.fontSizeFrac : (zone.fontSize || 0.05);
        /* Largest size whose single line fits the zone's height (line height is 1.5×),
           so the stepper can't pick a size the box can never hold. */
        const sizeCap       = Math.max(8, Math.min(80, Math.floor(_zoneBox(zone)(fmt).h / 1.5 * REF)));
        const fontSizeNum   = Math.max(8, Math.min(sizeCap, Math.round(fontSizeFrac * REF)));

        const applyFmt = (patch) => {
          this.state.textFormat[zKey] = { ...(this.state.textFormat[zKey] || {}), ...patch };
          this._applyTzFormat(zKey);
          this._markTzDirty(zKey);
          this._renderTextStyleSection();
        };

        /* Font pills */
        const fontRow = document.createElement('div');
        /* Phones: one swipeable line per row, so the whole panel fits under the book. */
        const oneLine = compact ? 'flex-wrap:nowrap;overflow-x:auto;scrollbar-width:none;' : 'flex-wrap:wrap;';
        fontRow.style.cssText = `display:flex;${oneLine}gap:${compact?8:6}px`;
        this.FONTS.forEach(f => {
          const on = f.id === activeFontId;
          const btn = document.createElement('button');
          btn.style.cssText = `background:${on?'rgba(201,165,95,0.14)':'rgba(255,255,255,0.04)'};border:1px solid ${on?'rgba(201,165,95,0.55)':'rgba(243,238,228,0.1)'};border-radius:20px;padding:${compact?'8px 14px':'4px 11px'};cursor:pointer;flex:none;white-space:nowrap;font-family:${f.family};font-size:${compact?14:12}px;color:${on?'#F3EEE4':'rgba(243,238,228,0.58)'};transition:all .2s ease`;
          btn.textContent = f.label;
          btn.addEventListener('click', () => applyFmt({ fontId: f.id, family: f.family }));
          fontRow.appendChild(btn);
        });
        container.appendChild(fontRow);

        /* Font size stepper: − [18] + */
        const sizeRow = document.createElement('div');
        sizeRow.style.cssText = 'display:flex;flex:none;align-items:center;gap:0;background:rgba(255,255,255,0.04);border:1px solid rgba(243,238,228,0.1);border-radius:6px;overflow:hidden;align-self:flex-start';
        const btnCss = `all:unset;width:${compact?44:32}px;height:${compact?40:30}px;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:${compact?20:16}px;color:rgba(243,238,228,0.55);transition:background .15s,color .15s`;
        const minusBtn = document.createElement('button');
        minusBtn.innerHTML = '&minus;';
        minusBtn.style.cssText = btnCss;
        minusBtn.addEventListener('mouseenter', () => { minusBtn.style.background='rgba(201,165,95,0.1)'; minusBtn.style.color='#C9A55F'; });
        minusBtn.addEventListener('mouseleave', () => { minusBtn.style.background=''; minusBtn.style.color='rgba(243,238,228,0.55)'; });
        minusBtn.addEventListener('click', () => applyFmt({ fontSizeFrac: Math.max(8, fontSizeNum - 1) / REF }));
        const numSpan = document.createElement('span');
        numSpan.textContent = fontSizeNum;
        numSpan.style.cssText = `min-width:${compact?44:36}px;text-align:center;font-size:${compact?14:12}px;color:rgba(243,238,228,0.75);border-left:1px solid rgba(243,238,228,0.08);border-right:1px solid rgba(243,238,228,0.08);line-height:1;padding:0 4px`;
        const plusBtn = document.createElement('button');
        plusBtn.textContent = '+';
        plusBtn.style.cssText = btnCss;
        plusBtn.addEventListener('mouseenter', () => { plusBtn.style.background='rgba(201,165,95,0.1)'; plusBtn.style.color='#C9A55F'; });
        plusBtn.addEventListener('mouseleave', () => { plusBtn.style.background=''; plusBtn.style.color='rgba(243,238,228,0.55)'; });
        plusBtn.addEventListener('click', () => applyFmt({ fontSizeFrac: Math.min(sizeCap, fontSizeNum + 1) / REF }));
        sizeRow.appendChild(minusBtn); sizeRow.appendChild(numSpan); sizeRow.appendChild(plusBtn);
        if (!compact) container.appendChild(sizeRow);   /* phones: joins the alignment row */

        /* Colour swatches + custom picker */
        const colorRow = document.createElement('div');
        colorRow.style.cssText = `display:flex;gap:${compact?12:7}px;align-items:center;${compact ? 'flex-wrap:nowrap;overflow-x:auto;scrollbar-width:none;padding:2px' : 'flex-wrap:wrap'}`;
        const TEXT_COLORS = [
          { hex:'#1C1814', label:'Dark' },
          { hex:'#FFFFFF', label:'White' },
          { hex:'#C9A55F', label:'Gold' },
          { hex:'#F3EEE4', label:'Cream' },
          { hex:'#5A4B3B', label:'Warm' },
        ];
        TEXT_COLORS.forEach(({ hex, label }) => {
          const on = hex.toLowerCase() === activeColor.toLowerCase();
          const sw = document.createElement('button');
          sw.title = label;
          sw.style.cssText = `all:unset;width:${compact?32:18}px;height:${compact?32:18}px;border-radius:50%;cursor:pointer;background:${hex};flex-shrink:0;box-shadow:${on?`0 0 0 2px #C9A55F,inset 0 0 0 1px rgba(243,238,228,0.2)`:'inset 0 0 0 1px rgba(243,238,228,0.18)'};transition:box-shadow .15s`;
          sw.addEventListener('click', () => applyFmt({ color: hex }));
          colorRow.appendChild(sw);
        });
        const colorPicker = document.createElement('input');
        colorPicker.type = 'color';
        colorPicker.value = /^#[0-9a-f]{6}$/i.test(activeColor) ? activeColor : '#1c1814';
        colorPicker.style.cssText = `width:${compact?32:18}px;height:${compact?32:18}px;border:none;padding:0;cursor:pointer;background:none;border-radius:50%;overflow:hidden;opacity:0.6`;
        colorPicker.title = 'Custom colour';
        colorPicker.addEventListener('input', () => applyFmt({ color: colorPicker.value }));
        colorRow.appendChild(colorPicker);
        container.appendChild(colorRow);

        /* Alignment + Bold/Italic row */
        const alignRow = document.createElement('div');
        alignRow.style.cssText = `display:flex;gap:${compact?8:5}px;align-items:center;${oneLine}`;
        if (compact) alignRow.appendChild(sizeRow);
        ALIGN_SVGS.forEach(({ a, svg }) => {
          const on = a === activeAlign;
          const btn = document.createElement('button');
          btn.style.cssText = `background:${on?'rgba(201,165,95,0.14)':'rgba(255,255,255,0.04)'};border:1px solid ${on?'rgba(201,165,95,0.55)':'rgba(243,238,228,0.1)'};border-radius:5px;padding:${compact?'10px 16px':'5px 12px'};flex:none;cursor:pointer;color:${on?'#C9A55F':'rgba(243,238,228,0.5)'};display:inline-flex;align-items:center;transition:all .2s ease`;
          btn.innerHTML = svg;
          btn.addEventListener('click', () => applyFmt({ align: a }));
          alignRow.appendChild(btn);
        });
        /* Bold */
        const boldBtn = document.createElement('button');
        const boldOn = !!fmt.bold;
        boldBtn.textContent = 'B';
        boldBtn.style.cssText = `background:${boldOn?'rgba(201,165,95,0.14)':'rgba(255,255,255,0.04)'};border:1px solid ${boldOn?'rgba(201,165,95,0.55)':'rgba(243,238,228,0.1)'};border-radius:5px;padding:${compact?'10px 16px':'5px 12px'};flex:none;cursor:pointer;font-weight:700;font-size:${compact?15:12}px;color:${boldOn?'#C9A55F':'rgba(243,238,228,0.5)'};transition:all .2s ease`;
        boldBtn.addEventListener('click', () => applyFmt({ bold: !boldOn }));
        alignRow.appendChild(boldBtn);
        /* Italic */
        const italicBtn = document.createElement('button');
        const italicOn = !!fmt.italic;
        italicBtn.textContent = 'I';
        italicBtn.style.cssText = `background:${italicOn?'rgba(201,165,95,0.14)':'rgba(255,255,255,0.04)'};border:1px solid ${italicOn?'rgba(201,165,95,0.55)':'rgba(243,238,228,0.1)'};border-radius:5px;padding:${compact?'10px 16px':'5px 12px'};flex:none;cursor:pointer;font-style:italic;font-size:${compact?15:12}px;color:${italicOn?'#C9A55F':'rgba(243,238,228,0.5)'};transition:all .2s ease`;
        italicBtn.addEventListener('click', () => applyFmt({ italic: !italicOn }));
        alignRow.appendChild(italicBtn);
        container.appendChild(alignRow);
      };

      fill(textStyleBody, false);
      fill(mobTextStyleBody, true);
    }

    /* ── Add / renumber spreads ── */
    _renumberViews() {
      let p = 1;
      this.VIEWS.forEach(v => {
        if (v.kind === 'spread') { v.pages = [p, p + 1]; p += 2; }
        else                     { v.pages = [p]; p += 1; }
      });
    }

    /* Moves every per-page piece of state (photos, captions, layout, text,
       background, dirty/print-upload tracking, cached filmstrip thumbnail)
       from view index `from` to view index `to`. Inserting or removing a
       spread shifts the array position of every later page without this —
       every one of those state maps is keyed by the numeric view index, so
       a page's own content would silently end up attributed to the wrong
       page (e.g. the last page's photo appearing to "jump" onto a newly
       inserted blank spread). Always marks the destination dirty so the
       print pipeline re-renders and re-uploads it under its new index,
       regardless of what the server previously had stored for that slot. */
    _shiftViewState(from, to) {
      if (from === to) return;
      const s = this.state;

      ['spreadBg', 'layouts', 'captions'].forEach(prop => {
        const map = s[prop];
        if (Object.prototype.hasOwnProperty.call(map, from)) { map[to] = map[from]; delete map[from]; }
        else delete map[to];
      });

      if (s.dirtyViews.has(from)) { s.dirtyViews.delete(from); s.dirtyViews.add(to); }
      else s.dirtyViews.delete(to);

      const movePrefixed = (map, fromPfx, toPfx) => {
        const moving = Object.keys(map).filter(k => k.startsWith(fromPfx));
        Object.keys(map).forEach(k => { if (k.startsWith(toPfx)) delete map[k]; });
        moving.forEach(k => { map[toPfx + k.slice(fromPfx.length)] = map[k]; delete map[k]; });
      };
      ['slotImages', 'slotBlobs', 'slotTransforms'].forEach(prop => movePrefixed(s[prop], `s${from}:`, `s${to}:`));
      ['textContent', 'textFormat'].forEach(prop => movePrefixed(s[prop], `${from}:`, `${to}:`));

      if (this._filmThumbCache) {
        const staleAtDest = this._filmThumbCache.get(to);
        if (staleAtDest) try { URL.revokeObjectURL(staleAtDest); } catch(_e) {}
        const moving = this._filmThumbCache.get(from);
        if (moving !== undefined) { this._filmThumbCache.set(to, moving); this._filmThumbCache.delete(from); }
        else this._filmThumbCache.delete(to);
      }
      if (this._filmThumbTimers && this._filmThumbTimers.has(from)) {
        clearTimeout(this._filmThumbTimers.get(from));
        this._filmThumbTimers.delete(from);
      }

      s.dirtyViews.add(to);
    }

    _addSpread() {
      const MAX_SPREADS = 37; // 76 pages max
      if (this.VIEWS.filter(v => v.kind === 'spread').length >= MAX_SPREADS) return;
      const insertAt = this.VIEWS.length - 1; // before the last single page
      /* The page currently at insertAt is about to be pushed to insertAt+1
         by the splice below — move its state with it before the array
         shifts, or it ends up mis-attributed to the new blank spread. */
      this._shiftViewState(insertAt, insertAt + 1);
      this.VIEWS.splice(insertAt, 0, { kind: 'spread', pages: [] });
      /* Carry the last "apply to all" background onto the new page too, so
         it matches the rest of the book instead of defaulting to plain
         paper. Only kicks in if that feature was actually used this session. */
      if (this._bgApplyAllUsed) {
        if (this._lastAppliedAllBg) {
          this.state.spreadBg[insertAt] = this._lastAppliedAllBg;
          this.state.dirtyViews.add(insertAt);
        } else {
          delete this.state.spreadBg[insertAt];
        }
      }
      this._renumberViews();
      // Sync pricing: base book has 9 spreads; each extra spread = 1 sleeve = ₹65
      try {
        const pricing = JSON.parse(localStorage.getItem('oiCraftPricing:' + this._scope) || '{}');
        pricing.sleeves = Math.max(0, this.VIEWS.filter(v => v.kind === 'spread').length - 9);
        localStorage.setItem('oiCraftPricing:' + this._scope, JSON.stringify(pricing));
      } catch(e) {}
      this._renderPrice();
      this._renderFilmstrip();
      this._renderSpecs();
      this._goSpread(insertAt);
      const pSec = document.getElementById('oi2-sec-pages');
      if (pSec && pSec.open) this._renderMobileFilmstrip();
      /* Persist the shift + any inherited background — spreadBg only lives
         locally, everything else syncs to the server draft. */
      this._saveLocalStyle({ spreadBg: this.state.spreadBg });
      this._autoSaveDraft();
    }

    _removeSpread() {
      const spreadIdxs = [];
      this.VIEWS.forEach((v, i) => { if (v.kind === 'spread') spreadIdxs.push(i); });
      if (spreadIdxs.length <= 9) return; // never below the base 9 spreads / 20 pages
      const vi = spreadIdxs[spreadIdxs.length - 1]; // always the last spread, mirroring _addSpread

      const _pfx = 's' + vi + ':';
      const hasPhoto = Object.keys(this.state.slotImages).some(k => k.startsWith(_pfx));
      if (hasPhoto && !confirm('This spread has photos on it. Remove it anyway? The photos placed here will be cleared.')) return;

      delete this.state.layouts[vi];
      delete this.state.captions[vi];
      delete this.state.spreadBg[vi];
      this.state.dirtyViews.delete(vi);
      this._filmThumbCache.delete(vi);
      for (const k of Object.keys(this.state.slotImages)) {
        if (k.startsWith(_pfx)) {
          /* Only revoke if no upload entry still references this URL —
             blob URLs shared between slotImages and state.uploads must not be freed. */
          const url = this.state.slotImages[k];
          const stillUsed = this.state.uploads.some(u => u.url === url || u.originalUrl === url);
          if (!stillUsed) try { URL.revokeObjectURL(url); } catch(_e) {}
          delete this.state.slotImages[k];
        }
      }
      for (const k of Object.keys(this.state.slotBlobs))      { if (k.startsWith(_pfx)) delete this.state.slotBlobs[k]; }
      for (const k of Object.keys(this.state.slotTransforms)) { if (k.startsWith(_pfx)) delete this.state.slotTransforms[k]; }
      const _tpfx = vi + ':';
      for (const k of Object.keys(this.state.textContent)) { if (k.startsWith(_tpfx)) delete this.state.textContent[k]; }
      for (const k of Object.keys(this.state.textFormat))  { if (k.startsWith(_tpfx)) delete this.state.textFormat[k]; }

      /* The final page (currently at vi+1) is about to shift down to vi
         once the removed spread's slot is spliced out — move its state
         down with it, or it's mis-attributed to whatever ends up at vi. */
      this._shiftViewState(vi + 1, vi);

      this.VIEWS.splice(vi, 1);
      this._renumberViews();

      // Sync pricing: base book has 9 spreads; each extra spread = 1 sleeve = ₹65
      try {
        const pricing = JSON.parse(localStorage.getItem('oiCraftPricing:' + this._scope) || '{}');
        pricing.sleeves = Math.max(0, this.VIEWS.filter(v => v.kind === 'spread').length - 9);
        localStorage.setItem('oiCraftPricing:' + this._scope, JSON.stringify(pricing));
      } catch(e) {}
      this._renderPrice();
      this._renderFilmstrip();
      this._renderSpecs();
      this._goSpread(vi - 1); // land on the new last spread
      const pSec = document.getElementById('oi2-sec-pages');
      if (pSec && pSec.open) this._renderMobileFilmstrip();
      /* Persist the removal + shift — spreadBg only lives locally,
         everything else syncs to the server draft. */
      this._saveLocalStyle({ spreadBg: this.state.spreadBg });
      this._autoSaveDraft();
    }

    /* ── Render: filmstrip ── */
    _renderFilmstrip() {
      const { filmSpreads } = this.el;
      if (!filmSpreads) return;
      filmSpreads.innerHTML = '';
      this.VIEWS.forEach((v, i) => {
        const isWide = v.kind === 'spread';
        const def    = this.layoutDef(i);
        const btn = document.createElement('button');
        btn.dataset.filmSpread = i;
        btn.style.cssText = 'background:none;border:none;padding:6px 4px 0;cursor:pointer;display:flex;flex-direction:column;align-items:center;gap:7px;flex:none;opacity:0.55;transition:opacity .3s ease,transform .3s cubic-bezier(.16,1,.3,1);transform:none';
        const preview = document.createElement('div');
        preview.style.cssText = `position:relative;width:${isWide?'90px':'46px'};height:46px;background:${spreadBgColor(this.state.spreadBg[i])};padding:3px;border-radius:1px;box-shadow:0 2px 6px rgba(0,0,0,0.3);transition:box-shadow .3s ease;box-sizing:border-box;overflow:hidden`;
        const _cachedThumb = this._filmThumbCache.get(i);
        if (_cachedThumb) {
          const _ti = document.createElement('img');
          _ti.src = _cachedThumb;
          _ti.dataset.filmThumb = '1';
          _ti.style.cssText = 'position:absolute;inset:3px;width:calc(100% - 6px);height:calc(100% - 6px);object-fit:cover;border-radius:1px';
          preview.appendChild(_ti);
        } else {
          def.slots.forEach(sr => {
            const cell = document.createElement('div');
            cell.style.cssText = [
              'position:absolute',
              `left:${(sr.x*100).toFixed(2)}%`,
              `top:${(sr.y*100).toFixed(2)}%`,
              `width:${(sr.w*100).toFixed(2)}%`,
              `height:${(sr.h*100).toFixed(2)}%`,
              'background:rgba(28,24,20,0.16)',
              'border-radius:1px',
              'box-sizing:border-box',
            ].join(';');
            preview.appendChild(cell);
          });
        }
        const lbl = document.createElement('div');
        lbl.style.cssText = 'font-size:9px;letter-spacing:1.6px;color:rgba(243,238,228,0.4)';
        lbl.textContent = isWide ? `${v.pages[0]}–${v.pages[1]}` : String(v.pages[0]);
        btn.appendChild(preview); btn.appendChild(lbl);
        btn.addEventListener('click', () => this._goSpread(i));
        filmSpreads.appendChild(btn);
      });

      /* "+" add spread button */
      const spreadCount = this.VIEWS.filter(v => v.kind === 'spread').length;
      if (spreadCount < 37) {
        const addBtn = document.createElement('button');
        addBtn.title = 'Add spread';
        addBtn.style.cssText = 'background:none;border:1px dashed rgba(201,165,95,0.35);border-radius:3px;padding:0;width:46px;height:46px;cursor:pointer;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:5px;flex:none;margin-top:6px;opacity:0.6;transition:opacity .25s ease,border-color .25s ease;align-self:flex-start';
        addBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><line x1="7" y1="2" x2="7" y2="12" stroke="#C9A55F" stroke-width="1.5" stroke-linecap="round"/><line x1="2" y1="7" x2="12" y2="7" stroke="#C9A55F" stroke-width="1.5" stroke-linecap="round"/></svg><span style="font-size:8px;letter-spacing:1.4px;color:rgba(201,165,95,0.75);text-transform:uppercase;line-height:1">Add</span>';
        addBtn.addEventListener('mouseenter', () => { addBtn.style.opacity = '1'; addBtn.style.borderColor = 'rgba(201,165,95,0.65)'; });
        addBtn.addEventListener('mouseleave', () => { addBtn.style.opacity = '0.6'; addBtn.style.borderColor = 'rgba(201,165,95,0.35)'; });
        addBtn.addEventListener('click', () => this._addSpread());
        filmSpreads.appendChild(addBtn);
      }

      /* "−" remove spread button */
      if (spreadCount > 9) {
        const rmBtn = document.createElement('button');
        rmBtn.title = 'Remove last spread';
        rmBtn.style.cssText = 'background:none;border:1px dashed rgba(243,238,228,0.2);border-radius:3px;padding:0;width:46px;height:46px;cursor:pointer;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:5px;flex:none;margin-top:6px;opacity:0.55;transition:opacity .25s ease,border-color .25s ease;align-self:flex-start';
        rmBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><line x1="2" y1="7" x2="12" y2="7" stroke="rgba(243,238,228,0.75)" stroke-width="1.5" stroke-linecap="round"/></svg><span style="font-size:8px;letter-spacing:1.4px;color:rgba(243,238,228,0.5);text-transform:uppercase;line-height:1">Remove</span>';
        rmBtn.addEventListener('mouseenter', () => { rmBtn.style.opacity = '1'; rmBtn.style.borderColor = 'rgba(243,238,228,0.4)'; });
        rmBtn.addEventListener('mouseleave', () => { rmBtn.style.opacity = '0.55'; rmBtn.style.borderColor = 'rgba(243,238,228,0.2)'; });
        rmBtn.addEventListener('click', () => this._removeSpread());
        filmSpreads.appendChild(rmBtn);
      }

      this._updateFilmstripActive();
    }

    /* ── Filmstrip thumbnails ── */
    _scheduleFilmThumb(spreadIdx) {
      if (!this._filmThumbTimers) this._filmThumbTimers = new Map();
      const existing = this._filmThumbTimers.get(spreadIdx);
      if (existing) clearTimeout(existing);
      this._filmThumbTimers.set(spreadIdx, setTimeout(() => {
        this._filmThumbTimers.delete(spreadIdx);
        this._renderFilmThumb(spreadIdx).catch(() => {});
      }, 600));
    }

    async _renderFilmThumb(spreadIdx) {
      const { filmSpreads } = this.el;
      if (!filmSpreads) return;
      const v = this.VIEWS[spreadIdx];
      if (!v) return;
      const isWide = v.kind === 'spread';
      const def    = this.layoutDef(spreadIdx);

      const hasPhoto = def.slots.some((_, si) => {
        const k = `s${spreadIdx}:${si}`;
        return this.state.slotImages[k] || this.state.slotBlobs[k];
      });
      const hasText = def.textZones && def.textZones.some(tz => this.state.textContent[`${spreadIdx}:${tz.key}`]);
      if (!hasPhoto && !hasText) return;

      const W = isWide ? 180 : 90, H = 90;
      let blob;
      try {
        blob = await renderSpreadFromSlots(
          spreadIdx, def.slots, this.state.slotImages, isWide,
          W, H, 0.72, this.state.slotBlobs, this.state.slotTransforms,
          def.textZones, this.state.textContent, this.state.textFormat,
          this.state.spreadBg[spreadIdx] || INTERIOR_PAPER_COLOR, null, false, this.font().family
        );
      } catch(e) { return; } /* decode failure: skip thumbnail update */
      const url = URL.createObjectURL(blob);

      /* Revoke the previous cached URL to avoid memory leaks */
      const prev = this._filmThumbCache.get(spreadIdx);
      if (prev) URL.revokeObjectURL(prev);
      this._filmThumbCache.set(spreadIdx, url);
      /* Keep the phone Pages strip current too, if it is open. */
      const mobBtn = this.el.mobFilmstrip && this.el.mobFilmstrip.querySelector(`[data-mob-film-spread="${spreadIdx}"]`);
      if (mobBtn && mobBtn.firstElementChild) this._setMobFilmThumb(mobBtn.firstElementChild, url);

      /* Update DOM if the filmstrip button is still in the document */
      const btn = filmSpreads.querySelector(`[data-film-spread="${spreadIdx}"]`);
      if (!btn) return;
      const preview = btn.querySelector('div');
      if (!preview) return;

      let timg = preview.querySelector('[data-film-thumb]');
      if (!timg) {
        /* First time: hide the grey slot cells and insert a photo img */
        preview.querySelectorAll('div').forEach(c => { c.style.display = 'none'; });
        timg = document.createElement('img');
        timg.dataset.filmThumb = '1';
        timg.style.cssText = 'position:absolute;inset:3px;width:calc(100% - 6px);height:calc(100% - 6px);object-fit:cover;border-radius:1px';
        preview.appendChild(timg);
      }
      timg.src = url;
    }

    _updateFilmstripActive() {
      const { filmCover, filmSpreads } = this.el;
      const isCover = this.state.view === 'cover';
      const si = this.state.spreadIdx;

      if (filmCover) {
        const active = isCover;
        filmCover.style.opacity   = active ? '1' : '0.55';
        filmCover.style.transform = active ? 'translateY(-2px)' : 'none';
        const lbl = filmCover.querySelector('[data-filmstrip-cover-label]');
        if (lbl) lbl.style.color = active ? '#C9A55F' : 'rgba(243,238,228,0.4)';
      }
      if (filmSpreads) {
        filmSpreads.querySelectorAll('[data-film-spread]').forEach(btn => {
          const idx = parseInt(btn.dataset.filmSpread, 10);
          const active = !isCover && idx === si;
          btn.style.opacity   = active ? '1' : '0.55';
          btn.style.transform = active ? 'translateY(-2px)' : 'none';
          const preview = btn.querySelector('div');
          if (preview) preview.style.boxShadow = active
            ? '0 0 0 1px rgba(201,165,95,0.75),0 6px 14px rgba(0,0,0,0.35)'
            : '0 2px 6px rgba(0,0,0,0.3)';
          const lbl = btn.children[1];
          if (lbl) lbl.style.color = active ? '#C9A55F' : 'rgba(243,238,228,0.4)';
        });
      }
    }

    /* Invalidate cached thumb, update the preview div background, and re-render the canvas thumb */
    _refreshFilmThumb(spreadIdx) {
      const { filmSpreads } = this.el;
      if (!filmSpreads) return;
      /* Update background of the filmstrip preview div immediately */
      const btn = filmSpreads.querySelector(`[data-film-spread="${spreadIdx}"]`);
      if (btn) {
        const preview = btn.querySelector('div');
        if (preview) preview.style.background = spreadBgColor(this.state.spreadBg[spreadIdx]);
      }
      /* Discard stale cached canvas thumb and queue a fresh one */
      const prev = this._filmThumbCache.get(spreadIdx);
      if (prev) { URL.revokeObjectURL(prev); this._filmThumbCache.delete(spreadIdx); }
      this._scheduleFilmThumb(spreadIdx);
    }

    /* ── Render: well ── */
    /* The canonical "is this reference the same photo?" test.
       This question was previously hand-rolled in seven places with 2-, 3- and
       4-field variants — the two-field one could report a placed photo as unused
       once blob URLs were refreshed. Anything asking it should call this. */
    _resolveUpload(ref) {
      if (!ref) return null;
      const uploads = this.state.uploads || [];
      return uploads.find(u =>
        (u.assetKey && u.assetKey === ref) ||
        u.url === ref || u.originalUrl === ref ||
        u.previewUrl === ref || u.thumbUrl === ref
      ) || null;
    }

    /* Where each photo is used. Derived on read and deliberately NOT cached onto
       the upload objects: a stored count is a second source of truth, and every
       hard bug in this editor has come from two representations of one fact
       drifting apart. Returns Map<upload, { slots:[key], onCover:bool }>. */
    _photoUsage() {
      const usage = new Map();
      for (const up of (this.state.uploads || [])) usage.set(up, { slots: [], onCover: false });

      for (const [slotKey, ref] of Object.entries(this.state.slotImages || {})) {
        const up = this._resolveUpload(ref);
        if (up && usage.has(up)) usage.get(up).slots.push(slotKey);
      }

      const coverUp = this._resolveUpload(this.state.coverSourceAssetKey)
        || this._resolveUpload(this.state.coverImage);
      if (coverUp && usage.has(coverUp) && !this.state.coverRemoved) {
        usage.get(coverUp).onCover = true;
      }
      return usage;
    }

    /* True when this photo sits anywhere in the book — a spread slot or the cover. */
    _isPlaced(up, usage) {
      const u = (usage || this._photoUsage()).get(up);
      return !!u && (u.slots.length > 0 || u.onCover);
    }

    _applyPlacedState(tile, placed, isMob) {
      tile.classList.toggle('is-placed', placed);
      tile.title = placed ? 'Already placed in your book — you can place it again' : '';
      const sr = tile.querySelector('.oi2-well-used-sr');
      if (sr) sr.textContent = placed ? 'Placed in your book' : '';
      /* Desktop fading (and its hover restore) is CSS; the mobile rail is styled
         inline and has no hover, so it's set here. */
      if (isMob) {
        const img = tile.querySelector('img');
        if (img) img.style.opacity = placed ? '0.45' : '1';
      }
    }

    /* Repaints just the placed badges on tiles that already exist. _renderWell()
       rebuilds every thumbnail, which stutters on a 60-photo book mid-drag; this
       touches a class and a title per tile instead. */
    _refreshWellUsage() {
      const usage   = this._photoUsage();
      const uploads = this.state.uploads || [];
      [[this.el.uploadGrid, false], [this.el.mobUploadGrid, true]].forEach(([grid, isMob]) => {
        if (!grid) return;
        grid.querySelectorAll('[data-upload-idx]').forEach(tile => {
          const up = uploads[Number(tile.dataset.uploadIdx)];
          if (up) this._applyPlacedState(tile, this._isPlaced(up, usage), isMob);
        });
      });
    }

    _renderWell() {
      const { uploadGrid, uploadEmpty, uploadCount,
              mobUploadGrid, mobUploadEmpty, mobUploadCount } = this.el;
      const usage = this._photoUsage();
      const { uploads, selectedUpload, activeSlot } = this.state;
      const countStr = uploads.length === 0 ? '—' : String(uploads.length).padStart(2, '0');
      if (uploadCount) uploadCount.textContent = countStr;
      if (mobUploadCount) mobUploadCount.textContent = countStr;
      if (uploadEmpty) uploadEmpty.style.display = uploads.length ? 'none' : '';
      if (mobUploadEmpty) {
        mobUploadEmpty.style.display = uploads.length ? 'none' : '';
        mobUploadEmpty.textContent = activeSlot
          ? 'Add photos above to fill this slot.'
          : 'Add photos above — tap any photo to place it instantly.';
      }

      const makeThumb = (up, i, isMob) => {
        const div = document.createElement('div');
        const sel = !isMob && selectedUpload === i;
        if (isMob) {
          div.style.cssText = `position:relative;flex:none;width:72px;height:72px;overflow:hidden;border-radius:3px;cursor:pointer;transition:box-shadow .25s ease;animation:oi2-thumbIn .45s cubic-bezier(.16,1,.3,1) both;box-shadow:0 0 0 1px rgba(243,238,228,0.12)`;
        } else {
          div.className = 'oi2-well-thumb';
          div.style.cssText = `position:relative;aspect-ratio:1/1;overflow:hidden;border-radius:2px;cursor:grab;transition:box-shadow .25s ease;animation:oi2-thumbIn .45s cubic-bezier(.16,1,.3,1) both;box-shadow:${sel?'0 0 0 2px #C9A55F':'0 0 0 1px rgba(243,238,228,0.12)'}`;
        }
        const img = document.createElement('img');
        img.src = up.thumbUrl || up.url;
        img.draggable = false; /* prevent native image drag intercepting the div's custom dragstart */
        img.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block';
        div.appendChild(img);

        /* "Already in your book" state. Dimming (not a border) because gold rings
           already mean "selected" on these tiles, and because the customer's real
           question while building is "what haven't I placed yet?" — fading the used
           ones makes the remaining photos the bright ones.

           The tick is what stops dimming reading as "disabled": these photos can
           absolutely be placed again, so hover restores full opacity (desktop CSS).
           pointer-events:none throughout, matching .oi2-well-grip/.oi2-well-label —
           an overlay that eats the click would break drag-to-place. */
        const tick = document.createElement('span');
        tick.className = 'oi2-well-used';
        tick.setAttribute('aria-hidden', 'true');
        tick.innerHTML = isMob
          ? ''   /* 72px tile: a 12px tick is mush — the dot alone reads cleanly */
          : '<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5"><path d="M20 6 9 17l-5-5"/></svg>';
        div.appendChild(tick);
        /* Not decoration: opacity tells a screen reader nothing. */
        const sr = document.createElement('span');
        sr.className = 'oi2-well-used-sr';
        sr.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap';
        div.appendChild(sr);
        /* The badge markup always exists; only this class decides whether it shows,
           so the live refresh below is a class toggle rather than DOM surgery. */
        div.dataset.uploadIdx = String(i);
        this._applyPlacedState(div, this._isPlaced(up, usage), isMob);

        /* Drag affordance — lives on the photo (the thing you pick up), not the
           slot. Ambient hand icon at rest; hands off to an on-photo "Drag" label
           on hover so the two don't double up. */
        if (!isMob) {
          const grip = document.createElement('span');
          grip.className = 'oi2-well-grip';
          grip.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M8 11V6a1.5 1.5 0 0 1 3 0v4"/><path d="M11 10.5V5a1.5 1.5 0 0 1 3 0v5"/><path d="M14 10.5V6a1.5 1.5 0 0 1 3 0v6"/><path d="M17 12V9a1.5 1.5 0 0 1 3 0v6c0 2.8-2.2 5-5 5h-2c-1.7 0-2.7-.6-3.7-1.8L6 14.6c-.6-.7-.5-1.7.2-2.2.6-.4 1.4-.4 1.9.2L10 14.5"/></svg>';
          div.appendChild(grip);

          const label = document.createElement('span');
          label.className = 'oi2-well-label';
          label.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M8 11V6a1.5 1.5 0 0 1 3 0v4"/><path d="M11 10.5V5a1.5 1.5 0 0 1 3 0v5"/><path d="M14 10.5V6a1.5 1.5 0 0 1 3 0v6"/><path d="M17 12V9a1.5 1.5 0 0 1 3 0v6c0 2.8-2.2 5-5 5h-2c-1.7 0-2.7-.6-3.7-1.8L6 14.6c-.6-.7-.5-1.7.2-2.2.6-.4 1.4-.4 1.9.2L10 14.5"/></svg><span>Drag</span>';
          div.appendChild(label);
        }

        /* Delete button */
        const del = document.createElement('button');
        del.innerHTML = '&times;';
        del.title = 'Remove photo';
        del.style.cssText = 'position:absolute;top:3px;right:3px;width:18px;height:18px;border-radius:50%;background:rgba(0,0,0,0.65);color:#fff;border:none;cursor:pointer;font-size:14px;line-height:1;display:flex;align-items:center;justify-content:center;padding:0;' + (isMob ? 'opacity:1' : 'opacity:0;transition:opacity .15s');
        if (!isMob) {
          div.addEventListener('mouseenter', () => del.style.opacity = '1');
          div.addEventListener('mouseleave', () => del.style.opacity = '0');
        }
        del.addEventListener('click', e => {
          e.stopPropagation();
          /* Removing a photo does not just drop it from the well: it clears every
             slot the photo occupies, marks those views dirty, and the next render
             uploads a blank page over the customer's spread. There is no undo and
             the source stays in Drive, so the loss is invisible on the server —
             which is how a customer's pages 3 and 4 were silently emptied while
             the photos sat safely in their project folder.

             On mobile the button is opacity:1 on every thumbnail in a scrolling
             rail, so a stray tap is enough. Name the pages and ask first. */
          const _usedOn = (() => {
            const urls = new Set([up?.url, up?.previewUrl, up?.originalUrl].filter(Boolean));
            const seen = new Set();
            for (const k of Object.keys(this.state.slotImages)) {
              if (!urls.has(this.state.slotImages[k])) continue;
              const vi = parseInt(k.split(':')[0].slice(1), 10);
              if (!isNaN(vi)) seen.add(vi);
            }
            return [...seen].sort((a, b) => a - b);
          })();
          if (_usedOn.length) {
            const where = _usedOn.map(vi => this.viewLabel(vi)).join(', ');
            const many  = _usedOn.length > 1;
            if (!confirm('This photo is used on ' + where + '.\n\n'
                       + 'Removing it will leave ' + (many ? 'those pages' : 'that page')
                       + ' empty. This cannot be undone.')) return;
          } else if (isMob) {
            /* Unused photo, but the button is permanently visible here. */
            if (!confirm('Remove this photo from your book?')) return;
          }
          const [removed] = this.state.uploads.splice(i, 1);
          if (removed) {
            /* Clear slots using any URL variant from this upload (previewUrl = url for new entries;
               originalUrl may differ for restored photos still awaiting preview generation) */
            const matchUrls = new Set([removed.url, removed.previewUrl, removed.originalUrl].filter(Boolean));
            Object.keys(this.state.slotImages).forEach(k => {
              if (matchUrls.has(this.state.slotImages[k])) {
                delete this.state.slotImages[k];
                delete this.state.slotBlobs[k];
                const vi = parseInt(k.split(':')[0].slice(1), 10);
                if (!isNaN(vi)) this.state.dirtyViews.add(vi);
              }
            });
            /* Revoke all object URLs held by this entry (deduped to avoid double-revoke) */
            new Set([removed.url, removed.previewUrl, removed.thumbUrl, removed.originalUrl].filter(Boolean))
              .forEach(u => { try { URL.revokeObjectURL(u); } catch(e) {} });
            if (removed.idbId != null) {
              this.store.delete(removed.idbId).catch(() => {});
              const map = this._readSlotMap();
              Object.keys(map).forEach(k => { if (map[k] === removed.idbId) delete map[k]; });
              this._writeSlotMap(map);
            }
            /* Clear the cover if it referenced this photo's stable assetKey */
            if (removed.assetKey && this.state.coverSourceAssetKey === removed.assetKey) {
              this._removeCoverPhoto();
            }
            this._autoSaveDraft();
            this._saveLocalStyle({ uploadedCount: this.state.uploads.length });
          }
          if (this.state.selectedUpload !== null && this.state.selectedUpload >= this.state.uploads.length) {
            this.state.selectedUpload = this.state.uploads.length ? this.state.uploads.length - 1 : null;
          }
          if (this.state.view === 'spread') this._renderSpread();
          this._renderWell();
        });
        div.appendChild(del);

        div.addEventListener('click', () => {
          if (isMob && this.state.view === 'cover') {
            /* Cover view: place directly as cover photo */
            this._placeCover(up.url, { sourceAssetKey: up.assetKey });
            return;
          }
          if (isMob && this.state.activeSlot) {
            /* Slot-pick mode: place photo into the tapped slot, then reveal result */
            this._placeSlot(this.state.activeSlot, up.url);
            this.state.activeSlot = null;
            this._renderSpread();
            this._renderWell();
            this._closeMobSheet();
            this._kickBackgroundUploads();
          } else if (isMob) {
            /* No active slot: place in next empty slot and jump to that spread */
            const nextKey = this._nextEmptySlot();
            if (nextKey) {
              this._placeSlot(nextKey, up.url);
              const viewIdx = parseInt(nextKey.split(':')[0].slice(1), 10);
              this._goSpread(viewIdx);
              this._closeMobSheet();
              this._kickBackgroundUploads();
            } else {
              /* All slots full — select for manual replacement via slot-first flow */
              this._selectUpload(i);
            }
          } else {
            this._selectUpload(i);
          }
        });
        if (!isMob) {
          div.setAttribute('draggable', 'true');
          div.addEventListener('dragstart', e => e.dataTransfer.setData('text/plain', up.assetKey));
        } else {
          /* Touch drag/drop for mobile — finger moves >4px starts a drag, smaller = tap */
          div.addEventListener('touchstart', e => {
            if (e.target.closest('button')) return;
            const t0 = e.touches[0];
            const startX = t0.clientX, startY = t0.clientY;
            const rect = div.getBoundingClientRect();
            let clone = null, dragging = false, scrolling = false, currentSlotEl = null;

            const onMove = ev => {
              const t = ev.touches[0];
              const dx = t.clientX - startX, dy = t.clientY - startY;
              const dist = Math.hypot(dx, dy);
              /* Wait for enough movement to determine intent */
              if (!dragging && !scrolling && dist < 8) return;
              if (!dragging && !scrolling) {
                /* Horizontal dominant → user is scrolling the photo strip, let browser handle it */
                if (Math.abs(dx) > Math.abs(dy)) {
                  scrolling = true;
                  document.removeEventListener('touchmove', onMove);
                  document.removeEventListener('touchend', onEnd);
                  return;
                }
                /* Vertical/diagonal → drag intent, take over */
                dragging = true;
                clone = div.cloneNode(true);
                clone.style.cssText = `position:fixed;width:${rect.width}px;height:${rect.height}px;opacity:0.85;pointer-events:none;z-index:9999;border-radius:6px;overflow:hidden;box-shadow:0 6px 20px rgba(0,0,0,0.35)`;
                document.body.appendChild(clone);
              }
              if (!dragging) return;
              ev.preventDefault();
              clone.style.top  = (t.clientY - rect.height / 2) + 'px';
              clone.style.left = (t.clientX - rect.width  / 2) + 'px';
              /* Find slot under finger (hide clone so elementFromPoint sees through it) */
              clone.style.visibility = 'hidden';
              const under = document.elementFromPoint(t.clientX, t.clientY);
              clone.style.visibility = '';
              /* The cover is a drop target too. Only [data-slot] was looked for, so on
                 phones and tablets a photo dragged onto the cover was silently
                 dropped — desktop (HTML5 drag) always accepted it. */
              const slotEl = under?.closest('[data-slot]') ||
                (this.state.view === 'cover' ? under?.closest('[data-cover-slot]') : null);
              if (currentSlotEl && currentSlotEl !== slotEl) currentSlotEl.style.outline = '';
              currentSlotEl = slotEl || null;
              if (currentSlotEl) currentSlotEl.style.outline = '2.5px solid #C9A84C';
            };

            const onEnd = () => {
              document.removeEventListener('touchmove', onMove);
              document.removeEventListener('touchend', onEnd);
              if (clone) { document.body.removeChild(clone); clone = null; }
              if (currentSlotEl && currentSlotEl.hasAttribute('data-cover-slot')) {
                currentSlotEl.style.outline = '';
                /* Same placement as tapping a photo and then the cover. */
                const sameCover = (up.assetKey && up.assetKey === this.state.coverSourceAssetKey) ||
                  [up.url, up.originalUrl, up.previewUrl].filter(Boolean).includes(this.state.coverImage);
                if (!sameCover) {
                  this._placeCover(up.url, { sourceAssetKey: up.assetKey });
                  this.state.selectedUpload = null;
                  this._renderWell();
                }
              } else if (currentSlotEl) {
                currentSlotEl.style.outline = '';
                const key = currentSlotEl.dataset.slot;
                this._placeSlot(key, up.originalUrl || up.url);
                this.state.activeSlot = null;
                this._renderSpread();
                this._renderWell();
                this._kickBackgroundUploads();
              }
            };

            document.addEventListener('touchmove', onMove, { passive: false });
            document.addEventListener('touchend', onEnd, { passive: true });
          }, { passive: true });
        }
        return div;
      };

      if (uploadGrid) {
        uploadGrid.innerHTML = '';
        uploads.forEach((up, i) => uploadGrid.appendChild(makeThumb(up, i, false)));
      }
      if (mobUploadGrid) {
        mobUploadGrid.innerHTML = '';
        /* Slot-pick mode hint row */
        if (activeSlot && uploads.length) {
          const hint = document.createElement('div');
          hint.style.cssText = 'flex:none;align-self:center;padding:0 4px 0 2px;font-size:9.5px;letter-spacing:1.5px;text-transform:uppercase;color:#C9A55F;white-space:nowrap';
          hint.textContent = '↑ Pick';
          mobUploadGrid.appendChild(hint);
        }
        uploads.forEach((up, i) => mobUploadGrid.appendChild(makeThumb(up, i, true)));
      }
      /* ── Photo rail (phone ≤600px, persistent strip above the book) ── */
      const { mobRailPhotos } = this.el;
      if (mobRailPhotos) {
        mobRailPhotos.innerHTML = '';
        if (activeSlot && uploads.length) {
          const hint = document.createElement('div');
          hint.style.cssText = 'flex:none;align-self:center;font-size:9px;letter-spacing:1.5px;text-transform:uppercase;color:#C9A55F;white-space:nowrap;padding-right:2px';
          hint.textContent = 'Pick →';
          mobRailPhotos.appendChild(hint);
        }
        uploads.forEach((up, i) => mobRailPhotos.appendChild(makeThumb(up, i, true)));
      }
    }

    /* ── Render: price ── */
    _renderPrice() {
      let pricing = {};
      try { pricing = JSON.parse(localStorage.getItem('oiCraftPricing:' + this._scope) || '{}'); } catch(e) {}
      const base  = (pricing.base || 0) + (pricing.adder || 0) + (pricing.sleeves || 0) * 130;
      const total = base > 0 ? base + this._unbrandedFee() : 0;
      const { price } = this.el;
      if (price) price.textContent = total > 0 ? '₹' + Number(total).toLocaleString('en-IN') : '—';
    }

    /* ── Render: specs ── */
    _renderSpecs() {
      const { specs } = this.el;
      if (!specs) return;
      let pricing = {};
      try { pricing = JSON.parse(localStorage.getItem('oiCraftPricing:' + this._scope) || '{}'); } catch(e) {}
      const spreadCount = this.VIEWS.filter(v => v.kind === 'spread').length;
      const pages = 20 + (pricing.sleeves || 0) * 2;
      const { trimW, trimH } = this._formatDims();
      const rows = [
        ['Trim',    `${trimW} × ${trimH} in`],
        ['Binding', 'Hardcover'],
        ['Pages',   `${pages} (${spreadCount} spread${spreadCount !== 1 ? 's' : ''})`],
        ['Paper',   pricing.material || '—'],
        ['Finish',  pricing.finish   || '—'],
      ];
      specs.innerHTML = rows.map(([k,v]) =>
        `<div style="display:flex;align-items:baseline;justify-content:space-between;gap:12px"><div style="font-size:11.5px;color:rgba(243,238,228,0.36)">${k}</div><div style="font-size:11.5px;color:rgba(243,238,228,0.66);text-align:right">${v}</div></div>`
      ).join('');
    }

    /* ── Render all ── */
    _renderAll() {
      this._renderCover();
      this._renderFilmstripCover();
      this._renderRight();
      if (this.state.view === 'spread') this._renderSpread();
    }

    /* ── Load photos from IDB ── */
    async _loadPhotos() {
      try {
        const [photos, cover] = await Promise.all([this.store.getAll(this._scope), this.store.getCover(this._scope)]);
        if (cover) {
          const url = URL.createObjectURL(cover.blob);
          /* Restoring the saved cover: keep its saved crop (state.coverTransform
             came from oiCraft). Resetting here lost a cover crop on every reload
             of a draft not yet on the server. */
          this._placeCover(url, { resetCrop: false });
        } else if (this.state.coverPhoto) {
          /* Use destination image carried over from book-selector */
          this._placeCover(this.state.coverPhoto);
        }
        if (photos.length) {
          /* p.assetKey / p.sourceWidth / p.sourceHeight present for photos added after Issue 11/12;
             fall back to stable per-record IDB id for older entries. */
          this.state.uploads = photos.map(p => {
            const originalUrl = URL.createObjectURL(p.blob);
            return {
              file: p.blob, name: p.filename || '', idbId: p.id,
              assetKey: p.assetKey || `photo-${p.id}`,
              url: originalUrl, originalUrl, previewUrl: null, thumbUrl: null,
              sourceWidth:       p.sourceWidth        || null,
              sourceHeight:      p.sourceHeight       || null,
              /* Restore persisted content fingerprint (SHA-256 of original source file)
                 and original byte count so _acquireUploadEntry can match re-dragged photos
                 without re-hashing the IDB blob (which may differ from the source, e.g. HEIC). */
              fingerprint:       p.contentFingerprint || null,
              originalFileSize:  p.originalFileSize   || null,
            };
          });
          /* Nothing is selected after a restore: a selected photo is placed by the next
             tap on the cover or an empty slot, so pre-selecting photo #1 made a tap on
             the cover (to crop it) swap the cover for that photo. */
          this.state.selectedUpload = null;
          this._renderWell();
          /* Deduplicate before restoring slots so the slot-map already reflects retained idbIds. */
          this._restoring = true;
          await this._deduplicateUploads();
          const savedMap = this._readSlotMap();
          if (Object.keys(savedMap).length) {
            /* Detect cross-session format change: if the book size changed since the last
               background pre-render, Drive has stale spreads at the old dimensions. */
            let _pricingLoad = {};
            try { _pricingLoad = JSON.parse(localStorage.getItem('oiCraftPricing:' + this._scope) || '{}'); } catch(e) {}
            const _sizeChanged = !!(_pricingLoad.lastPrerenderedFormat && _pricingLoad.lastPrerenderedFormat !== _pricingLoad.format);
            /* Restore without marking dirty — Drive already has these files */
            for (const [key, idbId] of Object.entries(savedMap)) {
              const up = this.state.uploads.find(u => u.idbId === idbId);
              if (up) this._placeSlot(key, up.url, { markDirty: false });
            }
            /* Format changed — pre-renders in Drive are the wrong size; force all restored views to re-upload */
            if (_sizeChanged) {
              for (const key of Object.keys(savedMap)) {
                const vi = parseInt(key.split(':')[0].slice(1), 10);
                if (!isNaN(vi)) this.state.dirtyViews.add(vi);
              }
            }
          }
          /* (If projectId but no local slot map: _loadFromServer will reconstruct it from server slotAssignments) */
          /* Re-render spread view in case it was already showing before IDB resolved */
          if (this.state.view === 'spread') this._renderSpread();
          /* Generate compressed preview+thumb for each restored photo in the background */
          this._generatePreviewsFor(this.state.uploads);
          this._initProject();
          this._restoring = false;
          /* For signed-in projects, always refresh server metadata and reconcile slot map
             even when photos are already cached locally. */
          if (this.state.projectId) await this._loadFromServer();
          this._kickBackgroundUploads();
        } else {
          /* Try server restore */
          if (this.state.projectId) await this._loadFromServer();
        }
      } catch(e) {}
    }

    /* Sequentially generate thumbnail (240 px) and preview (1600 px) for each upload that
       lacks them. Called after IDB/server restore.
       Slots continue to display originalUrl (full quality) — only the upload-rail thumbnails
       switch to the compressed versions. */
    async _generatePreviewsFor(uploads) {
      for (const up of uploads) {
        if (up.previewUrl) continue;
        /* Each step decodes a full-size original. While Preview book is being drawn
           that competed with it for the CPU (desktop: ~32 s instead of ~5 s), so wait
           until the preview is built, then carry on. */
        while (this._previewBuilding) await new Promise(r => setTimeout(r, 250));
        if (up.previewUrl) continue;
        try {
          if (typeof createImageBitmap !== 'undefined') {
            const bm = await createImageBitmap(up.file);
            if (!up.sourceWidth) { up.sourceWidth = bm.width; up.sourceHeight = bm.height; }
            const [previewUrl, thumbUrl] = await Promise.all([
              this._scaleBitmapToUrl(bm, 1600),
              this._scaleBitmapToUrl(bm, 240),
            ]);
            bm.close();
            up.previewUrl = previewUrl; up.thumbUrl = thumbUrl;
            /* Do NOT update up.url, slotImages, or img.src — slots already show originalUrl
               at full quality and we don't want a visible downgrade to 1600 px. */
          } else {
            /* Fallback: capture dims via Image, use originalUrl for display */
            if (!up.sourceWidth) {
              const dims = await new Promise(resolve => {
                const img = new Image();
                img.onload  = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
                img.onerror = () => resolve(null);
                img.src = up.originalUrl;
              });
              if (dims) { up.sourceWidth = dims.w; up.sourceHeight = dims.h; }
            }
            up.previewUrl = up.originalUrl; up.thumbUrl = up.originalUrl;
          }
          this._renderWell();
        } catch(e) { /* keep originalUrl as display fallback */ }
      }
    }

    _autoFillSlots() {
      /* Use the blob URLs already created in _loadPhotos (state.uploads) so we don't
         create duplicate URLs for the same blobs, and _placeSlot can find the upload
         entry to populate slotBlobs correctly. */
      let pi = 0;
      this.VIEWS.forEach((v, i) => {
        const def = this.layoutDef(i);
        def.slots.forEach((_, si) => {
          const key = `s${i}:${si}`;
          if (!this.state.slotImages[key] && this.state.uploads[pi]) {
            this._placeSlot(key, this.state.uploads[pi].url, { markDirty: false });
            pi++;
          }
        });
      });
    }

    /* Remove exact-content duplicate upload entries using SHA-256 fingerprints.
       Keeps the entry with the lower idbId (first inserted). Remaps slot-map, slotImages,
       slotBlobs, and cover references to the retained entry. Revokes blob URLs of removed
       entries and deletes their IDB records.

       Identity is always established by SHA-256, never by filename, size, or lastModified.
       When an entry carries a stored fingerprint (contentFingerprint from IDB), that value
       is trusted directly — the IDB blob may be a converted JPEG whose hash would differ
       from the original source file (e.g. HEIC), so we never re-hash IDB blobs unless no
       stored fingerprint exists. Entries whose fingerprint cannot be determined are skipped;
       we never delete IDB records without exact content confirmation. */
    async _deduplicateUploads() {
      if (this.state.uploads.length < 2) return;
      const seen = new Map();   /* fingerprint → retained entry */
      const dups  = [];         /* { dup, keep } pairs */

      for (const up of [...this.state.uploads]) {
        if (!up.file) continue;
        try {
          /* Prefer the stored fingerprint (SHA-256 of the original source, e.g. HEIC bytes)
             over re-hashing the IDB blob, which may be a converted JPEG with a different hash. */
          let fp = up.fingerprint || null;
          if (!fp) {
            fp = await this._fingerprintFile(up.file);
            if (!fp) continue; /* hash unavailable — never delete on partial evidence */
            up.fingerprint = fp;
          }
          const prior = seen.get(fp);
          if (prior) {
            /* Retain the older record (lower idbId). */
            const [keep, dup] = (prior.idbId != null && up.idbId != null && up.idbId < prior.idbId)
              ? [up, prior]
              : [prior, up];
            if (keep !== prior) seen.set(fp, keep);
            dups.push({ dup, keep });
          } else {
            seen.set(fp, up);
          }
        } catch {}
      }
      if (!dups.length) return;

      const slotMap = this._readSlotMap();
      let slotMapChanged = false;
      for (const { dup, keep } of dups) {
        /* Remap IDB slot-map entries pointing to the removed idbId */
        if (dup.idbId != null && keep.idbId != null) {
          for (const k of Object.keys(slotMap)) {
            if (slotMap[k] === dup.idbId) { slotMap[k] = keep.idbId; slotMapChanged = true; }
          }
        }
        /* Remap in-memory slot images and blobs */
        const dupUrls = new Set([dup.url, dup.originalUrl, dup.previewUrl].filter(Boolean));
        for (const k of Object.keys(this.state.slotImages)) {
          if (dupUrls.has(this.state.slotImages[k])) {
            this.state.slotImages[k] = keep.originalUrl || keep.url;
            if (keep.file) this.state.slotBlobs[k] = keep.file;
          }
        }
        /* Remap cover — update assetKey, persist it, and refresh the cover DOM */
        if (this.state.coverImage && dupUrls.has(this.state.coverImage)) {
          this.state.coverImage          = keep.originalUrl || keep.url;
          this.state.coverSourceAssetKey = keep.assetKey;
          this._saveLocalStyle({ coverSourceAssetKey: keep.assetKey });
          const { coverPhoto } = this.el;
          if (coverPhoto) {
            const prevHq = coverPhoto.dataset.oiHqSrc;
            if (prevHq) { try { URL.revokeObjectURL(prevHq); } catch {} }
            const hqSrc = keep.file ? URL.createObjectURL(keep.file) : (keep.originalUrl || keep.url);
            if (keep.file) coverPhoto.dataset.oiHqSrc = hqSrc;
            else delete coverPhoto.dataset.oiHqSrc;
            coverPhoto.src = hqSrc;
            coverPhoto.style.display = 'block';
          }
        }
        /* Revoke duplicate blob URLs to free memory */
        this._revokeUploadUrls(dup);
        /* Delete the duplicate IDB record */
        if (dup.idbId != null) this.store.delete(dup.idbId).catch(() => {});
        /* Remove from in-memory uploads */
        const idx = this.state.uploads.indexOf(dup);
        if (idx !== -1) this.state.uploads.splice(idx, 1);
      }
      if (slotMapChanged) this._writeSlotMap(slotMap);
      this._renderWell();
      if (this.state.view === 'spread') this._renderSpread();
    }

    /* Returns the key of the first unfilled slot across all views, or null. */
    _nextEmptySlot() {
      for (let i = 0; i < this.VIEWS.length; i++) {
        const def = this.layoutDef(i);
        for (let si = 0; si < def.slots.length; si++) {
          const key = `s${i}:${si}`;
          if (!this.state.slotImages[key]) return key;
        }
      }
      return null;
    }

    async _loadFromServer() {
      if (!this.state.projectId || this._loadingFromServer) return;
      this._loadingFromServer = true;
      this._restoring = false;
      const _restoreScope = this._scope, _restoreEpoch = this._epoch;
      try {
        /* Fetch assets list + editor state (parallel) */
        const [assetsRes, projRes] = await Promise.all([
          fetch(`${WORKER_URL}/api/book-projects/${this.state.projectId}/assets`, { credentials: 'include' }),
          fetch(`${WORKER_URL}/api/book-projects/${this.state.projectId}`,         { credentials: 'include' }),
        ]);
        if (!assetsRes.ok || !projRes.ok) {
          if (assetsRes.status === 401 || projRes.status === 401) document.dispatchEvent(new CustomEvent('oi:session-expired'));
          return;
        }
        if (this._scope !== _restoreScope || this._epoch !== _restoreEpoch) return;
        const { assets }  = await assetsRes.json();
        const proj        = projRes.ok ? await projRes.json() : {};
        const editorState = proj.editorState || {};
        /* Cache spread asset keys that have Drive files — used to detect cleared spreads */
        const _svrSpreadKeys = new Set((assets || []).filter(a => a.asset_key?.startsWith('spread:') && a.upload_status === 'done').map(a => a.asset_key));
        if (_svrSpreadKeys.size) try { localStorage.setItem('oi_spread_keys:' + this._scope, JSON.stringify([..._svrSpreadKeys])); } catch(_e) {}

        /* Phase 1: Restore coverTransform before any _placeCover call */
        if (editorState.coverTransform) {
          const ct = editorState.coverTransform;
          this.state.coverTransform = {
            scale: Math.max(0.5, Math.min(10, ct.scale || 1)),
            nx:    Math.max(-1, Math.min(1, ct.nx   || 0)),
            ny:    Math.max(-1, Math.min(1, ct.ny   || 0)),
          };
        }

        /* Phase 2: Restore style / layout fields.
           Apply explicit empty strings (use 'in' checks) so a cleared server field resets local state. */
        if ('coverLayout' in editorState) this.state.coverLayout = editorState.coverLayout || null;
        if (editorState.showBackLogo !== undefined) {
          this.state.showBackLogo = editorState.showBackLogo;
          this._syncBackLogoUi();
          this._renderPrice();
        }
        if (editorState.showCoverText !== undefined) {
          this.state.showCoverText = editorState.showCoverText !== false;
          const engraveToggle = this.root.querySelector('[data-engrave-toggle]');
          const mobEngraveToggle = this.root.querySelector('[data-mob-engrave-toggle]');
          if (engraveToggle) engraveToggle.checked = this.state.showCoverText;
          if (mobEngraveToggle) mobEngraveToggle.checked = this.state.showCoverText;
          this._applyEngraveToggle(this.state.showCoverText);
        }
        if (editorState.showSpineText !== undefined) {
          this.state.showSpineText = editorState.showSpineText !== false;
        }
        if (editorState.spineDir !== undefined) this.state.spineDir = _spineDir(editorState.spineDir);
        this._syncSpineToggles();
        if (editorState.clothId && this.CLOTHS.find(c => c.id === editorState.clothId)) this.state.clothId = editorState.clothId;
        if (this._knownFoil(editorState.foilId))                                      this.state.foilId  = this._knownFoil(editorState.foilId);
        if (editorState.fontId  && this.FONTS.find(f => f.id === editorState.fontId))   this.state.fontId  = editorState.fontId;
        /* Restore per-spread layouts and captions */
        if (editorState.layouts     && typeof editorState.layouts     === 'object') this.state.layouts     = editorState.layouts;
        if (editorState.spreadModes && typeof editorState.spreadModes === 'object') this.state.spreadModes = editorState.spreadModes;
        if (editorState.captions    && typeof editorState.captions    === 'object') this.state.captions    = editorState.captions;
        if (editorState.textContent && typeof editorState.textContent === 'object') this.state.textContent = editorState.textContent;
        if (editorState.textFormat  && typeof editorState.textFormat  === 'object') this.state.textFormat  = editorState.textFormat;
        if (editorState.spreadBg    && typeof editorState.spreadBg    === 'object') this.state.spreadBg    = editorState.spreadBg;
        /* Restore text fields — server is authoritative for signed-in projects */
        if ('title'     in editorState) this.state.title     = String(editorState.title    ?? '').toUpperCase();
        if ('subtitle'  in editorState) this.state.subtitle  = String(editorState.subtitle ?? '').toUpperCase();
        if ('spineText' in editorState) this.state.spineText = String(editorState.spineText ?? '').toUpperCase();
        /* A draft save that never reached the server leaves the server copy without
           the customer's latest wording, and photos come back regardless (IDB and
           Drive), so the book reopens with pictures but no text. _autoSaveDraft has
           always written a local copy for exactly this case; nothing ever read it. */
        this._mergeLocalBackup(editorState);
        this._syncTextInputs();
        /* Restore pricing fields into their scoped key so format/material/finish/sleeves survive */
        const _restoredFormat  = 'format'  in editorState ? editorState.format                                  : null;
        let   _restoredSleeves = 'sleeves' in editorState ? Math.max(0, parseInt(editorState.sleeves || 0, 10)) : null;
        if (['format', 'material', 'finish', 'sleeves'].some(k => k in editorState)) {
          try {
            const _pr = JSON.parse(localStorage.getItem('oiCraftPricing:' + this._scope) || '{}');
            /* Only apply server-restored pricing when no local pricing exists (cross-device / fresh
               session). When the user just came from the craft page, local pricing is authoritative
               and must not be overwritten by a stale server snapshot from a previous session. */
            const _hasLocalPricing = !!(_pr.format || _pr.base);
            /* When local pricing wins, use its sleeves for VIEWS reconciliation too — otherwise the
               reconciliation block below would use the stale server count and remove spreads the
               user just added on the craft page (price shows correct but spread count resets). */
            if (_hasLocalPricing && _restoredSleeves !== null) {
              _restoredSleeves = Math.max(0, parseInt(_pr.sleeves || 0, 10));
            }
            /* Size, paper and finish are only ever chosen in the book selector (which
               opens the editor with ?booksize=) or filled in by the format picker right
               before a save. So unless this load came from the selector, a valid size on
               the server is the book's real one, and a different local copy is stale —
               e.g. the copies a selector URL wrote into other books when switching books
               kept that URL. Pages (sleeves) keep the local-first rule above. */
            const _fromSelector  = new URLSearchParams(location.search).has('booksize');
            const _serverHasSize = !!_normalizePricingFields(editorState.format, editorState.material).base;
            if (!_hasLocalPricing || (_serverHasSize && !_fromSelector)) {
              if (editorState.format   !== undefined) _pr.format   = editorState.format;
              if (editorState.material !== undefined) _pr.material = editorState.material;
              if (editorState.finish   !== undefined) _pr.finish   = editorState.finish;
              if (!_hasLocalPricing && editorState.sleeves !== undefined) _pr.sleeves = editorState.sleeves;
              if (_hasLocalPricing) _pr.variantId = editorState.variantId ?? null;
              else if (editorState.variantId != null) _pr.variantId = editorState.variantId;
              if (editorState.extraVariantId != null) _pr.extraVariantId = editorState.extraVariantId;
            }
            /* Always recompute base+adder from the current (local-priority) format+material
               so all aliases resolve correctly and the invariant base>0 ↔ valid format holds. */
            const _norm = _normalizePricingFields(_pr.format, _pr.material);
            _pr.format = _norm.canonFormat;
            _pr.base   = _norm.base;
            _pr.adder  = _norm.adder;
            /* Validate restored variant against canonical format+paper tier+finish.
               Clear it if stale so _handleCart() re-resolves from the variant map. */
            if (_pr.variantId) {
              const _vts = window.OI_VARIANTS || [];
              const _vt  = _vts.find(v => v.id === _pr.variantId);
              if (_vt) {
                const _vn = _normalizePricingFields(_vt.opt1, _vt.opt2);
                if (_vn.canonFormat !== _norm.canonFormat || _vn.adder !== _norm.adder || _vt.opt3 !== _pr.finish) {
                  _pr.variantId = null;
                }
              }
              /* Not clearing when variant is absent from OI_VARIANTS — it may be from a
                 different product context (selector vs. preview). Cart API rejects truly
                 invalid IDs at checkout; an overly-eager clear here causes false errors. */
            }
            localStorage.setItem('oiCraftPricing:' + this._scope, JSON.stringify(_pr));
          } catch(e) {}

          /* Reconcile live VIEWS and geometry so restored format/sleeves take effect immediately.
             Runs before _renderAll() (Phase 3 end) so cover and spread draw at correct dimensions.
             Does not mark any view dirty or trigger uploads. */
          if (_restoredSleeves !== null) {
            const MAX_SPREADS    = 37;
            const targetSpreads  = Math.min(9 + _restoredSleeves, MAX_SPREADS);
            const currentSpreads = this.VIEWS.filter(v => v.kind === 'spread').length;
            const _delta         = targetSpreads - currentSpreads;

            if (_delta > 0) {
              /* Add extra spreads before the last single page */
              for (let _i = 0; _i < _delta; _i++) {
                this.VIEWS.splice(this.VIEWS.length - 1, 0, { kind: 'spread', pages: [] });
              }
            } else if (_delta < 0) {
              /* Remove excess trailing spreads and purge their now-stale state */
              const _targetLen     = 1 + targetSpreads + 1;
              const _firstRemove   = _targetLen - 1;
              const _removeCount   = -_delta;
              for (let vi = _firstRemove; vi < _firstRemove + _removeCount; vi++) {
                delete this.state.layouts[vi];
                delete this.state.captions[vi];
                this.state.dirtyViews.delete(vi);
                const _pfx = 's' + vi + ':';
                for (const k of Object.keys(this.state.slotImages)) {
                  if (k.startsWith(_pfx)) {
                    /* Only revoke if no upload entry still references this URL —
                       blob URLs shared between slotImages and state.uploads must not be freed. */
                    const url = this.state.slotImages[k];
                    const stillUsed = this.state.uploads.some(u => u.url === url || u.originalUrl === url);
                    if (!stillUsed) try { URL.revokeObjectURL(url); } catch(_e) {}
                    delete this.state.slotImages[k];
                  }
                }
                for (const k of Object.keys(this.state.slotBlobs))     { if (k.startsWith(_pfx)) delete this.state.slotBlobs[k]; }
                for (const k of Object.keys(this.state.slotTransforms)) { if (k.startsWith(_pfx)) delete this.state.slotTransforms[k]; }
              }
              this.VIEWS.splice(_firstRemove, _removeCount);
              /* If the user was viewing a now-removed spread, fall back to cover */
              if (this.state.view === 'spread' && this.state.spreadIdx >= this.VIEWS.length) {
                this.state.view = 'cover';
              }
            }
            if (_delta !== 0) this._renumberViews();
          }

          /* Apply format geometry and refresh all format-dependent UI without marking dirty */
          this._applyFormat();
          this._renderFilmstrip();
          const _pSecPages = document.getElementById('oi2-sec-pages');
          if (_pSecPages && _pSecPages.open) this._renderMobileFilmstrip();
          this._renderPrice();
          this._renderSpecs();
        }

        /* Phase 3: Cover source restore.
           The 'cover' asset key is the flattened print wrap and must NEVER be passed
           to _placeCover. Source identity is tracked separately via coverSourceAssetKey. */
        const csk = editorState.coverSourceAssetKey;  /* undefined = old project; null = explicit empty; 'photo-*'/'coverPhoto' = known */
        const coverNeedsPhotoDownload = !editorState.coverRemoved && csk && csk.startsWith('photo-');

        if (!editorState.coverRemoved && !coverNeedsPhotoDownload) {
          if (csk === 'coverPhoto') {
            /* Destination/default image. Older drafts sometimes retained the sentinel but
               lost its URL when resumed from My Books. Recover the canonical destination
               artwork; if no canonical image exists, checkout validation below stops the
               customer rather than silently exporting a blank front cover. */
            let durableCoverPhoto = editorState.coverPhoto || this.state.coverPhoto || null;
            if (!durableCoverPhoto && proj.destination) {
              const destinations = window.OI_DESTINATIONS || [];
              const match = destinations.find(d => d.city && d.city.toLowerCase() === String(proj.destination).toLowerCase());
              durableCoverPhoto = match?.image || null;
            }
            if (durableCoverPhoto) {
              this.state.coverPhoto = durableCoverPhoto;
              this._saveLocalStyle({ coverPhoto: durableCoverPhoto });
              this._placeCover(durableCoverPhoto, { resetCrop: false, sourceAssetKey: 'coverPhoto' });
            }
          } else if (!('coverSourceAssetKey' in editorState) && editorState.coverPhoto) {
            /* Backward compat: old project saved before Issue 11 — use coverPhoto if present */
            this._placeCover(editorState.coverPhoto, { resetCrop: false, sourceAssetKey: 'coverPhoto' });
          }
          /* csk === null (new project, cover cleared/never set): cloth-only; placeholder stays */
        }
        /* editorState.coverRemoved === true → cloth-only; placeholder stays */

        this._renderAll();

        /* Phase 4: Photo download (slots + deferred cover-photo-N) */
        const { slotAssignments = {}, slotTransforms = {}, photoMeta = [] } = editorState;

        /* If photos already exist in IDB, reconstruct slot map from server slotAssignments
           by matching assetKey — no re-downloading needed. */
        if (this.state.uploads.length) {
          const byAssetKey = Object.fromEntries(this.state.uploads.map(u => [u.assetKey, u]));
          if (Object.keys(slotAssignments).length) {
            /* Prefer locally-saved transforms over server — local is always the most recent
               user crop (_saveTransforms fires synchronously on every crop gesture).
               Only fall back to server transforms when localStorage has none (new device / cleared). */
            if (Object.keys(slotTransforms).length && !Object.keys(this.state.slotTransforms).length) {
              this.state.slotTransforms = slotTransforms;
              try { localStorage.setItem('oiSlotTransforms:' + this._scope, JSON.stringify(slotTransforms)); } catch(e) {}
            }
            const newSlotMap = {};
            for (const [slotKey, assetKey] of Object.entries(slotAssignments)) {
              const up = byAssetKey[assetKey];
              if (up?.idbId != null) {
                newSlotMap[slotKey] = up.idbId;
                this._placeSlot(slotKey, up.url, { markDirty: false });
              }
            }
            if (Object.keys(newSlotMap).length) this._writeSlotMap(newSlotMap);
          }
          if (coverNeedsPhotoDownload) {
            const coverUp = byAssetKey[csk];
            if (coverUp) this._placeCover(coverUp.url, { resetCrop: false, sourceAssetKey: csk });
          }
          if (this.state.view === 'spread') this._renderSpread();
          /* Re-upload any IDB photos that never reached Drive (e.g. page closed mid-upload).
             Also update confirmed count so save status shows correctly. */
          const serverPhotoKeys = new Set(
            (assets || []).filter(a => a.asset_type === 'photo' && a.upload_status === 'done').map(a => a.asset_key)
          );
          let confirmed = 0;
          for (const up of this.state.uploads) {
            if (serverPhotoKeys.has(up.assetKey)) { confirmed++; }
            else if (up.file) { this._savePhotoBackground(up.file, up.name, up.assetKey); }
          }
          this._photosConfirmedOnServer = confirmed;
          return;
        }

        const needsDownload = photoMeta.length > 0 && Object.keys(slotAssignments).length > 0;
        if (!needsDownload && !coverNeedsPhotoDownload) return;

        this._restoring = true;
        this._renderSaveStatus();

        /* Index server assets by assetKey for fast lookup */
        const byKey = Object.fromEntries((assets || []).map(a => [a.asset_key, a]));

        /* Download each source photo, store in IDB, build upload entries */
        const newUploads = [];
        for (const meta of photoMeta) {
          const asset = byKey[meta.assetKey];
          if (!asset?.drive_file_id) continue;
          try {
            const blobRes = await fetch(`${WORKER_URL}/api/photos/file/${asset.drive_file_id}`, { credentials: 'include' });
            if (!blobRes.ok) {
              if (blobRes.status === 401) { document.dispatchEvent(new CustomEvent('oi:session-expired')); return; }
              continue;
            }
            const blob  = await blobRes.blob();
            const fname = meta.filename || asset.file_name || 'photo.jpg';
            const file  = new File([blob], fname, { type: blob.type || 'image/jpeg' });
            if (this._scope !== _restoreScope || this._epoch !== _restoreEpoch) return;
            const idbId = await this.store.add(file, fname, meta.assetKey, meta.sourceWidth || null, meta.sourceHeight || null, this._scope, meta.contentFingerprint || null, meta.originalFileSize || null);
            const originalUrl = URL.createObjectURL(blob);
            newUploads.push({ file, name: fname, idbId, assetKey: meta.assetKey,
              url: originalUrl, originalUrl, previewUrl: null, thumbUrl: null,
              sourceWidth: meta.sourceWidth || null, sourceHeight: meta.sourceHeight || null,
              fingerprint: meta.contentFingerprint || null, originalFileSize: meta.originalFileSize || null });
          } catch(e) { /* skip unrecoverable photo */ }
        }
        if (!newUploads.length && !coverNeedsPhotoDownload) return;

        if (newUploads.length) {
          this.state.uploads = newUploads;
          /* Nothing is selected after a restore: a selected photo is placed by the next
             tap on the cover or an empty slot, so pre-selecting photo #1 made a tap on
             the cover (to crop it) swap the cover for that photo. */
          this.state.selectedUpload = null;
          this._renderWell();

          /* Restore slot transforms — prefer local if already set (same-device resume),
             else apply server version (new device / cleared storage) */
          if (!Object.keys(this.state.slotTransforms).length) {
            this.state.slotTransforms = slotTransforms;
            try { localStorage.setItem('oiSlotTransforms:' + this._scope, JSON.stringify(slotTransforms)); } catch(e) {}
          }

          /* Restore slot assignments */
          const newSlotMap = {};
          for (const [slotKey, assetKey] of Object.entries(slotAssignments)) {
            const up = newUploads.find(u => u.assetKey === assetKey);
            if (up?.idbId != null) {
              newSlotMap[slotKey] = up.idbId;
              this._placeSlot(slotKey, up.url, { markDirty: false });
            }
          }
          this._writeSlotMap(newSlotMap);
        }

        /* Phase 5: Deferred cover restore for photo-N type (blob available after download) */
        if (coverNeedsPhotoDownload) {
          const coverUp = newUploads.find(u => u.assetKey === csk);
          if (coverUp) this._placeCover(coverUp.url, { resetCrop: false, sourceAssetKey: csk });
        }

        if (this.state.view === 'spread') this._renderSpread();
        if (newUploads.length) this._generatePreviewsFor(newUploads);
      } catch(e) { /* silent — degraded to blank canvas */ }
      finally {
        this._loadingFromServer = false;
        this._restoring = false;
        this._renderSaveStatus();
        if (this.state.dirtyViews.size && this.state.projectId) this._kickBackgroundUploads();
        /* Unconditional, unlike the spread kick above. Reopening a finished book
           leaves no spread dirty, so gating the cover on dirtyViews would mean a
           customer who changes nothing and goes straight to Add to Cart still
           renders the cover at checkout — the exact case this was built for. The
           fingerprint makes an unchanged cover cost one hash. */
        this._kickCoverUpload();
      }
    }

    /* Build a { slotKey → 'photo-N' } map from the current slot arrangement.
       Derives from in-memory state.slotImages so it stays correct even when the
       localStorage slot map is partially evicted (common on mobile) or when a
       photo was placed before its async IDB write back-filled the map. */
    _buildSlotAssignments() {
      const result = {};
      for (const [slotKey, slotUrl] of Object.entries(this.state.slotImages)) {
        const up = this.state.uploads.find(u =>
          u.url === slotUrl || u.originalUrl === slotUrl || u.previewUrl === slotUrl
        );
        if (up?.assetKey) result[slotKey] = up.assetKey;
      }
      return result;
    }

    /* ── Auto-save draft to server ── */
    _autoSaveDraft() {
      /* Keep the customer's latest text locally even if the network save fails. */
      try { localStorage.setItem('oiTextBackup:' + this._scope, JSON.stringify({
        textContent: this.state.textContent, textFormat: this.state.textFormat,
        title: this.state.title, subtitle: this.state.subtitle, spineText: this.state.spineText,
        layouts: this.state.layouts, spreadBg: this.state.spreadBg,
        showCoverText: this.state.showCoverText, showSpineText: this.state.showSpineText,
        savedAt: new Date().toISOString(),
      })); } catch (_) {}
      if (this._preflighting) return;
      if (!this.state.projectId) return;
      if (!(window.__oiCustomer?.id || window.__oiSession?.authenticated)) return;
      this._saveDirtyViews();
      if (this._autoSaveTimer) clearTimeout(this._autoSaveTimer);
      this._autoSaveTimer = setTimeout(() => this._flushDraft(), 3000);
      /* Cover-only edits — cloth, foil, typeface, title, spine, crop — reach here
         but mark no spread dirty, so they would otherwise never trigger a cover
         render. Debounced past typing; the fingerprint makes an unchanged cover
         a single hash. */
      if (this._coverKickTimer) clearTimeout(this._coverKickTimer);
      this._coverKickTimer = setTimeout(() => this._kickCoverUpload(), 4000);
    }

    /* Recover work the server copy is missing from the local backup that
       _autoSaveDraft writes on every edit.

       Text stays strictly additive: only keys that came back empty are
       supplied, so a stale local copy can never overwrite newer wording that
       did reach the server. Layout and background cannot use that rule - a
       layout key always holds a value, so "fill only the empty ones" would
       recover nothing - and are decided by _backupLayoutPlan on timestamps.

       Safe to call more than once, and safe when no backup exists. */
    _mergeLocalBackup(editorState) {
      let backup;
      try { backup = JSON.parse(localStorage.getItem('oiTextBackup:' + this._scope) || 'null'); } catch (_) { return; }
      if (!backup || typeof backup !== 'object') return;
      const filled = [];

      for (const field of ['title', 'subtitle', 'spineText']) {
        const have = String(this.state[field] ?? '').trim();
        const had  = String(backup[field]     ?? '').trim();
        if (!have && had) { this.state[field] = had.toUpperCase(); filled.push(field); }
      }
      /* textContent/textFormat are keyed per zone, so merge key by key: one zone
         reaching the server must not discard another that did not. */
      for (const map of ['textContent', 'textFormat']) {
        const src = backup[map];
        if (!src || typeof src !== 'object') continue;
        this.state[map] = this.state[map] || {};
        for (const [k, v] of Object.entries(src)) {
          const cur = this.state[map][k];
          const curEmpty = cur == null || (typeof cur === 'string' && !cur.trim());
          const srcEmpty = v   == null || (typeof v   === 'string' && !v.trim());
          if (curEmpty && !srcEmpty) { this.state[map][k] = v; filled.push(`${map}.${k}`); }
        }
      }
      /* Layout and per-spread background, which this function used to ignore
         even though _autoSaveDraft had already stored them. */
      const plan = _backupLayoutPlan(editorState, backup);
      for (const map of ['layouts', 'spreadBg']) {
        const add = plan[map];
        if (!add || !Object.keys(add).length) continue;
        this.state[map] = Object.assign(this.state[map] || {}, add);
      }
      if (plan.keys.length) {
        filled.push(...plan.keys);
        /* oiCraft is what init() hydrates state.layouts from, so the recovery
           has to land there too or the next plain reload discards it again. */
        this._saveLocalStyle({ layouts: this.state.layouts, spreadBg: this.state.spreadBg });
      }

      if (filled.length) {
        console.warn('[OI] recovered unsaved work from local backup (' + plan.mode + '):', filled.join(', '));
        /* A recovered layout changes what every spread renders, so repaint
           rather than leave the canvas showing the server's version. */
        if (plan.keys.length) this._renderAll();
        /* Push it back up so the recovery is not lost on the next device. */
        this._autoSaveDraft();
      }
    }

    async _flushDraft({ required = false } = {}) {
      const projectId = this.state.projectId;
      if (!projectId) {
        if (required) throw new Error('Your book is not ready to save. Please try again.');
        return;
      }
      let pricing = {};
      try { pricing = JSON.parse(localStorage.getItem('oiCraftPricing:' + this._scope) || '{}'); } catch(e) {}
      const draft = {
        title:           this.state.title,
        subtitle:        this.state.subtitle,
        spineText:       this.state.spineText,
        clothId:         this.state.clothId,
        foilId:          this.state.foilId,
        fontId:          this.state.fontId,
        layouts:         this.state.layouts,
        spreadModes:     this.state.spreadModes,
        coverLayout:     this.state.coverLayout,
        showBackLogo:         this.state.showBackLogo,
        showCoverText:        this.state.showCoverText,
        showSpineText:        this.state.showSpineText,
        spineDir:             this.state.spineDir,
        coverTransform:       this.state.coverTransform,
        coverSourceAssetKey:  this.state.coverSourceAssetKey,
        coverRemoved:         this.state.coverRemoved,
        captions:             this.state.captions,
        coverPhoto:           this.state.coverPhoto,
        format:          pricing.format,
        material:        pricing.material,
        finish:          pricing.finish,
        sleeves:         pricing.sleeves || 0,
        variantId:       pricing.variantId      || null,
        extraVariantId:  pricing.extraVariantId  || null,
        /* Slot state — enables cross-device photo + arrangement restore */
        slotAssignments: this._buildSlotAssignments(),
        slotTransforms:  this.state.slotTransforms,
        spreadBg:        this.state.spreadBg,
        textContent:     this.state.textContent,
        textFormat:      this.state.textFormat,
        photoMeta:       this.state.uploads.map(u => ({ assetKey: u.assetKey, filename: u.name || '', sourceWidth: u.sourceWidth || null, sourceHeight: u.sourceHeight || null, contentFingerprint: u.fingerprint || null, originalFileSize: u.originalFileSize || null })),
        photoCount:      this.state.uploads.length,
        spreadCount:     this.VIEWS.length,
        /* Stamped so a later restore can tell whether the local backup holds
           edits this copy never received. handleProjectDraft stores the body
           verbatim apart from the Drive folder keys, so it comes back on open. */
        savedAt:         new Date().toISOString(),
      };
      /* Capture now, then serialize requests: an older slow save must never
         overwrite newer wording. Checkout awaits the acknowledged final save. */
      const body = JSON.stringify(draft);
      const save = (this._draftSaveChain || Promise.resolve()).catch(() => {}).then(async () => {
        const ac = new AbortController();
        const timer = setTimeout(() => ac.abort(), 15000);
        try {
        const dr = await fetch(`${WORKER_URL}/api/book-projects/${projectId}/draft`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body, signal: ac.signal,
        });
        if (dr.status === 401) document.dispatchEvent(new CustomEvent('oi:session-expired'));
        const result = await dr.json().catch(() => ({}));
        /* 409 is not a connection fault and retrying cannot clear it: the
           revision is ordered or finalizing, so ensureWritableRevision refuses
           the write. Telling the customer to check their connection would be a
           lie, and they would carry on editing a book that cannot take edits. */
        if (dr.status === 409) {
          this._draftSaveLocked = 'This book has already been ordered. Its print files are locked to protect your order.';
          throw new Error(this._draftSaveLocked);
        }
        if (!dr.ok || result.ok !== true) throw new Error('Your latest text could not be saved. Please check your connection and try again.');
        this._draftSaveError  = null;
        this._draftSaveLocked = null;
        this._renderSaveStatus();
        return result;
        } finally { clearTimeout(timer); }
      });
      this._draftSaveChain = save;
      try { return await save; }
      catch (e) {
        this._draftSaveError = e;
        /* Surface it immediately: the whole failure mode here is the customer
           believing their text is safe when it is not. */
        this._renderSaveStatus();
        /* A paid order is deliberately immutable. Preserve that explanation
           through the checkout path instead of incorrectly inviting the
           customer to retry a save which the server must reject. */
        if (required) {
          if (this._draftSaveLocked) throw new Error(this._draftSaveLocked);
          throw new Error('Your latest text could not be saved. Your text is still in the editor; please try again.');
        }
      }
    }

    /* ── My Books modal ── */
    async _showMyBooks() {
      const overlay = document.getElementById('oi-mybooks-overlay');
      if (!overlay) return;
      overlay.style.display = 'flex';

      const list = document.getElementById('oi-mybooks-list');
      if (list) list.innerHTML = '<div style="color:rgba(243,238,228,0.4);font-size:13px;padding:24px 0;text-align:center">Loading…</div>';

      let projects = [];
      try {
        const res = await fetch(`${WORKER_URL}/api/book-projects`, { credentials: 'include' });
        if (res.status === 401) { document.dispatchEvent(new CustomEvent('oi:session-expired')); return; }
        if (res.ok) ({ projects } = await res.json());
      } catch(e) {}

      if (!list) return;
      if (!projects.length) {
        list.innerHTML = '<div style="color:rgba(243,238,228,0.4);font-size:13px;padding:24px 0;text-align:center">No saved books yet.</div>';
        return;
      }

      list.innerHTML = '';
      projects.forEach(p => {
        const card = document.createElement('div');
        card.style.cssText = 'display:flex;align-items:center;gap:16px;padding:14px 0;border-bottom:1px solid rgba(243,238,228,0.08);cursor:pointer';

        /* Thumbnail */
        /* A 64px box holding a small book in the book's real shape — square for
           8x8, wide for 12x8, tall for 8x10 / 8x12 — with a spine edge and a
           shadow, so the list reads as books rather than as photo squares. */
        const thumb = document.createElement('div');
        thumb.style.cssText = 'width:64px;height:64px;flex-shrink:0;display:flex;align-items:center;justify-content:center';
        if (p.coverFileId) {
          const book = document.createElement('div');
          book.style.cssText = 'position:relative;width:52px;height:52px;border-radius:1px 3px 3px 1px;overflow:hidden;background:rgba(243,238,228,0.08);box-shadow:0 4px 10px rgba(0,0,0,0.45),0 1px 2px rgba(0,0,0,0.4);transition:width .25s ease,height .25s ease';
          const img = document.createElement('img');
          img.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block';
          img.alt = '';
          const spine = document.createElement('div');
          spine.style.cssText = 'position:absolute;left:0;top:0;bottom:0;width:5px;background:linear-gradient(90deg,rgba(0,0,0,0.38),rgba(0,0,0,0.12) 60%,rgba(255,255,255,0.10));pointer-events:none';
          /* Shimmer until the cover lands (a print wrap is 2-4 MB). */
          if (!document.getElementById('oi-thumb-sweep-css')) {
            const s = document.createElement('style');
            s.id = 'oi-thumb-sweep-css';
            s.textContent = '@keyframes oi-thumb-sweep{from{background-position:120% 0}to{background-position:-120% 0}}' +
              '.oi-thumb-sweep{position:absolute;inset:0;z-index:2;pointer-events:none;background:linear-gradient(100deg,transparent 25%,rgba(243,238,228,0.30) 50%,transparent 75%);background-size:250% 100%;animation:oi-thumb-sweep 1.6s ease-in-out infinite}' +
              '@media (prefers-reduced-motion:reduce){.oi-thumb-sweep{animation:none;background:rgba(243,238,228,0.08)}}';
            document.head.appendChild(s);
          }
          const sweep = document.createElement('div');
          sweep.className = 'oi-thumb-sweep';
          book.appendChild(sweep);
          const showPlaceholder = () => { thumb.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(243,238,228,0.3)" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>'; };
          img.onerror = showPlaceholder;
          img.onload = () => {
            sweep.remove();
            /* Fit the book into the box by its real proportions. */
            const ar = img.naturalWidth / img.naturalHeight, MAX = 60;
            if (!(ar > 0)) return;
            book.style.width  = Math.round(ar >= 1 ? MAX : MAX * ar) + 'px';
            book.style.height = Math.round(ar >= 1 ? MAX / ar : MAX) + 'px';
          };
          /* The cover asset is the whole print wrap (back | spine | front); shown as
             is, the thumbnail cropped out its middle — mostly spine. Show the front
             panel, the same cut My Books uses. */
          fetch(`${WORKER_URL}/api/photos/file/${p.coverFileId}`, { credentials: 'include' })
            .then(r => r.ok ? r.blob() : Promise.reject())
            .then(blob => _frontCoverThumbUrl(blob, 160))
            .then(url => { img.src = url; })
            .catch(showPlaceholder);
          book.appendChild(img);
          book.appendChild(spine);
          thumb.appendChild(book);
        } else {
          thumb.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(243,238,228,0.3)" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>';
        }
        card.appendChild(thumb);

        /* Info */
        const info = document.createElement('div');
        info.style.cssText = 'flex:1;min-width:0';
        const title = p.title || p.destination || 'Untitled Book';
        const dt = p.lastActivity ? new Date(p.lastActivity + 'Z').toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }) : '';
        /* isOrdered comes from the paid order, not status, which later edits overwrite. */
        const statusBadge = p.isOrdered ? (p.orderNumber ? 'Order ' + p.orderNumber : 'Ordered')
          : p.status === 'added_to_cart' ? 'In cart' : p.status === 'ready' ? 'Ready' : 'Draft';
        info.innerHTML = `<div style="font-size:13px;font-weight:500;color:#F3EEE4;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${title}</div>
          <div style="font-size:10.5px;letter-spacing:1.2px;color:rgba(243,238,228,0.38);margin-top:3px">${dt}${dt && statusBadge ? ' · ' : ''}${statusBadge}</div>`;
        card.appendChild(info);

        /* Buttons */
        const btns = document.createElement('div');
        btns.style.cssText = 'display:flex;flex-direction:column;gap:6px;flex-shrink:0';

        /* Only drafts can be continued or deleted. An ordered book shows a label
           instead: its print files live in the project's Drive folder, which Delete
           removes, and the server's status check alone is not enough (status is
           overwritten by later edits — order #1011 reads "ready"). isOrdered comes
           from the paid order and never changes. The worker refuses too. */
        if (p.isOrdered) {
          card.style.cursor = 'default';
          const tag = document.createElement('div');
          tag.textContent = 'Ordered';
          tag.style.cssText = 'display:inline-flex;align-items:center;gap:7px;padding:8px 14px;border-radius:2px;border:1px solid rgba(201,165,95,0.35);background:rgba(201,165,95,0.08);color:#C9A55F;font-size:10px;letter-spacing:2px;text-transform:uppercase;white-space:nowrap';
          tag.insertAdjacentHTML('afterbegin', '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>');
          btns.appendChild(tag);
          /* What they ordered — the order preview shows the final print files saved
             at checkout (cover and every page), not the editor's current state. */
          const viewBtn = document.createElement('a');
          viewBtn.href = '/pages/order-preview?p=' + encodeURIComponent(p.projectId);
          viewBtn.textContent = 'View order';
          viewBtn.style.cssText = 'display:block;text-align:center;text-decoration:none;padding:6px 14px;border-radius:2px;border:1px solid rgba(243,238,228,0.18);background:transparent;color:rgba(243,238,228,0.75);font-size:9px;letter-spacing:1.8px;text-transform:uppercase;white-space:nowrap;transition:color .2s,border-color .2s';
          viewBtn.addEventListener('mouseenter', () => { viewBtn.style.color = '#F3EEE4'; viewBtn.style.borderColor = 'rgba(201,165,95,0.5)'; });
          viewBtn.addEventListener('mouseleave', () => { viewBtn.style.color = 'rgba(243,238,228,0.75)'; viewBtn.style.borderColor = 'rgba(243,238,228,0.18)'; });
          btns.appendChild(viewBtn);
          card.appendChild(btns);
          list.appendChild(card);
          return;
        }

        const continueBtn = document.createElement('button');
        continueBtn.textContent = 'Continue';
        continueBtn.style.cssText = 'padding:8px 16px;border-radius:2px;border:1px solid rgba(201,165,95,0.5);background:transparent;color:#C9A55F;font-size:10px;letter-spacing:2px;text-transform:uppercase;cursor:pointer;white-space:nowrap';
        continueBtn.addEventListener('click', () => this._openProject(p));
        btns.appendChild(continueBtn);

        const delBtn = document.createElement('button');
        delBtn.textContent = 'Delete';
        delBtn.style.cssText = 'padding:6px 16px;border-radius:2px;border:1px solid rgba(243,238,228,0.12);background:transparent;color:rgba(243,238,228,0.3);font-size:9px;letter-spacing:1.8px;text-transform:uppercase;cursor:pointer;white-space:nowrap;transition:color .2s,border-color .2s';
        delBtn.addEventListener('mouseenter', () => { delBtn.style.color = '#E57373'; delBtn.style.borderColor = 'rgba(229,115,115,0.4)'; });
        delBtn.addEventListener('mouseleave', () => { delBtn.style.color = 'rgba(243,238,228,0.3)'; delBtn.style.borderColor = 'rgba(243,238,228,0.12)'; });
        delBtn.addEventListener('click', async () => {
          if (!confirm('Delete this book? This cannot be undone.')) return;
          delBtn.textContent = '…'; delBtn.disabled = true;
          try {
            const r = await fetch(`${WORKER_URL}/api/book-projects/${p.projectId}`, {
              method: 'DELETE', credentials: 'include',
            });
            if (r.ok) {
              card.remove();
              /* Wipe local state for the deleted project so returning to the editor
                 doesn't auto-recreate it and re-upload the photos. */
              const deletedScope = 'project:' + p.projectId;
              ['oiCraft', 'oiCraftPricing', 'oiSlotMap', 'oiSlotTransforms'].forEach(k => {
                try { localStorage.removeItem(k + ':' + deletedScope); } catch(e) {}
              });
              this.store.clearByScope(deletedScope).catch(() => {});
              if (localStorage.getItem('oiActiveBook') === deletedScope) {
                try { localStorage.removeItem('oiActiveBook'); } catch(e) {}
                this._reloadClean();
              }
            }
            else { delBtn.textContent = 'Error'; setTimeout(() => { delBtn.textContent = 'Delete'; delBtn.disabled = false; }, 2000); }
          } catch(e) { delBtn.textContent = 'Error'; setTimeout(() => { delBtn.textContent = 'Delete'; delBtn.disabled = false; }, 2000); }
        });
        btns.appendChild(delBtn);

        card.appendChild(btns);
        list.appendChild(card);
      });
    }

    _openProject(p) {
      this._epoch++;
      const scope = 'project:' + p.projectId;
      /* Only write minimum identity when the target scope has never been cached locally.
         If oiCraft:<scope> already exists (e.g. returning to a previously-opened project)
         we leave it intact so the user's saved cloth/foil/font/layouts/etc. survive the
         project switch.  The slot map and all other scoped keys are also left intact. */
      if (!localStorage.getItem('oiCraft:' + scope)) {
        try {
          localStorage.setItem('oiCraft:' + scope, JSON.stringify({
            projectId:          p.projectId,
            bookRefId:          p.bookRefId          || '',
            destination:        p.destination        || '',
            projectDestination: p.destination        || '',
            title:              p.title    != null   ? p.title    : '',
            subtitle:           p.subtitle != null   ? p.subtitle : '',
          }));
        } catch(e) {}
      }
      try { localStorage.setItem('oiActiveBook', scope); } catch(e) {}
      this._reloadClean();
    }

    /* Reload the editor without the URL it was opened with. A link from the book
       selector carries that book's booksize/paper/destination, and the editor
       applies those to whatever book loads; keeping them on a book switch gave
       every book the selector's size, or bounced back to the selector's book.
       preview_theme_id is kept so a theme preview stays on that theme. */
    _reloadClean() {
      const keep = new URLSearchParams();
      const theme = new URLSearchParams(location.search).get('preview_theme_id');
      if (theme) keep.set('preview_theme_id', theme);
      const qs = keep.toString();
      window.location.href = location.pathname + (qs ? '?' + qs : '');
    }

    _renderSaveStatus() {
      /* .oi2-hdr-save is display:none below 900px, so the header line below
         reaches desktop only. Render the banner first and unconditionally. */
      this._renderAlert();
      const el = this.el.saveStatus;
      if (!el) return;
      const isSignedIn = !!(window.__oiCustomer?.id || window.__oiSession?.authenticated);
      const hasPhotos  = this.state.uploads.length > 0;
      const pulse = `<div style="width:5px;height:5px;border-radius:50%;background:#C9A55F;flex-shrink:0;animation:oi2-pulse 1.2s ease-in-out infinite"></div>`;
      const dot   = (c, glow) => `<div style="width:5px;height:5px;border-radius:50%;background:${c};flex-shrink:0${glow?';box-shadow:0 0 8px rgba(126,154,110,0.8)':''}"></div>`;

      if (this._restoring) {
        el.innerHTML = pulse + 'Restoring from cloud…';
      } else if (this._draftSaveLocked) {
        /* A locked revision also sets _draftSaveError, which would otherwise
           fall through to "check connection" below — and the header would
           contradict the banner it sits directly above. */
        el.innerHTML = dot('#D4844A', false) + 'Ordered — changes not saved';
      } else if (this._uploadStuck && this.state.dirtyViews.size) {
        el.innerHTML = `<div style="width:5px;height:5px;border-radius:50%;background:#D4844A;flex-shrink:0"></div>Couldn't save — check connection&nbsp;<button data-retry-upload style="all:unset;cursor:pointer;text-decoration:underline;color:#D4844A;font-size:inherit">Retry</button>`;
        const rb = el.querySelector('[data-retry-upload]');
        if (rb) rb.addEventListener('click', () => { this._uploadStuck = false; this._renderSaveStatus(); this._kickBackgroundUploads(); }, { once: true });
      } else if (this._photoUploadStuck && this._stuckPhotoUploads.length) {
        /* Individual source photos failed 5x in a row — surfaced, not silent. This never
           blocks checkout: _handleCart() doesn't read this flag or _photosConfirmedOnServer. */
        const n = this._stuckPhotoUploads.length;
        el.innerHTML = `<div style="width:5px;height:5px;border-radius:50%;background:#D4844A;flex-shrink:0"></div>Couldn't save ${n} photo${n > 1 ? 's' : ''} — check connection&nbsp;<button data-retry-photo-upload style="all:unset;cursor:pointer;text-decoration:underline;color:#D4844A;font-size:inherit">Retry</button>`;
        const rb = el.querySelector('[data-retry-photo-upload]');
        if (rb) rb.addEventListener('click', () => this._retryStuckPhotoUploads(), { once: true });
      } else if (this._initPending) {
        el.innerHTML = dot('#C9A55F', false) + 'Setting up…';
      } else if (this._kicking) {
        el.innerHTML = pulse + 'Saving to cloud…';
      } else if (!isSignedIn && hasPhotos) {
        el.innerHTML = dot('#C9A55F', false) + 'Browser only — sign in to save';
      } else if (hasPhotos && isSignedIn && this._photosConfirmedOnServer < this.state.uploads.length) {
        el.innerHTML = dot('#C9A55F', false) + 'Saving to cloud…';
      } else {
        /* A failed draft save must never read as saved. dirtyViews tracks spread
           renders, not wording, so a text-only edit whose save failed fell
           through to "Saved to cloud" — the customer was told their text was
           safe, then found it gone on reload. */
        if (this._draftSaveError && isSignedIn) {
          el.innerHTML = dot('#E07070', false) + 'Text not saved — check connection';
          return;
        }
        /* Cloud status is determined by the cart reference saved after a successful checkout upload */
        let _ref = null;
        try { _ref = JSON.parse(localStorage.getItem('oi_cart_ref:' + this._scope) || 'null'); } catch(e) {}
        const _cleanInCloud = !!(_ref && this.state.dirtyViews.size === 0);
        if (_cleanInCloud) {
          el.innerHTML = dot('#7E9A6E', true) + 'Saved to cloud';
        } else if (this.state.dirtyViews.size > 0 && hasPhotos && isSignedIn) {
          el.innerHTML = dot('#C9A55F', false) + 'Changes pending — add to cart to save';
        } else {
          el.innerHTML = dot('#7E9A6E', true) + (isSignedIn ? 'Saved to cloud' : 'Saved');
        }
      }
    }

    /* A book that is not being saved is worth interrupting someone for, and the
       header status line cannot do it: 11.5px at 42% opacity, and hidden
       outright below 900px, so for every phone and tablet customer this banner
       is the only report they get. Three states qualify; everything else stays
       in the quiet header line. */
    _renderAlert() {
      const el = this.el.alert;
      if (!el) return;
      const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const isSignedIn = !!(window.__oiCustomer?.id || window.__oiSession?.authenticated);
      const hasWork    = this.state.uploads.length > 0
                      || Object.keys(this.state.layouts || {}).length > 0;
      let tone = null, text = '', action = '';

      if (this._draftSaveLocked) {
        tone = 'locked';
        text = this._draftSaveLocked + ' Any order you already placed is unaffected — to change this book, start a new one from My Books.';
      } else if (this._draftSaveError && isSignedIn) {
        tone = 'error';
        text = 'Your latest changes have not been saved. They are still here in the editor — keep this tab open.';
        action = 'retry';
      } else if (!isSignedIn && hasWork) {
        tone = 'warn';
        text = 'This book lives only in this browser. Sign in to save it to your account.';
        action = 'signin';
      }

      if (!tone) { el.style.display = 'none'; el.innerHTML = ''; return; }

      const skin = {
        locked: { bg: '#3A2A1E', bd: '#D4844A' },
        error:  { bg: '#3A1F1F', bd: '#E07070' },
        warn:   { bg: '#312818', bd: '#C9A55F' },
      }[tone];
      el.style.display      = 'flex';
      el.style.background   = skin.bg;
      el.style.borderBottom = '1px solid ' + skin.bd;
      el.innerHTML =
        '<span style="color:' + skin.bd + ';flex-shrink:0;font-size:14px;line-height:1.2">⚠</span>' +
        '<span style="flex:1">' + esc(text) + '</span>' +
        (action === 'retry'
          ? '<button data-alert-retry style="all:unset;cursor:pointer;text-decoration:underline;white-space:nowrap;color:' + skin.bd + '">Try again</button>'
          : '') +
        (action === 'signin'
          ? '<button data-alert-signin style="all:unset;cursor:pointer;text-decoration:underline;white-space:nowrap;color:' + skin.bd + '">Sign in</button>'
          : '');

      const retry = el.querySelector('[data-alert-retry]');
      if (retry) retry.addEventListener('click', () => {
        this._draftSaveError = null;
        this._renderSaveStatus();
        this._flushDraft();
      }, { once: true });
      /* Reuses the gate the 401 path already shows rather than a second login UI. */
      const signin = el.querySelector('[data-alert-signin]');
      if (signin) signin.addEventListener('click', () => {
        document.dispatchEvent(new CustomEvent('oi:session-expired'));
      }, { once: true });
    }

    /* ── Background project init ── */
    _initProject() {
      if (this._initInProgress) return this._initProjectPromise;
      this._initInProgress = true;
      return (this._initProjectPromise = (async () => {
      try {
      const session    = window.__oiSession !== null ? window.__oiSession
                       : await (window.__oiSessionPromise || Promise.resolve({ authenticated: false }));
      const isSignedIn = !!(window.__oiCustomer?.id || session?.authenticated);
      if (!isSignedIn || this.state.projectId) return;
      try {
        let saved = {};
        try { saved = JSON.parse(localStorage.getItem('oiCraft:' + this._scope) || '{}'); } catch(e) {}

        /* Verify cached projectId against server — destination is not a reliable identity key.
           If the project is ordered/expired/missing, clear it and create a fresh one. */
        if (saved.projectId) {
          try {
            const vr = await fetch(`${WORKER_URL}/api/book-projects/${saved.projectId}`, { credentials: 'include' });
            if (vr.status === 401) { document.dispatchEvent(new CustomEvent('oi:session-expired')); return; }
            if (vr.ok) {
              const proj = await vr.json();
              const dead = ['ordered', 'expired', 'failed'];
              if (proj.projectId && !dead.includes(proj.status)) {
                this.state.projectId = proj.projectId;
                /* Ensure scope matches the confirmed projectId */
                const confirmedScope = 'project:' + proj.projectId;
                if (this._scope !== confirmedScope) {
                  const old = this._scope;
                  this._scope = confirmedScope;
                  try { localStorage.setItem('oiActiveBook', confirmedScope); } catch(e) {}
                  this.store.adoptScope(old, confirmedScope).catch(() => {});
                  /* Carry pricing (format/paper/finish/variantId) to the new scope so
                     the cart can still resolve variantId after a project-ID reassignment */
                  try {
                    const _oldP = localStorage.getItem('oiCraftPricing:' + old);
                    if (_oldP && !localStorage.getItem('oiCraftPricing:' + confirmedScope)) {
                      localStorage.setItem('oiCraftPricing:' + confirmedScope, _oldP);
                    }
                  } catch(_e) {}
                }
                this._renderSaveStatus();
                this._kickBackgroundUploads();
                this._drainPendingPhotoUploads();
                if (!this.state.uploads.length && !this._pendingPhotoUploads.length) {
                  this._loadFromServer();
                }
                return;
              }
            }
          } catch(e) {}
          /* Invalid or expired — clear so a fresh project is created below */
          delete saved.projectId; delete saved.bookRefId;
          try { localStorage.setItem('oiCraft:' + this._scope, JSON.stringify(saved)); } catch(e) {}
        }

        /* Show "Setting up…" while the init call runs.
           For native Shopify users, wait for the sync that sets the oi_auth cookie first. */
        if (window.__oiSyncPromise) await window.__oiSyncPromise;
        this._initPending = true;
        this._renderSaveStatus();
        const dest     = saved.destination || 'Archive';
        const destSlug = dest.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        const res = await fetch(`${WORKER_URL}/api/photos/init`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ destination: dest, destinationSlug: destSlug, destinationId: destSlug }),
        });
        this._initPending = false;
        if (!res.ok) {
          if (res.status === 401) document.dispatchEvent(new CustomEvent('oi:session-expired'));
          this._renderSaveStatus(); return;
        }
        const { projectId, bookRefId } = await res.json();
        this.state.projectId = projectId;

        /* Adopt draft scope → project scope, migrating all scoped keys */
        const projectScope = 'project:' + projectId;
        if (this._scope !== projectScope) {
          const oldScope = this._scope;
          this._scope = projectScope;
          try { localStorage.setItem('oiActiveBook', projectScope); } catch(e) {}
          this.store.adoptScope(oldScope, projectScope).catch(() => {});
          try {
            for (const key of ['oiCraft', 'oiSlotMap', 'oiSlotTransforms', 'oiCraftPricing', 'oiDirtyViews']) {
              const v = localStorage.getItem(key + ':' + oldScope);
              if (v) { localStorage.setItem(key + ':' + projectScope, v); localStorage.removeItem(key + ':' + oldScope); }
            }
          } catch(e) {}
          try { saved = JSON.parse(localStorage.getItem('oiCraft:' + this._scope) || '{}'); } catch(e) {}
        }

        saved.projectId = projectId; saved.bookRefId = bookRefId;
        saved.projectDestination = dest;
        localStorage.setItem('oiCraft:' + this._scope, JSON.stringify(saved));
        this._renderSaveStatus();
        /* Now that projectId is ready, kick spreads + any queued individual photo uploads */
        this._kickBackgroundUploads();
        this._drainPendingPhotoUploads();
        /* On a new device IDB is empty — restore photos + slots from server now that we have a projectId */
        if (!this.state.uploads.length && !this._pendingPhotoUploads.length) {
          this._loadFromServer();
        }
      } catch(e) { this._initPending = false; this._renderSaveStatus(); }
      } finally { this._initInProgress = false; }
      })());
    }

    /* Save a photo to IDB; queue the original file for upload to Drive once a
       projectId is available. Upload itself goes through _queuePhotoUpload
       (bounded concurrency + retry) rather than firing directly — never blocks
       or throws past this point, so callers can fire-and-forget as before. */
    async _savePhotoBackground(file, name, assetKey) {
      const up = this.state.uploads.find(u => u.assetKey === assetKey);
      try {
        if (up?.idbId == null) {
          const idbId = await this.store.add(file, name, assetKey, up?.sourceWidth, up?.sourceHeight, this._scope, up?.fingerprint || null, up?.originalFileSize || null);
          /* Back-fill idbId onto the upload entry — find by stable assetKey, not index */
          if (up) {
            up.idbId = idbId;
            /* Write slot map now that we have an idbId — _placeSlot skipped this because
               idbId was null when it was first called from _readFiles (async race). */
            const upUrls = [up.url, up.originalUrl, up.previewUrl].filter(Boolean);
            const map = this._readSlotMap();
            let changed = false;
            for (const [slotKey, slotUrl] of Object.entries(this.state.slotImages)) {
              if (upUrls.includes(slotUrl)) { map[slotKey] = idbId; changed = true; }
            }
            if (changed) this._writeSlotMap(map);
          }
        }
      } catch(e) {}
      if (!this.state.projectId) {
        /* projectId not ready yet — queue by assetKey so deletion before init is safe */
        this._pendingPhotoUploads.push({ file, assetKey });
        return;
      }
      this._queuePhotoUpload(file, name, assetKey);
    }

    /* Upload any individual photo files that were queued before the projectId was ready. */
    _drainPendingPhotoUploads() {
      const pending = this._pendingPhotoUploads.splice(0);
      for (const { file, assetKey } of pending) {
        /* Skip photos that were deleted while the upload was queued */
        const up = this.state.uploads.find(u => u.assetKey === assetKey);
        if (!up) continue;
        this._queuePhotoUpload(file, up.name, assetKey);
      }
    }

    /* Add a photo to the bounded-concurrency upload queue. Safe to call repeatedly
       for the same assetKey — a photo already queued, uploading, or backing off
       is not duplicated. This is the single entry point for pushing a source
       photo to Drive; every caller (new file, drained-pending, reconciliation
       re-upload) routes through here so every attempt gets the same retry. */
    _queuePhotoUpload(file, name, assetKey) {
      if (this._photoUploadKeysPending.has(assetKey)) return;
      this._photoUploadKeysPending.add(assetKey);
      this._photoUploadQueue.push({ file, name, assetKey, attempts: 0 });
      this._pumpPhotoQueue();
    }

    /* Fill free concurrency slots from the queue. Each slot runs independently —
       one photo backing off does not hold up others waiting behind it. */
    _pumpPhotoQueue() {
      while (this._photoUploadActive < this._photoUploadConcurrency && this._photoUploadQueue.length) {
        const item = this._photoUploadQueue.shift();
        this._photoUploadActive++;
        this._uploadPhotoAttempt(item).finally(() => {
          this._photoUploadActive--;
          this._pumpPhotoQueue();
        });
      }
    }

    /* One upload attempt for a queued photo. On failure, schedules a backoff retry
       (5s→10s→20s→40s, capped 60s) without holding a concurrency slot during the
       wait. After 5 consecutive failures, parks the item and surfaces a visible
       "Couldn't save — Retry" status instead of failing silently forever. */
    async _uploadPhotoAttempt(item) {
      const { file, assetKey } = item;
      if (!this.state.projectId || !this.state.uploads.find(u => u.assetKey === assetKey)) {
        /* projectId lost (scope switch) or photo removed while queued — drop it */
        this._photoUploadKeysPending.delete(assetKey);
        return;
      }
      try {
        const uploadOrder = this.state.uploads.findIndex(u => u.assetKey === assetKey);
        await uploadBlob(this.state.projectId, file, Math.max(0, uploadOrder), assetKey);
        this._photoUploadKeysPending.delete(assetKey);
        this._photosConfirmedOnServer++;
        this._saveLocalStyle({ uploadedCount: this.state.uploads.length });
        this._renderSaveStatus();
      } catch(e) {
        item.attempts++;
        if (item.attempts >= 5) {
          this._photoUploadStuck = true;
          this._stuckPhotoUploads.push(item);
          this._photoUploadKeysPending.delete(assetKey);
          this._renderSaveStatus();
          return; /* stop auto-retrying — wait for the user to click Retry */
        }
        const delay = Math.min(5000 * Math.pow(2, item.attempts - 1), 60_000);
        setTimeout(() => {
          /* Re-check the photo still exists before re-queueing after the wait */
          if (!this.state.uploads.find(u => u.assetKey === assetKey)) { this._photoUploadKeysPending.delete(assetKey); return; }
          this._photoUploadQueue.push(item);
          this._pumpPhotoQueue();
        }, delay);
      }
    }

    /* Re-queue every parked photo after the user clicks Retry on the stuck-upload status. */
    _retryStuckPhotoUploads() {
      const items = this._stuckPhotoUploads.splice(0);
      this._photoUploadStuck = false;
      for (const item of items) {
        item.attempts = 0;
        this._photoUploadKeysPending.add(item.assetKey);
        this._photoUploadQueue.push(item);
      }
      this._renderSaveStatus();
      this._pumpPhotoQueue();
    }

    async _spreadRenderFingerprint(i, dpi = 300, checkoutSnapshot = null) {
      const view = this.VIEWS[i];
      const snap = checkoutSnapshot || null;
      const def = this.layoutDef(i, snap?.layouts || this.state.layouts);
      const dims = this._formatDims();
      const assignments = snap?.slotAssignments || this._buildSlotAssignments();
      const slots = def.slots.map((_slot, slotIdx) => {
        const key = `s${i}:${slotIdx}`;
        const assetKey = assignments[key] || null;
        const upload = assetKey ? (snap?.uploadsByAssetKey?.[assetKey] || this.state.uploads.find(item => item.assetKey === assetKey)) : null;
        return {
          key,
          assetKey,
          sourceFingerprint: upload?.fingerprint || null,
          sourceSize: upload?.originalFileSize || null,
          transform: (snap?.slotTransforms || this.state.slotTransforms)[key] || null,
        };
      });
      const text = (def.textZones || []).map(zone => ({
        key: zone.key,
        value: (snap?.textContent || this.state.textContent)[`${i}:${zone.key}`] || '',
        format: (snap?.textFormat || this.state.textFormat)[`${i}:${zone.key}`] || {},
      }));
      return _sha256({
        renderVersion: PRINT_RENDER_VERSION,
        renderEnvironment: _renderEnvironment(),
        kind: view.kind,
        index: i,
        dpi,
        quality: PRINT_Q,
        canvas: {
          width: Math.round(dims.trimW * dpi) * (view.kind === 'spread' ? 2 : 1),
          height: Math.round(dims.trimH * dpi),
        },
        format: dims,
        layoutId: (snap?.layouts || this.state.layouts)?.[i] || null,
        layoutDefinition: { slots: def.slots, textZones: def.textZones || [] },
        /* Text boxes without their own font print in the book's font. */
        ...((def.textZones || []).length ? { textDefaultFamily: snap?.fontFamily || this.font().family } : {}),
        background: (snap?.spreadBg || this.state.spreadBg)[i] || INTERIOR_PAPER_COLOR,
        slots,
        text,
      });
    }

    async _coverRenderFingerprint(pageWpx, pageHpx) {
      const sourceKey = this.state.coverSourceAssetKey || null;
      const upload = sourceKey ? this.state.uploads.find(item => item.assetKey === sourceKey) : null;
      return _sha256({
        renderVersion: PRINT_RENDER_VERSION,
        renderEnvironment: _renderEnvironment(),
        kind: 'cover', dpi: 300, quality: PRINT_Q,
        pageWpx, pageHpx, wrapIn: COVER_WRAP_IN, spineWidthIn: SPINE_WIDTH_IN,
        sourceKey,
        sourceFingerprint: upload?.fingerprint || null,
        sourceUrl: sourceKey === 'coverPhoto' ? (this.state.coverPhoto || null) : null,
        coverRemoved: this.state.coverRemoved,
        coverTransform: this.state.coverTransform,
        coverLayout: this._coverLayout(),
        showCoverText: this.state.showCoverText,
        showSpineText: this.state.showSpineText,
        showBackLogo: this.state.showBackLogo,
        title: this.state.title,
        subtitle: this.state.subtitle,
        spineText: this.state.spineText,
        clothId: this.state.clothId,
        foilId: this.state.foilId,
        fontId: this.state.fontId,
        fontFamily: this.font().family,
        /* Only when not regular, so fonts at 400 keep their existing fingerprint
           and only covers whose weight changed are rendered again. */
        ...(this._coverFontWeight() !== 400 ? { fontWeight: this._coverFontWeight() } : {}),
        ...(this._emptyTextMark() ? { emptyText: this._emptyTextMark() } : {}),
        ...(this._textFitMark() ? { textFit: this._textFitMark() } : {}),
        ...(this._digitsMark() ? { digits: this._digitsMark() } : {}),
        ...(this._spineMark() ? { spine: this._spineMark() } : {}),
        coverStyle: COVER_STYLE,
        clothHex: this.cloth().hex,
        stampColor: this._stampColor(),
      });
    }

    _printStateSnapshot() {
      return {
        title: this.state.title,
        subtitle: this.state.subtitle,
        spineText: this.state.spineText,
        clothId: this.state.clothId,
        foilId: this.state.foilId,
        fontId: this.state.fontId,
        layouts: this.state.layouts,
        captions: this.state.captions,
        spreadBg: this.state.spreadBg,
        coverLayout: this.state.coverLayout,
        showBackLogo: this.state.showBackLogo,
        showCoverText: this.state.showCoverText,
        showSpineText: this.state.showSpineText,
        spineDir: this.state.spineDir,
        coverTransform: this.state.coverTransform,
        coverPhoto: this.state.coverPhoto,
        coverSourceAssetKey: this.state.coverSourceAssetKey,
        coverRemoved: this.state.coverRemoved,
        slotAssignments: this._buildSlotAssignments(),
        slotTransforms: this.state.slotTransforms,
        textContent: this.state.textContent,
        textFormat: this.state.textFormat,
        spreadModes: this.state.spreadModes || {},
        spreadCount: this.VIEWS.length,
      };
    }

    /* Upload all dirty spread views in background at final 300 DPI (sequential,
       lowest priority). The server records the complete render fingerprint so a
       checkout revision may reuse only byte-equivalent artwork. */
    async _kickBackgroundUploads() {
      if (this._kicking || this._restoring || this._previewBuilding || !this.state.projectId) return;
      /* Keep the cover current alongside the spreads. Deliberately before the
         dirtyViews early-return: cover-only edits (cloth, foil, typeface, title)
         never mark a spread dirty, so hanging this off that return would leave
         the cover stale exactly when only the cover changed. Fire-and-forget —
         it has its own guards and must not hold up spread uploads. */
      this._kickCoverUpload();
      /* Restore dirty views that were persisted before a page kill (iOS, crash, forced close).
         Runs once per session after projectId is confirmed. */
      if (!this._dirtyViewsRestored) {
        this._dirtyViewsRestored = true;
        try {
          const _dv = JSON.parse(localStorage.getItem('oiDirtyViews:' + this._scope) || '[]');
          if (Array.isArray(_dv)) _dv.forEach(i => { if (Number.isFinite(i)) this.state.dirtyViews.add(i); });
        } catch(_e) {}
      }
      /* One-time re-upload: drawfit-v2 (2026-08-25) fixed canvas pan at scale=1 so crop
         positions are rendered correctly. Re-mark all filled spreads dirty on first run
         so existing Drive files get overwritten with the corrected crop. */
      try {
        const _migKey = 'oi_dfv2:' + this._scope;
        if (!localStorage.getItem(_migKey) && Object.keys(this.state.slotImages).length) {
          for (let _mi = 0; _mi < this.VIEWS.length; _mi++) {
            const _mdef = this.layoutDef(_mi);
            if (_mdef.slots.some((_, _msi) => this.state.slotImages[`s${_mi}:${_msi}`] || this.state.slotBlobs[`s${_mi}:${_msi}`])) {
              this.state.dirtyViews.add(_mi);
            }
          }
          localStorage.setItem(_migKey, '1');
          this._saveDirtyViews();
        }
      } catch(_e) {}
      if (!this.state.dirtyViews.size) return;
      /* Background uploads at 200 DPI overwrite Drive files — any saved cart ref is now stale */
      try { localStorage.removeItem('oi_cart_ref:' + this._scope); } catch(_e) {}
      this._kicking = true;
      this._kickTotal = this._savedCount + this.state.dirtyViews.size;
      this._renderSaveStatus();
      const _kickScope = this._scope, _kickEpoch = this._epoch;
      let _consecutiveFailures = 0;
      try {
        for (const i of [...this.state.dirtyViews].sort()) {
          if (!this.state.dirtyViews.has(i)) continue;
          if (this._scope !== _kickScope || this._epoch !== _kickEpoch) break;
          if (this._preflighting) break; /* cart flow owns uploads — stop background */
          if (this._previewBuilding) break; /* yield the CPU to the book preview; resumed when it is built */
          const v   = this.VIEWS[i];
          const def = this.layoutDef(i);
          const isWide = v.kind === 'spread';
          const d = this._formatDims();
          const DPI = 300;
          const pageWpx   = Math.round(d.trimW * DPI);
          const pageHpx   = Math.round(d.trimH * DPI);
          const spreadWpx = isWide ? pageWpx * 2 : pageWpx;
          const slotImages = { ...this.state.slotImages };
          const slotBlobs  = { ...this.state.slotBlobs };
          const projectId  = this.state.projectId;
          this.state.dirtyViews.delete(i);
          this._saveDirtyViews();
          const _assetKey   = `spread:${String(i + 1).padStart(3, '0')}`;
          const _hasContent = def.slots.some((_, _si) => slotImages[`s${i}:${_si}`] || slotBlobs[`s${i}:${_si}`])
            || def.textZones?.some(zone => this.state.textContent[`${i}:${zone.key}`]);
          /* Fingerprint includes crop, zoom, text formatting, background, geometry,
             source identity, quality and renderer version. */
          let _thisFp = '';
          if (_hasContent) {
            _thisFp = await this._spreadRenderFingerprint(i, DPI);
            try { if (localStorage.getItem(`oi_sfp:${projectId}:${i}`) === _thisFp) { this._renderSaveStatus(); continue; } } catch(_e) {}
          }
          /* Cleared spread: previously uploaded but now empty → upload blank white page */
          if (!_hasContent) {
            let _prevKeys; try { _prevKeys = JSON.parse(localStorage.getItem('oi_spread_keys:' + this._scope) || '[]'); } catch(_e) { _prevKeys = []; }
            if (!_prevKeys.includes(_assetKey)) { this._renderSaveStatus(); continue; }
          }
          try {
            const slotTransforms = { ...this.state.slotTransforms };
            let blob;
            if (!_hasContent) {
              const _bc = document.createElement('canvas');
              _bc.width = spreadWpx; _bc.height = pageHpx;
              const _bctx = _bc.getContext('2d');
              applySpreadBackground(_bctx, this.state.spreadBg[i], spreadWpx, pageHpx);
              blob = await new Promise(r => _bc.toBlob(r, 'image/jpeg', PRINT_Q));
            } else {
              blob = await renderSpreadFromSlots(i, def.slots, slotImages, isWide, spreadWpx, pageHpx, PRINT_Q, slotBlobs, slotTransforms, def.textZones, this.state.textContent, this.state.textFormat, this.state.spreadBg[i] || INTERIOR_PAPER_COLOR, null, false, this.font().family);
            }
            let _releaseActive;
            this._activeBackgroundUpload = new Promise(resolve => { _releaseActive = resolve; });
            try {
              await uploadBlob(projectId, blob, i, _assetKey, DPI, _thisFp || await this._spreadRenderFingerprint(i, DPI));
            } finally {
              _releaseActive();
              this._activeBackgroundUpload = null;
            }
            this._savedCount++;
            _consecutiveFailures = 0;
            this._uploadStuck = false;
            if (_hasContent && _thisFp) try { localStorage.setItem(`oi_sfp:${projectId}:${i}`, _thisFp); } catch(_e) {}
            try { const _ks = new Set(JSON.parse(localStorage.getItem('oi_spread_keys:' + this._scope) || '[]')); _ks.add(_assetKey); localStorage.setItem('oi_spread_keys:' + this._scope, JSON.stringify([..._ks])); } catch(_e) {}
            this._renderSaveStatus();
          } catch(e) {
            this.state.dirtyViews.add(i); /* re-mark if upload fails */
            this._saveDirtyViews();
            _consecutiveFailures++;
            /* Exponential backoff: 5 s, 10 s, 20 s, 40 s — cap at 60 s. Stop after 5 consecutive fails. */
            if (_consecutiveFailures >= 5) { this._uploadStuck = true; break; }
            await new Promise(r => setTimeout(r, Math.min(5000 * Math.pow(2, _consecutiveFailures - 1), 60_000)));
          }
        }
      } finally {
        this._kicking = false;
        this._renderSaveStatus();
        this._autoSaveDraft();
        if (!this.state.dirtyViews.size) {
          try {
            const _pp = JSON.parse(localStorage.getItem('oiCraftPricing:' + this._scope) || '{}');
            if (_pp.format) { _pp.lastPrerenderedFormat = _pp.format; localStorage.setItem('oiCraftPricing:' + this._scope, JSON.stringify(_pp)); }
          } catch(_e) {}
        }
      }
      /* Re-kick if dirty views were added while the loop was awaiting uploads.
         Skip if preflighting (cart flow owns) or stuck (waiting for user Retry). */
      if (this.state.dirtyViews.size && this.state.projectId && !this._preflighting && !this._uploadStuck) this._kickBackgroundUploads();
    }

    /* Render and upload the cover in the background, the way spreads already are.
       Until now the cover was the one asset produced ONLY at checkout, so every
       cover fault — a source that would not restore, a font that was not ready,
       a decode that went wrong — first surfaced as a failed Add to Cart. Doing it
       while the customer edits means faults appear next to the thing that caused
       them, and checkout has nothing of its own left to upload.

       Fingerprint-cached in the same shape as oi_sfp, so an unchanged cover costs
       one hash. Never throws: a background failure leaves the cached fingerprint
       unset, so the next kick retries and checkout still renders it if needed. */
    async _kickCoverUpload() {
      if (this._coverKicking || this._preflighting || this._restoring) return;
      if (!this.state.projectId) return;
      if (!(window.__oiCustomer?.id || window.__oiSession?.authenticated)) return;

      const projectId = this.state.projectId;
      const d = this._formatDims();
      const pageWpx = Math.round(d.trimW * 300), pageHpx = Math.round(d.trimH * 300);

      /* Same durability rule preflight enforces: a chosen cover photo that cannot
         be restored must not become a valid-looking cloth-only cover. Skip quietly
         here — preflight is still the gate that reports it. */
      const srcKey = this.state.coverSourceAssetKey;
      const coverUp = srcKey ? this.state.uploads.find(u => u.assetKey === srcKey) : null;
      const fallback = srcKey === 'coverPhoto' ? this.state.coverPhoto : null;
      if (!this.state.coverRemoved && srcKey && !coverUp?.file && !this.state.coverImage && !fallback) return;

      this._coverKicking = true;
      let srcUrl = null, srcTemp = false;
      try {
        const fp = await this._coverRenderFingerprint(pageWpx, pageHpx);
        try { if (localStorage.getItem(`oi_cfp:${projectId}`) === fp) return; } catch (_) {}

        if (coverUp?.file) { srcUrl = URL.createObjectURL(coverUp.file); srcTemp = true; }
        else srcUrl = this.state.coverImage || fallback || null;

        const blob = await renderCoverWrapBlob(srcUrl, {
          clothHex:       this.cloth().hex,
          title:          this.state.title,
          subtitle:       this.state.subtitle,
          spineText:      this.state.showSpineText ? this.state.spineText : '',
          spineDir:       this.state.spineDir,
          stampColor:     this._stampColor(),
          fontFamily:     this.font().family,
          fontWeight:     this._coverFontWeight(),
          pageWpx, pageHpx,
          wrapIn:         COVER_WRAP_IN,
          spineWidthIn:   SPINE_WIDTH_IN,
          dpi:            300,
          quality:        PRINT_Q,
          coverLayout:    this._coverLayout(),
          showCoverText:  this.state.showCoverText,
          showBackLogo:   this.state.showBackLogo,
          coverTransform: this.state.coverTransform,
        });
        /* Preflight may have started while this was rendering; it owns uploads. */
        if (this._preflighting || this.state.projectId !== projectId) return;
        await uploadBlob(projectId, blob, 0, 'cover', 300, fp);

        try {
          const thumb = await coverFrontThumbBlob(blob, 400, pageWpx, pageHpx,
            Math.round(COVER_WRAP_IN * 300), Math.round(SPINE_WIDTH_IN * 300));
          if (!this._preflighting) await uploadBlob(projectId, thumb, 0, 'cover:thumb');
        } catch (_) { /* thumbnail is cosmetic — never fail the cover for it */ }

        try { localStorage.setItem(`oi_cfp:${projectId}`, fp); } catch (_) {}
        this._renderSaveStatus();
      } catch (e) {
        console.warn('[OI] background cover render failed, will retry:', e?.message || e);
      } finally {
        if (srcTemp && srcUrl) URL.revokeObjectURL(srcUrl);
        this._coverKicking = false;
      }
    }

    /* Stable fingerprint of everything that goes into the cover render.
       Used to detect whether the cover needs to be re-uploaded at checkout. */
    _coverKey() {
      return [
        this.state.coverSourceAssetKey || '',
        this.state.coverRemoved ? '1' : '0',
        this.state.title        || '',
        this.state.subtitle     || '',
        this.state.spineText    || '',
        this.state.clothId      || '',
        this.state.foilId       || '',
        (this.state.fontId || '') + (this._coverFontWeight() !== 400 ? '@' + this._coverFontWeight() : ''),
        this.state.coverLayout  || '',
        this.state.showBackLogo ? '1' : '0',
        COVER_STYLE,
        JSON.stringify(this.state.coverTransform || {}),
        this.state.showCoverText ? '1' : '0',
        this.state.showSpineText ? '1' : '0',
        ...(this._emptyTextMark() ? [this._emptyTextMark()] : []),
        ...(this._textFitMark() ? [this._textFitMark()] : []),
        ...(this._digitsMark() ? [this._digitsMark()] : []),
        ...(this._spineMark() ? [this._spineMark()] : []),
      ].join('|');
    }

    /* Render + upload the current spread in background if it is dirty. */
    _saveDirtySpread() {
      if (this.state.view !== 'spread') return;
      if (this._preflighting) return; /* cart flow owns uploads — don't race at 200 DPI */
      const i = this.state.spreadIdx;
      if (!this.state.dirtyViews.has(i) || !this.state.projectId) return;
      const projectId = this.state.projectId;
      const v   = this.VIEWS[i];
      const def = this.layoutDef(i);
      const isWide = v.kind === 'spread';
      const d      = this._formatDims();
      const DPI    = 300;
      const pageWpx   = Math.round(d.trimW * DPI);
      const pageHpx   = Math.round(d.trimH * DPI);
      const spreadWpx = isWide ? pageWpx * 2 : pageWpx;
      const slotImages = { ...this.state.slotImages };
      const slotBlobs  = { ...this.state.slotBlobs };
      this.state.dirtyViews.delete(i);
      Promise.all([
        renderSpreadFromSlots(i, def.slots, slotImages, isWide, spreadWpx, pageHpx, PRINT_Q, slotBlobs, this.state.slotTransforms, def.textZones, this.state.textContent, this.state.textFormat, this.state.spreadBg[i] || INTERIOR_PAPER_COLOR, null, false, this.font().family),
        this._spreadRenderFingerprint(i, DPI),
      ])
        .then(([blob, fingerprint]) => {
          /* Preflight may have started while the render was in-flight; abort here instead
             of uploading a 200 DPI duplicate that races the 300 DPI cart render. */
          if (this._preflighting) { this.state.dirtyViews.add(i); return; }
          return uploadBlob(projectId, blob, i, `spread:${String(i + 1).padStart(3, '0')}`, DPI, fingerprint);
        })
        .then(res => { if (res !== undefined) { this._savedCount++; this._renderSaveStatus(); } })
        .catch(() => { this.state.dirtyViews.add(i); }); /* re-mark if upload fails */
    }

    /* ── Cart ── */
    /* ── Preview book ──
       Shows the customer the whole book as it will print, as a page-turning
       book (assets/oi-flipbook.js, the same viewer as the order preview), so
       they can order with confidence. Every page is drawn by the checkout
       renderers themselves — renderCoverWrapBlob and renderSpreadFromSlots,
       called with the same arguments _preflight uses — only at screen
       resolution instead of 300 DPI. Both work in proportions of the canvas,
       so framing, text and backgrounds match the print file. Display only:
       nothing is uploaded, saved or marked dirty. */
    _initBookPreview() {
      this.root.querySelectorAll('[data-book-preview-btn]').forEach(btn => {
        btn.addEventListener('click', () => this._openBookPreview(btn));
      });
      /* My Books → Preview links here with ?resume=<id>&preview=1. Open the preview
         at once, but only draw it after the book has fully loaded: rendering while
         photos are still coming back from the cloud would show empty pages. The flag
         is dropped from the address so a refresh lands in the editor. */
      const params = new URLSearchParams(location.search);
      if (params.get('preview') === '1') {
        params.delete('preview');
        const qs = params.toString();
        try { history.replaceState(history.state, '', location.pathname + (qs ? '?' + qs : '') + location.hash); } catch (_) {}
        this._openBookPreview(null, { waitForBook: true });
      }
    }

    /* Resolves once the book's photos and saved state are in: the initial load, then
       any server restore it or _initProject started (they can follow each other). */
    async _waitForBookLoaded(timeoutMs = 90000) {
      const t0 = Date.now();
      try { await this._photosLoaded; } catch (_) {}
      let quietSince = 0;
      while (Date.now() - t0 < timeoutMs) {
        const busy = this._loadingFromServer || this._restoring;
        if (busy) quietSince = 0;
        else if (!quietSince) quietSince = Date.now();
        else if (Date.now() - quietSince > 800) return true;
        await new Promise(r => setTimeout(r, 150));
      }
      return false;
    }

    _loadFlipbookViewer() {
      if (window.OIFlipbook) return Promise.resolve();
      if (this._flipbookLoad) return this._flipbookLoad;
      const src = window.__oiFlipbookAssets?.viewer;
      if (!src) return Promise.reject(new Error('Preview is not available on this page.'));
      this._flipbookLoad = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = src; s.onload = resolve;
        s.onerror = () => { this._flipbookLoad = null; reject(new Error('Preview could not load — check your connection.')); };
        document.head.appendChild(s);
      });
      return this._flipbookLoad;
    }

    async _openBookPreview(btn, { waitForBook = false } = {}) {
      if (this._bookPreview && !this._bookPreview.isClosed()) return;
      if (btn) btn.disabled = true;
      try { await this._loadFlipbookViewer(); }
      catch (e) {
        if (btn) btn.disabled = false;
        window.__oiRemovePreviewBoot?.();   /* the My Books cover screen, if any */
        this._showUploadError?.(e.message);
        return;
      }
      if (btn) btn.disabled = false;

      const d = this._formatDims();
      /* Enough for a sharp full-screen page on a retina screen, a fraction of print. */
      const DPI = window.matchMedia('(pointer: coarse)').matches ? 110 : 150;
      const pageW = Math.round(d.trimW * DPI), pageH = Math.round(d.trimH * DPI);
      const fb = window.OIFlipbook.open({
        title: this.state.title || 'Your book',
        subtitle: 'Preview · exactly as it will print',
        pageAspect: pageW / pageH,
        pageFlipSrc: window.__oiFlipbookAssets.pageFlip,
        note: 'This is your book as it stands right now. Close the preview to keep editing.',
      });
      this._bookPreview = fb;
      /* The real overlay is up: drop the cover screen painted for My Books → Preview. */
      window.__oiRemovePreviewBoot?.();
      /* Background saving re-renders edited pages at 300 DPI; running it at the
         same time made each preview page take up to ~3 s. Pause it while the
         preview is built (dirty pages stay queued) and resume straight after. */
      this._previewBuilding = true;

      try {
        if (waitForBook) {
          fb.progress(0, 1, 'Opening your book…');
          await this._waitForBookLoaded();
          if (fb.isClosed()) return;
          /* Title and format come from the restored book, not the defaults at open. */
          const d2 = this._formatDims();
          if (d2.trimW !== d.trimW || d2.trimH !== d.trimH) {
            fb.close();
            this._previewBuilding = false;
            return this._openBookPreview(null);
          }
          fb.setTitle?.(this.state.title || 'Your book');
        }
        const snap  = this._createCheckoutSnapshot();
        const total = this.VIEWS.length + 1;
        let done = 0;
        const tick = () => fb.progress(++done, total);
        fb.progress(0, total);

        /* Front and back covers, cut from the same wrap the printer receives. */
        let front = null, back = null;
        {
          const coverUp  = this.state.uploads.find(u => u.assetKey === this.state.coverSourceAssetKey);
          let coverSrc = this.state.coverImage || null, temp = false;
          if (coverUp?.file) { coverSrc = URL.createObjectURL(coverUp.file); temp = true; }
          try {
            const wrapBlob = await renderCoverWrapBlob(coverSrc, {
              clothHex:       this.cloth().hex,
              title:          this.state.title,
              subtitle:       this.state.subtitle,
              spineText:      this.state.showSpineText ? this.state.spineText : '',
              spineDir:       this.state.spineDir,
              stampColor:     this._stampColor(),
              fontFamily:     this.font().family,
              fontWeight:     this._coverFontWeight(),
              pageWpx: pageW, pageHpx: pageH,
              wrapIn:         COVER_WRAP_IN,
              spineWidthIn:   SPINE_WIDTH_IN,
              dpi:            DPI,
              quality:        0.9,
              coverLayout:    this._coverLayout(),
              showCoverText:  this.state.showCoverText,
              showBackLogo:   this.state.showBackLogo,
              coverTransform: this.state.coverTransform,
            });
            const bm = await createImageBitmap(wrapBlob);
            const wrapPx = Math.round(COVER_WRAP_IN * DPI), spinePx = Math.round(SPINE_WIDTH_IN * DPI);
            front = this._previewCrop(bm, wrapPx + pageW + spinePx, wrapPx, pageW, pageH);
            back  = this._previewCrop(bm, wrapPx, wrapPx, pageW, pageH);
            bm.close();
          } catch (e) {
            console.warn('[OI preview] cover', e?.message || e);
            front = this._previewProblemPage(pageW, pageH, 'The cover could not be drawn', e?.message);
          } finally {
            if (temp && coverSrc) URL.revokeObjectURL(coverSrc);
          }
        }
        tick();
        if (fb.isClosed()) return;

        /* Screen resolution doesn't need the original files: slotImages already
           holds each photo's 1600px preview (same pixels, scaled, upright), and
           decoding the 12 MP originals made the preview take ~17 s on desktop.
           The original is used only where there is no preview URL. */
        /* A book restored from the cloud is another story: its slots point at the
           full-size originals (previews are generated later, in the background), and
           drawing those decoded each one at full size — ~42 s for a 10-photo book.
           Per slot: the 1600px preview if it exists by now; otherwise hand the
           renderer the original file, which it decodes straight to the small size it
           needs (_decodeForDraw) instead of at full resolution. */
        const byUrl = new Map();
        for (const up of this.state.uploads || []) {
          if (up.originalUrl) byUrl.set(up.originalUrl, up);
          if (up.url) byUrl.set(up.url, up);
        }
        const previewImages = {}, previewBlobs = {};
        for (const [k, url] of Object.entries(snap.slotImages || {})) {
          const up = url ? byUrl.get(url) : null;
          if (up?.previewUrl && up.previewUrl !== url && up.previewUrl !== up.originalUrl) previewImages[k] = up.previewUrl;
          else {
            previewImages[k] = url;
            if (url && up && url === up.originalUrl && snap.slotBlobs?.[k]) previewBlobs[k] = snap.slotBlobs[k];
          }
        }
        for (const [k, b] of Object.entries(snap.slotBlobs || {})) if (!snap.slotImages[k]) previewBlobs[k] = b;

        /* Interior pages, in print order. Rendered one at a time to keep memory low. */
        const interior = [];   /* { canvas, num, wide: true|false } in order */
        for (let i = 0; i < this.VIEWS.length; i++) {
          if (fb.isClosed()) return;
          const v = this.VIEWS[i];
          const isWide = v.kind === 'spread';
          const def = this.layoutDef(i, snap.layouts);
          const nums = v.pages || [];
          try {
            const blob = await renderSpreadFromSlots(
              i, def.slots, previewImages, isWide,
              isWide ? pageW * 2 : pageW, pageH, 0.9,
              previewBlobs, snap.slotTransforms,
              def.textZones, snap.textContent, snap.textFormat,
              snap.spreadBg[i] || INTERIOR_PAPER_COLOR, null, false, snap.fontFamily || this.font().family
            );
            const bm = await createImageBitmap(blob);
            if (isWide) {
              interior.push({ canvas: this._previewCrop(bm, 0, 0, pageW, pageH), num: nums[0] ?? null, wide: true });
              interior.push({ canvas: this._previewCrop(bm, pageW, 0, pageW, pageH), num: nums[1] ?? null, wide: true, second: true });
            } else {
              interior.push({ canvas: this._previewCrop(bm, 0, 0, pageW, pageH), num: nums[0] ?? null, wide: false });
            }
            bm.close();
          } catch (e) {
            /* Show the problem where it is instead of failing the whole preview —
               it is exactly what the customer needs to fix before ordering. */
            const label = nums.length >= 2 ? `Pages ${nums[0]}–${nums[1]}` : `Page ${nums[0] ?? i + 1}`;
            const msg = e?.message || 'This page could not be drawn.';
            if (isWide) {
              interior.push({ canvas: this._previewProblemPage(pageW, pageH, label, msg), num: nums[0] ?? null, wide: true });
              interior.push({ canvas: this._previewProblemPage(pageW, pageH, label, ''), num: nums[1] ?? null, wide: true, second: true });
            } else {
              interior.push({ canvas: this._previewProblemPage(pageW, pageH, label, msg), num: nums[0] ?? null, wide: false });
            }
          }
          tick();
        }
        if (fb.isClosed()) return;

        /* Lay out like the printed book: front cover alone on the right, then
           pairs. A single first page faces an endpaper; every spread opens
           across the fold; the back cover closes the book on the left. */
        const pages = [{ canvas: front || window.OIFlipbook.endpaper(pageW, pageH, false), num: null }];
        interior.forEach((p, k) => {
          if (p.wide && !p.second && pages.length % 2 === 0) pages.push({ canvas: window.OIFlipbook.endpaper(pageW, pageH, false), num: null });
          if (!p.wide && k === 0 && pages.length % 2 === 1) pages.push({ canvas: window.OIFlipbook.endpaper(pageW, pageH, true), num: null });
          pages.push({ canvas: p.canvas, num: p.num });
        });
        if (pages.length % 2 === 0) pages.push({ canvas: window.OIFlipbook.endpaper(pageW, pageH, false), num: null });
        pages.push({ canvas: back || window.OIFlipbook.endpaper(pageW, pageH, true), num: null });

        await fb.show({ pages, pageW, pageH });
      } catch (e) {
        console.error('[OI preview]', e);
        fb.fail('The preview could not be prepared. Please try again.');
      } finally {
        this._previewBuilding = false;
        this._kickBackgroundUploads();
      }
    }

    _previewCrop(src, sx, sy, w, h) {
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      c.getContext('2d').drawImage(src, sx, sy, w, h, 0, 0, w, h);
      return c;
    }

    /* A page that could not be drawn, saying why, so it is fixed before ordering. */
    _previewProblemPage(w, h, heading, message) {
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#F6F1E7'; ctx.fillRect(0, 0, w, h);
      ctx.strokeStyle = 'rgba(176,58,46,0.55)'; ctx.lineWidth = Math.max(2, w * 0.004);
      ctx.setLineDash([w * 0.02, w * 0.015]);
      ctx.strokeRect(w * 0.06, h * 0.06, w * 0.88, h * 0.88);
      ctx.setLineDash([]);
      ctx.fillStyle = '#9E3B2F'; ctx.textAlign = 'center'; ctx.textBaseline = 'top';
      ctx.font = `600 ${Math.round(h * 0.04)}px 'Work Sans', sans-serif`;
      ctx.fillText(heading, w / 2, h * 0.38);
      if (message) {
        ctx.fillStyle = '#4A3F36';
        ctx.font = `400 ${Math.round(h * 0.03)}px 'Work Sans', sans-serif`;
        const words = String(message).split(/\s+/), lines = [];
        let line = '';
        for (const word of words) {
          const t = line ? line + ' ' + word : word;
          if (ctx.measureText(t).width > w * 0.76 && line) { lines.push(line); line = word; } else line = t;
        }
        if (line) lines.push(line);
        lines.slice(0, 6).forEach((ln, k) => ctx.fillText(ln, w / 2, h * 0.46 + k * h * 0.045));
      }
      return c;
    }

    _initCart() {
      const { cartBtn } = this.el;
      if (cartBtn) cartBtn.addEventListener('click', () => this._handleCart());

      /* Reset button and preflighting when the page is restored from the browser's
         back-forward cache (user navigated to /checkout then hit Back). Without this,
         the button stays disabled and _preflighting stays true from the last flow. */
      window.addEventListener('pageshow', e => {
        if (!e.persisted) return;
        this._preflighting = false;
        this._kicking = false;
        this._uploadStuck = false;
        if (cartBtn) {
          cartBtn.disabled = false;
          cartBtn.textContent = 'Add to cart';
          cartBtn.style.background = '';
        }
        if (typeof _oiUploadOverlay !== 'undefined') _oiUploadOverlay.hide();
        /* Re-kick uploads when page is restored from bfcache — state in memory
           is still good; this ensures any mid-upload that was frozen gets retried. */
        this._kickBackgroundUploads();
        if (this._stuckPhotoUploads.length) this._retryStuckPhotoUploads();
      });
      /* Persist dirty views before the page is killed or backgrounded by iOS.
         pagehide fires more reliably than beforeunload on mobile browsers. */
      window.addEventListener('pagehide', () => { this._saveDirtyViews(); }, { passive: true });
    }

    /* Returns the customer's saved phone number, showing the gate sheet if they
       don't have one yet. Resolves only after a number is saved — never rejects.
       Caches the result on window.__oiCustomer so subsequent cart clicks are instant. */
    _collectPhone() {
      if (window.__oiCustomer?._phone) return Promise.resolve(window.__oiCustomer._phone);

      return new Promise(resolve => {
        const gate  = document.getElementById('oi-phone-gate');
        const input = document.getElementById('oi-phone-input');
        const wrap  = document.getElementById('oi-phone-input-wrap');
        const err   = document.getElementById('oi-phone-err');
        const btn   = document.getElementById('oi-phone-save');

        /* Reset and open */
        input.value = '';
        err.textContent = '';
        btn.disabled = true;
        btn.textContent = 'Save & Add to Cart';
        wrap.classList.remove('err');
        gate.classList.add('open');
        setTimeout(() => input.focus(), 420);

        const onInput = () => {
          // Allow +, digits, spaces, dashes, parens — strip everything else
          let v = input.value.replace(/[^\d\s+\-()]/g, '');
          // Strip leading whitespace before checking + position (space before + would wrongly remove it)
          v = v.replace(/^\s+/, '');
          // Keep + only at the very start
          if (v.indexOf('+') > 0) v = v.replace(/\+/g, '');
          else if ((v.match(/\+/g) || []).length > 1) v = '+' + v.replace(/\+/g, '');
          input.value = v;
          wrap.classList.remove('err');
          err.textContent = '';
          btn.disabled = (v.replace(/\D/g, '').length < 7);
        };

        const closeGate = () => {
          gate.classList.remove('open');
          input.removeEventListener('input', onInput);
          document.removeEventListener('keydown', onKeyDown);
          btn.onclick = null;
        };

        const submit = async () => {
          const phone = input.value.replace(/[\s\-()]/g, '').trim();
          if (!/^\+[1-9]\d{6,14}$/.test(phone)) {
            wrap.classList.add('err');
            err.textContent = 'Enter your number with country code, e.g. +91 98765 43210';
            return;
          }
          btn.disabled = true;
          btn.textContent = 'Saving…';
          try {
            const phoneAc = new AbortController();
            const phoneT  = setTimeout(() => phoneAc.abort(), 10_000);
            const r = await fetch(`${WORKER_URL}/api/customer/phone`, {
              method: 'POST', credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ phone }),
              signal: phoneAc.signal,
            }).finally(() => clearTimeout(phoneT));
            if (r.status === 401) throw new Error('auth_expired');
            if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || 'save_failed'); }
            if (window.__oiCustomer) window.__oiCustomer._phone = phone;
            closeGate();
            resolve(phone);
          } catch(e) {
            btn.disabled = false;
            btn.textContent = 'Save & Add to Cart';
            err.textContent = e.name === 'AbortError'       ? 'Request timed out — check your connection.'
                            : e.message === 'auth_expired'  ? 'Session expired — please sign out and back in.'
                            : (e.message && e.message !== 'save_failed') ? e.message
                            : 'Could not save — please check your connection.';
          }
        };

        /* Enter key submits, Escape cancels the cart flow entirely */
        const onKeyDown = e => {
          if (e.key === 'Enter'  && !btn.disabled) { submit(); return; }
          if (e.key === 'Escape') { closeGate(); resolve(null); }
        };
        input.addEventListener('input', onInput);
        document.addEventListener('keydown', onKeyDown);
        btn.onclick = submit;
      });
    }

    /* Runs checkout-start + cover render/upload + spread renders/uploads in the background
       while the customer enters their phone number in the gate.
       signal: AbortSignal — call _pfAc.abort() to cancel if the customer bails early. */
    async _preflight(cartProjectId, pageWpx, pageHpx, signal, checkoutSnapshot = null) {
      clearTimeout(this._autoSaveTimer);
      await this._validateCheckoutText();
      await this._flushDraft({ required: true });
      if (signal.aborted) return;
      /* Render from a frozen map, never from the editor's live image maps.
         Mobile restores and object-URL cleanup can otherwise change those maps
         after the preview was approved but before this asynchronous render. */
      const renderSnapshot = checkoutSnapshot || this._createCheckoutSnapshot();
      const snapshotMissing = this._findIncompletePhotoSlots(renderSnapshot);
      if (snapshotMissing.length) {
        const error = new Error(this._incompletePhotoMessage(snapshotMissing));
        error.viewIdx = snapshotMissing[0].viewIdx;
        throw error;
      }
      if (this._activeBackgroundUpload) await this._activeBackgroundUpload.catch(() => {});

      /* A non-empty source key means the customer chose a photo. Never turn a missing
         source into a valid-looking cloth-only cover: that is exactly how a blank
         _FINAL cover escaped for order 1025 after a cross-session resume. */
      const expectedCoverKey = this.state.coverSourceAssetKey;
      const coverUpload = this.state.uploads.find(u => u.assetKey === expectedCoverKey);
      const recoverableDefault = expectedCoverKey === 'coverPhoto' ? this.state.coverPhoto : null;
      if (!this.state.coverRemoved && expectedCoverKey && !coverUpload?.file && !this.state.coverImage && !recoverableDefault) {
        throw new Error('Your cover photo could not be restored. Please return to the cover and choose the photo again.');
      }
      const prog = (pct, lbl) => {
        this._preflightProgress = { pct, lbl };
        _oiUploadOverlay.update(pct, lbl);
      };
      /* Content sanity-check warnings collected while rendering print files below.
         Never blocks checkout — only flagged on the order for a human to review
         (see props['_Preflight_Warnings'] in _handleCart). Reset per attempt. */
      this._preflightWarnings = [];

      const expectedAssetFingerprints = { cover: await this._coverRenderFingerprint(pageWpx, pageHpx) };
      for (let i = 0; i < this.VIEWS.length; i++) {
        expectedAssetFingerprints[`spread:${String(i + 1).padStart(3, '0')}`] = await this._spreadRenderFingerprint(i, 300, renderSnapshot);
      }

      /* checkout-start — bumps revision so uploads from prior attempts don't satisfy finalize */
      prog(0, 'Preparing your book…');
      const bumpAc = new AbortController();
      /* 90s, not 15s: checkout-start copies every reusable page in Drive (4 at a
         time) before replying, so a book with 20+ already-rendered pages took
         ~15s and the client aborted it — "Could not start checkout" on every
         retry, getting worse as more pages became reusable. */
      const bumpT  = setTimeout(() => bumpAc.abort(), 90_000);
      signal.addEventListener('abort', () => bumpAc.abort(), { once: true });
      const bumpRes = await fetch(`${WORKER_URL}/api/book-projects/${cartProjectId}/checkout-start`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ spreadCount: this.VIEWS.length, expectedAssets: expectedAssetFingerprints }),
        signal: bumpAc.signal,
      }).finally(() => clearTimeout(bumpT)).catch(() => ({ ok: false }));
      if (signal.aborted) return;
      if (!bumpRes.ok) throw new Error('Could not start checkout — please try again.');

      /* Cover */
      prog(3, 'Saving cover…');
      const bumpBody = await bumpRes.json().catch(() => ({}));
      const reusedAssets = new Set(Array.isArray(bumpBody.reusedAssetKeys) ? bumpBody.reusedAssetKeys : []);
      if (!reusedAssets.has('cover')) {
        const coverUp   = this.state.uploads.find(u => u.assetKey === this.state.coverSourceAssetKey);
        const coverFile = coverUp?.file || null;
        let coverSrc = this.state.coverImage || null;
        let coverSrcTemp = false;
        if (coverFile) { coverSrc = URL.createObjectURL(coverFile); coverSrcTemp = true; }
        try {
          const spineOn         = this.state.showSpineText;
          const cartCoverLayout = this._coverLayout();
          const showCoverText   = this.state.showCoverText;
          /* Retried as one unit, like the spreads. The cover is rendered before
             them, so an un-retried blip here aborts the entire checkout before a
             single page is uploaded — which is consistent with order 1027's two
             failed attempts, both of which have spreads in D1 but no cover. */
          const coverBlob = await _retryRender(async () => {
            const b = await renderCoverWrapBlob(coverSrc, {
              clothHex:      this.cloth().hex,
              title:         this.state.title,
              subtitle:      this.state.subtitle,
              spineText:     spineOn ? this.state.spineText : '',
              spineDir:      this.state.spineDir,
              stampColor:    this._stampColor(),
              fontFamily:    this.font().family,
              fontWeight:    this._coverFontWeight(),
              pageWpx, pageHpx,
              wrapIn:        COVER_WRAP_IN,
              spineWidthIn:  SPINE_WIDTH_IN,
              dpi:           300,
              quality:       PRINT_Q,
              coverLayout:     cartCoverLayout,
              showCoverText:   showCoverText,
              showBackLogo:    this.state.showBackLogo,
              coverTransform:  this.state.coverTransform,
            });
            if (signal.aborted) throw new Error('aborted');
            await uploadBlob(cartProjectId, b, 0, 'cover', 300, expectedAssetFingerprints.cover);
            return b;
          }, { signal, label: 'cover' });
          if (signal.aborted) return;
          try {
            const thumbBlob = await coverFrontThumbBlob(
              coverBlob, 400, pageWpx, pageHpx,
              Math.round(COVER_WRAP_IN * 300), Math.round(SPINE_WIDTH_IN * 300)
            );
            if (!signal.aborted) await uploadBlob(cartProjectId, thumbBlob, 0, 'cover:thumb');
            /* Also cache as data URL so the cart page shows the preview instantly on this session */
            const reader = new FileReader();
            const dataUrl = await new Promise((res, rej) => { reader.onload = () => res(reader.result); reader.onerror = rej; reader.readAsDataURL(thumbBlob); });
            localStorage.setItem('oi_cover_preview', dataUrl);
          } catch(e) {}
        } finally {
          if (coverSrcTemp && coverSrc) URL.revokeObjectURL(coverSrc);
        }
      }

      /* Spreads */
      const failedSpreads = [];
      const emptySpreadErrors = [];
      const _totalPg = this.VIEWS.filter(v => v.kind === 'spread').length * 2;
      for (let i = 0; i < this.VIEWS.length; i++) {
        if (signal.aborted) return;
        try {
          const _v   = this.VIEWS[i];
          const _pg  = _v?.pages?.length >= 2 ? `${_v.pages[0]}–${_v.pages[1]}` : String(i + 1);
          const _lbl = `Saving pages ${_pg} of ${_totalPg}…`;
          prog(10 + Math.round((i / this.VIEWS.length) * 84), _lbl);
          const _assetKey = `spread:${String(i + 1).padStart(3, '0')}`;
          const def    = this.layoutDef(i, renderSnapshot.layouts);
          const isWide = _v.kind === 'spread';
          /* Empty-content check must come BEFORE the reuse check. The background
             loop uploads a blank page for a spread that was cleared, and that
             blank render carries a valid fingerprint — so letting reuse
             short-circuit first would carry the blank forward and skip this
             block entirely. _findIncompletePhotoSlots catches empty photo slots
             earlier, but a text-only layout has no slots, so this is the only
             guard standing between an empty text page and checkout. */
          const _hasContentPf = def.slots.some((_, _si) => renderSnapshot.slotImages[`s${i}:${_si}`] || renderSnapshot.slotBlobs[`s${i}:${_si}`])
            || (def.textZones && def.textZones.some(tz => renderSnapshot.textContent[`${i}:${tz.key}`]));
          if (!_hasContentPf) {
            this.state.dirtyViews.delete(i);
            emptySpreadErrors.push(_v?.pages?.length >= 2 ? `pages ${_v.pages[0]}–${_v.pages[1]}` : `page ${_v.pages?.[0] ?? i + 1}`);
            continue;
          }
          if (reusedAssets.has(_assetKey)) {
            this.state.dirtyViews.delete(i);
            prog(10 + Math.round(((i + 1) / this.VIEWS.length) * 84), _lbl);
            continue;
          }
          /* Render AND upload together inside the retry: a failed upload needs a
             fresh blob, and re-rendering also re-decodes the source, which is
             what recovers a decode that went wrong under memory pressure.
             Warnings are collected per attempt and rolled back on failure so a
             retried page cannot report its warnings twice. */
          const _warnMark = this._preflightWarnings.length;
          await _retryRender(async () => {
            this._preflightWarnings.length = _warnMark;
            const blob = await renderSpreadFromSlots(
              i, def.slots, renderSnapshot.slotImages, isWide,
              isWide ? pageWpx * 2 : pageWpx, pageHpx, PRINT_Q,
              renderSnapshot.slotBlobs, renderSnapshot.slotTransforms,
              def.textZones, renderSnapshot.textContent, renderSnapshot.textFormat,
              renderSnapshot.spreadBg[i] || INTERIOR_PAPER_COLOR,
              this._preflightWarnings, false, renderSnapshot.fontFamily || this.font().family
            );
            if (signal.aborted) throw new Error('aborted');
            await uploadBlob(cartProjectId, blob, i, _assetKey, 300, expectedAssetFingerprints[_assetKey]);
          }, { signal, label: `spread ${_pg}` });
          if (signal.aborted) return;
          prog(10 + Math.round(((i + 1) / this.VIEWS.length) * 84), _lbl);
          this.state.dirtyViews.delete(i);
        } catch(spreadErr) {
          if (signal.aborted) return;
          const _v = this.VIEWS[i];
          failedSpreads.push(_v?.pages?.length >= 2 ? `Pages ${_v.pages[0]}–${_v.pages[1]}` : `Page ${_v.pages?.[0] ?? i + 1}`);
          console.error('[OI preflight] spread', i, spreadErr?.message || spreadErr);
        }
      }
      if (emptySpreadErrors.length) {
        throw new Error(`Please add photos to ${emptySpreadErrors.join(', ')} before checking out.`);
      }
      if (failedSpreads.length) {
        const _allFailed = failedSpreads.length >= this.VIEWS.length - 1;
        throw new Error(_allFailed
          ? 'Pages could not be saved — please check your connection and try again.'
          : `Some pages could not be saved (${failedSpreads.join(', ')}) — please try again.`);
      }

      /* All 300 DPI uploads succeeded — save a cart reference so the next Add to Cart
         can skip checkout-start and re-upload if nothing has changed. */
      try {
        this.state.dirtyViews.clear();
        localStorage.setItem('oi_cart_ref:' + this._scope, JSON.stringify({
          p: cartProjectId,
          n: this.VIEWS.length,
          c: this._coverKey(),
          t: this._checkoutTextKey(),
        }));
      } catch(_e) {}
      this._preflightExpectedAssetFingerprints = expectedAssetFingerprints;
    }

    _buildCheckoutUrl(phone) {
      const p = new URLSearchParams();
      if (phone) p.set('checkout[shipping_address][phone]', phone);
      const email = window.__oiCustomer?.email;
      if (email) p.set('checkout[email]', email);
      const qs = p.toString();
      return qs ? `/checkout?${qs}` : '/checkout';
    }

    _showFormatPicker(onConfirm) {
      const _old = document.getElementById('oi-fmt-pick');
      if (_old) _old.remove();
      const variants = window.OI_VARIANTS || [];

      /* Build unique paper and finish options from live variants */
      const paperSeen = new Map(); /* adder → first opt2 label */
      const finishSet = new Set();
      variants.forEach(v => {
        const n = _normalizePricingFields(v.opt1, v.opt2);
        if (!paperSeen.has(n.adder)) paperSeen.set(n.adder, v.opt2);
        finishSet.add(v.opt3);
      });

      /* Read existing pricing; format is locked to whatever the project already has */
      let cur = {};
      try { cur = JSON.parse(localStorage.getItem('oiCraftPricing:' + this._scope) || '{}'); } catch(e) {}
      const curNorm   = _normalizePricingFields(cur.format, cur.material);
      /* If format is blank, _formatDims() defaults the canvas to square — match that */
      const lockedFmt = curNorm.canonFormat || 'square';
      const curFinish = cur.finish || 'Matte';

      const PAPER_LABELS = { 0:'Premium Paper', 300:'Non-Tearable Paper (+₹300)' };
      const sel = (cond) => cond ? ' selected' : '';

      const paperOpts = [...paperSeen.entries()].map(([adder, lbl]) =>
        `<option value="${adder}"${sel(adder === curNorm.adder)}>${PAPER_LABELS[adder] || lbl}</option>`).join('');
      const finishOpts = [...finishSet].map(f =>
        `<option value="${f}"${sel(f === curFinish)}>${f}</option>`).join('');

      const SEL_CSS = "width:100%;padding:9px 12px;background:rgba(243,238,228,0.06);border:1px solid rgba(243,238,228,0.15);border-radius:4px;color:rgba(243,238,228,0.85);font-family:'Work Sans',sans-serif;font-size:13px;cursor:pointer;outline:none";
      const LBL_CSS = "display:block;font-size:10px;letter-spacing:1.8px;text-transform:uppercase;color:rgba(243,238,228,0.45);margin-bottom:5px;font-family:'Work Sans',sans-serif";

      const overlay = document.createElement('div');
      overlay.id = 'oi-fmt-pick';
      overlay.style.cssText = 'position:fixed;inset:0;background:rgba(15,20,28,0.75);backdrop-filter:blur(6px);z-index:9999;display:flex;align-items:center;justify-content:center;padding:20px;box-sizing:border-box';
      overlay.innerHTML = `<div style="background:#1A2433;border:1px solid rgba(243,238,228,0.12);border-radius:8px;padding:28px;width:100%;max-width:340px;display:flex;flex-direction:column;gap:16px">
        <div>
          <p style="margin:0 0 6px;font-size:10px;letter-spacing:2.4px;text-transform:uppercase;color:rgba(243,238,228,0.45);font-family:'Work Sans',sans-serif">Almost there</p>
          <p style="margin:0;font-size:14px;color:rgba(243,238,228,0.85);font-family:'Work Sans',sans-serif;line-height:1.5">Choose your paper type and finish to complete your order.</p>
        </div>
        <div><span style="${LBL_CSS}">Paper type</span><select id="oi-fp-ppr" style="${SEL_CSS}">${paperOpts}</select></div>
        <div><span style="${LBL_CSS}">Finish</span><select id="oi-fp-fin" style="${SEL_CSS}">${finishOpts}</select></div>
        <p id="oi-fp-err" style="display:none;margin:0;font-size:12px;color:#e07070;font-family:'Work Sans',sans-serif"></p>
        <div style="display:flex;gap:10px;margin-top:4px">
          <button id="oi-fp-cancel" style="flex:0 0 auto;padding:10px 16px;background:transparent;border:1px solid rgba(243,238,228,0.2);border-radius:3px;color:rgba(243,238,228,0.55);font-family:'Work Sans',sans-serif;font-size:11px;letter-spacing:1.6px;text-transform:uppercase;cursor:pointer">Cancel</button>
          <button id="oi-fp-ok" style="flex:1;padding:10px 18px;background:#C9A55F;border:none;border-radius:3px;color:#191510;font-family:'Work Sans',sans-serif;font-size:11px;letter-spacing:2px;text-transform:uppercase;cursor:pointer;font-weight:500">Confirm &amp; continue</button>
        </div>
      </div>`;
      document.body.appendChild(overlay);

      overlay.querySelector('#oi-fp-cancel').onclick = () => overlay.remove();
      overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });

      overlay.querySelector('#oi-fp-ok').onclick = () => {
        const adder  = parseInt(overlay.querySelector('#oi-fp-ppr').value, 10);
        const finish = overlay.querySelector('#oi-fp-fin').value;
        const vt = (window.OI_VARIANTS || []).find(v => {
          const n = _normalizePricingFields(v.opt1, v.opt2);
          return n.canonFormat === lockedFmt && n.adder === adder && v.opt3 === finish;
        });
        const errEl = overlay.querySelector('#oi-fp-err');
        if (!vt) {
          errEl.textContent = 'This combination is not available — please choose a different option.';
          errEl.style.display = '';
          return;
        }
        const norm = _normalizePricingFields(vt.opt1, vt.opt2);
        let p = {};
        try { p = JSON.parse(localStorage.getItem('oiCraftPricing:' + this._scope) || '{}'); } catch(e) {}
        /* Preserve existing format — only fill it in if it was genuinely blank */
        if (!p.format) p.format = norm.canonFormat;
        p.base     = norm.base;
        p.adder    = norm.adder;
        p.material = vt.opt2;
        p.finish   = finish;
        p.variantId = vt.id;
        try { localStorage.setItem('oiCraftPricing:' + this._scope, JSON.stringify(p)); } catch(e) {}
        overlay.remove();
        onConfirm();
      };
    }


    async _validateCheckoutText() {
      const d = this._formatDims();
      const h = Math.round(d.trimH * 300);
      for (let i = 0; i < this.VIEWS.length; i++) {
        if (this.state.spreadModes?.[i] === 'collage') continue;
        const def = this.layoutDef(i);
        if (!def.textZones?.length) continue;
        for (const zone of def.textZones) {
          const key = `${i}:${zone.key}`;
          if (!this.state.textContent[key]) continue;
          const fmt = this.state.textFormat[key] || {};
          if (!fmt.family) this.state.textFormat[key] = { ...fmt, family: this.font().family };
        }
        const wide = this.VIEWS[i].kind === 'spread';
        try {
          await renderSpreadFromSlots(i, [], {}, wide, Math.round(d.trimW * 300) * (wide ? 2 : 1),
            h, PRINT_Q, {}, {}, def.textZones, this.state.textContent, this.state.textFormat, null, null, true, this.font().family);
        } catch (e) {
          e.message = this.viewLabel(i) + ': ' + e.message;
          e.textViewIdx = i;
          throw e;
        }
      }
    }

    _checkoutTextKey() {
      return JSON.stringify({
        textContent: this.state.textContent, textFormat: this.state.textFormat,
        title: this.state.title, subtitle: this.state.subtitle, spineText: this.state.spineText,
      });
    }

    /* Freeze the exact photo inputs used for checkout before the phone gate and
       other async work begin. The editor preview is DOM-driven, while the print
       renderer is canvas-driven; using the live maps after an async restore
       meant a photo could still be visible in the preview but absent when the
       canvas drew. Keep original File/Blob references when available so an
       object URL being revoked later cannot turn an approved slot into a blank
       print page. */
    _createCheckoutSnapshot() {
      const slotImages = { ...this.state.slotImages };
      const slotBlobs = { ...this.state.slotBlobs };
      const slotAssignments = this._buildSlotAssignments();
      const uploadsByAssetKey = {};
      for (const upload of this.state.uploads || []) {
        if (upload?.assetKey) uploadsByAssetKey[upload.assetKey] = upload;
      }
      for (const [slotKey, assetKey] of Object.entries(slotAssignments)) {
        const upload = uploadsByAssetKey[assetKey];
        if (!slotBlobs[slotKey] && upload?.file) slotBlobs[slotKey] = upload.file;
        if (!slotImages[slotKey] && upload) slotImages[slotKey] = upload.originalUrl || upload.url || null;
      }
      const clone = value => {
        try { return JSON.parse(JSON.stringify(value || {})); } catch (_) { return { ...(value || {}) }; }
      };
      return {
        slotImages, slotBlobs, slotAssignments, uploadsByAssetKey,
        slotTransforms: clone(this.state.slotTransforms),
        layouts:       clone(this.state.layouts),
        textContent:    clone(this.state.textContent),
        textFormat:     clone(this.state.textFormat),
        spreadBg:       clone(this.state.spreadBg),
        fontFamily:     this.font().family,   /* default for text boxes with no font of their own */
      };
    }

    _findIncompletePhotoSlots(checkoutSnapshot = null) {
      const missing = [];
      const slotImages = checkoutSnapshot?.slotImages || this.state.slotImages;
      const slotBlobs  = checkoutSnapshot?.slotBlobs  || this.state.slotBlobs;
      const layouts    = checkoutSnapshot?.layouts    || this.state.layouts;
      for (let i = 0; i < this.VIEWS.length; i++) {
        const view = this.VIEWS[i];
        const def = this.layoutDef(i, layouts);
        def.slots.forEach((slot, slotIdx) => {
          const key = `s${i}:${slotIdx}`;
          if (slotImages[key] || slotBlobs[key]) return;
          let pages = Array.isArray(view?.pages) ? view.pages.slice() : [i + 1];
          if (view?.kind === 'spread' && pages.length >= 2) {
            const left = Number(slot.x) || 0;
            const right = left + (Number(slot.w) || 0);
            if (right <= 0.5) pages = [pages[0]];
            else if (left >= 0.5) pages = [pages[1]];
          }
          missing.push({ viewIdx: i, slotIdx, key, pages });
        });
      }
      return missing;
    }

    /* Checks that can block checkout but need no rendering, so they belong with
       the empty-slot and text-overflow guards that already run before the
       overlay opens. Both of these used to fire from inside _preflight — after
       the saving animation had started — which meant a customer with an
       unrestorable cover or an unloaded font watched a progress ring before
       being told something they could have been told instantly.

       Returns an error message, or null when there is nothing to report. */
    async _fastFailChecks() {
      /* Cover source. A non-empty source key means the customer chose a photo;
         a missing one must never quietly become a cloth-only cover, which is how
         a blank _FINAL cover escaped on order 1025. Pure state inspection. */
      const key = this.state.coverSourceAssetKey;
      const up  = key ? this.state.uploads.find(u => u.assetKey === key) : null;
      const fallback = key === 'coverPhoto' ? this.state.coverPhoto : null;
      if (!this.state.coverRemoved && key && !up?.file && !this.state.coverImage && !fallback) {
        return { message: 'Your cover photo could not be restored. Please return to the cover and choose the photo again.',
                 goCover: true };
      }

      /* Fonts. The export refuses to render fallback type, so a face that has
         not loaded fails the render anyway — better to say so before the ring
         appears than after. Only the faces this book actually uses. */
      const families = new Set([this.font().family]);
      for (const k of Object.keys(this.state.textFormat || {})) {
        const fam = this.state.textFormat[k]?.family;
        if (fam && this.state.textContent?.[k]) families.add(fam);
      }
      try {
        await _requireCanvasFonts([...families]);
      } catch (e) {
        return { message: e?.message || 'A font is still loading. Please try again in a moment.' };
      }

      return null;
    }

    _incompletePhotoMessage(missing) {
      const pages = [...new Set(missing.flatMap(item => item.pages))].sort((a, b) => Number(a) - Number(b));
      const pageText = pages.length === 1 ? `page ${pages[0]}` : `pages ${pages.join(', ')}`;
      const slotText = missing.length === 1 ? '1 photo slot is empty' : `${missing.length} photo slots are empty`;
      return `${slotText} on ${pageText}. Please add the missing photo${missing.length === 1 ? '' : 's'} before adding to cart.`;
    }

    async _handleCart() {
      if (this._preflighting) return; /* prevent concurrent flows from double-click */
      this._preflighting = true; /* block background auto-saves immediately; reset on every exit path */
      const { cartBtn, cartErr } = this.el;
      const showErr = msg => {
        if (cartErr) { cartErr.textContent = msg; cartErr.style.display = ''; }
        console.error('[OI cart]', msg);
        /* data-cart-err is inside .oi2-right which is display:none on mobile — toast is always visible */
        let toast = document.getElementById('oi-cart-err-toast');
        if (!toast) {
          toast = document.createElement('div');
          toast.id = 'oi-cart-err-toast';
          toast.style.cssText = 'position:fixed;top:68px;right:16px;background:#1c150c;border:1px solid rgba(224,112,112,.35);' +
            'color:#e07070;font-size:12px;letter-spacing:.3px;padding:10px 16px;border-radius:4px;z-index:9999;' +
            'max-width:300px;line-height:1.5;box-shadow:0 8px 24px rgba(0,0,0,.45);display:none';
          document.body.appendChild(toast);
        }
        toast.textContent = msg;
        toast.style.display = 'block';
        clearTimeout(toast._hideTimer);
        toast._hideTimer = setTimeout(() => { toast.style.display = 'none'; }, 6000);
      };
      if (cartErr) cartErr.style.display = 'none';

      const isSignedIn = !!(window.__oiCustomer?.id || window.__oiSession?.authenticated);
      if (!isSignedIn) {
        this._preflighting = false;
        showErr('Please sign in before adding to cart.');
        return;
      }

      /* Fail fast before phone checks, project creation, rendering, or uploads. */
      const incompleteSlots = this._findIncompletePhotoSlots();
      if (incompleteSlots.length) {
        this._preflighting = false;
        this._goSpread(incompleteSlots[0].viewIdx);
        this.state.activeSlot = incompleteSlots[0].key;
        this._renderSpread();
        this._renderRight();
        showErr(this._incompletePhotoMessage(incompleteSlots));
        return;
      }

      clearTimeout(this._autoSaveTimer);
      try { await this._validateCheckoutText(); }
      catch (e) {
        if (Number.isInteger(e.textViewIdx)) this._goSpread(e.textViewIdx);
        this._preflighting = false;
        showErr(e.message);
        return;
      }

      /* Cover source and font readiness — both knowable without rendering, so
         they are reported here rather than from inside _preflight, where the
         customer would already be watching the saving animation. */
      const _fastFail = await this._fastFailChecks();
      if (_fastFail) {
        if (_fastFail.goCover) this._goCover();
        this._preflighting = false;
        showErr(_fastFail.message);
        return;
      }

      /* Early format guard — show inline picker before any async work if variant unresolvable */
      if ((window.OI_VARIANTS || []).length) {
        let _ep = {};
        try { _ep = JSON.parse(localStorage.getItem('oiCraftPricing:' + this._scope) || '{}'); } catch(e) {}
        const _en = _normalizePricingFields(_ep.format, _ep.material);
        const _ef = _ep.finish || 'Matte';
        const _eHas = !!(_ep.variantId
          || (window.OI_VARIANTS || []).find(v => { const n = _normalizePricingFields(v.opt1, v.opt2); return n.canonFormat === _en.canonFormat && n.adder === _en.adder && v.opt3 === _ef; }));
        if (!_eHas) {
          this._preflighting = false;
          this._showFormatPicker(() => this._handleCart());
          return;
        }
      }

      /* Phone gate — show bottom sheet if customer hasn't saved a WhatsApp number yet.
         Checks D1 first (cached after first check), then shows the sheet if needed.
         Resolves only once the number is confirmed saved — cart cannot proceed without it. */
      if (cartBtn) { cartBtn.disabled = true; cartBtn.textContent = 'Checking…'; cartBtn.style.background = '#7E9A6E'; }
      try {
        /* Check D1 for existing phone (skipped if already cached in memory) */
        if (!window.__oiCustomer?._phone) {
          const pr = await fetch(`${WORKER_URL}/api/customer/phone`, { credentials: 'include' }).catch(() => null);
          if (pr?.ok) {
            const pd = await pr.json().catch(() => ({}));
            if (pd.phone && window.__oiCustomer) window.__oiCustomer._phone = pd.phone;
          }
        }
      } catch(e) { /* network error — fall through, sheet will handle it */ }
      if (cartBtn) { cartBtn.disabled = false; cartBtn.textContent = 'Add to cart'; cartBtn.style.background = ''; }

      /* Read pricing + saved state before the phone gate so the preflight can start immediately */
      let pricing = {};
      try { pricing = JSON.parse(localStorage.getItem('oiCraftPricing:' + this._scope) || '{}'); } catch(e) {}
      let saved = {};
      try { saved = JSON.parse(localStorage.getItem('oiCraft:' + this._scope) || '{}'); } catch(e) {}
      let cartProjectId = this.state.projectId || saved.projectId;

      /* If projectId is not ready yet, wait for _initProject (in-flight or retry a prior
         failure). Show "Preparing…" so the button is disabled and the user sees feedback. */
      if (!cartProjectId) {
        if (cartBtn) { cartBtn.disabled = true; cartBtn.textContent = 'Preparing…'; cartBtn.style.background = '#7E9A6E'; }
        try { await this._initProject(); } catch(_e) {}
        if (cartBtn) { cartBtn.disabled = false; cartBtn.textContent = 'Add to cart'; cartBtn.style.background = ''; }
        /* Scope may have changed (draft → project), re-read both pricing and saved state */
        try { pricing = JSON.parse(localStorage.getItem('oiCraftPricing:' + this._scope) || '{}'); } catch(e) {}
        try { saved   = JSON.parse(localStorage.getItem('oiCraft:'         + this._scope) || '{}'); } catch(e) {}
        cartProjectId = this.state.projectId || saved.projectId;
      }

      /* Skip checkout-start + re-render + re-upload if nothing has changed since the last
         successful 300 DPI cart upload. The cart ref (saved in localStorage by _preflight)
         encodes the project, spread count, and cover fingerprint. All three must match AND
         no spread can be dirty. When the skip fires, _pfPromise stays null and the flow
         goes straight to finalize — which succeeds immediately because the files are already
         in Drive from the previous upload. */
      let _cartRef = null;
      try { _cartRef = JSON.parse(localStorage.getItem('oi_cart_ref:' + this._scope) || 'null'); } catch(e) {}
      let _canSkipPreflight = !!(_cartRef
        && _cartRef.p === cartProjectId
        && _cartRef.n === this.VIEWS.length
        && _cartRef.c === this._coverKey()
        && _cartRef.t === this._checkoutTextKey()
        && this.state.dirtyViews.size === 0);

      /* Hold checkout state until the phone gate is complete. */
      this._preflighting = true;
      this._preflightProgress = { pct: 0, lbl: '' };
      let _pfAc = new AbortController();
      const _pfD  = this._formatDims();
      /* Capture the render inputs before the phone gate. The overlay later
         prevents edits, but this also protects mobile URL/image restoration
         while that gate is open. */
      const _checkoutSnapshot = this._createCheckoutSnapshot();
      const _snapshotMissing = this._findIncompletePhotoSlots(_checkoutSnapshot);
      if (_snapshotMissing.length) {
        _pfAc.abort();
        this._preflighting = false;
        this._goSpread(_snapshotMissing[0].viewIdx);
        this.state.activeSlot = _snapshotMissing[0].key;
        this._renderSpread();
        this._renderRight();
        showErr(this._incompletePhotoMessage(_snapshotMissing));
        return;
      }
      let _pfPromise = null;

      let _phone;
      try { _phone = await this._collectPhone(); }
      catch(e) {
        _pfAc.abort(); if (_pfPromise) _pfPromise.catch(() => {}); this._preflighting = false;
        showErr('An unexpected error occurred. Please refresh the page and try again.');
        if (cartBtn) { cartBtn.disabled = false; cartBtn.textContent = 'Add to cart'; cartBtn.style.background = '#C9A55F'; }
        return;
      }
      if (!_phone) { _pfAc.abort(); if (_pfPromise) _pfPromise.catch(() => {}); this._preflighting = false; return; }

      /* Resolve variantId from scoped pricing only — never use another project's global key.
         Resolution order:
           1. Scoped pricing.variantId (written by selector/craft/server restore)
           2. Live OI_VARIANTS lookup using canonical format + paper tier + finish
           3. One-time import of legacy oi_pv_vid if it matches canonical configuration */
      let variantId = pricing.variantId ? parseInt(pricing.variantId, 10) : 0;
      const _allVariants = window.OI_VARIANTS || [];
      const _pNorm = _normalizePricingFields(pricing.format, pricing.material);
      /* finish may be absent on projects created before this field was persisted;
         default to 'Matte' so the resolve doesn't fail just because finish is blank */
      const _finish = pricing.finish || 'Matte';
      if (!variantId && _allVariants.length) {
        /* Resolve from live variant map */
        const _resolved = _allVariants.find(v => {
          const _vn = _normalizePricingFields(v.opt1, v.opt2);
          return _vn.canonFormat === _pNorm.canonFormat && _vn.adder === _pNorm.adder && v.opt3 === _finish;
        });
        if (_resolved) {
          variantId = _resolved.id;
          pricing.variantId = _resolved.id;
          if (!pricing.finish) pricing.finish = _finish;
          try { localStorage.setItem('oiCraftPricing:' + this._scope, JSON.stringify(pricing)); } catch(e) {}
        }
      }
      if (!variantId) {
        /* One-time legacy import: oi_pv_vid is only valid for this project if it matches
           the canonical format, paper tier, and finish stored in scoped pricing */
        const _legVid = parseInt(localStorage.getItem('oi_pv_vid') || '0', 10);
        if (_legVid && _allVariants.length) {
          const _legVt = _allVariants.find(v => v.id === _legVid);
          if (_legVt) {
            const _lvn = _normalizePricingFields(_legVt.opt1, _legVt.opt2);
            if (_lvn.canonFormat === _pNorm.canonFormat && _lvn.adder === _pNorm.adder && _legVt.opt3 === _finish) {
              variantId = _legVid;
              pricing.variantId = _legVid;
              if (!pricing.finish) pricing.finish = _finish;
              try { localStorage.setItem('oiCraftPricing:' + this._scope, JSON.stringify(pricing)); } catch(e) {}
              try { localStorage.removeItem('oi_pv_vid'); } catch(e) {} /* retire global key */
            }
          }
        }
      }
      if (!variantId) {
        _pfAc.abort(); this._preflighting = false;
        if ((window.OI_VARIANTS || []).length) {
          this._showFormatPicker(() => this._handleCart());
        } else {
          showErr('Format not configured — please <a href="/pages/the-book" style="color:#C9A55F">choose your size and paper</a> before adding to cart.');
        }
        return;
      }

      const sleeves = Math.max(0, this.VIEWS.filter(v => v.kind === 'spread').length - 9);
      /* Extra-page variant: prefer scoped, then live constant, then legacy global */
      const extraVid = (pricing.extraVariantId ? parseInt(pricing.extraVariantId, 10) : 0)
        || (window.OI_EXTRA_VID ? parseInt(window.OI_EXTRA_VID, 10) : 0)
        || parseInt(localStorage.getItem('oi_pv_extra_vid') || '0', 10);
      if (sleeves > 0 && !extraVid) {
        _pfAc.abort(); this._preflighting = false;
        showErr('Extra-page pricing is not configured — please reload the page and try again.');
        return;
      }
      /* Unbranded cover: a paid option, charged as its own line like extra pages */
      const unbranded    = this.state.showBackLogo === false;
      const unbrandedVid = window.OI_UNBRANDED_VID ? parseInt(window.OI_UNBRANDED_VID, 10) : 0;
      if (unbranded && !unbrandedVid) {
        _pfAc.abort(); this._preflighting = false;
        showErr('Unbranded cover pricing is not configured — please reload the page and try again.');
        return;
      }

      const props = {
        'Book Title':        this.state.title      || '—',
        'Subtitle':          this.state.subtitle   || '—',
        'Spine Text':        this.state.spineText  || '—',
        'Cover Colour':      this.cloth().label,
        'Text Colour':       this.foil().label,
        'Typeface':          this.font().label,
        'Format':            ({ square:'8 × 8', landscape:'12 × 8', portrait810:'8 × 10', portrait:'8 × 12', portrait812:'8 × 12' }[pricing.format] || pricing.format || '—'),
        'Paper':             pricing.material      || '—',
        'Finish':            pricing.finish        || '—',
        'Pages':             String(this.VIEWS.filter(v => v.kind === 'spread').length * 2 + 2),
        'Cover Layout':      this._hasCoverText() ? 'Photo with title' : 'Photo, no text',
        'Back Cover Logo':   this.state.showBackLogo ? 'Yes' : 'No',
        '_Preview_URL':      cartProjectId ? `${WORKER_URL}/api/book-projects/${cartProjectId}/cover-preview` : undefined,
      };
      /* Show the full upload overlay only when we're actually uploading.
         When skipping preflight (nothing changed), just update the button. */
      _canSkipPreflight = _canSkipPreflight && _cartRef.t === this._checkoutTextKey() && this.state.dirtyViews.size === 0;
      const _showOverlay = !_canSkipPreflight;
      if (_showOverlay) {
        if (cartBtn) { cartBtn.disabled = true; cartBtn.textContent = 'Saving…'; cartBtn.style.background = '#7E9A6E'; }
        _oiUploadOverlay.show();

        /* The overlay prevents editing while the saved text and print files are captured. */
        if (!_pfPromise && cartProjectId) {
          _pfAc = new AbortController();
          this._preflightProgress = { pct: 0, lbl: '' };
          _pfPromise = this._preflight(cartProjectId, Math.round(_pfD.trimW * 300), Math.round(_pfD.trimH * 300), _pfAc.signal, _checkoutSnapshot);
        }

        /* Sync overlay ring to wherever the preflight has already reached */
        if (this._preflightProgress.pct > 0 || this._preflightProgress.lbl) {
          _oiUploadOverlay.update(this._preflightProgress.pct, this._preflightProgress.lbl);
        }
      } else {
        if (cartBtn) { cartBtn.disabled = true; cartBtn.textContent = 'Finalizing…'; cartBtn.style.background = '#7E9A6E'; }
      }

      if (!cartProjectId) {
        if (_showOverlay) _oiUploadOverlay.hide(); this._preflighting = false;
        showErr('Your upload folder is not ready. Please wait a moment and try again.');
        if (cartBtn) { cartBtn.disabled = false; cartBtn.textContent = 'Add to cart'; cartBtn.style.background = '#C9A55F'; }
        return;
      }
      saved.projectId = cartProjectId;
      props['_Book_Project_ID'] = cartProjectId;
      if (saved.bookRefId) props['Book Ref'] = saved.bookRefId;

      /* Await the acknowledged text save and matching print uploads. */
      if (_pfPromise) {
        try {
          await _pfPromise;
        } catch(e) {
          if (_showOverlay) _oiUploadOverlay.hide(); this._preflighting = false;
          showErr(e.message || 'Your book could not be saved. Please try again.');
          if (Number.isInteger(e.textViewIdx)) this._goSpread(e.textViewIdx);
          if (cartBtn) { cartBtn.disabled = false; cartBtn.textContent = 'Add to cart'; cartBtn.style.background = '#C9A55F'; }
          /* Resume background uploads for any spreads that failed during preflight */
          this._kickBackgroundUploads();
          return;
        }
      }

      /* Flag the order for review if the print-file render caught anything off —
         never blocks checkout, just leaves a trace on the order for staff to check
         before it goes to print (see _preflight's font/overflow sanity checks). */
      if (this._preflightWarnings && this._preflightWarnings.length) {
        props['_Preflight_Warnings'] = this._preflightWarnings.join(' | ').slice(0, 500);
      }

      /* Finalize — retry while server reports assets still propagating to Drive.
         Any non-transient error or timeout throws so the button is re-enabled. */
      let finalizedRevision = null;
      try {
        if (cartBtn) cartBtn.textContent = 'Finalizing…';
        if (_showOverlay) _oiUploadOverlay.update(97, 'Finalizing…');
        let finalized = false;
        for (let attempt = 0; attempt < 12; attempt++) {
          if (attempt > 0) {
            if (cartBtn) cartBtn.textContent = `Finalizing… (${attempt}/11)`;
            await new Promise(r => setTimeout(r, 6000));
          }
          const finAc  = new AbortController();
          const finT   = setTimeout(() => finAc.abort(), 15_000);
          let _pricing = {};
          try { _pricing = JSON.parse(localStorage.getItem('oiCraftPricing:' + this._scope) || '{}'); } catch(_e) {}
          const _printSpec = {
            format:    _pricing.format   || null,
            paper:     _pricing.material || null,
            finish:    _pricing.finish   || null,
            variantId: _pricing.variantId || null,
          };
          const finRes = await fetch(`${WORKER_URL}/api/book-projects/${cartProjectId}/finalize`, {
            method:'POST', credentials:'include',
            headers:{ 'Content-Type':'application/json' },
            /* Declare the fingerprint expected for every print asset so finalize can
               verify the stored files ARE this design, not merely that files exist.
               Omitted when the preflight was skipped (nothing was re-rendered, so
               there is no fresh expectation to assert). */
            body: JSON.stringify({ spreadCount: this.VIEWS.length, printSpec: _printSpec, requireTextBackup: true, requireCoverBackup: true,
              textSnapshot: JSON.parse(this._checkoutTextKey()),
              requirePrintStateBackup: true,
              printStateSnapshot: this._printStateSnapshot(),
              expectedAssets: this._preflightExpectedAssetFingerprints || undefined }),
            signal: finAc.signal,
          }).finally(() => clearTimeout(finT)).catch(e => ({ ok: false, json: async () => ({ error: e.name === 'AbortError' ? 'still uploading' : e.message }) }));
          const finBody = await finRes.json().catch(() => ({}));
          if (finRes.ok) {
            finalizedRevision = Number.parseInt(finBody.revision, 10);
            if (!Number.isInteger(finalizedRevision) || finalizedRevision < 1) {
              throw new Error('Your book was saved, but its print revision could not be verified. Please try again.');
            }
            finalized = true;
            break;
          }
          const finErr  = (finBody.error || '').toLowerCase();
          if (finRes.status === 401) document.dispatchEvent(new CustomEvent('oi:session-expired'));
          if (finErr.includes('still uploading')) continue;
          throw new Error(finBody.error || 'Could not finalize your book. Please try again.');
        }
        if (!finalized) {
          /* Drive files may have been cleaned up — clear the cart ref so the next attempt
             does a full re-upload rather than trying to skip again. */
          try { localStorage.removeItem('oi_cart_ref:' + this._scope); } catch(_e) {}
          throw new Error('Your book could not be finalized — the upload may be taking longer than expected. Please try again.');
        }
      } catch(e) {
        if (_showOverlay) _oiUploadOverlay.hide(); this._preflighting = false;
        showErr(e.message || 'Your book could not be saved. Please try again.');
        if (cartBtn) { cartBtn.disabled = false; cartBtn.textContent = 'Add to cart'; cartBtn.style.background = '#C9A55F'; }
        this._kickBackgroundUploads();
        return;
      }

      /* Bind checkout to the exact immutable revision returned by finalize.
         Private line-item properties are hidden from the customer-facing cart,
         but arrive on the paid-order webhook so fulfilment never has to guess
         between same-named Drive assets from multiple revisions. */
      props['_Book_Revision'] = String(finalizedRevision);
      if (saved.bookRefId) props['_Book_Reference'] = saved.bookRefId;

      /* Enforce one book per checkout.
         - Same project + same verified revision already in cart → go straight to checkout.
         - Same project with an old/missing revision → silently replace it below.
         - Anything else in cart → always clear first.
         Always-clear handles cross-device: if a customer added a book on their phone
         and now checks out on a laptop, Shopify merges their server-side cart after
         the session-local cart.js returns empty — so we must clear unconditionally. */
      try {
        const _cartCheckRes = await fetch('/cart.js');
        if (_cartCheckRes.ok) {
          const _cartData = await _cartCheckRes.json();
          /* The fee line must match the logo choice too, or a removed (or stale)
             unbranded-cover charge would ride straight through to checkout. */
          const _feeLines = (_cartData.items || []).filter(item => item.variant_id === unbrandedVid).reduce((n, item) => n + item.quantity, 0);
          const _alreadyInCart = (_cartData.items || []).some(
            item => item.properties?._Book_Project_ID === cartProjectId
              && String(item.properties?._Book_Revision || '') === String(finalizedRevision)
          ) && _feeLines === (unbranded ? 1 : 0);
          if (_alreadyInCart) {
            if (_showOverlay) _oiUploadOverlay.hide();
            this._preflighting = false;
            window.location.href = this._buildCheckoutUrl(_phone);
            return;
          }
        }
      } catch(_e) {}
      /* Always clear before adding — eliminates any prior books regardless of how
         they got into the cart (same session, different device, Shopify cart merge) */
      await fetch('/cart/clear.js', { method: 'POST' }).catch(() => {});

      const items = [{ id: variantId, quantity: 1, properties: props }];
      if (sleeves > 0 && extraVid) items.push({ id: extraVid, quantity: sleeves * 2, properties: { '_Book_Project_ID': saved.projectId } });
      if (unbranded && unbrandedVid) items.push({ id: unbrandedVid, quantity: 1, properties: { '_Book_Project_ID': saved.projectId } });

      try {
        const res = await fetch('/cart/add.js', {
          method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ items }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.description || body.message || `Cart error (${res.status})`);
        }
        /* Success — show confirmation then go straight to checkout,
           carrying the phone so Shopify pre-fills the shipping address field */
        if (cartBtn) {
          cartBtn.textContent = 'Added to bag ✓';
          cartBtn.style.background = '#5A7A4A';
        }
        if (_showOverlay) {
          await _oiUploadOverlay.finish();
          _oiUploadOverlay.hide();
        }
        window.location.href = this._buildCheckoutUrl(_phone);
      } catch(e) {
        console.error('[OI] cart/add.js error:', e.message);
        if (_showOverlay) _oiUploadOverlay.hide(); this._preflighting = false;
        showErr(e.message);
        if (cartBtn) { cartBtn.disabled = false; cartBtn.textContent = 'Add to cart'; cartBtn.style.background = '#C9A55F'; }
      }
    }
  }

  /* ── Boot ── */
  function init() {
    document.querySelectorAll('[data-oi-book-preview]').forEach(root => {
      if (root.dataset.oiBookPreviewInit) return;
      root.dataset.oiBookPreviewInit = 'true';
      const app = new OiBookPreview(root);
      root._oiPreview = app;
      root.__oiApp = app;
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
